/**
 * THERAPY SPECIALTIES CONFIGURATION
 *
 * This file contains the complete configuration for all therapy specialties available on the platform.
 * It serves as the central source of truth for therapy categories, their metadata, and SEO content.
 *
 * WHAT THIS FILE DOES:
 * - Defines 50+ therapy specialties (anksioznost, depresija, stres, etc.)
 * - Maps URL slugs to comprehensive specialty data including labels, descriptions, and FAQ items
 * - Provides search terms for therapist matching and filtering
 * - Contains SEO metadata (titles, descriptions) for specialty landing pages
 * - Defines relationships between related specialties for cross-linking
 * - Includes detailed FAQ sections with Serbian-language Q&A for each specialty
 * - Supports both individual and couples therapy specialties
 *
 * KEY FEATURES:
 * - All content is in Serbian language for local market
 * - Each specialty includes practical, actionable FAQ answers
 * - Search terms are optimized for therapist discovery and matching
 * - Specialty relationships enable smart recommendations
 * - Consistent structure allows for easy maintenance and expansion
 *
 * USAGE:
 * - Used by specialty landing pages (/terapija/[specialty])
 * - Powers therapist search and filtering functionality
 * - Generates dynamic SEO content for better search visibility
 * - Provides FAQ content for user education and engagement
 * - Enables related specialty suggestions and cross-navigation
 */

// Mapping between URL slugs and search terms for therapy specialties
const THERAPY_SPECIALTIES_CONTENT = {
  // 1
  anksioznost: {
    label: "Anksioznost",
    title: "Online terapeuti za anksioznost",
    searchTerm:
      "anksioznost anksiozni poremecaj anksiozni poremećaj simptomi anksioznosti panicni napadi napadi panike briga nervoza strepnja teskoba tjeskoba nelagoda uznemirenost strah panika",
    therapistSearchTerms: ["anksioznost"],
    description:
      "Tražiš online terapeuta za anksioznost? Na Rešimo Probleme možeš da pregledaš profile terapeuta koji rade sa anksioznošću, uporediš njihov pristup, cenu sesanse i slobodne termine, pa izabereš osobu koja ti odgovara.",
    guideLink: {
      href: "/blog/anksioznost-simptomi-lecenje",
      label:
        "Saznaj više o simptomima anksioznosti i kada je dobro potražiti podršku",
    },
    seoTitle: "Online terapeuti za anksioznost",
    seoDescription:
      "Pronađi online terapeuta za anksioznost. Uporedi profile, pristupe, cene i slobodne termine, pa zakaži sesiju na Rešimo Probleme.",
    relatedConditions: ["panika", "socijalna-anksioznost", "fobije", "stres"],
    directoryContent: {
      title: "Online terapeuti za anksioznost",
      description:
        "Ako te briga, napetost ili stalno iščekivanje iscrpljuju, online razgovor sa terapeutom može biti prvi korak. Na Rešimo Probleme možeš da pregledaš profile terapeuta koji navode rad sa anksioznošću, uporediš njihov pristup, cenu i slobodne termine, pa izabereš osobu za razgovor. Ne moraš unapred da znaš da li je u pitanju anksioznost da bi krenuo/la od onoga što te trenutno opterećuje.",
      seoTitle: "Terapeut za anksioznost online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za anksioznost. Uporedi profile, pristupe, cene i slobodne termine, pa zakaži razgovor koji ti odgovara.",
      faqItems: [
        {
          question: "Kako da pronađem psihologa za anksioznost online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa anksioznošću. Uporedi opis rada, cenu i termine pre zakazivanja.",
        },
        {
          question: "Da li moram unapred da znam da imam anksioznost?",
          answer:
            "Ne moraš. Možeš početi od brige, napetosti, izbegavanja ili telesnih reakcija koje primećuješ, a sa terapeutom razjasniti šta ti je potrebno.",
        },
        {
          question: "Kako da izaberem terapeuta za anksioznost?",
          answer:
            "Uporedi kako terapeuti opisuju svoj rad, koje teme navode, cenu i termine. Na prvom razgovoru možeš da pitaš kako pristupaju brizi, paničnim reakcijama ili situacijama koje izbegavaš.",
        },
        {
          question: "Mogu li da zakažem razgovor ako imam napade panike?",
          answer:
            "Možeš. Pri izboru profila proveri da li terapeut navodi rad sa anksioznošću ili paničnim napadima i na prvom razgovoru objasni šta se dešava. Ako se osećaš neposredno ugroženo, obrati se hitnoj službi.",
        },
      ],
    },
    passes: true,
    faqItems: [
      {
        question: "Kako da izaberem online terapeuta za anksioznost?",
        answer:
          "Pregledaj profil terapeuta, opis njegovog pristupa, cenu sesanse i slobodne termine. Izaberi osobu čiji ti način rada i iskustvo deluju relevantno za ono kroz šta prolaziš.",
      },
      {
        question: "Šta mogu da proverim pre nego što zakažem sesiju?",
        answer:
          "Na profilu terapeuta možeš da vidiš kako predstavlja svoj rad, specijalizacije, cenu sesanse i dostupne termine. Tako možeš da uporediš više opcija pre nego što odlučiš kome želiš da se javiš.",
      },
      {
        question: "Kako izgleda online sesija sa terapeutom?",
        answer:
          "Online sesija se održava putem video poziva u zakazanom terminu. Pre prve sesije možeš da pročitaš profil terapeuta i izabereš vreme koje ti odgovara.",
      },
      {
        question: "Da li moram unapred da znam šta mi je?",
        answer:
          "Ne moraš. Možeš da počneš od onoga što te trenutno opterećuje, a sa terapeutom da istražiš šta ti je potrebno i da li vam njegov pristup odgovara.",
      },
      {
        question: "Šta ako mi prvi terapeut ne odgovara?",
        answer:
          "Možeš da nastaviš pretragu i izabereš drugog terapeuta. Važno je da pronađeš osobu sa kojom se osećaš dovoljno sigurno da otvoreno razgovaraš.",
      },
      {
        question: "Gde mogu da saznam više o simptomima anksioznosti?",
        answer:
          "Za informacije o simptomima i mogućim narednim koracima pročitaj naš vodič o anksioznosti. Ako želiš da pronađeš terapeuta, nastavi sa pregledom profila na ovoj stranici.",
      },
    ],
  },

  // 2
  depresija: {
    label: "Depresija",
    guideLink: {
      href: "/blog/depresija-nije-znak-slabosti-razbijamo-stigmu-i-trazimo-pomoc",
      label: "Pročitaj vodič o depresiji i traženju podrške",
    },
    title: "Online terapija za depresiju: pronađi psihoterapeuta i olakšanje",
    searchTerm:
      "depresija terapija za depresiju online terapija za depresiju psiholog za depresiju psihoterapeut za depresiju simptomi depresije kako prepoznati depresiju depresija lecenje bez lekova anhedonija bezvoljnost umor nesanica poremecaj sna beznadeznost apatija",
    therapistSearchTerms: ["depresija"],
    description:
      "Kada se tuga, praznina ili bezvoljnost zadrže i počnu da utiču na svakodnevno funkcionisanje, moguće je da su prisutni simptomi depresije. Gubitak interesovanja (anhedonija), umor, poremećaj sna, teškoće sa koncentracijom i osećaj beznadežnosti često prate depresiju. Mnogi ljudi pretražuju kako prepoznati depresiju i traže terapiju za depresiju kada osete da su se povukli, izgubili motivaciju ili im je sve teže da se pokrenu.\n\nOnline terapija za depresiju omogućava da se brzo povežeš sa stručnom podrškom, bez obzira na lokaciju. Na Rešimo Probleme možeš pronaći psihologa za depresiju ili psihoterapeuta za depresiju koji radi u pristupima kao što su KBT (kognitivno-bihejvioralna terapija), interpersonalna ili integrativna terapija. Fokus je na razumevanju obrazaca mišljenja, postepenom vraćanju energije, strukturisanju dana i definisanju koraka koji ti pomažu da se osećaš stabilnije.\n\nAko ti je potrebna diskretna i dostupna pomoć, zakazivanje je jednostavno. Izaberi terapeuta i rezerviši online seansu u terminu koji ti odgovara, uz jasne ciljeve i praćenje napretka tokom procesa oporavka.",
    directoryContent: {
      title: "Online terapeuti za depresivna osećanja i teškoće",
      description:
        "Ako se već neko vreme osećaš bezvoljno, iscrpljeno ili povučeno, razgovor sa terapeutom može biti mesto da o tome govoriš. Na Rešimo Probleme možeš da pregledaš profile terapeuta koji navode rad sa depresijom, uporediš pristup, cenu i slobodne termine, pa izabereš osobu za online razgovor. Ne moraš unapred da imaš dijagnozu ni spreman odgovor na sva pitanja da bi počeo/la.",
      seoTitle: "Terapeut za depresiju online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za razgovor o depresivnim osećanjima i teškoćama. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za depresiju online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa depresijom. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li moram unapred da znam da li imam depresiju?",
          answer:
            "Ne moraš. Možeš početi od onoga što ti je teško u svakodnevici, kao što su povlačenje, umor, san ili gubitak interesa, i sa terapeutom razjasniti šta ti je potrebno.",
        },
        {
          question: "Kako da izaberem terapeuta za depresivna osećanja?",
          answer:
            "Pročitaj kako terapeut opisuje svoj rad, koje teme navodi, cenu i termine. Na prvom razgovoru možeš da kažeš šta se promenilo u tvojoj svakodnevici i pitaš kako bi izgledao dalji rad.",
        },
        {
          question: "Kada da uz terapiju potražim i psihijatrijsku pomoć?",
          answer:
            "Ako su simptomi jaki, dugo traju, značajno remete funkcionisanje ili imaš misli o samopovređivanju, važno je da se obratiš psihijatru ili hitnoj službi. Terapeut može biti deo šire podrške, ali ne zamenjuje hitnu ili psihijatrijsku procenu.",
        },
      ],
    },
    seoTitle: "Online terapija za depresiju i psiholog | Rešimo Probleme",
    seoDescription:
      "Zakaži online terapiju za depresiju sa licenciranim psihoterapeutom ili psihologom. Brzo pronađi terapeuta i rezerviši termin.",
    relatedConditions: [
      "postporodjajna-depresija",
      "zalovanje",
      "anksioznost",
      "burnout",
    ],
    passes: true,
    faqItems: [
      {
        question: "Kako prepoznati depresiju i koji su simptomi depresije?",
        answer:
          "Simptomi depresije često uključuju dugotrajnu tugu ili prazninu, bezvoljnost, anhedoniju, umor, poremećaj sna i teškoće sa koncentracijom. Ako primetiš da ovo traje duže od dve nedelje i remeti funkcionisanje, razgovor sa psihologom za depresiju može biti važan prvi korak.",
      },
      {
        question: "Koja je razlika između tuge i depresije?",
        answer:
          "Tuga je obično vezana za konkretan događaj i vremenom slabi, dok depresija traje duže i zahvata energiju, san, motivaciju i svakodnevne obaveze. Zato mnogi koji se pitaju kako prepoznati depresiju traže procenu kod psihoterapeuta za depresiju.",
      },
      {
        question: "Da li je online terapija za depresiju efikasna?",
        answer:
          "Da, online terapija za depresiju može biti efikasna uz jasne ciljeve i redovne seanse. KBT (kognitivno-bihejvioralna terapija) i drugi pristupi se dobro primenjuju online, uz vežbe između susreta i praćenje napretka.",
      },
      {
        question: "Kako izabrati psihoterapeuta ili psihologa za depresiju?",
        answer:
          "Traži psihologa za depresiju ili psihoterapeuta za depresiju koji ima iskustvo sa depresivnim simptomima i koristi strukturisan pristup, kao što je KBT ili interpersonalna terapija. Na Rešimo Probleme možeš uporediti profile i pronaći terapeuta koji ti najviše odgovara.",
      },
      {
        question: "Koliko traje terapija za depresiju i kada se vide pomaci?",
        answer:
          "Terapija za depresiju se često planira u ciklusima od 8 do 20 seansi, u zavisnosti od intenziteta simptoma i ciljeva. Pomaci se mogu primetiti već nakon nekoliko susreta, posebno kada postoji jasan plan i dosledan rad.",
      },
      {
        question: "Da li je moguće lečenje depresije bez lekova?",
        answer:
          "Kod blage do umerene depresije, psihoterapija je često prvi izbor i lečenje depresije bez lekova može biti dovoljno. Ako su simptomi jači ili dugotrajniji, terapeut može predložiti konsultaciju sa psihijatrom za zajednički plan podrške.",
      },
      {
        question: "Šta očekivati na prvoj online seansi za depresiju?",
        answer:
          "Prva online seansa za depresiju obuhvata upoznavanje i procenu simptoma depresije, kao i razgovor o ciljevima. Psihoterapeut predlaže okvir rada i dogovarate naredne korake, uključujući tempo i format online terapije za depresiju.",
      },
    ],
  },

  // 3
  stres: {
    title: "Terapija za hronični stres i iscrpljenost",
    label: "Stres",
    guideLink: {
      href: "/blog/burnout-kako-prepoznati-i-prevladati-iscrpljenost",
      label: "Pročitaj kako prepoznati burnout i iscrpljenost",
    },
    searchTerm:
      "stres napetost pritisak tenzija preopterećenost hronični stres iscrpljenost razdražljivost simptomi stresa stres na poslu upravljanje stresom",
    therapistSearchTerms: ["stres"],
    description:
      "Ako osećaš stalnu napetost, iscrpljenost, loš san ili pad koncentracije, moguće je da je stres postao hroničan. Terapeuti za stres pomažu da prepoznaš okidače, uvedeš strategije upravljanja stresom i postaviš jasne granice na poslu i kod kuće. Rezerviši online sesiju sa dostupnim terapeutom.",
    directoryContent: {
      title: "Online terapeuti za stres i preopterećenost",
      description:
        "Ako te pritisak na poslu, stalna dostupnost ili previše obaveza drže u napetosti, razgovor sa terapeutom može pomoći da sagledaš šta te iscrpljuje. Na Rešimo Probleme možeš da pregledaš profile terapeuta koji navode rad sa stresom, uporediš opis rada, cenu i slobodne termine, pa zakažeš online razgovor. Kreni od konkretnih situacija u kojima najčešće osećaš preopterećenost.",
      seoTitle: "Terapeut za stres online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za stres. Uporedi profile, cene i slobodne termine, pa zakaži razgovor koji ti odgovara.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za stres online?",
          answer:
            "Pregledaj profile terapeuta koji navode rad sa stresom, pa uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Kako da znam da li je stres postao hroničan?",
          answer:
            "Stres može biti hroničan kada se napetost, umor, razdražljivost, loš san ili slabija koncentracija zadržavaju nedeljama i počnu da utiču na posao, odnose ili zdravlje.",
        },
        {
          question: "Da li je stres na poslu isto što i burnout?",
          answer:
            "Nije nužno. Stres može biti privremen odgovor na zahtevan period, dok burnout obično uključuje dugotrajniju iscrpljenost i osećaj udaljenosti od posla. Razgovor sa terapeutom može pomoći da sagledaš obrazac i naredne korake.",
        },
        {
          question: "Šta mogu da očekujem od prvog razgovora o stresu?",
          answer:
            "Možeš opisati situacije koje te najviše opterećuju, kako stres utiče na san, telo ili odnose i šta želiš da promeniš. Zajedno sa terapeutom možeš proceniti da li želite da nastavite rad.",
        },
      ],
    },
    seoTitle: "Kako smanjiti hronični stres uz online terapiju",
    seoDescription:
      "Online terapija za stres pomaže da prepoznaš simptome hroničnog stresa, smanjiš napetost i povratiš fokus. Zakaži termin sa terapeutom.",
    relatedConditions: [
      "burnout",
      "karijera-posao",
      "finansijski-stres",
      "anksioznost",
    ],
    passes: true,
    faqItems: [
      {
        question: "Koji su najčešći simptomi hroničnog stresa?",
        answer:
          "Dugotrajan umor, napetost u telu, problemi sa snom, razdražljivost, glavobolje i slabija koncentracija. Ako traju nedeljama i remete funkcionisanje, stres je verovatno hroničan.",
      },
      {
        question: "Kako da smanjim stres u kratkom roku?",
        answer:
          "Najbrže pomažu kratke vežbe disanja, kratka šetnja, rasterećenje obaveza i planiranje prioriteta. Terapeut pomaže da izabereš tehnike koje realno možeš da primeniš svaki dan.",
      },
      {
        question: "Kako se nositi sa stresom na poslu?",
        answer:
          "Rad na granicama, jasna komunikacija o rokovima, pauze bez ekrana i realniji standardi učinka. U terapiji se uči kako da prepoznaš okidače i sprečiš sagorevanje.",
      },
      {
        question: "Da li online terapija pomaže kod stresa i anksioznosti?",
        answer:
          "Da. Online terapija pomaže da razumeš vezu između stresa i anksioznosti, naučiš KBT alate i smanjiš telesnu napetost kroz vežbe regulacije.",
      },
      {
        question: "Kada je vreme da potražim pomoć zbog stresa?",
        answer:
          "Ako simptomi traju više nedelja, utiču na posao, odnose ili san, ili se javljaju napadi panike, vreme je za razgovor sa terapeutom.",
      },
    ],
  },

  // 4
  odnosi: {
    label: "Problemi u vezi",
    guideLink: {
      href: "/blog/odnosi-i-mentalno-zdravlje-kako-izgraditi-zdrave-veze",
      label: "Pročitaj vodič o odnosima i mentalnom zdravlju",
    },
    therapistSearchTerms: [
      "Emocionalno zanemarivanje i poremećeni odnosi (porodični, partnerski)",
      "Individualana Partnerska Porodicna Psihoterapija",
      "Izazovi u porodičnim i partnerskim odnosima",
      "Komunikacija i konflikti u odnosima",
      "Partnerska problematika",
      "Partnerska terapija",
      "Partnerska/bracna psihoterapija",
      "Partnerski odnosi",
      "Partnerski odnosi (preljuba/komunikacija)",
      "Partnerski odnosi, raskidi i emotivne dileme",
      "Partnerski problemi",
      "Podrska u partnerskim odnosima",
      "Problemi u partnerskim odnosima",
      "Problemi u porodičnim i partnerskim odnosima",
      "Toksični partnerski odnosi",
    ],
    title:
      "Online terapija za probleme u vezi - pronađi psihologa i olakšaj odnos",
    searchTerm:
      "odnosi veza partnerski odnos ljubav brak komunikacija konflikt nevera ljubomora rastanak nepoverenje distanca terapija za probleme u vezi terapija za partnerske odnose terapija parova savetovanje za parove online terapija za parove bračno savetovanje psiholog za probleme u vezi kako pronaći psihologa za probleme u vezi kako izabrati terapeuta za probleme u vezi online savetovanje za parove",
    description:
      "Ako se u vezi ili braku često vrtite u istim svađama, osećate distancu, gubite poverenje ili vas prate ljubomora i sumnje, terapija za probleme u vezi može doneti olakšanje. Na ovoj stranici možeš da pronađeš psihologa ili psihoterapeuta za odnose koji radi online terapiju za parove i pojedince. Ne mora da bude “poslednja šansa” — dovoljno je da osećaš da komunikacija ne funkcioniše ili da konflikti postaju učestaliji. U radu na partnerskim odnosima koristimo savetovanje za parove i terapiju parova, kao i pristupe poput KBT-a, sistemske i integrativne terapije, fokusirane na veštine komunikacije, postavljanje granica i razumevanje obrazaca u odnosu. To može pomoći kod tema kao što su nevera, emotivna udaljenost, različite potrebe, ili teškoće u dogovorima oko svakodnevice i roditeljstva. Bračno savetovanje je dostupno i za parove u dugim vezama, ne samo za venčane, a online format omogućava da se uključite iz različitih lokacija.\n\nKada razmišljaš kako izabrati terapeuta za probleme u vezi, na Rešimo Probleme možeš pronaći psihologa za probleme u vezi ili psihoterapeuta za odnose koji radi online terapiju za parove i pojedince. Možeš lako pregledati profile, uporediti pristupe i zakazati online terapiju za parove u terminu koji vam odgovara. Rezerviši sesiju u nekoliko koraka i započni proces koji vodi ka jasnijoj komunikaciji, većoj bliskosti i stabilnijem odnosu.",
    directoryContent: {
      title: "Online terapeuti za izazove u partnerskim odnosima",
      description:
        "Ako želiš da razgovaraš o vezi, komunikaciji, raskidu ili odnosu sa partnerom, ovde možeš da pronađeš terapeuta koji navodi rad sa partnerskim odnosima. Uporedi profile, cenu i slobodne termine, pa zakaži online razgovor. Za zajednički rad oba partnera pogledaj i terapiju za parove.",
      seoTitle: "Terapeut za partnerske odnose online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za partnerske odnose. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za partnerske odnose?",
          answer:
            "Pregledaj profile terapeuta koji navode rad sa partnerskim odnosima, pa uporedi opis rada, cenu i slobodne termine.",
        },
        {
          question: "Da li mogu da zakažem razgovor samo za sebe?",
          answer:
            "Da. Ova stranica pomaže da pronađeš terapeuta za individualni razgovor o temi odnosa.",
        },
        {
          question: "Gde da pronađemo terapiju za parove?",
          answer:
            "Ako želite da na razgovoru učestvuju oba partnera, pogledajte stranicu terapije za parove i proverite profile terapeuta koji nude taj format.",
        },
        {
          question: "Koliko košta online razgovor?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja.",
        },
      ],
    },
    seoTitle: "Online terapija za probleme u vezi i parove | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapiju za probleme u vezi sa licenciranim psihoterapeutom. Savetovanje za parove, terapija parova i brzo zakazivanje.",
    relatedConditions: [
      "za-parove",
      "problemi-vezivanja",
      "kodependencija",
      "razvod-razlaz",
    ],
    passes: true,
    faqItems: [
      {
        question: "Kada je terapija za probleme u vezi pravi izbor?",
        answer:
          "Ako su konflikti česti, komunikacija teška, a osećaj bliskosti slabi, terapija za probleme u vezi može pomoći. Posebno je korisna kada se javlja ljubomora, nepoverenje, distanca ili razmišljate o raskidu. Mnogi parovi pretražuju online terapiju za parove kada shvate da im je potrebna stručna podrška za vraćanje bliskosti.",
      },
      {
        question:
          "Da li je savetovanje za parove isto što i bračno savetovanje?",
        answer:
          "Savetovanje za parove i bračno savetovanje imaju isti cilj: bolju komunikaciju, više razumevanja i stabilniji odnos. Važno je stanje odnosa, a ne formalni status, pa je dostupno i partnerima u vanbračnoj vezi. Online savetovanje za parove omogućava fleksibilno zakazivanje iz različitih lokacija.",
      },
      {
        question: "Kako izgleda terapija parova i šta radimo na sesijama?",
        answer:
          "Terapija parova počinje dogovorom oko ciljeva, zatim se radi na obrascima komunikacije i rešavanju konflikata. Terapeut koristi alate iz KBT-a, sistemske ili integrativne terapije, uz praktične vežbe između susreta. Online terapija za parove omogućava isti kvalitet rada kao i uživo, sa dodatnom fleksibilnošću za parove koji su na različitim lokacijama.",
      },
      {
        question: "Kako izabrati terapeuta za probleme u vezi i šta tražiti?",
        answer:
          "Kada razmišljaš kako pronaći psihologa za probleme u vezi, traži licenciranog psihoterapeuta sa iskustvom u radu sa parovima i pristupom koji ti odgovara (KBT, sistemska, integrativna terapija). Na Rešimo Probleme možeš pregledati profile terapeuta, uporediti pristupe i izabrati online terapiju za parove koja najbolje odgovara tvojim potrebama.",
      },
      {
        question:
          "Da li online terapija za parove funkcioniše ako smo na različitim lokacijama?",
        answer:
          "Da, online terapija za parove omogućava da se uključite iz različitih mesta, što je posebno korisno za parove na dužim relacijama. Sesije su strukturisane isto kao uživo, a fokus je na kvalitetu dijaloga i dogovora. Online savetovanje za parove eliminiše geografske prepreke i omogućava kontinuitet rada.",
      },
      {
        question:
          "Mogu li da radim sa psihologom za probleme u vezi i ako partner ne želi?",
        answer:
          "Možeš. Individualni rad sa psihologom ili psihoterapeutom za probleme u vezi pomaže da razumeš svoje potrebe, postaviš granice i jasnije komuniciraš u vezi. Često promena jednog partnera pozitivno utiče na dinamiku odnosa, pa je individualna online terapija za probleme u vezi korisna opcija.",
      },
      {
        question: "Koliko obično traje terapija za partnerske odnose?",
        answer:
          "Trajanje terapije parova zavisi od ciljeva i intenziteta problema, ali često je 8–12 susreta uz zadatke između sesija. Terapeut i par zajedno procenjuju tempo i napredak. Online terapija za parove omogućava redovnost i kontinuitet rada bez obzira na lokaciju partnera.",
      },
    ],
  },

  // 5
  trauma: {
    label: "Trauma i PTSP",
    guideLink: {
      href: "/blog/ptsp-razumevanje-traume-i-put-do-oporavka",
      label: "Pročitaj vodič o PTSP-u i traumi",
    },
    title: "Online terapeuti za traumu i PTSP - siguran put ka oporavku",
    searchTerm:
      "trauma ptsp posttraumatski stres posttraumatski stresni poremecaj okidači flashback noćne more hipervigilnost terapeuti za traumu online terapija emdr",
    therapistSearchTerms: ["trauma"],
    description:
      "Traumatska iskustva mogu da ostave posledice kao što su 'flashback', noćne more, napetost, hipervigilnost i izbegavanje okidača. Ako se pitaš da li je u pitanju trauma ili PTSP i treba ti stručna podrška, važan je siguran i strukturisan pristup.\n\nNa Resimo Probleme možeš pronaći terapeuta za traumu i PTSP i započeti online terapija uz EMDR i trauma-informisane pristupe. Pregledaj profile, izaberi pristup i zakaži termin koji ti odgovara — online terapija omogućava kontinuitet rada bez obzira na lokaciju.",
    directoryContent: {
      title: "Online terapeuti za razgovor o traumatskim iskustvima",
      description:
        "Ako želiš da razgovaraš o teškom ili traumatskom iskustvu, možeš potražiti terapeuta čiji profil navodi rad sa traumom. Na Rešimo Probleme uporedi opis rada, pristup, cenu i slobodne termine, pa zakaži online razgovor. Tempo i teme razgovora dogovarate zajedno, a ne moraš unapred da znaš koji pristup ti najviše odgovara.",
      seoTitle: "Terapeut za traumu online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za razgovor o traumatskim iskustvima. Uporedi profile, pristupe, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za traumu online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa traumom. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li moram odmah da govorim o svim detaljima iskustva?",
          answer:
            "Ne moraš. Na prvom razgovoru možeš reći samo onoliko koliko ti je prijatno i pitati terapeuta kako pristupa ovakvim temama.",
        },
        {
          question: "Mogu li da pitam terapeuta koji pristup koristi?",
          answer:
            "Da. Na profilu vidiš informacije koje terapeut javno navodi, a na razgovoru možeš pitati kako radi i šta možeš da očekuješ od procesa.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Online terapija za traumu i PTSP danas | Resimo Probleme",
    seoDescription:
      "Pronađi online terapiju za traumu i PTSP uz licencirane terapeute. Uporedi pristupe, izaberi terapeuta i zakaži termin brzo i diskretno, bez čekanja. Od kuće.",
    relatedConditions: [
      "ptsp",
      "trauma-iz-detinjstva",
      "emocionalno-zlostavljanje",
      "emdr",
    ],
    passes: true,
    faqItems: [
      {
        question: "Kako prepoznati traumu ili PTSP i kada potražiti terapeuta?",
        answer:
          "Trauma i PTSP se često prepoznaju kroz flashback, noćne more, pojačanu napetost, hipervigilnost i izbegavanje okidača. Ako simptomi traju i remete san, odnose ili funkcionisanje, korisno je da razgovaraš sa terapeutom za traumu. Rana podrška pomaže da se osećaš sigurnije i stabilnije.",
      },
      {
        question:
          "Da li online terapija za traumu i PTSP može da bude efikasna?",
        answer:
          "Da, online terapija za traumu može biti efikasna kada postoji jasan plan, tempo koji ti odgovara i tehnike smirivanja. Licencirani terapeut vodi proces korak po korak i prati stabilnost između seansi. Online terapija omogućava kontinuitet rada bez obzira na lokaciju.",
      },
      {
        question: "Šta je EMDR terapija i kada se koristi kod traume?",
        answer:
          "EMDR terapija je strukturisan pristup koji pomaže reprocesuiranje traumatskih uspomena. Koristi se kod traume i PTSP kada postoji dovoljno stabilnosti i jasna procena terapeuta. Mnogi klijenti navode smanjenje intenziteta okidača i telesne napetosti.",
      },
      {
        question: "Kako izgleda prva seansa kod terapeuta za traumu?",
        answer:
          "Prva seansa obično uključuje procenu simptoma, mapiranje okidača i dogovor o ciljevima. Terapeut uvodi tehnike stabilizacije poput disanja, uzemljenja i resursa pre rada na sećanjima. Važno je da se osećaš sigurno i da tempo bude prilagođen tebi.",
      },
      {
        question: "Koliko traje terapija za traumu i PTSP?",
        answer:
          "Trajanje zavisi od vrste i intenziteta traume, ali se često radi u fazama: stabilizacija, reprocesuiranje i integracija. Neki klijenti osete olakšanje nakon nekoliko meseci, dok je drugima potreban duži rad. Terapeut za traumu prati napredak i prilagođava plan.",
      },
      {
        question: "Kako da izaberem terapeuta za traumu na Resimo Probleme?",
        answer:
          "Na Resimo Probleme možeš pregledati profile, iskustvo i pristupe poput EMDR-a ili trauma-informisane terapije. Uporedi terapeute za traumu, pročitaj opise i izaberi onog ko ti deluje kao dobar izbor. Zakaži online terapiju u terminu koji ti odgovara.",
      },
    ],
  },

  // 6
  samopouzdanje: {
    label: "Jačanje samopouzdanja",
    title:
      "Online terapeuti za samopouzdanje: izgradi sigurnost i samopoštovanje",
    searchTerm:
      "samopouzdanje samopoštovanje nisko samopouzdanje terapija za samopouzdanje online terapeut samopouzdanje simptomi samouverenost asertivnost samovrednovanje",
    therapistSearchTerms: ["samopouzdanje"],
    description:
      "Osećaš nesigurnost, strah od odbijanja ili poteškoće u postavljanju granica? Ovi simptomi niskog samopouzdanja mogu uticati na odnose, posao i svakodnevni život. Na Rešimo Probleme možeš pronaći terapeuta za samopouzdanje i započeti online terapiju koja će ti pomoći da izgradiš stabilno samopoštovanje i sigurniji nastup.\n\nPoveži se sa licenciranim psihologom specijalizovanim za rad na samopouzdanju. Kroz individualnu psihoterapiju online radićete na prepoznavanju uzroka nesigurnosti, promeni negativnih uverenja i razvoju zdravijeg odnosa prema sebi. Zakaži termin i kreni ka većoj samouverenosti.",
    directoryContent: {
      title: "Online terapeuti za samopouzdanje i nesigurnost",
      description:
        "Ako želiš da razgovaraš o nesigurnosti, samopoštovanju ili postavljanju granica, ovde možeš da pronađeš terapeuta koji navodi rad sa samopouzdanjem. Uporedi profile, cenu i slobodne termine, pa zakaži online razgovor.",
      seoTitle: "Terapeut za samopouzdanje online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za samopouzdanje i nesigurnost. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za samopouzdanje?",
          answer:
            "Pregledaj profile terapeuta koji navode rad sa samopouzdanjem, pa uporedi opis rada, cenu i slobodne termine.",
        },
        {
          question: "Šta mogu da očekujem od prvog razgovora?",
          answer:
            "Možeš reći šta te trenutno opterećuje, postaviti pitanja i čuti kako terapeut radi.",
        },
        {
          question: "Mogu li da uporedim terapeute pre zakazivanja?",
          answer:
            "Da. Na profilima možeš uporediti opis rada, cenu sesanse i dostupne termine.",
        },
        {
          question: "Koliko košta online razgovor?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja.",
        },
      ],
    },
    seoTitle: "Online terapija za samopouzdanje danas | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za samopouzdanje i nisko samopoštovanje. Licencirani psiholozi pomažu u jačanju samouverenosti, postavljanju granica i prevazilaženju straha od odbijanja.",
    relatedConditions: [
      "imposter-sindrom",
      "telesna-slika",
      "socijalna-anksioznost",
      "kodependencija",
    ],
    passes: true,
    faqItems: [
      {
        question:
          "Kako prepoznati simptome niskog samopouzdanja i kada potražiti terapeuta?",
        answer:
          "Simptomi niskog samopouzdanja uključuju strah od odbijanja, izbegavanje novih izazova, poteškoće u postavljanju granica i negativno samovrednovanje. Ako ovi simptomi utiču na odnose ili posao, online terapeut za samopouzdanje može pomoći u izgradnji stabilnijeg samopoštovanja.",
      },
      {
        question: "Koje vrste terapije pomažu kod problema sa samopouzdanjem?",
        answer:
          "Kognitivno-bihejvioralna terapija (KBT) je posebno efikasna za samopouzdanje jer pomaže u prepoznavanju i promeni negativnih uverenja. Takođe se koriste EMDR za traumatska iskustva i individualna psihoterapija za lični razvoj. Online terapija za samopouzdanje omogućava rad sa terapeutom specijalizovanim za ove pristupe.",
      },
      {
        question: "Kako izabrati online terapeuta za samopouzdanje?",
        answer:
          "Traži licenciranog psihologa ili psihoterapeuta sa iskustvom u radu na samopouzdanju i samopoštovanju. Na Rešimo Probleme možeš pregledati profile terapeuta, pročitati opise pristupa i izabrati online terapiju koja odgovara tvojim potrebama - od KBT do integrativnih metoda.",
      },
      {
        question: "Da li online terapija za samopouzdanje daje rezultate?",
        answer:
          "Da, online terapija za samopouzdanje je efikasna jer se fokusira na praktične vežbe i praćenje napretka. Terapeut vodi proces jačanja samouverenosti kroz strukturirane sesije, zadatke između termina i praćenje promena u samovrednovanju.",
      },
      {
        question: "Koliko traje terapija za samopouzdanje i šta očekivati?",
        answer:
          "Trajanje zavisi od individualnih potreba, ali većina klijenata vidi poboljšanja nakon 8-12 sesija. Online terapeut za samopouzdanje će sa tobom dogovoriti ciljeve, pratiti napredak i prilagođavati pristup kako bi izgradio stabilnije samopoštovanje.",
      },
      {
        question:
          "Kako se pripremiti za prvu sesiju sa terapeutom za samopouzdanje?",
        answer:
          "Zapiši svoje ciljeve i simptome niskog samopouzdanja. Razmisli o situacijama kada se osećaš nesigurno i šta bi želeo da promeniš. Online terapija za samopouzdanje počinje procenom i dogovorom o pristupu koji će ti pomoći u jačanju samouverenosti.",
      },
    ],
  },

  // 7
  "za-parove": {
    passes: true,
    label: "Terapija za parove",
    guideLink: {
      href: "/blog/par-terapija-kada-i-kako-moze-pomoci-tvom-odnosu",
      label: "Pročitaj vodič o terapiji za parove",
    },
    title:
      "Online terapija za parove i bračno savetovanje sa licenciranim terapeutima",
    searchTerm:
      "terapija za parove bračna terapija bračni terapeut bračno savetovanje partnerska terapija savetovalište za parove psihoterapeut za parove bračni savetnik partnersko savetovanje problemi u braku komunikacija u vezi konflikti u vezi neverstvo terapija parova online terapija za parove EFT Gottman",
    couples: true,
    description:
      "Problemi u komunikaciji, česte svađe, udaljenost ili gubitak poverenja česti su u partnerskim odnosima i braku. Kada pokušaji da sami rešite stvari ne daju rezultat, terapija za parove pruža strukturiran prostor za otvoren razgovor uz podršku licenciranog terapeuta.\n\nNa Rešimo Probleme možeš pronaći bračnog savetnika ili psihoterapeuta za parove koji radi sa vama kroz EFT (emocionalno fokusiranu terapiju), Gottmanovu metodu ili kognitivno-bihejvioralnu terapiju. Cilj je da prepoznate obrasce koji vas razdvajaju, naučite da komunicirate bez optužbi i ponovo izgradite bliskost.\n\nPregledaj profile terapeuta za parove, uporedi pristupe i cene, i zakaži online sesiju bračnog savetovanja. Terapija za parove je dostupna i za parove u dugim vezama, ne samo za venčane.",
    seoTitle: "Terapija za parove online: bračni savetnik | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za parove i bračno savetovanje. Licencirani terapeuti, EFT i Gottman metoda. Uporedi profile i zakaži sesiju na Rešimo Probleme.",
    relatedConditions: [
      "odnosi",
      "problemi-vezivanja",
      "razvod-razlaz",
      "seksualna-terapija",
    ],
    directoryContent: {
      title: "Terapija za parove online i bračno savetovanje",
      description:
        "Terapija za parove može biti prostor za razgovor o komunikaciji, konfliktima, udaljenosti, poverenju ili važnim odlukama u odnosu. Na Rešimo Probleme možeš da pregledaš profile terapeuta koji nude rad sa parovima, uporediš njihov pristup, cenu i slobodne termine, pa zakažeš online razgovor. Možete se uključiti iz istog ili različitih prostora, ako vam to odgovara.",
      seoTitle: "Terapija za parove online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za parove i bračno savetovanje. Uporedi profile, pristupe, cene i slobodne termine, pa zakažite razgovor.",
      faqItems: [
        {
          question: "Kako da pronađemo terapeuta za parove online?",
          answer:
            "Na ovoj stranici možete da pregledate profile terapeuta koji nude rad sa parovima. Uporedite njihov opis rada, cenu i dostupne termine pre zakazivanja.",
        },
        {
          question:
            "Da li moramo biti u braku da bismo išli na terapiju za parove?",
          answer:
            "Ne. Terapija za parove je namenjena partnerima koji žele razgovor o svom odnosu, bez obzira na to da li su u braku, dugoj vezi ili se tek suočavaju sa važnom odlukom.",
        },
        {
          question: "Možemo li da se uključimo iz različitih gradova?",
          answer:
            "Da. Online razgovor može biti praktičan kada partneri nisu na istoj lokaciji. Pre zakazivanja proverite da li vam termin odgovara oboma.",
        },
        {
          question: "Šta ako partner/ka nije siguran/na da želi terapiju?",
          answer:
            "Možete najpre razgovarati o tome šta bi svako od vas želeo da razjasni. Ako partner ne želi zajednički razgovor, možeš i individualno razgovarati sa terapeutom o svojoj situaciji.",
        },
        {
          question: "Koliko košta online terapija za parove?",
          answer:
            "Cenu sesanse za parove vidiš na profilu terapeuta pre zakazivanja. Možete uporediti više profila i izabrati termin i cenu koji vam odgovaraju.",
        },
      ],
    },
    faqItems: [
      {
        question: "Šta je terapija za parove i kome je namenjena?",
        answer:
          "Terapija za parove (partnerska terapija, bračno savetovanje) je oblik psihoterapije u kojem oba partnera razgovaraju sa terapeutom o problemima u odnosu. Namenjena je svim parovima koji žele da poboljšaju komunikaciju, reše konflikte ili donesu važne odluke, bez obzira da li su u braku, dugoj vezi ili tek počinju zajednički život.",
      },
      {
        question: "Koje metode koriste terapeuti za parove na Rešimo Probleme?",
        answer:
          "Naši terapeuti rade kroz pristupe kao što su EFT (emocionalno fokusirana terapija) za obnovu emocionalne povezanosti, Gottmanova metoda za jačanje intimnosti i upravljanje sukobima, te kognitivno-bihejvioralna terapija parova (CBCT) za promenu negativnih obrazaca razmišljanja. Na profilu svakog terapeuta možeš videti koji pristup koristi.",
      },
      {
        question: "Kada treba ići na terapiju za parove?",
        answer:
          "Česti znaci da je terapija potrebna: ponavljajuće svađe oko istih tema, osećaj udaljenosti ili nepovezanosti, gubitak poverenja nakon neverstva, poteškoće u komunikaciji, ili razmišljanje o razvodu. Terapija pomaže i parovima koji žele da preventivno ojačaju odnos, ne samo onima u krizi.",
      },
      {
        question: "Koliko košta online terapija za parove?",
        answer:
          "Cena zavisi od terapeuta i trajanja sesije. Na platformi možeš filtrirati terapeute po ceni, videti tačan iznos po sesiji i uporediti profile pre zakazivanja. Sesije za parove obično traju 60 do 80 minuta. Plaćanje je moguće karticom, PayPal-om, Apple Pay-om ili Google Pay-om.",
      },
      {
        question: "Da li online terapija za parove funkcioniše?",
        answer:
          "Da. Istraživanja pokazuju da je online terapija za parove jednako efikasna kao terapija uživo. Prednost je što oba partnera mogu da se priključe iz različitih lokacija, termini su fleksibilniji, a razgovor se odvija u poznatom okruženju, što smanjuje tenziju i olakšava otvorenost.",
      },
      {
        question: "Šta ako partner/ka ne želi da ide na terapiju?",
        answer:
          "Kreni individualno. Rad na sopstvenim obrascima komunikacije i reakcijama često smanji tenziju u odnosu i motiviše partnera da se uključi. Mnogi terapeuti na platformi rade i individualno i sa parovima, tako da možeš početi sam/a i kasnije preći na zajedničke sesije.",
      },
      {
        question: "Da li terapija za parove pomaže kod razmišljanja o razvodu?",
        answer:
          "Terapija pomaže parovima da donesu informisanu odluku, bez obzira na ishod. Kroz strukturirane razgovore možete razjasniti šta svako želi, smanjiti konflikte i definisati sledeće korake. Neki parovi obnove odnos, a nekima terapija pomogne da se razidu na dostojanstven način.",
      },
    ],
  },

  // 8
  porodica: {
    label: "Porodična terapija",
    title:
      "Online porodična terapija: jači odnosi i bolja komunikacija u porodici",
    searchTerm:
      "porodična terapija porodični terapeut online porodična terapija sistemska porodična terapija porodični konflikti roditeljstvo granice u porodici porodična komunikacija problemi u porodici terapija za porodicu porodična psihoterapija porodični odnosi porodični problemi",
    therapistSearchTerms: ["porodica"],
    description:
      "Porodična terapija pomaže da se reše konflikti, poboljša komunikacija i uspostave zdrave granice u porodici. Kada su prisutni česti sukobi, problemi u vaspitanju, anksioznost kod dece ili teškoće u partnerskim odnosima koji utiču na celu porodicu, sistemska porodična terapija može doneti olakšanje.\n\nNa Rešimo Probleme možeš pronaći licenciranog porodičnog terapeuta ili sistemskog psihoterapeuta koji radi online. Terapija se fokusira na poboljšanje međusobnih odnosa, razumevanje porodičnih obrazaca i jačanje porodične dinamike. Možeš se uključiti iz različitih lokacija, što je posebno korisno za porodice koje žive u različitim gradovima.\n\nPregledaj profile terapeuta, uporedi pristupe i zakazi online sesiju u terminu koji odgovara celoj porodici. Terapija pomaže kod tema kao što su konflikti između roditelja, tinejdžerske teškoće, gubitak u porodici ili promene poput razvoda.",
    directoryContent: {
      title: "Online porodična terapija: bolja komunikacija i manje konflikata",
      description:
        "Online porodična terapija može pomoći kada se konflikti ponavljaju, komunikacija postane teška ili porodica prolazi kroz razvod, gubitak, roditeljske dileme ili druge velike promene. Na Rešimo Probleme najpre su prikazani terapeuti koji na profilu navode rad sa porodicom ili sistemsku porodičnu psihoterapiju. Pregledaj njihov pristup, cenu i slobodne termine. Članovi porodice mogu da se uključe iz različitih gradova ili zemalja, pa zakaži razgovor sa terapeutom čiji način rada odgovara temi o kojoj želiš da razgovarate.",
      seoTitle:
        "Online porodična terapija i sistemska psihoterapija | Rešimo Probleme",
      seoDescription:
        "Pronađi online porodičnu terapiju za konflikte, komunikaciju, granice i velike promene u porodici. Uporedi terapeute, cene i termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Šta je online porodična terapija i kada može da pomogne?",
          answer:
            "Online porodična terapija je razgovor sa terapeutom o obrascima odnosa, komunikaciji i konfliktima koji utiču na porodicu. Može biti koristan prostor kada se sukobi ponavljaju, kada je teško dogovoriti granice ili kada porodica prolazi kroz razvod, gubitak, promene u roditeljstvu ili druge zahtevne periode.",
        },
        {
          question:
            "Kako da izaberem porodičnog ili sistemskog terapeuta online?",
          answer:
            "Prvo pogledaj da li terapeut na profilu navodi porodičnu terapiju, sistemsku psihoterapiju ili rad sa porodicom. Zatim uporedi njegov opis rada, cenu i termine, pa na prvom razgovoru proveri da li radi sa temom i sastavom porodice koji ti je važan.",
        },
        {
          question:
            "Mogu li članovi porodice da se uključe iz različitih gradova ili zemalja?",
          answer:
            "Da, članovi porodice mogu da se uključe iz različitih gradova ili zemalja ako terapeut proceni da takav format odgovara temi razgovora. Pre zakazivanja proveri na profilu i sa terapeutom da li prima više učesnika u online sesiji.",
        },
        {
          question: "Koliko košta online porodična terapija?",
          answer:
            "Cena online porodične terapije zavisi od terapeuta i broja učesnika, kada terapeut nudi poseban format za parove ili porodice. Na svakom profilu možeš da vidiš cenu i slobodne termine pre zakazivanja.",
        },
      ],
    },
    seoTitle:
      "Online porodična terapija i sistemska psihoterapija | Rešimo Probleme",
    seoDescription:
      "Pronađi online porodičnu terapiju sa licenciranim terapeutom. Sistemska terapija za konflikte, komunikaciju i jače porodične veze.",
    relatedConditions: ["roditeljstvo", "odnosi", "razvod-razlaz", "za-parove"],
    passes: true,
    faqItems: [
      {
        question: "Šta je porodična terapija i kada je potrebna?",
        answer:
          "Porodična terapija je oblik psihoterapije koji se fokusira na poboljšanje odnosa i komunikacije unutar porodice. Potrebna je kada postoje česti sukobi, problemi u vaspitanju, anksioznost ili depresija kod članova porodice, ili značajne životne promene poput razvoda, gubitka ili selidbe.",
      },
      {
        question: "Kako funkcioniše online porodična terapija?",
        answer:
          "Online porodična terapija omogućava da se svi članovi uključe iz udobnosti svog doma, što je posebno korisno za porodice na različitim lokacijama. Terapeut vodi razgovore o komunikaciji, granicama i porodičnim obrascima, koristeći sistemske pristupe poput KBT-a ili integrativne terapije.",
      },
      {
        question: "Ko sve treba da prisustvuje porodičnoj terapiji?",
        answer:
          "Idealno je uključiti ključne članove porodice koji su deo problema ili promena. Terapeut predlaže sastav po temi - to mogu biti roditelji i deca, samo roditelji, ili specifični članovi. Čak i ako neko ne želi da učestvuje, individualni rad sa jednim članom može biti koristan.",
      },
      {
        question: "Može li se raditi online ako nismo u istom gradu?",
        answer:
          "Da, online porodična terapija je idealna za porodice koje žive na različitim lokacijama. Više članova može se uključiti iz različitih gradova ili čak zemalja, što eliminiše geografske prepreke i omogućava kontinuitet rada.",
      },
      {
        question: "Koji su ciljevi porodične terapije?",
        answer:
          "Ciljevi porodične terapije uključuju poboljšanje komunikacije, postavljanje zdravih granica, razumevanje međusobnih uloga, rešavanje konflikata i jačanje porodične kohezije. Terapeut pomaže da se identifikuju disfunkcionalni obrasci i uvode konstruktivniji načini interakcije.",
      },
      {
        question: "Koliko traje porodična terapija i kada se vide rezultati?",
        answer:
          "Trajanje zavisi od problema, ali često je 8-15 sesija uz zadatke između termina. Prva poboljšanja u komunikaciji mogu se primetiti nakon nekoliko sesija, dok dublje promene zahtevaju vreme i dosledan rad.",
      },
      {
        question: "Da li je porodična terapija efikasna za tinejdžere?",
        answer:
          "Da, porodična terapija može biti vrlo korisna za tinejdžere. Pomaže u rešavanju konflikata sa roditeljima, autonomiji, granicama i komunikaciji. Terapeut može raditi sa celom porodicom ili fokusirati na specifične teme vezane za tinejdžerski period.",
      },
    ],
  },

  // 9
  panika: {
    label: "Panični napadi",
    guideLink: {
      href: "/blog/napad-panike-sta-je-to-kako-prepoznati-i-sta-raditi",
      label: "Pročitaj vodič o napadima panike",
    },
    title: "Terapija za panične napade: KBT tretman i prevencija",
    searchTerm:
      "panični napadi panicni napad simptomi panični napadi i anksioznost depresija i panicni napadi terapija za paniku terapeut za paniku online terapija panika kbt za paniku kognitivno bihevioralna terapija za paniku kako prestati panični napad šta pomaže kod panike",
    therapistSearchTerms: ["panični napadi"],
    description:
      "Doživljavaš panične napade sa iznenadnim strahom, palpitacijama, gušenjem ili osećajem gubitka kontrole? Panični napadi mogu biti veoma intenzivni i često se javljaju zajedno sa anksioznošću ili depresijom. Na Rešimo Probleme pronađi stručnu pomoć kroz online terapiju za panične napade.\n\nKBT (kognitivno-bihejvioralna terapija) je najefikasniji pristup za panične napade, sa fokusom na prepoznavanje okidača, tehnike disanja i postepeno izlaganje strahovima. Terapeuti za panične napade pomažu u razumevanju simptoma, razvoju strategija za smirivanje i dugoročnom rešavanju uzroka.\n\nOnline terapija za panične napade omogućava da se povežeš sa licenciranim psihologom bez čekanja. Uporedi terapeute, pročitaj recenzije i zakazi online sesiju koja ti odgovara. Pronađi rešenje za panične napade danas.",
    directoryContent: {
      title: "Online terapeuti za panične napade i intenzivnu anksioznost",
      description:
        "Ako želiš da razgovaraš o paničnim napadima ili intenzivnoj anksioznosti, ovde možeš da pronađeš terapeuta koji navodi rad sa tom temom. Uporedi profile, cenu i slobodne termine, pa zakaži online razgovor. Ako imaš hitan zdravstveni problem, obrati se hitnoj medicinskoj pomoći.",
      seoTitle: "Terapeut za panične napade online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za panične napade i anksioznost. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za panične napade?",
          answer:
            "Pregledaj profile terapeuta koji navode rad sa paničnim napadima, pa uporedi opis rada, cenu i slobodne termine.",
        },
        {
          question:
            "Da li mogu da zakažem razgovor ako nisam siguran/na šta mi se dešava?",
          answer:
            "Da. Možeš početi od onoga što doživljavaš i sa terapeutom razjasniti šta ti je potrebno.",
        },
        {
          question: "Šta ako imam hitan zdravstveni problem?",
          answer:
            "Ako imaš hitan zdravstveni problem ili se ne osećaš bezbedno, odmah se obrati hitnoj medicinskoj pomoći ili lokalnoj kriznoj službi.",
        },
        {
          question: "Koliko košta online razgovor?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja.",
        },
      ],
    },
    seoTitle: "Panični napadi: tretman i terapija | Rešimo Probleme",
    seoDescription:
      "Terapija za panične napade online. KBT tretman, simptomi i prevencija paničnih napada. Licencirani psiholozi za anksioznost i paniku.",
    relatedConditions: ["anksioznost", "agorafobija", "fobije", "kbt"],
    passes: true,
    faqItems: [
      {
        question: "Šta su panični napadi i koji su simptomi?",
        answer:
          "Panični napadi su iznenadni, intenzivni napadi straha i fizičkih simptoma koji se javljaju bez realne opasnosti. Simptomi paničnih napada uključuju palpitacije srca, gušenje, vrtoglavicu, drhtanje, preznojavanje, osećaj gubitka kontrole i strah od umiranja ili 'poludevanja'. Napadi obično traju 5-20 minuta i mogu biti veoma uznemirujući.",
      },
      {
        question:
          "Da li se panični napadi javljaju zajedno sa anksioznošću ili depresijom?",
        answer:
          "Da, panični napadi često se javljaju zajedno sa anksioznošću i depresijom. Mnogi ljudi sa paničnim poremećajem takođe imaju simptome anksioznosti, dok depresija može povećati rizik od paničnih napada. Terapeut može proceniti kako ova stanja međusobno utiču i ponuditi integrisan pristup lečenju.",
      },
      {
        question: "Kako KBT pomaže kod paničnih napada?",
        answer:
          "KBT (kognitivno-bihejvioralna terapija) je najefikasniji tretman za panične napade. Pomaže u prepoznavanju i promeni negativnih misaonih obrazaca koji izazivaju paniku, učenju tehnika disanja i relaksacije, te postepenom izlaganju situacijama koje izazivaju strah. KBT daje alate za dugoročnu kontrolu paničnih napada.",
      },
      {
        question: "Šta pomaže kada se desi panični napad?",
        answer:
          "Tokom paničnog napada pomažu vežbe dubokog disanja (4 sekunde udah, 4 sekunde izdah), grounding tehnike (fokusiranje na 5 stvari koje vidiš, 4 koje dodiruješ, 3 koje čuješ) i normalizacija simptoma ('ovo je neprijatno ali će proći'). Ako se napadi često ponavljaju, važno je potražiti stručnu pomoć.",
      },
      {
        question: "Koliko traje online terapija za panične napade?",
        answer:
          "Trajanje terapije zavisi od težine simptoma i ciljeva, ali obično traje 8-16 nedelja sa sesijama jednom nedeljno. Mnogi klijenti vide značajna poboljšanja već nakon 4-6 sesija, dok se kompletna kontrola paničnih napada postiže kontinuiranim radom i vežbama kod kuće.",
      },
      {
        question: "Da li online terapija daje rezultate kod paničnih napada?",
        answer:
          "Da, online terapija je jednako efikasna kao i uživo za tretman paničnih napada. Istraživanja pokazuju da KBT online daje odlične rezultate, posebno kada postoji redovna komunikacija sa terapeutom i primena vežbi između sesija. Online format omogućava fleksibilnost i pristup stručnjacima bez obzira na lokaciju.",
      },
      {
        question: "Kako izabrati terapeuta za panične napade?",
        answer:
          "Traži terapeuta sa iskustvom u radu sa paničnim poremećajem i KBT pristupom. Na Rešimo Probleme možeš pregledati profile terapeuta, pročitati recenzije i zakazati besplatnu konsultaciju. Obrati pažnju na terapeute koji se bave anksioznošću, fobijama i paničnim napadima, jer ova stanja često idu zajedno.",
      },
    ],
  },

  // 10
  burnout: {
    label: "Burnout: profesionalno sagorevanje",
    guideLink: {
      href: "/blog/burnout-kako-prepoznati-i-prevladati-iscrpljenost",
      label: "Pročitaj vodič o burnout-u",
    },
    title: "Terapija za burnout online: oporavak od profesionalnog sagorevanja",
    searchTerm:
      "burnout sagorevanje profesionalno sagorevanje hronični umor iscrpljenost radni stres emocionalna iscrpljenost cinizam depersonalizacija burnout sindrom burnout simptomi burnout lečenje burnout terapija burnout online burnout terapeut burnout oporavak burnout prevencija burnout uzroci burnout posao burnout stres burnout rad burnout problemi burnout pomoć burnout podrška burnout online terapija burnout kbt za burnout mindfulness za burnout burnout vežbe burnout rutine burnout granice burnout pauze burnout balans burnout energija burnout motivacija terapija za burnout lečenje burnouta burnout recovery burnout treatment",
    therapistSearchTerms: ["burnout"],
    description:
      "Osećaš se hronično umorno, bez energije i motivacije zbog posla? Profesionalno sagorevanje ili burnout je stanje iscrpljenosti koje utiče na produktivnost i zdravlje. Naši terapeuti pomažu u prepoznavanju simptoma burnouta, postavljanju zdravih granica i oporavku kroz strukturisan proces.\n\nNa Rešimo Probleme možeš pronaći licenciranog terapeuta za burnout koji radi online KBT, mindfulness ili integrativne pristupe. Fokus je na vraćanju energije, smanjenju cinizma prema poslu i izgradnji rutinskih pauza. Pregledaj profile terapeuta, uporedi pristupe i zakazi online sesiju u terminu koji ti odgovara — prvi korak ka balansu između posla i života.",
    directoryContent: {
      title: "Online terapeuti za burnout i pritisak na poslu",
      description:
        "Ako te radni pritisak, iscrpljenost ili odnos prema poslu opterećuju, razgovor sa terapeutom može biti prostor da sagledaš šta se dešava. Na Rešimo Probleme možeš da pregledaš profile terapeuta koji navode rad sa burnoutom, uporediš njihov pristup, cenu i slobodne termine, pa zakažeš online razgovor. Kreni od onoga što ti je trenutno najteže.",
      seoTitle: "Terapeut za burnout online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za burnout i pritisak na poslu. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za burnout online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa burnoutom. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li moram da znam da li je u pitanju burnout?",
          answer:
            "Ne moraš. Možeš početi od radnog pritiska, umora ili drugih teškoća koje primećuješ i sa terapeutom razjasniti šta ti je potrebno.",
        },
        {
          question: "Šta mogu da pitam terapeuta pre nego što zakažem?",
          answer:
            "Možeš proveriti opis rada na profilu, a na prvom razgovoru pitati kako terapeut pristupa temi posla, granica i svakodnevnog opterećenja.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle:
      "Terapija za burnout online - profesionalno sagorevanje | Rešimo Probleme",
    seoDescription:
      "Pronađi terapiju za burnout i lečenje burnouta online. Licencirani terapeuti pomažu u oporavku od hronične iscrpljenosti, vraćanju energije i postavljanju zdravih granica.",
    relatedConditions: ["karijera-posao", "stres", "mobing", "depresija"],
    passes: true,
    faqItems: [
      {
        question: "Šta je burnout i kako ga prepoznati?",
        answer:
          "Burnout je sindrom profesionalnog sagorevanja karakterisan hroničnom iscrpljenošću, cinizmom prema poslu i osećajem neučinkovitosti. Simptomi burnouta uključuju stalni umor, gubitak motivacije, razdražljivost i probleme sa koncentracijom. Ako traje mesecima i remeti svakodnevicu, vreme je za stručnu pomoć.",
      },
      {
        question: "Šta pomaže kod burnouta i kako se oporaviti?",
        answer:
          "Oporavak od burnouta počinje postavljanjem jasnih granica na poslu, redovnim pauzama i rutinom sna. Šta pomaže kod burnouta dodatno uključuje mindfulness vežbe, fizičku aktivnost i razgovor sa terapeutom. Online terapija za burnout pomaže u identifikovanju uzroka i izgradnji zdravijih obrazaca.",
      },
      {
        question: "Kako se leči burnout i koja je najbolja terapija?",
        answer:
          "Lečenje burnouta najčešće uključuje KBT (kognitivno-bihejvioralnu terapiju) koja pomaže u promeni negativnih obrazaca razmišljanja i ponašanja. Mindfulness i integrativni pristupi su takođe efikasni. Online terapija za burnout omogućava kontinuiranu podršku i praćenje napretka kod kuće.",
      },
      {
        question: "Da li je online terapija za burnout efikasna?",
        answer:
          "Da, online terapija za burnout je jednako efikasna kao i uživo, posebno za KBT i mindfulness pristupe. Možeš raditi iz udobnosti svog doma, bez putovanja, što je korisno kada se osećaš iscrpljeno. Na Rešimo Probleme možeš pronaći terapeuta za burnout sa relevantnim iskustvom.",
      },
      {
        question:
          "Koliko traje oporavak od burnouta i koliko sesija je potrebno?",
        answer:
          "Oporavak od burnouta zavisi od težine simptoma i trajanja, ali često traje 8-12 nedelja sa redovnim sesijama. Proces oporavka od burnouta uključuje faze od stabilizacije do pune rehabilitacije. Terapeut za burnout će proceniti tempo i prilagoditi plan. Prva poboljšanja se mogu primetiti nakon nekoliko sesija fokusiranih na granice i rutine.",
      },
      {
        question: "Kako izabrati terapeuta za burnout i šta tražiti?",
        answer:
          "Traži terapeuta za burnout sa iskustvom u radu sa stresom, iscrpljenošću ili profesionalnim sagorevanjem. Na Rešimo Probleme možeš pregledati profile, pristupe (KBT, mindfulness, ACT) i recenzije. Zakazi konsultaciju da vidiš da li vam pristupi odgovaraju.",
      },
      {
        question: "Da li moraš menjati posao da se oporaviš od burnouta?",
        answer:
          "Ne nužno. Mnogi se oporave od burnouta bez promene posla kroz bolje upravljanje granicama, delegiranje zadataka i rutine oporavka. Terapeut za burnout može pomoći u proceni da li je promena posla deo plana oporavka.",
      },
    ],
  },

  // 11
  emdr: {
    label: "EMDR terapija",
    guideLink: {
      href: "/blog/emdr-terapija-lecenje-trauma-i-fobija",
      label: "Pročitaj vodič o EMDR terapiji",
    },
    therapistQualificationTerms: ["EMDR (Level 1)", "EMDR praktičar"],
    title: "EMDR terapeuti online: obrada traume pokretima očiju",
    searchTerm:
      "EMDR terapija emdr desenzitizacija pokretom očiju reprocesuiranje pokretom očiju eye movement desensitization and reprocessing trauma ptsp posttraumatski stresni poremećaj",
    description:
      'EMDR (Eye Movement Desensitization and Reprocessing) je dokazano efikasan pristup za obradu trauma i PTSP-a koji koristi vođene pokrete očiju da bi se "prerađivala" traumatska sećanja. Ako se pitaš "šta je EMDR terapija" i kako može da ti pomogne, ova metoda je dokazano efikasna za ubrzano olakšanje simptoma traume, anksioznosti i PTSP-a.\n\nNa Rešimo Probleme možeš pronaći sertifikovane EMDR terapeute koji rade online. Proces uključuje bilateralnu stimulaciju mozga kroz pokrete očiju ili druge podražaje, što pomaže da se traumatska sećanja prerađuju na bezbedan način. Cilj je smanjenje emocionalne težine sećanja i razvoj novih, adaptivnijih uverenja o sebi.\n\nPronađi EMDR terapeuta koji ti odgovara, uporedi profile i zakaži online sesiju. EMDR online terapija nudi fleksibilnost i kontinuitet bez potrebe za putovanjem.',
    seoTitle: "EMDR Terapija Online: Sertifikovani Terapeuti od 30€",
    seoDescription:
      "Zakaži EMDR terapiju online sa sertifikovanim terapeutom. Obrada traume i PTSP-a kroz 6-12 sesija. Uporedi profile terapeuta, prvi termin za manje od 24h.",
    relatedConditions: ["trauma", "ptsp", "trauma-iz-detinjstva"],
    directoryContent: {
      title: "EMDR terapeuti online za rad sa traumatskim iskustvima",
      description:
        "EMDR je jedan od psihoterapijskih pristupa koje neki terapeuti koriste u radu sa traumatskim iskustvima. Na Rešimo Probleme možeš da pregledaš profile terapeuta koji u kvalifikacijama navode EMDR edukaciju, uporediš njihov pristup, cenu i slobodne termine, pa zakažeš online razgovor. Ako nisi siguran/na da li je ovaj pristup za tebe, razgovor sa terapeutom je mesto da to zajedno razjasnite.",
      seoTitle: "EMDR terapeuti online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta koji u kvalifikacijama navodi EMDR edukaciju. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem EMDR terapeuta online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji u kvalifikacijama navode EMDR edukaciju. Uporedi njihov opis rada, cenu sesanse i slobodne termine pre nego što zakažeš razgovor.",
        },
        {
          question: "Šta mogu da proverim na profilu EMDR terapeuta?",
          answer:
            "Na profilu možeš da proveriš kvalifikacije koje terapeut javno navodi, njegov opis rada, cenu i dostupne termine. To ti pomaže da izabereš osobu sa kojom želiš da razgovaraš.",
        },
        {
          question: "Da li je EMDR isto što i razgovorna terapija?",
          answer:
            "EMDR je specifičan psihoterapijski pristup koji terapeut može uključiti u rad kada proceni da je primeren. Na prvom razgovoru možeš pitati kako terapeut radi i šta možeš da očekuješ od procesa.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako nisam siguran/na da li je EMDR za mene?",
          answer:
            "Da. Ne moraš unapred da znaš koji ti pristup najviše odgovara. Možeš zakazati razgovor i sa terapeutom razjasniti svoje potrebe, očekivanja i naredne korake.",
        },
        {
          question: "Koliko košta online razgovor sa EMDR terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više profila i izabrati termin i cenu koji ti odgovaraju.",
        },
      ],
    },
    passes: true,

    faqItems: [
      {
        question: "Šta je EMDR terapija i kako radi?",
        answer:
          'EMDR (Eye Movement Desensitization and Reprocessing) je terapijski pristup koji koristi vođene pokrete očiju ili druge bilateralne stimulacije da bi se prerađivala traumatska sećanja. Dok se fokusiraš na uznemirujuće sećanje, terapeut te vodi kroz pokrete očiju levo-desno, što pomaže mozgu da prirodno "prerađuje" traumu i smanjuje njenu emocionalnu težinu.',
      },
      {
        question: "Za šta je EMDR najkorisniji?",
        answer:
          "EMDR je posebno efikasan za PTSP, traume iz detinjstva, saobraćajne nesreće, zlostavljanje, ali pomaže i kod anksioznosti, depresije, fobija, paničnih napada i hroničnog stresa. Koristi se i kod sportista za performans, kao i kod komplikovanih žalovanja.",
      },
      {
        question: "Kako teče EMDR sesija?",
        answer:
          "Sesija počinje pripremom i stabilizacijom gde terapeut uči tehnike za regulaciju emocija. Zatim se fokusiraš na traumatsko sećanje dok se koriste bilateralni podražaji (pokreti očiju, tapkanje ili zvukovi). Proces traje 60-90 minuta i može zahtevati 3-6 sesija zavisno od kompleksnosti traume.",
      },
      {
        question: "Da li EMDR ide online i kako?",
        answer:
          "Da, EMDR se uspešno radi online. Terapeut koristi vizuelne ili akustične podražaje sinhronizovane preko video poziva. Sertifikovani EMDR terapeuti imaju posebnu obuku za online rad i osiguravaju bezbednost procesa kroz jasne protokole.",
      },
      {
        question: "Koliko traje EMDR terapija i kada se vide rezultati?",
        answer:
          "Za jednu traumu mogu biti dovoljne 3-6 sesija, dok kompleksne traume zahtevaju više. Mnogi klijenti primećuju olakšanje već nakon nekoliko sesija, sa smanjenjem simptoma PTSP-a, anksioznosti ili noćnih mora. EMDR je brži od mnogih drugih terapija.",
      },
      {
        question: "Ko može biti EMDR terapeut?",
        answer:
          "EMDR terapeuti moraju biti licencirani psiholozi ili psihijatri sa dodatnom sertifikacijom EMDR International Association (EMDRIA) ili sličnih organizacija. U Srbiji postoji Asocijacija EMDR Srbija koja vodi evidenciju sertifikovanih terapeuta.",
      },
    ],
  },

  // 12
  kbt: {
    label: "KBT: kognitivno-bihevioralna terapija",
    guideLink: {
      href: "/blog/kognitivno-bihevioralna-terapija-kako-promeniti-misli-i-zivot",
      label: "Pročitaj vodič o KBT-u",
    },
    therapistSearchTerms: [
      "KBT",
      "KBT (Kognitivno-bihejvioralna terapija)",
      "Kognitivno bihejvioralna terapija (KBT)",
      "Kognitivno-bihejvioralna terapija",
      "Kognitivno-bihejvioralna terapija (KBT)",
    ],
    title: "Online KBT terapeuti: pregledaj profile i zakaži razgovor",
    searchTerm:
      "KBT kognitivno-bihevioralna terapija kognitivno bihevioralna terapija CBT kognitivna terapija bihevioralna terapija kbt terapija terapeut za kbt online kbt kbt online",
    description:
      "Ako tražiš KBT terapeuta, možeš da pregledaš javne profile osoba koje navode ovaj pristup u radu. Na profilu proveri šta terapeut navodi o obrazovanju, pristupu i oblastima rada, pa uporedi cenu i prikazane slobodne termine.\n\nKada izabereš osobu sa kojom želiš da razgovaraš, sa profila možeš da nastaviš na online zakazivanje. Za objašnjenje samog KBT pristupa pogledaj vodič o KBT-u.",
    seoTitle: "KBT terapeuti online | Kognitivno-bihevioralna terapija",
    seoDescription:
      "Pregledaj online KBT terapeute, njihove profile, pristupe, cene i prikazane slobodne termine, pa zakaži razgovor.",

    relatedConditions: ["anksioznost", "fobije", "depresija"],
    directoryContent: {
      title: "Online KBT terapeuti: uporedi profil, cenu i slobodne termine",
      description:
        "Ako tražiš KBT terapeuta, ovde možeš da pregledaš profile osoba koje javno navode ovaj pristup. Na profilu proveri šta terapeut navodi o obrazovanju, pristupu i oblastima rada, zatim uporedi cenu i prikazane slobodne termine. Kada izabereš osobu sa kojom želiš da razgovaraš, možeš da nastaviš na online zakazivanje. Za objašnjenje KBT pristupa pogledaj vodič o KBT-u.",
      seoTitle: "KBT terapeuti online | Rešimo Probleme",
      seoDescription:
        "Pronađi online KBT terapeuta. Uporedi profile, pristupe, cene i slobodne termine, pa zakaži razgovor koji odgovara tvojim potrebama.",
      faqItems: [
        {
          question: "Kako da pronađem KBT terapeuta online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji javno navode KBT pristup. Pročitaj šta navode o obrazovanju, načinu rada i oblastima rada, pa uporedi cenu i prikazane slobodne termine.",
        },
        {
          question: "Šta znači KBT, odnosno kognitivno-bihejvioralna terapija?",
          answer:
            "KBT je skraćenica za kognitivno-bihejvioralnu terapiju. Terapeut može objasniti kako taj pristup koristi u svom radu, a u vodiču o KBT-u možeš pročitati šire objašnjenje pristupa.",
        },
        {
          question: "Mogu li da uporedim više KBT terapeuta pre zakazivanja?",
          answer:
            "Da. Možeš da pregledaš više profila i uporediš ono što terapeuti javno navode o radu, kao i cenu i prikazane slobodne termine, pre nego što izabereš osobu za razgovor.",
        },
        {
          question: "Kako da zakažem online razgovor sa KBT terapeutom?",
          answer:
            "Kada izabereš terapeuta, otvori njegov profil i izaberi prikazani slobodan termin. Zatim nastavljaš kroz online zakazivanje.",
        },
        {
          question: "Koliko košta online KBT sesija?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više profila i izabrati termin i cenu koji ti odgovaraju.",
        },
      ],
    },
    passes: true,

    faqItems: [
      {
        question: "Šta je KBT i kako funkcioniše?",
        answer:
          "KBT je skraćenica za kognitivno-bihejvioralnu terapiju. Na profilu terapeuta možeš videti kako on javno opisuje svoj način rada, a vodič o KBT-u objašnjava sam pristup detaljnije.",
      },
      {
        question: "Za koje probleme pomaže KBT terapija?",
        answer:
          "Na profilu svakog terapeuta možeš videti oblasti rada koje javno navodi. Ako nisi siguran/na da li je KBT pristup dobar izbor za ono što te opterećuje, postavi terapeutu pitanje pre ili na prvom razgovoru.",
      },
      {
        question: "Kako pronaći terapeuta za KBT?",
        answer:
          "Pregledaj profile terapeuta koji javno navode KBT pristup. Uporedi šta navode o obrazovanju, načinu rada i oblastima rada, kao i cenu i prikazane slobodne termine.",
      },
      {
        question: "Da li online KBT funkcioniše jednako dobro kao uživo?",
        answer:
          "Terapeut na profilu navodi kako radi, a tokom razgovora možete razjasniti da li online format odgovara vašem dogovoru i okolnostima.",
      },
      {
        question: "Šta pomaže kod KBT i koliko traje tretman?",
        answer:
          "Trajanje i ciljeve rada dogovaraš sa terapeutom. Na početku razgovora možeš pitati kako pristupa temi koja ti je važna i šta možete realno planirati.",
      },
      {
        question: "Kako se pripremiti za KBT sesije?",
        answer:
          "Pre prvog razgovora možeš zabeležiti šta te trenutno opterećuje i koja pitanja želiš da postaviš. Ne moraš unapred da imaš dijagnozu ni tačno ime terapijskog pristupa.",
      },
      {
        question: "Da li KBT zahteva lekove?",
        answer:
          "O lekovima odlučuje lekar ili psihijatar u okviru svoje procene. Ako ti je to pitanje važno, reci ga terapeutu kako biste razjasnili koji je odgovarajući sledeći korak.",
      },
    ],
  },

  // 13
  ptsp: {
    label: "PTSP - posttraumatski stresni poremećaj",
    therapistSearchTerms: ["Trauma i PTSP", "Traumatska iskustva i PTSP"],
    guideLink: {
      href: "/blog/ptsp-razumevanje-traume-i-put-do-oporavka",
      label: "Pročitaj vodič o PTSP-u i traumi",
    },
    directoryContent: {
      title: "Online terapeut za podršku kod PTSP-a",
      description:
        "Ako se nakon traumatskog iskustva nosiš sa uznemirujućim sećanjima, napetošću ili izbegavanjem, ovde možeš pregledati profile terapeuta koji javno navode rad sa traumatskim iskustvima i PTSP-om. Razgovor sa terapeutom može biti prostor da zajedno procenite šta ti je sada potrebno.",
      seoTitle: "Online terapeut za PTSP podršku | Rešimo Probleme",
      seoDescription:
        "Pregledaj profile terapeuta koji navode rad sa traumatskim iskustvima i PTSP-om. Zakaži online razgovor na srpskom jeziku.",
      faqItems: [
        {
          question: "Kome je ova stranica namenjena?",
          answer:
            "Namenjena je osobama koje traže razgovor sa terapeutom nakon traumatskog iskustva ili zbog tema koje povezuju sa PTSP-om. Terapeut ne postavlja dijagnozu preko ove stranice.",
        },
        {
          question: "Kako da izaberem terapeuta?",
          answer:
            "Otvori profil, pročitaj koje teme i kvalifikacije terapeut navodi, pa izaberi osobu sa kojom želiš prvi razgovor. Na prvoj sesiji možeš pitati kako pristupa tvojoj situaciji.",
        },
        {
          question: "Da li ova stranica obećava određenu metodu?",
          answer:
            "Ne. Profili prikazuju ono što svaki terapeut javno navodi. Dogovor o pristupu, tempu i ciljevima nastaje u razgovoru sa izabranim terapeutom.",
        },
        {
          question: "Kada je potrebna hitna pomoć?",
          answer:
            "Ako si u neposrednoj opasnosti, imaš misli da povrediš sebe ili drugoga, ili ne možeš da ostaneš bezbedan/na, pozovi lokalnu hitnu službu ili osobu od poverenja. Ova stranica nije zamena za hitnu pomoć.",
        },
      ],
    },
    title: "Online terapeuti za PTSP - trauma terapija i EMDR tretmani",
    searchTerm:
      "PTSP posttraumatski stres posttraumatski stresni poremećaj flashback okidači hipervigilnost noćne more trauma terapija EMDR terapeut za PTSP online terapija PTSP",
    description:
      "Simptomi posttraumatskog stresnog poremećaja (PTSP) poput flashbackova, noćnih mora i hipervigilnosti mogu značajno otežati svakodnevni život. Pronađi terapeuta specijalizovanog za PTSP i trauma-informisanu psihoterapiju.\n\nNa Rešimo Probleme možeš pronaći licencirane terapeute za PTSP koji rade sa EMDR metodom, kognitivno-bihejvioralnom terapijom i drugim dokazanim pristupima za obradu traume. Online terapija za PTSP omogućava fleksibilno zakazivanje, rad na okidačima i povratak osećaja sigurnosti.\n\nPregledaj profile terapeuta, pročitaj recenzije i zakaži konsultaciju. Specijalizovani tretmani za PTSP pomažu u smanjenju simptoma i poboljšanju kvaliteta života.",
    seoTitle: "Terapija za PTSP online | EMDR terapeuti",
    seoDescription:
      "Pronađi online terapeuta za PTSP i trauma terapiju. EMDR tretmani i specijalizovani terapeuti pomažu u obradi traume — zakaži termin danas.",

    relatedConditions: ["trauma", "trauma-iz-detinjstva", "emdr", "nesanica"],
    passes: true,

    faqItems: [
      {
        question: "Šta pomaže kod PTSP simptoma?",
        answer:
          "EMDR terapija, trauma-informisana psihoterapija i kognitivno-bihejvioralne metode pokazuju se najefikasnijim za smanjenje PTSP simptoma poput flashbackova i hipervigilnosti.",
      },
      {
        question: "Kako pronaći terapeuta za PTSP?",
        answer:
          "Traži terapeuta sa iskustvom u radu sa traumom i PTSP-om. Na Rešimo Probleme možeš pregledati profile, recenzije i direktno zakazati online konsultaciju.",
      },
      {
        question: "Da li online terapija pomaže kod PTSP-a?",
        answer:
          "Da, online terapija za PTSP je efikasna i pristupačna opcija. Terapeuti koriste EMDR i druge metode putem video poziva, što omogućava kontinuirani rad na obradi traume.",
      },
      {
        question: "Koliko traje tretman za PTSP?",
        answer:
          "Trajanje zavisi od kompleksnosti traume, ali većina tretmana traje 8-15 sesija. EMDR terapija često daje brže rezultate kod određenih simptoma.",
      },
      {
        question: "Šta je EMDR i kako pomaže kod PTSP-a?",
        answer:
          "EMDR je specijalizovana metoda za obradu traumatskih sećanja kroz vođene pokrete očiju. Pomaže u smanjenju intenziteta flashbackova i okidača kod PTSP-a.",
      },
      {
        question: "Kada potražiti pomoć za PTSP?",
        answer:
          "Ako PTSP simptomi traju duže od mesec dana i ometaju svakodnevno funkcionisanje, vreme je da potražiš stručnu pomoć. Rani tretman daje bolje rezultate.",
      },
      {
        question: "Da li PTSP prolazi sam od sebe?",
        answer:
          "Ponekad se simptomi ublaže bez intervencije, ali bez tretmana PTSP često traje godinama. Terapija značajno ubrzava oporavak i smanjuje rizik od relapsa.",
      },
    ],
  },

  // 14
  okp: {
    label: "OKP: opsesivno-kompulzivni poremećaj",
    guideLink: {
      href: "/blog/okp-opsesivno-kompulsivni-poremecaj-sta-je-to-i-kako-se-leci",
      label: "Pročitaj vodič o OKP-u",
    },
    title: "Online terapeuti za OKP: smanji opsesije i rituale",
    searchTerm:
      "OKP OCD opsesivno-kompulzivni poremećaj opsesije kompulzije rituali provere pranje ruku nametljive misli izlaganje prevencija odgovora ERP KBT online terapija",
    therapistSearchTerms: ["OKP"],
    description:
      "Opsesivno-kompulzivni poremećaj (OKP) karakterišu nametljive opsesivne misli i kompulzivna ponašanja koja ometaju svakodnevni život. Ako se boriš sa ritualima provere, prekomernim pranjem ruku ili stalnim sumnjama, ovo može biti opsesivno-kompulzivni poremećaj. Online terapija za OKP pomaže da prepoznaš okidače i smanjiš intenzitet simptoma.\n\nNa Rešimo Probleme možeš pronaći terapeute specijalizovane za opsesivno-kompulzivni poremećaj koji rade sa ERP (izlaganje i prevencija odgovora) i KBT pristupima. Ovi metodi su dokazano efikasni za smanjenje opsesija i rituala, uz online sesije koje omogućavaju fleksibilno zakazivanje i kontinuirani rad na promeni. Pregledaj profile terapeuta, uporedi pristupe i zakaži termin već danas.",
    seoTitle: "Terapija za OKP online | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapiju za OKP sa specijalizovanim terapeutima. ERP i KBT pristupi za smanjenje opsesija i rituala — zakaži termin i započni promenu.",

    relatedConditions: ["anksioznost", "hipohondrija", "kbt"],
    directoryContent: {
      title: "OKP terapeuti online za razgovor o opsesijama i ritualima",
      description:
        "Ako te nametljive misli, provere ili rituali opterećuju i utiču na svakodnevicu, razgovor sa terapeutom može biti prvi korak ka razumevanju onoga što proživljavaš. Na Rešimo Probleme možeš da pregledaš profile terapeuta koji navode rad sa OKP-om, uporediš njihov pristup, cenu i slobodne termine, pa zakažeš online razgovor. Ne moraš unapred da znaš kako da opišeš sve što ti se dešava.",
      seoTitle: "OKP terapeuti online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta koji navodi rad sa OKP-om. Uporedi profile, pristupe, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za OKP online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa OKP-om. Uporedi opis rada, cenu i dostupne termine pre zakazivanja razgovora.",
        },
        {
          question: "Šta znači OKP?",
          answer:
            "OKP je skraćenica za opsesivno-kompulzivni poremećaj. Ako se prepoznaješ u nametljivim mislima, proverama ili ritualima, razgovor sa stručnom osobom može pomoći da razjasniš šta ti je potrebno.",
        },
        {
          question: "Da li moram unapred da znam da imam OKP?",
          answer:
            "Ne moraš. Možeš početi od iskustava koja te opterećuju i sa terapeutom razjasniti kako ih najbolje opisati i koji bi naredni koraci mogli imati smisla.",
        },
        {
          question: "Kako izgleda prvi online razgovor?",
          answer:
            "Prvi razgovor je prilika da predstaviš ono što ti je važno, postaviš pitanja i čuješ kako terapeut radi. Zatim možeš odlučiti da li želiš da nastavite saradnju.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom za OKP?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više profila i izabrati termin i cenu koji ti odgovaraju.",
        },
      ],
    },
    passes: true,

    faqItems: [
      {
        question: "Šta je OKP i kako prepoznati simptome?",
        answer:
          "OKP je opsesivno-kompulzivni poremećaj sa nametljivim mislima (opsesijama) i prisilnim radnjama (kompulzijama). Simptomi uključuju rituale provere, prekomerno pranje, brojanje ili stalne sumnje. Ako ovi rituali ometaju svakodnevni život više od sat vremena dnevno, vreme je za stručnu pomoć.",
      },
      {
        question: "Koji tretmani pomažu kod OKP?",
        answer:
          "Najefikasniji su ERP (izlaganje i prevencija odgovora) i KBT. ERP podrazumeva postepeno izlaganje strahovima bez rituala, dok KBT radi na promeni misaonih obrazaca. Lekovi poput SSRI mogu biti dodatak terapiji kod težih slučajeva.",
      },
      {
        question: "Koliko traje terapija za OKP?",
        answer:
          "Većina ljudi vidi poboljšanja u prvih 8-12 nedelja dosledne ERP terapije. Kompletan oporavak može trajati 3-6 meseci ili duže, zavisno od težine simptoma i redovnog vežbanja između sesija.",
      },
      {
        question: "Da li online terapija funkcioniše za OKP?",
        answer:
          "Da, online ERP i KBT su jednako efikasni kao terapija licem u lice. Terapeut vodi vežbe izlaganja, daje zadatke i prati napredak kroz aplikacije ili dnevnik. Mnogi pacijenti preferiraju online pristup zbog fleksibilnosti i privatnosti.",
      },
      {
        question: "Kako pronaći dobrog terapeuta za OKP?",
        answer:
          "Traži terapeute sa iskustvom u ERP terapiji i radu sa OKP. Proveri njihove recenzije, pristup i da li nude online sesije. Na našoj platformi možeš uporediti profile i zakaži konsultaciju da vidiš da li vam odgovara saradnja.",
      },
      {
        question: "Šta ako simptomi OKP utiču na posao ili odnose?",
        answer:
          "OKP često remeti svakodnevni život, ali pravovremena terapija može značajno poboljšati funkcionisanje. Terapeuti rade na strategijama za upravljanje simptomima tokom dana i uključivanju porodice u proces oporavka.",
      },
    ],
  },

  // 15
  "bipolarni-poremecaj": {
    passes: true,
    label: "Bipolarni poremećaj",
    title:
      "Psihoterapijska podrška za bipolarni poremećaj: pronađi terapeuta online",
    searchTerm:
      "bipolarni poremećaj bipolarni afektivni poremećaj manija hipomanija depresivne epizode ciklusi raspoloženja stabilizacija raspoloženja ciklotimija psihoterapija bipolarni KBT bipolarni poremećaj terapija bipolarnost podrška porodici bipolarni poremećaj i brak",
    description:
      "Bipolarni poremećaj (bipolarni afektivni poremećaj) donosi smenjivanje epizoda manije ili hipomanije sa periodima depresije, što utiče na raspoloženje, energiju, san i svakodnevno funkcionisanje. Lečenje obično uključuje kombinaciju lekova (stabilizatora raspoloženja) i psihoterapije.\n\nNa Rešimo Probleme možeš pronaći psihoterapeuta koji radi sa osobama sa bipolarnim poremećajem kroz KBT (kognitivno-bihejvioralnu terapiju), psihoedukaciju ili podršku u održavanju rutina i prepoznavanju ranih znakova epizoda.\n\nPregledaj profile terapeuta, uporedi pristupe i zakaži online sesiju. Psihoterapija uz farmakoterapiju može pomoći u stabilizaciji raspoloženja, boljem razumevanju okidača i svakodnevnoj organizaciji.",
    seoTitle: "Bipolarni poremećaj: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi psihoterapeuta za bipolarni poremećaj online. KBT, psihoedukacija i podrška uz lekove. Uporedi profile terapeuta i zakaži sesiju na Rešimo Probleme.",
    relatedConditions: ["depresija", "nesanica"],
    faqItems: [
      {
        question:
          "Koja je uloga psihoterapije uz lekove kod bipolarnog poremećaja?",
        answer:
          "Psihoterapija ne zamenjuje lekove, ali ih značajno dopunjuje. Terapeut pomaže u prepoznavanju ranih znakova epizoda, uspostavljanju stabilnih rutina (san, ishrana, aktivnost), upravljanju stresom i edukaciji o poremećaju. Istraživanja pokazuju da kombinacija farmakoterapije i psihoterapije daje bolje rezultate od samih lekova.",
      },
      {
        question: "Kako da prepoznam rane znake manije ili hipomanije?",
        answer:
          "Uobičajeni rani znaci: smanjena potreba za snom, povišena energija i raspoloženje, ubrzane misli, povećana pričljivost, impulzivno ponašanje (trošenje, rizične odluke). Vođenje dnevnika raspoloženja pomaže da uočiš obrasce. Kada primetiš rane znake, kontaktiraj terapeuta ili psihijatra pre nego što epizoda eskalira.",
      },
      {
        question: "Da li je bipolarni poremećaj izlečiv?",
        answer:
          "Bipolarni poremećaj je hroničan, ali uz adekvatno lečenje (lekovi i psihoterapija) većina osoba postiže stabilnost i kvalitetan život. Cilj terapije nije potpuno izlečenje, već upravljanje simptomima, prevencija epizoda i funkcionisanje u svakodnevnim aktivnostima. Redovni kontakt sa psihijatrom i psihoterapeutom je ključan.",
      },
      {
        question: "Koje vrste psihoterapije pomažu kod bipolarnog poremećaja?",
        answer:
          "Najčešće korišćene metode su KBT (kognitivno-bihejvioralna terapija) za prepoznavanje negativnih obrazaca razmišljanja, psihoedukacija za razumevanje poremećaja i lekova, terapija interpersonalnih i socijalnih ritmova (IPSRT) za stabilizaciju dnevnih rutina, te porodična terapija za edukaciju i podršku bliskih osoba.",
      },
      {
        question: "Kako bipolarni poremećaj utiče na brak i partnerski odnos?",
        answer:
          "Smene epizoda mogu opteretiti komunikaciju, poverenje i emotivnu bliskost. Partner često ne razume promene raspoloženja ili se oseća bespomoćno. Terapija za parove ili porodična terapija pomaže oba partnera da razumeju poremećaj, nauče kako da prepoznaju epizode i komuniciraju tokom teških perioda.",
      },
      {
        question: "Da li online terapija funkcioniše za bipolarni poremećaj?",
        answer:
          "Da, online psihoterapija je efikasna kao dopuna farmakoterapiji. Prednost je veća dostupnost (redovniji kontakti, bez putovanja), što je posebno važno za održavanje kontinuiteta između epizoda. Na Rešimo Probleme možeš zakazati sesiju sa terapeutom koji ima iskustvo u radu sa bipolarnim poremećajem.",
      },
      {
        question: "Da li dnevnik raspoloženja pomaže u terapiji?",
        answer:
          "Da. Svakodnevno beleženje raspoloženja, sna, energije i stresa pomaže tebi i terapeutu da uočite obrasce i rane znake epizoda. Terapeut koristi te podatke za prilagođavanje terapijskog plana i pravovremene intervencije pre nego što simptomi postanu intenzivni.",
      },
    ],
  },

  // 16
  borderline: {
    label: "Granični poremećaj ličnosti (BPD)",
    therapistSearchTerms: ["Granicni poremecaj"],
    guideLink: {
      href: "/blog/granicni-poremecaj-licnosti",
      label: "Pročitaj vodič o graničnom poremećaju ličnosti (BPD)",
    },
    searchTerm:
      "borderline granični poremećaj ličnosti BPD emocionalna disregulacija strah od napuštanja impulzivnost nestabilni odnosi DBT terapija regulacija emocija samopovređivanje",
    description:
      "Granični poremećaj ličnosti (BPD) donosi intenzivne emocije, strah od napuštanja i turbulentne odnose. DBT terapeuti ti pomažu da naučiš veštine regulacije emocija, tolerancije na distres i stabilizacije odnosa. Pronađi stručnjaka obučenog za rad sa BPD-om i zakaži prvu sesiju na Rešimo Probleme.",
    directoryContent: {
      title: "Online terapeut za borderline (BPD)",
      description:
        "Ako tražiš terapeuta za razgovor o intenzivnim emocijama, odnosima ili temi graničnog poremećaja ličnosti (BPD), ovde možeš da pregledaš javne profile terapeuta koji navode rad sa tom temom. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Stranica je namenjena pronalasku terapeuta, ne postavljanju dijagnoze.",
      seoTitle: "Terapeut za borderline (BPD) online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za razgovor o borderlineu (BPD). Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za borderline (BPD)?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa graničnim poremećajem ličnosti. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li ova stranica služi za BPD testiranje ili dijagnozu?",
          answer:
            "Ne. Stranica služi za pronalazak terapeuta za razgovor. Za dijagnostičku procenu obrati se odgovarajućoj zdravstvenoj ustanovi ili stručnjaku koji je pruža.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako nisam siguran/na da li je BPD prava tema?",
          answer:
            "Da. Možeš početi od onoga što doživljavaš i na prvom razgovoru pitati terapeuta kako pristupa temi koja te opterećuje.",
        },
        {
          question: "Šta mogu da proverim pre zakazivanja?",
          answer:
            "Na profilu možeš da proveriš kako terapeut opisuje svoj rad, cenu sesanse i slobodne termine. To ti pomaže da odlučiš sa kim želiš da razgovaraš.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle:
      "Terapeuti za borderline (BPD): DBT i regulacija emocija | Rešimo Probleme",
    seoDescription:
      "Pronađi DBT terapeuta za granični poremećaj ličnosti (BPD). Online terapija za regulaciju emocija, stabilizaciju odnosa i smanjenje samopovređivanja. Zakaži sesiju.",

    relatedConditions: ["dbt-terapija", "problemi-vezivanja", "odnosi"],
    passes: true,

    faqItems: [
      {
        question: "Šta je granični poremećaj ličnosti (BPD)?",
        answer:
          "Borderline poremećaj ličnosti (BPD) karakterišu intenzivne promene raspoloženja, impulzivno ponašanje, nestabilni odnosi, strah od napuštanja i često samopovređivanje. Terapija pomaže u regulaciji emocija i stabilizaciji odnosa.",
      },
      {
        question: "Koja terapija najbolje pomaže kod BPD?",
        answer:
          "DBT (Dijalektička bihevioralna terapija) je zlatni standard za lečenje BPD-a. Kombinuje individualne sesije sa treningom veština: mindfulness, regulacija emocija, tolerancija na distres i interpersonalne veštine. Takođe su efikasne MBT, schema terapija i TFP.",
      },
      {
        question: "Kako DBT pomaže u regulaciji emocija kod BPD?",
        answer:
          "DBT te uči konkretnim veštinama: prepoznavanje emocionalnih okidača, tehnike smirivanja (ice-dive, mindfulness), toleranciju na distres bez samopovređivanja i kako graditi stabilnije odnose. Istraživanja pokazuju značajno smanjenje simptoma.",
      },
      {
        question: "Može li online terapija biti efikasna za BPD?",
        answer:
          "Da. Online DBT terapija pokazuje dobre rezultate uz strukturisane radne listove, vežbe između sesija i redovnu komunikaciju sa terapeutom. Mnogi terapeuti nude i kriznu podršku putem poruka.",
      },
      {
        question: "Kako pronaći terapeuta specijalizovanog za BPD?",
        answer:
          "Traži terapeute obučene za DBT, MBT ili schema terapiju. Na Rešimo Probleme možeš filtrirati terapeute po specijalnosti 'borderline' i videti njihovo iskustvo sa graničnim poremećajem ličnosti. Zakaži prvu sesiju direktno online.",
      },
      {
        question: "Koliko dugo traje terapija za BPD?",
        answer:
          "DBT program obično traje 6-12 meseci sa individualnim i grupnim sesijama. Napredak je postepen — mnogi ljudi vide poboljšanje u regulaciji emocija i odnosima nakon prvih nekoliko meseci redovnog rada.",
      },
      {
        question: "Šta ako imam suicidalne misli ili samopovređujem se?",
        answer:
          "Odmah kontaktiraj terapeuta ili krizni centar. DBT terapija specifično radi na smanjenju samopovređivanja kroz veštine tolerancije na distres (hladna voda, kocka leda, urge surfing). U akutnoj krizi pozovi hitnu pomoć ili Centar Srce 0800-300-303.",
      },
    ],
  },

  // 17
  "adhd-odrasli": {
    passes: true,
    label: "ADHD kod odraslih",
    guideLink: {
      href: "/blog/adhd-kod-odraslih-znaci-simptomi-i-prvi-koraci-do-podrske",
      label: "Pročitaj vodič o ADHD-u kod odraslih",
    },
    therapistSearchTerms: [
      "ADHD",
      "ADHD - nepaznja, hiperaktivnost i impulsivnost",
    ],
    title:
      "Online terapeuti za ADHD kod odraslih: podrška za fokus, organizaciju i svakodnevne izazove",
    searchTerm:
      "ADHD kod odraslih poremećaj pažnje hiperaktivnost impulzivnost ADHD odrasli terapija ADD poremećaj pažnje kod odraslih ADHD simptomi odrasli fokus koncentracija prokrastinacija izvršne funkcije dezorganizacija upravljanje vremenom KBT za ADHD coaching ADHD dijagnoza ADHD odrasli ADHD i anksioznost ADHD i depresija",
    description:
      "ADHD (poremećaj pažnje i hiperaktivnosti) kod odraslih može da utiče na koncentraciju, organizaciju, upravljanje vremenom i odnose sa drugima. Simptomi poput prokrastinacije, zaboravnosti i impulsivnosti često se pogrešno tumače kao lenjost ili nedostatak volje. Pronađi terapeuta sa iskustvom u radu sa ADHD-om kod odraslih i započni rad na konkretnim strategijama za svakodnevno funkcionisanje.",
    directoryContent: {
      title: "Online terapeuti za ADHD kod odraslih",
      description:
        "Ako želiš da razgovaraš o pažnji, organizaciji, odlaganju obaveza ili drugim svakodnevnim teškoćama, možeš potražiti terapeuta čiji profil navodi rad sa ADHD-om kod odraslih. Na Rešimo Probleme uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Ova stranica služi za pronalazak terapeuta, a ne za procenu ili postavljanje dijagnoze.",
      seoTitle: "Terapeut za ADHD kod odraslih online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za ADHD kod odraslih. Uporedi profile, cene i slobodne termine, pa zakaži razgovor koji ti odgovara.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za ADHD kod odraslih?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa ADHD-om kod odraslih. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question:
            "Da li ova stranica služi za ADHD testiranje ili dijagnozu?",
          answer:
            "Ne. Ovde možeš pronaći terapeuta za razgovor o teškoćama koje te opterećuju. Ako tražiš dijagnostičku procenu, proveri sa odgovarajućom zdravstvenom ustanovom kako ona radi.",
        },
        {
          question: "Šta mogu da pitam terapeuta na prvom razgovoru?",
          answer:
            "Možeš opisati šta ti je teško u svakodnevici i pitati kako terapeut radi sa temama pažnje, organizacije i navika. Takođe možeš pitati da li je njegov pristup dobar izbor za tebe.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "ADHD kod odraslih: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za ADHD kod odraslih online. KBT, coaching i strategije za fokus i organizaciju. Uporedi profile i zakaži sesiju na Rešimo Probleme.",

    relatedConditions: [
      "prokrastinacija-motivacija",
      "karijera-posao",
      "stres",
    ],
    faqItems: [
      {
        question: "Šta je ADHD kod odraslih i kako se prepoznaje?",
        answer:
          "ADHD (poremećaj pažnje i hiperaktivnosti) kod odraslih se ispoljava kroz teškoće sa koncentracijom, organizacijom i kontrolom impulsa. Česti znaci su prokrastinacija, zaboravnost, prekidanje drugih u razgovoru i poteškoće sa praćenjem rokova. Simptomi se razlikuju od osobe do osobe, a mnogi odrasli saznaju za dijagnozu tek u tridesetim ili četrdesetim godinama.",
      },
      {
        question: "Koja vrsta terapije pomaže kod ADHD-a kod odraslih?",
        answer:
          "Kognitivno-bihejvioralna terapija (KBT) je najčešće preporučivan pristup za ADHD kod odraslih. Pomaže u prepoznavanju nefunkcionalnih obrazaca mišljenja i izgradnji praktičnih strategija za organizaciju, upravljanje vremenom i regulaciju emocija. Pored KBT-a, ADHD coaching se fokusira na konkretne veštine i svakodnevne navike.",
      },
      {
        question: "Da li terapija za ADHD pomaže i bez lekova?",
        answer:
          "Da, terapija može doneti značajna poboljšanja i bez medikamentozne terapije. KBT i coaching pomažu u izgradnji rutina, upravljanju prokrastinacijom i poboljšanju fokusa. Kod nekih osoba kombinacija terapije i lekova daje najbolje rezultate, ali to je individualna odluka koju donosiš sa svojim lekarom i terapeutom.",
      },
      {
        question: "Po čemu se ADHD razlikuje od ADD-a?",
        answer:
          "ADD (poremećaj deficita pažnje) je stariji termin koji se koristio za oblik bez hiperaktivnosti. Danas se koristi zajednički termin ADHD sa tri podtipa: pretežno nepažljiv (ranije ADD), pretežno hiperaktivno-impulzivan i kombinovani tip. Sva tri podtipa se uspešno tretiraju terapijom.",
      },
      {
        question: "Kako izgleda online terapija za ADHD?",
        answer:
          "Online sesije za ADHD se odvijaju putem video poziva i uključuju rad na konkretnim strategijama: tehnike upravljanja vremenom, sistemi organizacije, vežbe za poboljšanje fokusa i regulaciju emocija. Terapeut prilagođava pristup tvojim specifičnim izazovima i prati napredak iz sesije u sesiju.",
      },
      {
        question: "Da li ADHD utiče na odnose i posao?",
        answer:
          "ADHD može značajno uticati na partnerske odnose, prijateljstva i profesionalni život. Zaboravnost, impulsivnost i teškoće sa slušanjem mogu stvoriti nesporazume. Terapija pomaže u razvijanju komunikacionih veština, boljem razumevanju sopstvenih obrazaca ponašanja i izgradnji strategija koje olakšavaju svakodnevne interakcije.",
      },
      {
        question: "Koliko sesija je potrebno za ADHD terapiju?",
        answer:
          "Broj sesija zavisi od specifičnih izazova i ciljeva. Mnogi klijenti primete poboljšanje nakon 8-12 sesija KBT-a usmerenog na ADHD. Terapija se fokusira na konkretne veštine koje možeš primeniti odmah, a trajanje se prilagođava tvojim potrebama i napretku.",
      },
    ],
  },

  // 18
  "autizam-podrska": {
    passes: true,
    label: "Autizam (ASD): podrška",
    title:
      "Online podrška za autizam kod odraslih i porodica: pronađi terapeuta sa iskustvom",
    searchTerm:
      "autizam ASD spektar autizma Asperger autizam kod odraslih visoko funkcionalni autizam blagi autizam podrška za autizam savetovanje autizam socijalne veštine senzorna preopterećenost senzorna regulacija komunikacija rutine svakodnevica KBT autizam psihoterapija autizam porodica autizam",
    description:
      "Autizam (poremećaj iz spektra autizma, ASD) je neurorazvojno stanje koje utiče na komunikaciju, socijalne interakcije i senzornu obradu. Kod odraslih na spektru, izazovi mogu uključivati teškoće u razumevanju socijalnih pravila, senzornu preopterećenost i potrebu za jasnim rutinama. Pronađi terapeuta sa iskustvom u radu sa osobama na spektru autizma i porodicama, i započni rad na strategijama koje podržavaju svakodnevno funkcionisanje.",
    seoTitle: "Autizam (ASD): podrška i savetovanje online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za podršku osobama sa autizmom i porodicama online. Savetovanje, KBT i razvoj veština. Uporedi profile i zakaži sesiju na Rešimo Probleme.",

    relatedConditions: ["adhd-odrasli", "porodica", "roditeljstvo"],
    faqItems: [
      {
        question: "Kako terapija pomaže odraslima na spektru autizma?",
        answer:
          "Terapija za odrasle na spektru autizma se fokusira na razvoj komunikacionih i socijalnih veština, upravljanje senzornom preopterećenošću, regulaciju emocija i izgradnju rutina koje olakšavaju svakodnevni život. KBT (kognitivno-bihejvioralna terapija) pomaže u prepoznavanju obrazaca koji stvaraju poteškoće i razvijanju strategija za konkretne situacije.",
      },
      {
        question: "Da li je autizam nešto što se leči?",
        answer:
          "Autizam nije bolest i ne leči se. To je neurorazvojno stanje koje je deo toga ko je osoba. Terapija i savetovanje služe kao podrška za specifične izazove, poput teškoća u komunikaciji, anksioznosti ili senzorne preopterećenosti. Cilj nije promeniti osobu, već joj pomoći da živi kvalitetnije prema sopstvenim potrebama.",
      },
      {
        question: "Kako prepoznati autizam kod odraslih?",
        answer:
          "Mnogi odrasli saznaju da su na spektru tek u zrelom dobu. Česti znaci uključuju teškoće u razumevanju neverbalnih signala, potrebu za predvidljivim rutinama, intenzivna interesovanja za specifične teme, senzornu osetljivost i osećaj da su 'drugačiji' od okoline. Terapeut sa iskustvom može pomoći u razumevanju ovih obrazaca.",
      },
      {
        question: "Šta je visoko funkcionalni autizam?",
        answer:
          "Termin 'visoko funkcionalni autizam' (ranije poznat i kao Aspergerov sindrom) opisuje osobe na spektru koje nemaju intelektualnu ometenost i često funkcionišu samostalno. Izazovi mogu biti manje vidljivi spolja, ali uključuju teškoće sa socijalnim situacijama, senzornu preopterećenost i anksioznost. Terapija pomaže u razvijanju strategija za ove specifične izazove.",
      },
      {
        question: "Da li je sesija prilagođena senzornim potrebama?",
        answer:
          "Da. Terapeuti prilagođavaju tempo, strukturu i vizuelna sredstva tokom sesije. Online format može biti posebno pogodan jer klijent boravi u poznatom okruženju i kontroliše senzorne uslove. Sesije se mogu kraće trajati ako je to potrebno, uz jasnu strukturu i predvidljiv tok.",
      },
      {
        question: "Kako terapija pomaže porodicama osoba sa autizmom?",
        answer:
          "Porodično savetovanje pomaže članovima porodice da bolje razumeju autizam, prilagode komunikaciju i izgrade podršku u svakodnevnom životu. Terapeut može raditi na smanjenju konflikata, uspostavljanju zajedničkih rutina i podršci roditeljima u razumevanju senzornih i komunikacionih potreba deteta ili partnera na spektru.",
      },
      {
        question: "Da li online terapija funkcioniše za osobe na spektru?",
        answer:
          "Za mnoge osobe na spektru, online format je čak i bolji od klasičnog jer nema potrebe za putovanjem, čekaonicom i novim okruženjem. Sesije se odvijaju iz poznatog prostora, što smanjuje senzornu preopterećenost. Terapeut koristi vizuelne materijale, jasne korake i predvidljivu strukturu prilagođenu potrebama klijenta.",
      },
    ],
  },

  // 19
  zavisnosti: {
    therapistSearchTerms: [
      "Adikcija (alkoholizam)",
      "Bolesti zavisnosti -alkohol, PAS, kocka, internet, videoigre, kupovina",
      "Zavisnost od supstanci",
      "Zloupotreba psihoaktivnih supstanci",
    ],
    title:
      "Online terapeuti za zavisnosti: podrška u oporavku i prevenciji recidiva",
    label: "Zavisnosti",
    guideLink: {
      href: "/blog/online-psihoterapija-vodic",
      label: "Pročitaj kako izgleda online psihoterapija",
    },
    searchTerm:
      "zavisnosti zavisnost lečenje zavisnosti terapija za zavisnosti terapeut za zavisnosti bolesti zavisnosti oporavak od zavisnosti prevencija recidiva okidači craving apstinencija detoksikacija motivacioni intervju online terapija zavisnosti pomoć za zavisnost",
    description:
      "Zavisnost je kompleksna bolest koja zahteva stručnu podršku. Bez obzira da li se boriš sa zavisnošću od alkohola, droga, kockanja ili drugih oblika zavisnosti, prvi korak ka oporavku je razgovor sa terapeutom. Na Rešimo Probleme možeš pronaći licencirane terapeute koji koriste motivacioni intervju, KBT i druge dokazane metode za lečenje zavisnosti. Online terapija za zavisnosti omogućava diskretnu i fleksibilnu podršku u prevenciji recidiva, radu na okidačima i izgradnji plana oporavka.",
    directoryContent: {
      title: "Online terapeuti za razgovor o zavisnostima",
      description:
        "Ako želiš da razgovaraš o upotrebi supstanci, kockanju ili drugim navikama koje opterećuju tebe ili tvoje odnose, ovde možeš da pregledaš profile terapeuta koji navode rad sa zavisnostima. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Rešimo Probleme nije klinika, ustanova za lečenje zavisnosti niti zamena za medicinsku ili hitnu pomoć.",
      seoTitle: "Terapeut za zavisnosti online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za razgovor o zavisnostima. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za zavisnosti online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa zavisnostima. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li je ovo klinika ili lečenje zavisnosti?",
          answer:
            "Ne. Rešimo Probleme je platforma za pronalazak terapeuta i online razgovor. Ne pruža bolničko lečenje, detoksikaciju ni rehabilitacioni program.",
        },
        {
          question:
            "Da li mogu da zakažem razgovor zbog problema bliske osobe?",
          answer:
            "Da. Možeš zakazati razgovor da bi razjasnio/la šta te opterećuje i pitao/la terapeuta kako pristupa temi odnosa, granica i podrške bliskoj osobi.",
        },
        {
          question: "Kada treba potražiti medicinsku ili hitnu pomoć?",
          answer:
            "Ako postoji neposredna opasnost po zdravlje ili bezbednost, obrati se hitnoj medicinskoj pomoći. Online terapija ne zamenjuje hitnu ili medicinsku negu.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Lečenje zavisnosti online: pronađi terapeuta | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za zavisnosti online. Motivacioni intervju, KBT i planovi prevencije recidiva. Licencirani stručnjaci na platformi Rešimo Probleme.",

    relatedConditions: [
      "alkoholizam",
      "kockanje",
      "pusenje-odvikavanje",
      "digitalna-zavisnost",
    ],
    passes: true,

    faqItems: [
      {
        question: "Šta je zavisnost i da li se može izlečiti?",
        answer:
          "Zavisnost je hronična bolest koja utiče na mozak i ponašanje, ali se može uspešno lečiti. Uz stručnu podršku, motivaciju i strukturisan plan oporavka, mnogi ljudi postižu dugotrajnu apstinenciju i poboljšanje kvaliteta života.",
      },
      {
        question: "Kako da prepoznam da imam problem sa zavisnošću?",
        answer:
          "Znaci zavisnosti uključuju gubitak kontrole nad količinom ili učestalošću, nastavak uprkos negativnim posledicama, povlačenje iz društvenih aktivnosti, i pojavu cravinga ili apstinencijalnih simptoma. Ako prepoznaješ ove obrasce, razgovor sa terapeutom može pomoći.",
      },
      {
        question: "Da li je online terapija efikasna za lečenje zavisnosti?",
        answer:
          "Da, online terapija za zavisnosti može biti vrlo efikasna, posebno za motivacioni intervju, praćenje plana oporavka i ranu intervenciju. Omogućava diskreciju, fleksibilnost i redovnu podršku bez potrebe za fizičkim dolaskom.",
      },
      {
        question: "Šta je prevencija recidiva i kako funkcioniše?",
        answer:
          "Prevencija recidiva je strukturisan pristup koji uključuje mapiranje okidača, razvoj strategija za upravljanje cravingom, SOS plan za krizne situacije i izgradnju mreže podrške. Cilj je prepoznati rizične situacije pre nego što dovedu do povratka na staro ponašanje.",
      },
      {
        question: "Kako izgleda prvi razgovor sa terapeutom za zavisnosti?",
        answer:
          "Prva seansa obuhvata upoznavanje, procenu trenutnog stanja i istorije zavisnosti, razgovor o motivaciji za promenu i postavljanje početnih ciljeva. Terapeut predlaže okvir rada i zajedno pravite plan za naredne korake.",
      },
      {
        question: "Koliko traje lečenje zavisnosti?",
        answer:
          "Trajanje zavisi od vrste zavisnosti, njene težine i individualnih faktora. Istraživanja pokazuju da je za značajan napredak potrebno najmanje 3-6 meseci aktivnog rada, dok dugoročna podrška može trajati i duže radi održavanja oporavka.",
      },
      {
        question: "Kako mogu da pomognem članu porodice sa zavisnošću?",
        answer:
          "Podrška porodice je važna, ali i zahtevna. Terapeut može pomoći da naučiš kako postaviti zdrave granice, komunicirati bez optuživanja i brinuti o sebi dok podržavaš bližnjeg. Porodične sesije mogu biti deo plana lečenja.",
      },
    ],
  },

  // 20
  alkoholizam: {
    label: "Alkoholizam i zavisnost od alkohola",
    guideLink: {
      href: "/blog/online-psihoterapija-vodic",
      label: "Pročitaj kako izgleda online psihoterapija",
    },
    therapistSearchTerms: [
      "Adikcija (alkoholizam)",
      "Bolesti zavisnosti -alkohol, PAS, kocka, internet, videoigre, kupovina",
      "Zavisnost od supstanci",
    ],
    searchTerm:
      "alkoholizam zavisnost od alkohola odvikavanje od alkohola kako prestati piti apstinencija craving recidiv lečenje alkoholizma terapija za alkoholizam podrška porodice detoksikacija klubovi lečenih alkoholičara",
    description:
      "Kako prestati sa alkoholom i vratiti kontrolu nad životom? Zavisnost od alkohola utiče na zdravlje, porodicu i svakodnevno funkcionisanje. Gubitak kontrole nad količinom, žudnja za alkoholom, problemi u odnosima i na poslu. Na platformi Rešimo Probleme pronađi licencirane terapeute specijalizovane za lečenje alkoholizma online. Kroz KBT, motivacioni intervju i plan prevencije recidiva, dobij podršku za odvikavanje i trajnu promenu.",
    directoryContent: {
      title: "Online psiholog za problem sa alkoholom",
      description:
        "Ako želiš da razgovaraš o alkoholu, njegovom uticaju na svakodnevicu ili o odnosima koji su zbog toga opterećeni, ovde možeš da pronađeš terapeuta koji navodi rad sa zavisnostima. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Rešimo Probleme nije klinika ni program odvikavanja i ne pruža medicinsko zbrinjavanje apstinencijalnih simptoma.",
      seoTitle: "Psiholog za alkohol online | Rešimo Probleme",
      seoDescription:
        "Pronađi online psihologa za razgovor o problemu sa alkoholom. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem psihologa za problem sa alkoholom?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa zavisnostima. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li je ovo odvikavanje ili lečenje alkoholizma?",
          answer:
            "Ne. Rešimo Probleme je platforma za pronalazak terapeuta i online razgovor. Ne pruža bolničko lečenje, detoksikaciju ni program odvikavanja.",
        },
        {
          question: "Mogu li da zakažem razgovor ako brinem za blisku osobu?",
          answer:
            "Da. Možeš zakazati razgovor da bi razjasnio/la šta te opterećuje i pitao/la terapeuta kako pristupa temi podrške, komunikacije i granica.",
        },
        {
          question: "Kada treba potražiti medicinsku ili hitnu pomoć?",
          answer:
            "Ako postoji neposredna opasnost po zdravlje ili bezbednost, obrati se hitnoj medicinskoj pomoći. Online terapija ne zamenjuje hitnu ili medicinsku negu.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    title:
      "Online terapeuti za alkoholizam: kako prestati piti uz stručnu podršku",
    seoTitle:
      "Terapija za alkoholizam online: pronađi terapeuta | Rešimo Probleme",
    seoDescription:
      "Kako prestati piti? Pronađi licencirane terapeute za alkoholizam online. KBT, motivacioni intervju i podrška u odvikavanju. Zakaži sesiju.",
    relatedConditions: ["zavisnosti", "porodica", "depresija"],
    passes: true,

    faqItems: [
      {
        question:
          "Kako da prepoznam da li imam problem sa alkoholom i da li mi treba terapija?",
        answer:
          "Znaci upozorenja uključuju gubitak kontrole nad količinom koju piješ, žudnju (craving) za alkoholom, skrivanje konzumacije od bližnjih, konflikte u porodici ili na poslu, i zdravstvene probleme. Ako primećuješ ove simptome, razgovor sa terapeutom može pomoći da proceniš situaciju i napraviš plan.",
      },
      {
        question:
          "Koji tip terapije je najefikasniji za lečenje zavisnosti od alkohola?",
        answer:
          "Kognitivno-bihevioralna terapija (KBT) i motivacioni intervju su najefikasniji pristupi za alkoholizam. KBT pomaže da prepoznaš okidače i promeniš obrasce ponašanja, dok motivacioni intervju jača unutrašnju motivaciju za promenu. Kombinacija sa grupnom podrškom ili porodičnom terapijom dodatno poboljšava rezultate.",
      },
      {
        question:
          "Da li je cilj terapije uvek potpuna apstinencija od alkohola?",
        answer:
          "Ne uvek. Cilj se dogovara sa terapeutom prema tvojoj situaciji. Za neke osobe je cilj smanjenje konzumacije na bezbedan nivo, dok je za druge potpuna apstinencija najbolji put. Prioritet je tvoje zdravlje, bezbednost i kvalitet života.",
      },
      {
        question: "Kako online terapija može pomoći u odvikavanju od alkohola?",
        answer:
          "Online terapija pruža fleksibilnost i privatnost, možeš pristupiti sesijama od kuće, bez potrebe za putovanjem. Terapeuti koriste iste dokazane metode kao i u klinici: KBT, motivacioni intervju i strategije prevencije recidiva. Redovne sesije pomažu da održiš fokus i dobiješ podršku kada ti je najpotrebnija.",
      },
      {
        question:
          "Kako mogu pomoći članu porodice koji ima problem sa alkoholom?",
        answer:
          "Porodična terapija pomaže da razumeš zavisnost bez okrivljavanja, postaviš zdrave granice i pružiš podršku na konstruktivan način. Izbegavaj ultimatume i pretnje. Umesto toga, izrazi brigu i ponudi konkretnu pomoć u pronalaženju terapeuta. Tvoja podrška može biti ključna u motivaciji za lečenje.",
      },
      {
        question: "Šta ako dođe do recidiva tokom lečenja alkoholizma?",
        answer:
          "Recidiv nije neuspeh, već deo procesa oporavka za mnoge osobe. Važno je brzo se vratiti terapeutu, analizirati šta je bio okidač i prilagoditi plan prevencije. Istraživanja pokazuju da većina ljudi ima nekoliko pokušaja pre trajne promene. Upornost i podrška su ključni.",
      },
      {
        question:
          "Koliko traje lečenje alkoholizma i kada mogu očekivati rezultate?",
        answer:
          "Trajanje zavisi od stepena zavisnosti i individualnih faktora. Prva poboljšanja (bolja kontrola, manje žudnje) mogu se osetiti nakon nekoliko nedelja redovnih sesija. Za trajne promene preporučuje se kontinuirana terapija tokom više meseci, uz nastavak podrške kroz grupe ili povremene sesije.",
      },
    ],
  },

  // 21
  kockanje: {
    therapistSearchTerms: [
      "Bolesti zavisnosti - alkohol, PAS, kocka, internet, videoigre, kupovina",
      "Bolesti zavisnosti -alkohol, PAS, kocka, internet, videoigre, kupovina",
    ],
    title:
      "Online terapeuti za zavisnost od kockanja: prestani da kockaš uz stručnu podršku",
    label: "Zavisnost od kockanja",
    guideLink: {
      href: "/blog/kako-prestati-sa-kockanjem",
      label: "Pročitaj vodič o problemima sa kockanjem",
    },
    searchTerm:
      "kockanje zavisnost od kockanja klađenje kazino recidiv finansijski problemi gubitak kontrole kako prestati kockati lečenje od kocke odvikavanje od kockanja patološko kockanje kompulzivno kockanje online kockanje kladionica sportsko klađenje slot aparati terapija za kockanje terapeut za kockanje",
    description:
      "Osećaš da si izgubio kontrolu nad kockanjem? Ako su klađenje, kazino ili online igre počele su da utiču na tvoje finansije, odnose i mentalno zdravlje nisi sam. Zavisnost od kockanja (patološko kockanje) je ozbiljan problem koji pogađa sve više ljudi, kako u Srbiji, tako i u ostatku sveta. Uz online terapiju na platformi Rešimo Probleme, možeš raditi sa licenciranim terapeutom na prepoznavanju okidača, razvijanju strategija za kontrolu poriva i izradi plana finansijskog oporavka. Kognitivno-bihevioralna terapija pomaže da promeniš obrasce razmišljanja i ponašanja koji te vraćaju kockanju.",
    directoryContent: {
      title: "Psiholog za problem sa kockanjem online",
      description:
        "Ako kockanje, klađenje ili posledice tih navika opterećuju tebe ili tvoje odnose, ovde možeš da pronađeš terapeuta koji navodi rad sa zavisnostima i problemima sa kockanjem. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Ova stranica nije klinika, program odvikavanja niti zamena za hitnu ili medicinsku pomoć.",
      seoTitle: "Psiholog za kockanje online | Rešimo Probleme",
      seoDescription:
        "Pronađi online psihologa za razgovor o problemu sa kockanjem. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem psihologa za problem sa kockanjem?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa zavisnostima i problemima sa kockanjem. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li je ovo online lečenje ili odvikavanje od kocke?",
          answer:
            "Ne. Rešimo Probleme je platforma za pronalazak terapeuta i online razgovor. Ne pruža bolničko lečenje, detoksikaciju ni program odvikavanja.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako nisam siguran/na da li je kockanje problem?",
          answer:
            "Da. Možeš početi od onoga što ti ili tvoji bliski primećujete i na prvom razgovoru pitati terapeuta kako pristupa toj temi.",
        },
        {
          question: "Da li moram unapred da iznesem finansijske detalje?",
          answer:
            "Ne moraš. Na prvom razgovoru odlučuješ šta želiš da podeliš. Možeš početi od pitanja ili situacije koja te trenutno najviše opterećuje.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Kako prestati kockati: online terapija | Rešimo Probleme",
    seoDescription:
      "Zavisnost od kockanja se može lečiti. Pronađi online terapeuta za lečenje kockanja, rad na porivima i finansijski oporavak. Zakaži sesiju na Rešimo Probleme.",
    relatedConditions: ["zavisnosti", "alkoholizam", "odnosi", "depresija"],
    passes: true,

    faqItems: [
      {
        question: "Kako da prestanem da kockam?",
        answer:
          "Prvi korak je priznanje problema i odluka da potražiš pomoć. Efikasne strategije uključuju: blokiranje pristupa kockarskim sajtovima i aplikacijama, uvođenje finansijskih ograničenja (samo gotovina, nadzor nad računima), identifikovanje okidača i razvijanje alternativnih aktivnosti. Online terapija sa stručnjakom pomaže da razumeš korene zavisnosti i razviješ dugoročne strategije za apstinenciju.",
      },
      {
        question: "Koji su znaci zavisnosti od kockanja?",
        answer:
          "Ključni znaci uključuju: nemogućnost da se zaustaviš uprkos gubicima, potrebu za sve većim ulozima, laganje o kockanju, finansijske probleme zbog kockanja, zanemarivanje obaveza i odnosa, osećaj nemira kada ne kockaš, i neuspešne pokušaje da prestaneš. Ako prepoznaješ tri ili više znakova, vreme je da potražiš stručnu pomoć.",
      },
      {
        question: "Da li se zavisnost od kockanja može izlečiti?",
        answer:
          "Da, zavisnost od kockanja se može uspešno lečiti. Kognitivno-bihevioralna terapija (KBT) je najefikasnija metoda koja pomaže da promeniš destruktivne obrasce razmišljanja. Mnogi ljudi postižu trajnu apstinenciju uz kombinaciju psihoterapije, grupa podrške i rada na finansijskom oporavku. Oporavak je proces, a recidivi su deo puta, ključna je upornost.",
      },
      {
        question: "Kako da pomognem članu porodice koji kocka?",
        answer:
          "Zavisnost od kockanja pogađa celu porodicu. Izbegavaj ultimatume i okrivljavanje. Umesto toga, izrazi brigu konkretnim primerima kako kockanje utiče na vas. Ne pokrivaj dugove i ne posuđuj novac. Postavi zdrave granice i zaštiti porodične finansije. Predloži zajedničku terapiju ili razgovor sa stručnjakom. Tvoja podrška može biti ključna motivacija za lečenje.",
      },
      {
        question: "Da li online terapija pomaže kod zavisnosti od kockanja?",
        answer:
          "Da, online terapija je dokazano efikasna za lečenje zavisnosti od kockanja. Prednosti uključuju: diskreciju i privatnost, fleksibilnost termina, pristup stručnjacima bez obzira na lokaciju, i mogućnost redovne podrške. Radimo na prepoznavanju okidača, tehnikama kontrole poriva, i izradi plana prevencije recidiva.",
      },
      {
        question: "Kako se nositi sa porivom za kockanjem?",
        answer:
          "Kada osećaš poriv, primeni tehniku odlaganja: postavi tajmer na 15-30 minuta i sačekaj da poriv prođe. Koristi 'urge surfing': posmatraj poriv kao talas koji dolazi i odlazi bez da mu se predaješ. Pozovi osobu od poverenja, izađi napolje ili se bavi fizičkom aktivnošću. Podsetnik: gubici su trajni, sledeća opklada ih neće vratiti.",
      },
      {
        question: "Šta ako dođe do recidiva tokom lečenja?",
        answer:
          "Recidiv nije neuspeh, već deo procesa oporavka za mnoge osobe. Važno je brzo se vratiti terapeutu, analizirati šta je bio okidač i prilagoditi plan prevencije. Statistika pokazuje da većina ljudi ima nekoliko pokušaja pre trajne promene. Svaki pokušaj te uči nečem novom o tvom obrascu zavisnosti.",
      },
    ],
  },

  // 22
  "pusenje-odvikavanje": {
    passes: true,
    label: "Odvikavanje od pušenja",
    title:
      "Odvikavanje od pušenja uz podršku terapeuta online: prestanak pušenja sa planom",
    searchTerm:
      "odvikavanje od pušenja prestanak pušenja kako ostaviti cigarete pušenje zavisnost od nikotina nikotin craving apstinencija apstinencijska kriza plan prestanka pušenja navike okidači KBT pušenje motivacioni intervju savetovalište za odvikavanje od pušenja prestanak pušenja kriza debljanje posle prestanka pušenja",
    description:
      "Odvikavanje od pušenja je proces koji zahteva više od same odluke. Apstinencijska kriza, stres, navike i okidači mogu otežati prestanak. Terapeut sa iskustvom u radu sa zavisnostima pomaže da prepoznaš obrasce koji te drže u pušenju, napraviš konkretan plan prestanka i razviješ strategije za situacije visokog rizika. KBT i motivacioni intervju su pristupi sa dokazanom efikasnošću u odvikavanju od pušenja.",
    seoTitle: "Odvikavanje od pušenja: terapeut online | Rešimo Probleme",
    seoDescription:
      "Prestani da pušiš uz podršku terapeuta online. KBT, motivacioni intervju i plan prestanka pušenja. Uporedi profile terapeuta i zakaži sesiju na Rešimo Probleme.",
    relatedConditions: ["zavisnosti", "stres", "kbt"],
    faqItems: [
      {
        question: "Kako terapeut pomaže u odvikavanju od pušenja?",
        answer:
          "Terapeut pomaže da razumeš obrasce koji pokreću pušenje, prepoznaš okidače (stres, kafa, dosada, društvo) i razviješ zamenske strategije. Kroz KBT (kognitivno-bihejvioralnu terapiju) radiš na menjanju automatskih misli vezanih za cigarete, a motivacioni intervju pomaže da učvrstiš odluku i održiš motivaciju tokom teških trenutaka.",
      },
      {
        question: "Koliko traje proces odvikavanja od pušenja?",
        answer:
          "Fizička zavisnost od nikotina se značajno smanjuje u prvih 2-4 nedelje. Psihološka zavisnost, navike i okidači mogu trajati duže. Većina klijenata radi sa terapeutom 8-12 sesija, uz intenzivniji rad u prvim nedeljama i postepeno smanjivanje. Trajanje zavisi od godina pušenja, broja dnevnih cigareta i prethodnih pokušaja prestanka.",
      },
      {
        question: "Šta ako posrnem i ponovo zapalim?",
        answer:
          "Posrtaj (lapse) nije isto što i potpuni povratak pušenju (relapse). Većina ljudi koja uspešno prestane imala je više pokušaja pre konačnog prestanka. Sa terapeutom analiziraš šta je bio okidač, šta možeš drugačije sledeći put i odmah se vraćaš na plan. Svaki pokušaj je prilika za učenje, a ne razlog za odustajanje.",
      },
      {
        question: "Da li je terapija dovoljna ili su potrebni i lekovi?",
        answer:
          "Psihoterapija se može koristiti samostalno ili u kombinaciji sa nikotinskom zamenskom terapijom (flasteri, žvake) ili lekovima (bupropion, vareniklin) koje propisuje lekar. Istraživanja pokazuju da kombinacija terapije i medikamenata daje najbolje rezultate, ali mnogi uspešno prestanu i samo uz psihoterapijsku podršku.",
      },
      {
        question: "Kako se nositi sa apstinencijskom krizom?",
        answer:
          "Apstinencijska kriza (nervoza, razdražljivost, teškoće sa koncentracijom) je najintenzivnija u prvih 7-10 dana. Terapeut te priprema unapred: zajedno pravite plan za situacije visokog rizika koji uključuje tehnike disanja, fizičku aktivnost, zamenske radnje i podršku okoline. Sa svakim uspešno preživljenim porivom, samopouzdanje raste.",
      },
      {
        question: "Da li ću se udebljati ako prestanem da pušim?",
        answer:
          "Blagi porast težine (2-4 kg) je čest u prvih meseci nakon prestanka jer se metabolizam prilagođava i apetit se pojačava. U terapiji se radi na strategijama koje sprečavaju zamenu cigareta hranom, uključujući planiranje obroka, fizičku aktivnost i upravljanje emocijama koje su ranije vodile ka pušenju.",
      },
      {
        question:
          "Da li online terapija funkcioniše za odvikavanje od pušenja?",
        answer:
          "Da. Online format omogućava fleksibilnost koja je korisna tokom odvikavanja jer možeš zakazati sesiju upravo kada ti je podrška najpotrebnija. Nema stresa putovanja, a sesije se odvijaju iz tvog okruženja. Terapeut je dostupan za kratke konsultacije između regularnih sesija ako se pojavi jak poriv.",
      },
    ],
  },

  // 23
  nesanica: {
    label: "Nesanica i problemi sa snom",
    guideLink: {
      href: "/blog/nesanica-kako-poboljsati-san-i-kada-potraziti-pomoc",
      label: "Pročitaj vodič o nesanici",
    },
    therapistSearchTerms: [
      "Problemi sa spavanjem",
      "Teskoce povezane sa spavanjem/snom | Unapredjenje kvaliteta spavanja",
      "Nesanica",
    ],
    title: "Online terapeuti za nesanicu, CBT-I terapija i kvalitetniji san",
    searchTerm:
      "nesanica insomnija problemi sa snom teško usnivanje noćna buđenja buđenje tokom noći hronična nesanica CBT-I KBT za nesanicu higijena sna ritam spavanja loš san terapeut za nesanicu online terapija nesanica poremećaj spavanja",
    description:
      "Ako imaš problema sa spavanjem, budiš se tokom noći ili se osećaš umorno i bez energije uprkos dovoljno sati u krevetu, možda se boriš sa nesanicom. CBT-I (kognitivno-bihevioralna terapija za nesanicu) je naučno dokazan pristup koji pomaže u resetovanju ritma sna, smanjenju briga pred spavanje i formiranju zdravih navika. Pronađi online terapeuta za nesanicu na Rešimo Probleme i zakaži prvu sesiju već danas.",
    directoryContent: {
      title: "Online terapeut za nesanicu i probleme sa snom",
      description:
        "Ako želiš da razgovaraš o teškoćama sa snom, čestim buđenjima ili brigama koje se javljaju pred spavanje, ovde možeš da pronađeš terapeuta koji navodi rad sa nesanicom ili problemima sa spavanjem. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Ova stranica nije zamena za medicinsku procenu uzroka problema sa snom.",
      seoTitle: "Terapeut za nesanicu online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za nesanicu i probleme sa snom. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za nesanicu online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa nesanicom ili problemima sa spavanjem. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li je ova stranica za lekove za spavanje?",
          answer:
            "Ne. Na ovoj stranici pronalaziš terapeuta za razgovor. O lekovima, njihovoj bezbednosti i promenama terapije razgovaraj sa lekarom.",
        },
        {
          question: "Kada treba da potražim medicinsku procenu zbog sna?",
          answer:
            "Ako je problem sa snom nov, izražen, dugo traje ili ga prate drugi zdravstveni simptomi, obrati se lekaru radi procene. Terapeutski razgovor ne zamenjuje medicinski pregled.",
        },
        {
          question: "Šta mogu da pitam terapeuta na prvom razgovoru?",
          answer:
            "Možeš opisati šta ti je teško u vezi sa snom i pitati kako terapeut pristupa toj temi. Takođe možeš proveriti da li je njegov način rada dobar izbor za tebe.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Online terapija za nesanicu i CBT-I | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za nesanicu i probleme sa snom. CBT-I terapija pomaže da brže zaspiš i bolje spavaš — zakaži termin danas.",
    relatedConditions: ["stres", "anksioznost", "depresija"],
    passes: true,

    faqItems: [
      {
        question: "Šta je CBT-I i kako pomaže kod nesanice?",
        answer:
          "CBT-I (kognitivno-bihevioralna terapija za nesanicu) je naučno dokazan pristup koji kombinuje tehnike za promenu misli i ponašanja koja ometaju san. Uključuje higijenu sna, kontrolu stimulusa, ograničenje vremena u krevetu i kognitivno restrukturiranje. Istraživanja pokazuju da 70-80% osoba sa hroničnom nesanicom vidi poboljšanje nakon CBT-I tretmana.",
      },
      {
        question: "Kako pronaći terapeuta za nesanicu online?",
        answer:
          "Na Rešimo Probleme možeš pronaći online terapeuta za nesanicu filtriranjem po specijalnosti. Pretraži profile terapeuta koji imaju iskustvo sa CBT-I pristupom, pročitaj recenzije i zakaži prvu konsultaciju direktno putem platforme. Online format omogućava fleksibilnost termina.",
      },
      {
        question: "Da li online terapija za nesanicu funkcioniše?",
        answer:
          "Da, istraživanja pokazuju da je online CBT-I jednako efikasna kao terapija uživo. Online format omogućava pristup kvalifikovanim terapeutima bez obzira na lokaciju i fleksibilnost u zakazivanju. Većina osoba primećuje poboljšanje kvaliteta sna nakon 4-6 nedelja doslednog rada.",
      },
      {
        question: "Koliko traje tretman za nesanicu?",
        answer:
          "CBT-I terapija obično traje 6-8 nedelja, sa sesijama jednom nedeljno. Mnogi primećuju poboljšanje već nakon 2-3 sesije. Terapeut prilagođava trajanje prema intenzitetu simptoma i ciljevima, a naučene tehnike ostaju korisne i nakon završetka tretmana.",
      },
      {
        question: "Koji su simptomi hronične nesanice?",
        answer:
          "Simptomi hronične nesanice uključuju poteškoće sa usnivanjem, česta buđenja tokom noći, rano jutarnje buđenje, osećaj umora i neodmornosti uprkos dovoljno vremena u krevetu, probleme sa koncentracijom i razdražljivost. Ako simptomi traju duže od 3 meseca i utiču na dnevno funkcionisanje, preporučuje se konsultacija sa terapeutom.",
      },
      {
        question: "Da li terapija za nesanicu zahteva lekove?",
        answer:
          "CBT-I terapija je pristup bez lekova koji se preporučuje kao prva linija lečenja nesanice. Fokusira se na promenu navika i misaonih obrazaca koji ometaju san. Za mnoge osobe ovo je dovoljno. Ako je potrebno, terapeut može predložiti konsultaciju sa psihijatrom za kombinovani pristup.",
      },
      {
        question: "Šta pomaže kod nesanice osim terapije?",
        answer:
          "Pored CBT-I, pomaže održavanje redovnog ritma spavanja, izbegavanje kofeina i alkohola pre sna, ograničenje ekrana uveče, fizička aktivnost tokom dana i opuštajuće rutine pre spavanja. Terapeut za nesanicu ti pomaže da napraviš personalizovan plan higijene sna.",
      },
    ],
  },

  // 24
  zalovanje: {
    label: "Žalovanje i gubitak",
    therapistSearchTerms: [
      "Gubici i žalovanje",
      "Gubici | Tugovanje - postepeno vracanje emocionalne ravnoteze",
      "Gubitak bliske osobe",
      "Gubitak i žalovanje",
      "Osećanje tuge i proces žalovanja (gubitak)",
      "Podrška u procesu žalovanja usled gubitka",
      "Proces tugovanja",
      "Rad sa traumama i tugovanjem",
      "Razumijevanje i obrada gubitka i tuge",
    ],
    guideLink: {
      href: "/blog/kako-se-nositi-s-gubitkom-i-tugom",
      label: "Pročitaj vodič o gubitku i tugovanju",
    },
    title:
      "Online terapeuti za žalovanje i tugovanje posle gubitka bliske osobe",
    searchTerm:
      "žalovanje tugovanje tuga gubitak smrt bliske osobe faze žalovanja komplicirano žalovanje kako prevazići gubitak proces tugovanja bol nakon gubitka sećanje prilagođavanje smisao",
    description:
      "Gubitak bliske osobe donosi bol koja se teško opisuje rečima. Tugovanje može izgledati kao praznina, nespavanje, osećaj krivice ili nemogućnost da nastaviš sa svakodnevnim životom. Ako se osećaš zaglavljeno u žalovanju ili ne znaš da li je tvoja reakcija na gubitak normalna, razgovor sa terapeutom je siguran prvi korak.\n\nNa Rešimo Probleme pronađi online psihologa ili psihoterapeuta koji ima iskustvo u radu sa žalovanjem, tugovanjem i gubitkom. Terapeut ti pomaže da procesuiraš emocije, razumeš faze žalovanja i pronađeš način da sećanje na voljenu osobu nosiš dalje, bez pritiska da se 'prebaciš' na normalnost. Pregledaj dostupne terapeute, uporedi pristupe i zakaži online sesiju kad ti odgovara.",
    directoryContent: {
      title: "Online terapeut za žalovanje i gubitak",
      description:
        "Ako želiš da razgovaraš nakon gubitka bliske osobe ili o tugovanju koje te trenutno opterećuje, ovde možeš da pronađeš terapeuta koji navodi rad sa gubitkom i žalovanjem. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Ne postoji jedan ispravan način tugovanja niti moraš unapred da znaš šta želiš da kažeš.",
      seoTitle: "Terapeut za žalovanje online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za žalovanje i gubitak. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za žalovanje online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa žalovanjem i gubitkom. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li je u redu da potražim razgovor odmah nakon gubitka?",
          answer:
            "Da. Možeš zakazati razgovor kad god želiš podršku. Na prvom susretu možeš reći samo onoliko koliko ti je prijatno.",
        },
        {
          question:
            "Mogu li da zakažem razgovor zbog gubitka koji se desio ranije?",
          answer:
            "Da. Možeš početi od onoga što ti je sada teško i sa terapeutom razjasniti šta ti je potrebno od razgovora.",
        },
        {
          question: "Šta mogu da proverim pre zakazivanja?",
          answer:
            "Na profilu možeš da proveriš kako terapeut opisuje svoj rad, cenu sesanse i slobodne termine. To ti pomaže da izabereš osobu sa kojom želiš da razgovaraš.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Online terapija za žalovanje i gubitak | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za žalovanje i tugovanje posle gubitka bliske osobe. Uporedi pristupe, zakaži termin i dobij stručnu podršku iz svog doma.",
    relatedConditions: [
      "gubitak-ljubimca",
      "razvod-razlaz",
      "zivotne-promene",
      "depresija",
    ],
    passes: true,

    faqItems: [
      {
        question: "Koje su faze žalovanja i da li svi prolaze kroz iste faze?",
        answer:
          "Najpoznatiji model opisuje pet faza: poricanje, ljutnju, pregovaranje, depresiju i prihvatanje. Međutim, žalovanje nije linearno i ne mora svaka osoba proći sve faze istim redom. Terapeut ti pomaže da razumeš svoj proces tugovanja bez pritiska da se uklopiš u šablon.",
      },
      {
        question:
          "Kako da znam da li mi je potrebna terapija za žalovanje ili je to normalna tuga?",
        answer:
          "Tugovanje je normalna reakcija na gubitak. Stručna podrška je korisna ako bol ne popušta mesecima, ako remeti san, posao ili odnose, ili ako se javljaju jaka krivica i samookrivljavanje. Komplicirano žalovanje zahteva ciljanu terapijsku podršku.",
      },
      {
        question: "Šta je komplicirano žalovanje i po čemu se razlikuje?",
        answer:
          "Komplicirano žalovanje (prolongirano žalovanje) nastaje kada intenzivna tuga traje duže od 12 meseci i značajno ometa svakodnevno funkcionisanje. Razlikuje se od uobičajenog tugovanja po tome što se osoba oseća 'zaglavljeno' i teško prihvata gubitak. Terapeut koristi specifične pristupe za otključavanje tog procesa.",
      },
      {
        question: "Kako da se nosim sa gubitkom između terapijskih sesija?",
        answer:
          "Pomažu vežbe uzemljenja, razgovor sa osobom od poverenja, vođenje dnevnika i rituali sećanja (pisanje pisama, čuvanje albuma, obeležavanje godišnjica). Terapeut ti pomaže da napraviš plan za teže dane koji je realan i prilagođen tvom ritmu.",
      },
      {
        question: "Kako okolina može da pomogne osobi koja tuguje?",
        answer:
          "Najvažnije je slušati bez saveta i bez pritiska da se 'nastavi dalje'. Rečenice poput 'Tu sam za tebe' pomažu više nego 'Budi jak'. Ako nisi siguran/sigurna kako da podržiš blisku osobu, terapeut može dati konkretne smernice za podršku u žalovanju.",
      },
      {
        question: "Da li online terapija pomaže kod žalovanja?",
        answer:
          "Da. Online terapija za žalovanje pruža siguran prostor da podeliš emocije iz svog doma, u tempu koji ti odgovara. Na Rešimo Probleme pronađeš terapeuta sa iskustvom u radu sa gubitkom i zakazuješ sesiju bez čekanja.",
      },
      {
        question: "Koje terapijske metode se koriste u radu sa žalovanjem?",
        answer:
          "Najčešće se koriste psihodinamska terapija, geštalt terapija i kognitivno-bihejvioralna terapija (KBT). Geštalt pristup naglašava završavanje i integraciju gubitka, dok KBT pomaže sa automatskim mislima krivice i bespomoćnosti. Terapeut bira pristup prema tvojim potrebama.",
      },
    ],
  },

  // 25
  "poremecaji-ishrane": {
    passes: true,
    label: "Poremećaji ishrane",
    therapistSearchTerms: [
      "Problemi sa ishranom i nezadovoljstvo telesnim izgledom",
      "Poremecaji ishrane / Anoreksija, Bulimija",
      "Poremećaj telesne sheme i poremećaji ishrane",
    ],
    guideLink: {
      href: "/blog/poremecaji-prehrane-prepoznavanje-i-put-do-oporavka",
      label: "Pročitaj vodič o prepoznavanju teškoća u odnosu prema hrani",
    },
    title:
      "Online terapeuti za poremećaje ishrane: podrška za anoreksiju, bulimiju i kompulsivno prejedanje",
    searchTerm:
      "poremećaji ishrane anoreksija bulimija kompulsivno prejedanje binge eating poremećaj ishrane lečenje anoreksije lečenje bulimije slika tela restrikcija prejedanje terapija poremećaji ishrane KBT-E CBT poremećaji ishrane oporavak anoreksija nervoza bulimija nervoza emocionalno prejedanje odnos prema hrani",
    description:
      "Poremećaji ishrane, poput anoreksije, bulimije i kompulsivnog prejedanja, utiču na odnos prema hrani, telu i emocijama. Iza restriktivnog ili nekontrolisanog jedenja često stoje dublje teškoće sa samopouzdanjem, anksioznošću ili potrebom za kontrolom. Terapeut sa iskustvom u radu sa poremećajima ishrane pomaže da razumeš obrasce koji pokreću nezdrav odnos prema hrani i napraviš plan oporavka prilagođen tvojim potrebama.",
    directoryContent: {
      title: "Online terapeut za poremećaje ishrane",
      description:
        "Ako želiš da razgovaraš o odnosu prema hrani, telu ili obrascima ishrane koji te opterećuju, ovde možeš da pregledaš profile terapeuta koji navode rad sa poremećajima ishrane. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Terapeutski razgovor ne zamenjuje medicinsku procenu, koja može biti važna kada postoje fizički simptomi ili naglo pogoršanje.",
      seoTitle: "Terapeut za poremećaje ishrane online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za poremećaje ishrane i odnos prema hrani. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za poremećaje ishrane online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa poremećajima ishrane. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question:
            "Da li ova stranica služi za dijagnozu anoreksije ili bulimije?",
          answer:
            "Ne. Stranica služi za pronalazak terapeuta za razgovor. Dijagnostičku procenu i medicinske savete daje odgovarajući zdravstveni stručnjak.",
        },
        {
          question: "Kada treba da potražim medicinsku pomoć?",
          answer:
            "Ako imaš fizičke simptome, naglo pogoršanje ili sumnjaš da je zdravlje ugroženo, obrati se lekaru ili hitnoj medicinskoj pomoći. Online terapija ne zamenjuje hitnu ili medicinsku negu.",
        },
        {
          question: "Šta mogu da proverim na profilu terapeuta?",
          answer:
            "Na profilu možeš da proveriš kako terapeut opisuje svoj rad, cenu sesanse i slobodne termine. Na prvom razgovoru možeš pitati da li je njegov pristup dobar izbor za tvoju temu.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Poremećaji ishrane: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za poremećaje ishrane online. KBT, rad na slici tela i oporavak od anoreksije, bulimije i prejedanja. Zakaži sesiju na Rešimo Probleme.",
    relatedConditions: [
      "telesna-slika",
      "samopouzdanje",
      "anksioznost",
      "depresija",
    ],
    faqItems: [
      {
        question: "Kako terapija pomaže kod poremećaja ishrane?",
        answer:
          "Terapija za poremećaje ishrane radi na više nivoa: uspostavljanje redovnog obrasca ishrane, prepoznavanje okidača za restriktivno ili nekontrolisano jedenje, rad na iskrivljenoj slici tela i rešavanje emocija koje stoje iza ponašanja prema hrani. KBT-E (pojačana kognitivno-bihejvioralna terapija) je pristup sa najjačom dokaznom bazom za anoreksiju, bulimiju i kompulsivno prejedanje.",
      },
      {
        question:
          "Koja je razlika između anoreksije, bulimije i kompulsivnog prejedanja?",
        answer:
          "Anoreksija nervoza se karakteriše intenzivnim strahom od dobijanja na težini i restriktivnim unosom hrane. Bulimija nervoza uključuje epizode prejedanja praćene kompenzatornim ponašanjem (poput povraćanja ili preterane fizičke aktivnosti). Kompulsivno prejedanje (binge eating) podrazumeva epizode nekontrolisanog jedenja bez kompenzatornog ponašanja. Svi ovi poremećaji zahtevaju stručnu podršku.",
      },
      {
        question: "Da li je uz terapiju potrebna i medicinska podrška?",
        answer:
          "Za poremećaje ishrane je često potreban multidisciplinarni pristup. Psihoterapeut radi na emocijama, obrascima mišljenja i ponašanju prema hrani. U zavisnosti od težine stanja, lekar prati fizičko zdravlje, a nutricionista pomaže u uspostavljanju zdravog plana ishrane. Terapeut može pomoći u koordinaciji sa ostalim stručnjacima.",
      },
      {
        question: "Kako izgleda oporavak od poremećaja ishrane?",
        answer:
          "Oporavak je proces koji zahteva vreme i strpljenje. Počinje uspostavljanjem stabilnog obrasca ishrane, zatim se radi na razumevanju okidača, promeni odnosa prema telu i hrani i razvijanju zdravijih načina za regulaciju emocija. Većina klijenata primeti značajno poboljšanje nakon 20-40 sesija, ali trajanje zavisi od vrste i ozbiljnosti poremećaja.",
      },
      {
        question: "Da li online terapija može pomoći kod poremećaja ishrane?",
        answer:
          "Da, istraživanja pokazuju da je online terapija efikasna za poremećaje ishrane, posebno za bulimiju i kompulsivno prejedanje. Online format nudi privatnost koja je mnogima važna kod ove teme, kao i mogućnost rada sa specijalizovanim terapeutom bez obzira na lokaciju. Za teže oblike anoreksije može biti potrebna kombinacija sa medicinskim praćenjem.",
      },
      {
        question:
          "Kako prepoznati poremećaj ishrane kod sebe ili bliske osobe?",
        answer:
          "Česti znaci uključuju: preokupiranost hranom, kalorijama ili težinom, izbegavanje obroka u društvu, drastične promene u težini, skriveno jedenje, osećaj krivice nakon obroka, preterano vežbanje ili rituale oko hrane. Ako primetiš ove obrasce kod sebe ili bliske osobe, razgovor sa terapeutom može pomoći da razumeš šta se dešava i koji su sledeći koraci.",
      },
      {
        question: "Da li se poremećaji ishrane javljaju samo kod žena?",
        answer:
          "Ne. Iako su češći kod žena, poremećaji ishrane pogađaju osobe svih polova i uzrasta. Kod muškaraca se često javljaju u obliku opsednutosti mišićnom masom, preteranog vežbanja ili restriktivne ishrane. Zbog stigme, muškarci ređe traže pomoć, ali terapija je jednako efikasna bez obzira na pol.",
      },
    ],
  },

  // 26
  "socijalna-anksioznost": {
    passes: true,
    label: "Socijalna anksioznost",
    guideLink: {
      href: "/blog/socijalna-anksioznost",
      label: "Pročitaj vodič o socijalnoj anksioznosti",
    },
    therapistSearchTerms: [
      "Socijalna anksioznost",
      "Nisko samopouzdanje, socijalna anksioznost i socijalna fobija",
    ],
    title:
      "Online terapeuti za socijalnu anksioznost: prevaziđi strah od socijalnih situacija",
    searchTerm:
      "socijalna anksioznost socijalna fobija strah od ljudi strah od procene trema anksioznost u društvu izbegavanje socijalnih situacija socijalna anksioznost simptomi socijalna anksioznost lečenje KBT socijalna anksioznost kognitivno-bihevioralna terapija izlaganje stid sram crvenilo samopouzdanje javni nastup strah od prezentacije socijalna anksioznost iskustva socijalna anksioznost test",
    description:
      "Socijalna anksioznost (socijalna fobija) podrazumeva intenzivan strah od socijalnih situacija, procene drugih ljudi i mogućeg poniženja. Može da se manifestuje kroz izbegavanje druženja, tremu pri javnom nastupu, crvenilo ili osećaj blokade u razgovoru. Kognitivno-bihevioralna terapija (KBT) sa postepenim izlaganjem pomaže da prepoznaš obrasce izbegavanja, izgradiš sigurnost u socijalnim situacijama i smanjiš anksioznost korak po korak.",
    directoryContent: {
      title: "Online terapeuti za socijalnu anksioznost",
      description:
        "Ako ti strah od procene, razgovora ili javnog nastupa otežava svakodnevne situacije, ovde možeš da pregledaš profile terapeuta koji navode rad sa socijalnom anksioznošću. Uporedi njihov pristup, cenu i termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor.",
      seoTitle: "Terapeut za socijalnu anksioznost online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za socijalnu anksioznost. Pregledaj profile, cene i termine, pa zakaži razgovor na Rešimo Probleme.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za socijalnu anksioznost?",
          answer:
            "Na ovoj stranici su profili terapeuta čiji opisi rada navode socijalnu anksioznost. Pročitaj profil, pristup, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question:
            "Da li moram da znam da li imam socijalnu anksioznost pre razgovora?",
          answer:
            "Ne moraš unapred da postaviš sebi dijagnozu da bi razgovarao sa terapeutom. Pri zakazivanju možeš kratko opisati situacije koje ti stvaraju teškoću i pitanja koja želiš da otvoriš.",
        },
        {
          question:
            "Mogu li da izaberem terapeuta ako mi je neprijatno da pričam o tome?",
          answer:
            "Možeš da biraš terapeuta prema njegovom profilu, pristupu, ceni i terminu. Na prvom razgovoru možeš početi tempom koji ti odgovara, bez potrebe da odmah izneseš svaki detalj.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije i izabereš termin koji ti odgovara.",
        },
        {
          question: "Kada treba da potražim hitnu pomoć?",
          answer:
            "Ako si u neposrednoj opasnosti ili razmišljaš da povrediš sebe ili nekog drugog, obrati se hitnoj službi ili lokalnoj kriznoj pomoći odmah. Ova stranica je namenjena zakazivanju razgovora, ne hitnom zbrinjavanju.",
        },
      ],
    },
    seoTitle: "Socijalna anksioznost: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za socijalnu anksioznost online. KBT i postepeno izlaganje za strah od procene i socijalnih situacija. Zakaži sesiju na Rešimo Probleme.",
    relatedConditions: [
      "anksioznost",
      "usamljenost",
      "samopouzdanje",
      "fobije",
    ],
    faqItems: [
      {
        question:
          "Šta je socijalna anksioznost i kako se razlikuje od stidljivosti?",
        answer:
          "Socijalna anksioznost (socijalna fobija) je stanje u kojem strah od procene ili poniženja značajno utiče na svakodnevni život, posao ili odnose. Za razliku od stidljivosti, koja je crta ličnosti, socijalna anksioznost izaziva intenzivnu nelagodnost i često vodi ka izbegavanju situacija koje većina ljudi smatra uobičajenim.",
      },
      {
        question: "Kako KBT pomaže kod socijalne anksioznosti?",
        answer:
          "Kognitivno-bihevioralna terapija (KBT) je najefektivniji pristup za socijalnu anksioznost. Terapeut ti pomaže da prepoznaš negativne misli ('svi me gledaju', 'napraviću budalu od sebe'), testiraš ih kroz postepeno izlaganje realnim situacijama i razviješ nove, realističnije obrasce razmišljanja i ponašanja.",
      },
      {
        question: "Šta je postepeno izlaganje i da li je neprijatno?",
        answer:
          "Postepeno izlaganje znači da se korak po korak suočavaš sa situacijama koje izazivaju anksioznost, počevši od lakših ka težim. Terapeut pravi plan prilagođen tebi, tako da nikada nisi izložen nečemu za šta nisi spreman. Cilj nije da se osećaš neprijatno, već da postepeno izgradiš samopouzdanje.",
      },
      {
        question: "Koji su fizički simptomi socijalne anksioznosti?",
        answer:
          "Česti fizički simptomi uključuju crvenilo lica, ubrzano lupanje srca, znojenje dlanova, drhtanje glasa ili ruku, mučninu i osećaj 'prazne glave'. Ovi simptomi su normalna reakcija tela na strah i mogu se smanjiti kroz terapiju, tehnike disanja i postepeno izlaganje.",
      },
      {
        question: "Da li je za socijalnu anksioznost potreban i lek?",
        answer:
          "Mnogi ljudi postignu značajno poboljšanje samo kroz KBT, bez lekova. U nekim slučajevima, psihijatar može preporučiti kombinaciju terapije i medikamenata. Terapeut na platformi može da proceni tvoju situaciju i, ako je potrebno, preporuči konsultaciju sa psihijatrom.",
      },
      {
        question: "Da li online terapija funkcioniše za socijalnu anksioznost?",
        answer:
          "Istraživanja pokazuju da je online KBT jednako efikasna kao terapija uživo za socijalnu anksioznost. Za mnoge ljude, online format je čak lakši jer se sesija odvija iz poznatog okruženja, što smanjuje početnu anksioznost. Vežbe izlaganja se sprovode između sesija, u realnim situacijama.",
      },
      {
        question: "Koliko dugo traje terapija za socijalnu anksioznost?",
        answer:
          "KBT za socijalnu anksioznost obično traje 12 do 20 sesija, u zavisnosti od intenziteta simptoma i ličnih ciljeva. Mnogi ljudi primete poboljšanje već nakon prvih nekoliko nedelja, a napredak se nastavlja i nakon završetka terapije jer naučene tehnike ostaju trajno korisne.",
      },
    ],
  },

  // 27
  fobije: {
    label: "Fobije i strahovi",
    guideLink: {
      href: "/blog/anksioznost-simptomi-lecenje",
      label: "Pročitaj vodič o anksioznosti i strahovima",
    },
    therapistSearchTerms: [
      "Anksioznost, fobije, zdravstvena anksioznost",
      "Fobije",
      "Fobije i strahovi",
    ],
    title:
      "Online terapeuti za fobije i strahove: prevazidi strah uz stručnu podršku",
    searchTerm:
      "fobije specificne fobije specifične fobije strah od visine strah od letenja strah od zatvorenog prostora klaustrofobija agorafobija strah od paukova strah od igala strah od krvi strah od vožnje strah od javnog nastupa terapija za fobije fobije simptomi fobije vrste postepeno izlaganje KBT za fobije",
    description:
      "Fobije su intenzivni, iracionalni strahovi koji te nateraju da izbegavaš određene situacije, predmete ili mesta. Bilo da osećaš paniku u liftu, pri pogledu sa visine, u avionu ili u prisustvu insekata, taj strah ograničava tvoj svakodnevni život i samopouzdanje.\n\nNa Rešimo Probleme pronađi online terapeuta koji radi sa fobijama kroz KBT (kognitivno-bihejvioralnu terapiju) i postepeno izlaganje. Cilj je da korak po korak smanjiš reakciju straha i povratiš slobodu u situacijama koje si do sada izbegavao/la. Pregledaj profile terapeuta, uporedi pristupe i zakaži prvu sesiju.",
    directoryContent: {
      title: "Terapija za fobije online, pronađi terapeuta",
      description:
        "Ako želiš da razgovaraš o strahu koji te navodi na izbegavanje određenih situacija, mesta ili stvari, ovde možeš da pronađeš terapeuta koji navodi rad sa fobijama i strahovima. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Ne moraš unapred da znaš naziv straha da bi počeo/la.",
      seoTitle: "Terapija za fobije online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za fobije i strahove. Uporedi profile, cene i slobodne termine, pa zakaži razgovor koji ti odgovara.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za fobije online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa fobijama i strahovima. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li moram da znam koju fobiju imam?",
          answer:
            "Ne moraš. Možeš početi od situacije ili straha koji ti otežava svakodnevicu i sa terapeutom razjasniti šta ti je potrebno.",
        },
        {
          question: "Da li je ova stranica za testiranje ili dijagnozu fobije?",
          answer:
            "Ne. Stranica služi za pronalazak terapeuta za razgovor. Ako tražiš dijagnostičku procenu, proveri sa odgovarajućom zdravstvenom ustanovom kako je pruža.",
        },
        {
          question: "Šta mogu da proverim pre zakazivanja?",
          answer:
            "Na profilu možeš da proveriš kako terapeut opisuje svoj rad, cenu sesanse i slobodne termine. To ti pomaže da odlučiš sa kim želiš da razgovaraš.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Terapija za fobije online: prevazidi strah | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za fobije i strahove. KBT i postepeno izlaganje uz licencirane psihologe. Zakaži termin brzo i diskretno.",
    relatedConditions: [
      "anksioznost",
      "agorafobija",
      "panika",
      "socijalna-anksioznost",
    ],
    passes: true,

    faqItems: [
      {
        question: "Koje su najčešće vrste fobija?",
        answer:
          "Najčešće specifične fobije uključuju strah od visine (akrofobija), strah od letenja (aviofobija), strah od zatvorenog prostora (klaustrofobija), strah od paukova (arahnofobija), strah od igala, strah od krvi i strah od vožnje. Takođe su česte agorafobija i strah od javnog nastupa. Terapeut procenjuje vrstu i intenzitet fobije i prilagođava plan rada.",
      },
      {
        question: "Kako izgleda terapija za fobije?",
        answer:
          "Terapija za fobije najčešće koristi KBT pristup i postepeno izlaganje. Zajedno sa terapeutom praviš listu situacija poređanih po intenzitetu straha i korak po korak se izlažeš svakoj od njih. Cilj je da tvoj mozak nauči da situacija nije opasna, čime se strah smanjuje. Tempo prilagođavamo tvojim mogućnostima.",
      },
      {
        question: "Da li online terapija pomaže kod fobija?",
        answer:
          "Da. Istraživanja pokazuju da je online KBT za fobije jednako efikasna kao terapija uživo. Uz video sesije na Rešimo Probleme možeš raditi sa terapeutom iz svog doma, što je posebno korisno ako ti fobija otežava odlazak u ordinaciju. Između sesija dobijaš zadatke za vežbanje izlaganja.",
      },
      {
        question: "Koliko traje terapija za fobije?",
        answer:
          "Kod specifičnih fobija (npr. strah od letenja ili visine) značajno poboljšanje obično nastupa za 6 do 12 sesija. Složenije fobije poput agorafobije mogu zahtevati duži rad. Terapeut postavlja jasne ciljeve i prati napredak kroz skale straha i listu situacija koje postepeno osvajamo.",
      },
      {
        question: "Po čemu se fobija razlikuje od običnog straha?",
        answer:
          "Obični strah je proporcionalan opasnosti i nestaje kada opasnost prođe. Fobija je intenzivna reakcija na nešto što objektivno nije opasno i dovodi do aktivnog izbegavanja. Ako strah od visine, lifta ili paukova menja tvoje planove i svakodnevicu, verovatno se radi o fobiji koja se uspešno tretira terapijom.",
      },
      {
        question: "Šta je postepeno izlaganje i da li je bezbedno?",
        answer:
          "Postepeno izlaganje (gradirana ekspozicija) je tehnika u kojoj se korak po korak približavaš situaciji koja izaziva strah. Počinješ od blažih scenarija i napreduješ tek kada se osećaš spremno. Ceo proces vodiš sa terapeutom koji prati tvoje reakcije i prilagođava tempo. Tehnika je dobro istražena i bezbedna.",
      },
      {
        question: "Kako pronaći terapeuta za fobije na Rešimo Probleme?",
        answer:
          "Pretraži terapeute po specijalnosti 'fobije i strahovi', pregledaj njihove profile, iskustvo sa KBT pristupom i recenzije klijenata. Izaberi terapeuta koji ti odgovara i zakaži prvi termin online. Sesije su fleksibilne i prilagođene tvom rasporedu.",
      },
    ],
  },

  // 28
  "ljutnja-bes": {
    passes: true,
    label: "Kontrola besa i ljutnje",
    guideLink: {
      href: "/blog/kako-kontrolisati-emocije-tehnike-i-strategije",
      label: "Pročitaj vodič o upravljanju emocijama",
    },
    therapistSearchTerms: [
      "Bes",
      "Impulsivnost i nezdrava ljutnja",
      "Ljutnja/bes/agresivnost - upravljanje",
      "Bes i emocionalna regulacija",
      "Agresija i bes",
      "Ljubomora i epizode besa",
    ],
    title:
      "Online terapeuti za kontrolu besa i ljutnje: nauči da upravljaš emocijama bez agresije",
    searchTerm:
      "kontrola besa ljutnja bes upravljanje besom napadi besa agresija kontrola impulsa regulacija emocija iritabilnost frustracija ljutnja bez razloga napadi besa kod odraslih kako kontrolisati bes KBT bes REBT terapija besa kontrola besa terapija kako se osloboditi besa strah od sopstvenog besa bes u vezi bes i odnosi okidači besa",
    description:
      "Bes i ljutnja su prirodne emocije, ali kada izmiču kontroli, mogu da naruše odnose, posao i zdravlje. Napadi besa, iritabilnost bez jasnog razloga ili agresivne reakcije često ukazuju na dublje emocionalne obrasce koje je moguće promeniti. Kognitivno-bihevioralna terapija (KBT) i REBT pomažu da prepoznaš okidače besa, razumeš šta se krije iza ljutnje i razviješ zdrave načine da izraziš emocije bez destruktivnih posledica.",
    directoryContent: {
      title: "Online terapeuti za bes i ljutnju",
      description:
        "Ako te česta ljutnja, napetost ili konflikti opterećuju, ovde možeš da pregledaš profile terapeuta koji navode rad sa besom i emocionalnom regulacijom. Uporedi njihov pristup, cenu i termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor.",
      seoTitle: "Terapeut za kontrolu besa online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za bes i ljutnju. Pregledaj profile, cene i termine, pa zakaži razgovor na Rešimo Probleme.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za kontrolu besa?",
          answer:
            "Na ovoj stranici su profili terapeuta čiji opisi rada navode bes, ljutnju ili emocionalnu regulaciju. Možeš da uporediš njihov pristup, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li mogu da zakažem razgovor zbog čestih izliva besa?",
          answer:
            "Možeš da zakažeš razgovor ako želiš da razumeš kako bes utiče na tebe, odnose ili svakodnevne situacije. Pri zakazivanju možeš kratko napisati šta želiš da otvoriš na prvom razgovoru.",
        },
        {
          question:
            "Da li je razgovor za mene ako se ljutnja javlja u vezi ili porodici?",
          answer:
            "Možeš da izabereš terapeuta čiji profil odgovara temi koju želiš da obradiš. Ako je važno da učestvuje i partner, pogledaj i direktorijum za terapiju za parove.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije i izabereš termin koji ti odgovara.",
        },
        {
          question: "Kada treba da potražim hitnu pomoć zbog besa ili nasilja?",
          answer:
            "Ako postoji neposredan rizik da povrediš sebe ili nekog drugog, obrati se hitnoj službi ili lokalnoj kriznoj pomoći odmah. Ova stranica je namenjena zakazivanju razgovora, ne hitnoj intervenciji.",
        },
      ],
    },
    seoTitle: "Kontrola besa: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za kontrolu besa i ljutnje online. KBT, REBT i tehnike regulacije emocija. Uporedi profile terapeuta i zakaži sesiju na Rešimo Probleme.",
    relatedConditions: ["stres", "odnosi", "karijera-posao"],
    faqItems: [
      {
        question: "Šta se zapravo krije iza čestog besa i ljutnje?",
        answer:
          "Bes je često sekundarna emocija koja prikriva ranjivost, strah, razočaranje ili osećaj nemoći. Terapeut ti pomaže da istražiš šta stoji iza ljutnje, jer tek kada razumeš pravi uzrok, možeš da promeniš način na koji reaguješ.",
      },
      {
        question: "Kako KBT i REBT pomažu kod kontrole besa?",
        answer:
          "Kognitivno-bihevioralna terapija (KBT) i racionalno-emotivna bihevioralna terapija (REBT) pomažu da prepoznaš rigidna uverenja koja pojačavaju bes (npr. 'ovo nije fer', 'mora da bude po mom'). Kroz terapiju učiš da zameniš ove obrasce fleksibilnijim razmišljanjem i konstruktivnim reakcijama.",
      },
      {
        question: "Da li je normalno imati napade besa bez očiglednog razloga?",
        answer:
          "Može se dogoditi da bes eskalira naizgled bez razloga. Često su u pitanju nagomilani stres, umor, nezadovoljene potrebe ili stari obrasci reagovanja. Terapija pomaže da identifikuješ prave okidače i prepoznaš znake upozorenja pre nego što bes eskalira.",
      },
      {
        question: "Koje tehnike pomažu da se smirim u trenutku besa?",
        answer:
          "Efikasne tehnike uključuju time-out (privremeno povlačenje iz situacije), duboko disanje stomakom, prepoznavanje fizičkih znakova besa (ubrzan puls, napetost mišića) i svesno odlaganje reakcije. Terapeut ti pomaže da izgradiš lični plan za trenutke kada bes počne da raste.",
      },
      {
        question: "Kako bes utiče na partnerske i porodične odnose?",
        answer:
          "Nekontrolisan bes može da naruši poverenje, izazove strah kod partnera ili dece i stvori ciklus konflikta i krivice. Terapija pomaže da naučiš asertivnu komunikaciju, postavljanje granica i zdrave načine izražavanja nezadovoljstva bez agresije.",
      },
      {
        question: "Da li online terapija funkcioniše za probleme sa besom?",
        answer:
          "Istraživanja pokazuju da je online KBT jednako efikasna kao terapija uživo za upravljanje besom. Sesije iz poznatog okruženja mogu čak pomoći da se otvorenije razgovara o teškim temama. Između sesija radiš na konkretnim zadacima i tehnikama u svakodnevnim situacijama.",
      },
      {
        question: "Koliko sesija je potrebno za vidljive rezultate?",
        answer:
          "Većina ljudi primeti poboljšanje nakon 8 do 16 sesija KBT-a. Prve promene, poput boljeg prepoznavanja okidača i korišćenja tehnika smirivanja, mogu se javiti već posle nekoliko nedelja. Trajanje zavisi od intenziteta problema i ličnih ciljeva.",
      },
    ],
  },

  // 29
  "trauma-iz-detinjstva": {
    passes: true,
    label: "Trauma iz detinjstva",
    guideLink: {
      href: "/blog/ptsp-razumevanje-traume-i-put-do-oporavka",
      label: "Pročitaj vodič o traumi i PTSP-u",
    },
    therapistSearchTerms: ["Traume iz detinjstva"],
    title:
      "Online terapeuti za traumu iz detinjstva: rad na ranim iskustvima i emocionalnom isceljenju",
    searchTerm:
      "trauma iz detinjstva rane traume dečja trauma emocionalne rane zanemarivanje zlostavljanje emocionalno zlostavljanje potisnute traume znaci traume iz detinjstva traume iz detinjstva posledice kako izlečiti traumu lečenje traume EMDR trauma shema terapija unutrašnje dete privrženost nesiguran stil privrženosti razvojna trauma rana iskustva KBT trauma terapija traume",
    description:
      "Trauma iz detinjstva, bilo da je posledica zanemarivanja, emocionalnog ili fizičkog zlostavljanja ili odrastanja u nestabilnom okruženju, može da ostavi duboke posledice na odnose, samopouzdanje i emocionalno zdravlje u odraslom dobu. Potisnute traume često se manifestuju kroz anksioznost, poteškoće u vezama ili hronični osećaj nesigurnosti. EMDR, shema terapija i KBT pomažu da preradiš rana iskustva, prepoznaš obrasce koji potiču iz detinjstva i izgradiš sigurniji odnos prema sebi i drugima.",
    directoryContent: {
      title: "Online terapeut za traumu iz detinjstva",
      description:
        "Ako želiš da razgovaraš o ranijim iskustvima, odnosima ili obrascima koji ti danas stvaraju teškoće, ovde možeš da pronađeš terapeuta koji navodi rad sa traumama iz detinjstva. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Ne moraš unapred da znaš da li je određeno iskustvo trauma da bi počeo/la.",
      seoTitle: "Terapeut za traumu iz detinjstva online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za razgovor o traumama iz detinjstva. Uporedi profile, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za traumu iz detinjstva?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa traumama iz detinjstva. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question:
            "Da li moram da znam da li je iskustvo iz detinjstva trauma?",
          answer:
            "Ne moraš. Možeš početi od iskustva ili posledica koje te sada opterećuju i sa terapeutom razjasniti šta ti je potrebno.",
        },
        {
          question: "Da li moram odmah da govorim o detaljima?",
          answer:
            "Ne moraš. Na prvom razgovoru možeš reći samo onoliko koliko ti je prijatno i pitati terapeuta kako pristupa ovoj temi.",
        },
        {
          question: "Šta mogu da proverim pre zakazivanja?",
          answer:
            "Na profilu možeš da proveriš kako terapeut opisuje svoj rad, cenu sesanse i slobodne termine. To ti pomaže da izabereš osobu sa kojom želiš da razgovaraš.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Trauma iz detinjstva: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za traumu iz detinjstva online. EMDR, shema terapija i KBT za rad na ranim iskustvima. Uporedi profile i zakaži sesiju na Rešimo Probleme.",
    relatedConditions: [
      "trauma",
      "ptsp",
      "problemi-vezivanja",
      "shema-terapija",
    ],
    faqItems: [
      {
        question: "Koji su znaci traume iz detinjstva kod odraslih?",
        answer:
          "Trauma iz detinjstva može se manifestovati kroz poteškoće u održavanju bliskih odnosa, nizak osećaj sopstvene vrednosti, preteranu potrebu da se udovolji drugima, anksioznost, emocionalno povlačenje ili hroničan osećaj nesigurnosti. Mnogi ljudi ne povezuju ove obrasce sa ranim iskustvima dok ne počnu da ih istražuju u terapiji.",
      },
      {
        question: "Koja terapija je najefikasnija za traumu iz detinjstva?",
        answer:
          "Najefikasnije pristupe čine EMDR (desenzitizacija i reprocesiranje pokretima očiju), shema terapija i kognitivno-bihevioralna terapija (KBT). EMDR pomaže u preradi traumatskih sećanja, shema terapija radi na dubokim obrascima iz detinjstva, a KBT menja negativna uverenja i ponašanja. Terapeut bira pristup na osnovu tvoje specifične situacije.",
      },
      {
        question: "Da li potisnute traume mogu da utiču na sadašnji život?",
        answer:
          "Potisnute traume mogu značajno da utiču na svakodnevno funkcionisanje, čak i kada nisi svestan njihovog porekla. Mogu se manifestovati kroz intenzivne emocionalne reakcije, izbegavanje određenih situacija, teškoće u vezama ili telesne simptome poput napetosti i nesanice. Terapija pomaže da se ovi obrasci postepeno osvestle i prerade.",
      },
      {
        question: "Kako izgleda proces terapije za traumu iz detinjstva?",
        answer:
          "Terapija obično počinje sa stabilizacijom i izgradnjom sigurnog prostora. Terapeut ti pomaže da razviješ resurse za regulaciju emocija pre nego što se postepeno pristupi radu na traumatskim iskustvima. Cilj nije ponovo proživeti bolna sećanja, već ih integrisati na način koji smanjuje njihov emocionalni naboj i oslobađa prostor za nove obrasce.",
      },
      {
        question:
          "Da li je normalno da se tokom terapije jave intenzivne emocije?",
        answer:
          "Pojava intenzivnih emocija tokom rada na traumi je očekivan deo procesa. Terapeut te priprema za to kroz tehnike stabilizacije i regulacije, tako da imaš alate za upravljanje teškim osećanjima. Tempo rada se prilagođava tvoj kapacitetu, i nikada se ne ide brže nego što si spreman.",
      },
      {
        question: "Kako trauma iz detinjstva utiče na partnerske odnose?",
        answer:
          "Rana iskustva oblikuju stil privrženosti, koji utiče na to kako se povezuješ sa partnerom. Nesiguran stil privrženosti može da dovede do straha od bliskosti, potrebe za stalnim potvrđivanjem ili izbegavanja emocionalnog vezivanja. Terapija pomaže da prepoznaš ove obrasce i razviješ sigurnije načine povezivanja.",
      },
      {
        question: "Da li online terapija funkcioniše za rad na traumi?",
        answer:
          "Istraživanja pokazuju da je online terapija efikasna za rad na traumi, uključujući EMDR koji se uspešno sprovodi putem video sesija. Za mnoge ljude, sesija iz poznatog okruženja pruža dodatni osećaj sigurnosti koji je važan pri radu na osetljivim temama iz prošlosti.",
      },
    ],
  },

  // 30
  "seksualna-terapija": {
    passes: true,
    label: "Seksualna terapija",
    guideLink: {
      href: "/blog/online-psihoterapija-vodic",
      label: "Pročitaj kako izgleda online psihoterapija",
    },
    therapistSearchTerms: [
      "Problemi intimnosti i seksualnosti",
      "Seksualni problemi",
    ],
    title:
      "Online seksualna terapija: diskretna podrška za intimnost, libido i seksualne poteškoće",
    searchTerm:
      "seksualna terapija seksolog seksualne poteškoće seksualnost libido smanjen libido erektilna disfunkcija erektilne poteškoće prerana ejakulacija bol pri seksu vaginizam anksioznost performansa seksualna anksioznost intimnost u vezi seksualna terapija za parove seksolog online sensate focus seksualno zdravlje seksualni problemi komunikacija o seksu",
    description:
      "Seksualne poteškoće poput smanjenog libida, erektilne disfunkcije, prerane ejakulacije, bola pri seksu ili anksioznosti performansa utiču na kvalitet intimnog života i odnosa. Seksualna terapija je oblik razgovorne terapije koji pomaže da razumeš uzroke poteškoća, poboljšaš komunikaciju sa partnerom i razviješ zdraviji odnos prema seksualnosti. Pristup je diskretan, profesionalan i prilagođen individualnim potrebama ili radu sa parom.",
    seoTitle: "Seksualna terapija: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za seksualnu terapiju online. Diskretna podrška za libido, intimnost i seksualne poteškoće. Uporedi profile i zakaži sesiju na Rešimo Probleme.",
    relatedConditions: ["za-parove", "odnosi"],
    directoryContent: {
      title:
        "Seksualna terapija online za razgovor o intimnosti i seksualnim poteškoćama",
      description:
        "Seksualna terapija može biti prostor za razgovor o intimnosti, želji, komunikaciji sa partnerom ili poteškoćama koje utiču na seksualni život. Na Rešimo Probleme možeš da pregledaš profile terapeuta koji navode rad sa intimnošću i seksualnim temama, uporediš njihov pristup, cenu i slobodne termine, pa zakažeš online razgovor. Možeš doći sam/a ili sa partnerom, u skladu sa onim o čemu želiš da razgovaraš.",
      seoTitle: "Seksualna terapija online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za razgovor o intimnosti i seksualnim poteškoćama. Uporedi profile, cene i termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za seksualnu terapiju online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa intimnošću i seksualnim temama. Uporedi njihov opis rada, cenu i dostupne termine pre zakazivanja.",
        },
        {
          question: "Mogu li na seksualnu terapiju da dođem sam/a?",
          answer:
            "Da. Možeš zakazati individualni razgovor ili, ako oboje želite, razgovor sa partnerom. Na profilu terapeuta možeš videti da li nudi rad sa parovima.",
        },
        {
          question: "O kojim temama mogu da razgovaram na seksualnoj terapiji?",
          answer:
            "Možeš razgovarati o intimnosti, komunikaciji, razlikama u želji, nelagodnosti ili drugim temama koje utiču na seksualni život. Ti odlučuješ koliko želiš da podeliš i kojim tempom.",
        },
        {
          question: "Kako izgleda prvi online razgovor?",
          answer:
            "Prvi razgovor je prilika da objasniš šta ti je važno, postaviš pitanja i čuješ kako terapeut radi. Tako možeš proceniti da li ti pristup odgovara.",
        },
        {
          question: "Koliko košta online seksualna terapija?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više profila i izabrati termin i cenu koji ti odgovaraju.",
        },
      ],
    },
    faqItems: [
      {
        question: "Šta je seksualna terapija i kako izgleda sesija?",
        answer:
          "Seksualna terapija je oblik razgovorne psihoterapije fokusiran na seksualne poteškoće i intimnost. Sesija se odvija kroz razgovor sa terapeutom, bez fizičkog kontakta. Terapeut pomaže da istražiš uzroke poteškoća, razviješ bolje obrasce komunikacije sa partnerom i dobiješ konkretne tehnike za primenu između sesija.",
      },
      {
        question: "Koje seksualne poteškoće se mogu rešavati u terapiji?",
        answer:
          "Terapija pokriva širok spektar poteškoća: smanjen libido ili razliku u želji između partnera, erektilnu disfunkciju, preranu ejakulaciju, bol pri seksu (vaginizam, dispareunija), anksioznost performansa, poteškoće sa orgazmom i probleme sa slikom tela koji utiču na seksualnost.",
      },
      {
        question: "Da li mogu da dođem sam ili je potreban i partner?",
        answer:
          "Možeš doći sam ili sa partnerom. Individualna terapija je korisna za rad na ličnim barijerama, anksioznosti ili prošlim iskustvima. Terapija za parove pomaže kada je problem u komunikaciji, razlici u željama ili kada oba partnera žele da unaprede intimni odnos.",
      },
      {
        question: "Da li je to neprijatno i kako se čuva privatnost?",
        answer:
          "Potpuno razumljivo je osećati početnu nelagodnost. Terapeuti za seksualnu terapiju su obučeni da stvore siguran, neosudujući prostor. Ti određuješ tempo i teme. Online format dodatno pruža diskreciju jer se sesija odvija iz tvog privatnog okruženja, bez čekaonice.",
      },
      {
        question: "Da li je potrebno prvo otići kod lekara?",
        answer:
          "Za neke poteškoće poput bola pri seksu ili erektilne disfunkcije, preporučljivo je prvo isključiti medicinske uzroke kod lekara (urologa, ginekologa). Terapeut na platformi može da proceni tvoju situaciju i, ako je potrebno, preporuči medicinsku evaluaciju pre ili paralelno sa terapijom.",
      },
      {
        question: "Šta je sensate focus tehnika?",
        answer:
          "Sensate focus je vežba dodira između partnera koja se koristi u seksualnoj terapiji. Fokus je na senzacijama i povezanosti, bez pritiska na performanse ili krajnji cilj. Terapeut vodi parove kroz postepene faze, pomažući da se smanji anksioznost i obnovi fizička bliskost na opušten način.",
      },
      {
        question: "Koliko sesija je obično potrebno?",
        answer:
          "Seksualna terapija obično traje 8 do 16 sesija, u zavisnosti od prirode poteškoća i ličnih ciljeva. Neke poteškoće, poput anksioznosti performansa, mogu da se poboljšaju već posle nekoliko sesija. Kompleksniji problemi koji uključuju odnos ili prošla iskustva mogu zahtevati duži rad.",
      },
    ],
  },

  // 31
  "fertilitet-infertilitet": {
    passes: true,
    label: "Infertilitet: psihološka podrška",
    title:
      "Online podrška za infertilitet i neplodnost: terapeut uz tebe tokom celog procesa",
    searchTerm:
      "infertilitet neplodnost psihološka podrška infertilitet vantelesna oplodnja VTO IVF emocionalna podrška neplodnost stres neplodnost neuspela vantelesna oplodnja gubitak trudnoće pobačaj spontani pobačaj planiranje porodice fertilitet par i neplodnost sekundarni infertilitet tuga neplodnost anksioznost VTO savetovanje infertilitet",
    description:
      "Infertilitet (neplodnost) donosi intenzivan emocionalni stres, neizvesnost i pritisak koji mogu da utiču na mentalno zdravlje, partnerski odnos i svakodnevno funkcionisanje. Proces dijagnostike, vantelesne oplodnje (VTO) ili gubitak trudnoće često izazivaju tugu, anksioznost, osećaj krivice i izolacije. Psihološka podrška pomaže da se nosiš sa emocionalnim usponima i padovima, sačuvaš odnos sa partnerom i pronađeš unutrašnje resurse za svaki sledeći korak.",
    seoTitle: "Infertilitet: psihološka podrška online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za emocionalnu podršku tokom infertiliteta i VTO online. Rad na stresu, tuzi i odnosu. Uporedi profile i zakaži sesiju na Rešimo Probleme.",
    relatedConditions: ["za-parove", "zalovanje", "odnosi"],
    faqItems: [
      {
        question:
          "Zašto je psihološka podrška važna tokom lečenja infertiliteta?",
        answer:
          "Infertilitet je jedna od najstresnijih životnih situacija. Istraživanja pokazuju da nivo stresa kod žena koje prolaze VTO može biti uporediv sa onim kod ozbiljnih medicinskih dijagnoza. Terapija pomaže da upravljaš anksioznošću, očuvaš partnerski odnos i doneseš odluke iz jasnijeg emocionalnog prostora.",
      },
      {
        question: "Kako podneti neizvesnost tokom VTO procesa?",
        answer:
          "Čekanje rezultata i neizvesnost ishoda su jedan od najtežih aspekata VTO. Terapeut ti pomaže da razviješ strategije za dane čekanja, ograničiš preteranu potragu za informacijama, uvedeš strukture koje ti daju osećaj kontrole i pripremiš se za različite ishode bez samooptuživanja.",
      },
      {
        question:
          "Da li je normalno da se partneri različito nose sa neplodnošću?",
        answer:
          "Potpuno je normalno. Jedan partner može da bude fokusiran na akciju i rešenja, dok drugi više oseća tugu ili potrebu za razgovorom. Terapija pomaže parovima da razumeju i poštuju različite načine suočavanja i nađu zajednički jezik za podršku jedno drugom.",
      },
      {
        question:
          "Kako se nositi sa gubitkom trudnoće ili neuspelim VTO ciklusom?",
        answer:
          "Gubitak trudnoće ili neuspeo VTO ciklus donosi tugu koja zaslužuje prostor i poštovanje. Terapeut ti pomaže da proradiš gubitak, doneseš odluku o sledećim koracima u svom tempu i prepoznaš kada je potrebna pauza pre nego što nastaviš dalje.",
      },
      {
        question: "Kako postaviti granice prema porodici i okolini?",
        answer:
          "Pitanja poput 'kad ćete imati decu' ili dobronamernih saveta mogu biti izuzetno bolna. Terapija pomaže da postaviš jasne granice, pripremiš odgovore koji štite tvoju privatnost i odlučiš kome i koliko želiš da poveravamo o procesu.",
      },
      {
        question: "Da li mogu da dođem sam ili sa partnerom?",
        answer:
          "Oba pristupa su korisna. Individualna terapija pomaže da obradiš lična osećanja, dok rad sa partnerom jača komunikaciju i međusobnu podršku. Mnogi počnu individualno, pa uključe partnera kada osete potrebu za zajedničkim radom.",
      },
      {
        question:
          "Da li online terapija funkcioniše za podršku kod infertiliteta?",
        answer:
          "Online terapija pruža fleksibilnost koja je posebno važna tokom VTO procesa, kada su česti odlasci u kliniku i emocionalni usponi i padovi. Sesija iz poznatog okruženja može da olakša razgovor o bolnim temama, a zakazivanje se prilagođava tvom rasporedu i ciklusima tretmana.",
      },
    ],
  },

  // 32
  "perinatalna-podrska": {
    passes: true,
    label: "Postporođajna depresija i perinatalna podrška",
    therapistSearchTerms: ["Postporodjajna psiholoska stanja"],
    guideLink: {
      href: "/blog/postporodjajna-depresija",
      label: "Pročitaj vodič o postporođajnoj depresiji",
    },
    title:
      "Online terapeuti za postporođajnu depresiju i perinatalnu anksioznost: podrška za mame i tate",
    searchTerm:
      "postporođajna depresija postpartalna depresija perinatalna anksioznost baby blues postporođajna anksioznost postporođajna depresija simptomi postporođajna depresija lečenje koliko traje postporođajna depresija postporođajna depresija kod očeva intruzivne misli nakon porođaja postpartalna psihoza KBT postporođajna depresija interpersonalna terapija perinatalno mentalno zdravlje trudnoća anksioznost podrška nakon porođaja terapija za mame",
    description:
      "Postporođajna depresija pogađa do 1 od 5 žena nakon porođaja, a može se javiti i kod očeva. Simptomi poput duboke tuge, anksioznosti, nesanice i intruzivnih misli nisu znak slabosti, već signal da ti je potrebna podrška. Baby blues obično prođe za dve nedelje, ali ako se osećanja pojačavaju ili traju duže, razgovor sa terapeutom može napraviti veliku razliku. Na Rešimo Probleme možeš pronaći terapeuta sa iskustvom u perinatalnom mentalnom zdravlju, uporediti profile i zakazati online sesiju iz udobnosti svog doma, u vreme koje tebi odgovara.",
    directoryContent: {
      title: "Online terapeut za postporođajnu podršku",
      description:
        "Ako ti je period trudnoće ili roditeljstva doneo tugu, napetost, preopterećenost ili osećaj da ti je potrebna podrška, ovde možeš da pregledaš profil terapeuta koji navodi rad sa postporođajnim psihološkim stanjima. Pročitaj opis rada, cenu i slobodne termine, pa odluči da li želiš da zakažeš online razgovor.",
      seoTitle: "Terapeut za postporođajnu podršku online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za postporođajnu podršku. Pregledaj profil, cenu i termine, pa zakaži razgovor na Rešimo Probleme.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za postporođajnu podršku?",
          answer:
            "Na ovoj stranici je profil terapeuta koji navodi rad sa postporođajnim psihološkim stanjima. Pre zakazivanja možeš da pročitaš opis rada, proveriš cenu i pogledaš dostupne termine.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako nisam sigurna šta mi se dešava?",
          answer:
            "Možeš da zakažeš razgovor i kada nemaš jasan naziv za ono kroz šta prolaziš. Pri zakazivanju možeš kratko opisati kako se osećaš i šta bi volela da razumeš ili promeniš.",
        },
        {
          question: "Da li je ovaj razgovor namenjen samo majkama?",
          answer:
            "Profil navodi postporođajna psihološka stanja kao temu rada. Pre zakazivanja pročitaj ceo opis profila i po potrebi pošalji pitanje terapeutu o svojoj konkretnoj situaciji.",
        },
        {
          question: "Kako izgleda online razgovor sa terapeutom?",
          answer:
            "Nakon izbora termina zakazuješ video razgovor preko platforme. Profil terapeuta unapred prikazuje cenu, pa možeš da odlučiš pre nego što rezervišeš termin.",
        },
        {
          question: "Kada treba da potražim hitnu pomoć?",
          answer:
            "Ako si u neposrednoj opasnosti, razmišljaš da povrediš sebe ili nekog drugog, ili ne možeš da obezbediš bezbednost sebi ili detetu, obrati se hitnoj službi ili lokalnoj kriznoj pomoći odmah. Ova stranica nije zamena za hitnu pomoć.",
        },
      ],
    },
    seoTitle: "Postporođajna depresija: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za postporođajnu depresiju i perinatalnu anksioznost online. KBT i interpersonalna terapija. Zakaži sesiju na Rešimo Probleme.",
    relatedConditions: [
      "postporodjajna-depresija",
      "depresija",
      "anksioznost",
      "roditeljstvo",
    ],
    faqItems: [
      {
        question:
          "Koja je razlika između baby blues i postporođajne depresije?",
        answer:
          "Baby blues je blagi pad raspoloženja koji pogađa do 80% žena prvih dana nakon porođaja i prolazi sam za 10-14 dana. Postporođajna depresija je intenzivnija, traje duže od dve nedelje i uključuje simptome poput duboke tuge, gubitka interesovanja, anksioznosti, problema sa snom (čak i kad beba spava) i teškoće u povezivanju sa bebom. Ako simptomi ne prolaze, razgovor sa terapeutom može pomoći da prepoznaš šta se dešava i pronađeš odgovarajuću podršku.",
      },
      {
        question:
          "Koliko traje postporođajna depresija i da li se može izlečiti?",
        answer:
          "Bez tretmana, postporođajna depresija može trajati mesecima, pa i duže. Uz odgovarajuću podršku, većina žena oseti značajno poboljšanje unutar nekoliko nedelja do meseci. Kognitivno-bihevioralna terapija (KBT) i interpersonalna terapija (IPT) su među najefikasnijim pristupima. Terapija pomaže da prepoznaš obrasce mišljenja, izgradiš strategije za suočavanje i postepeno povratiš ravnotežu.",
      },
      {
        question: "Da li postporođajna depresija može pogoditi i očeve?",
        answer:
          "Da, istraživanja pokazuju da oko 10% očeva doživi postporođajnu depresiju, posebno u prvih godinu dana nakon rođenja deteta. Simptomi mogu uključivati povlačenje, iritabilnost, gubitak motivacije i teškoće u povezivanju sa bebom. Terapija je jednako dostupna i korisna za tate, bilo individualno ili zajedno sa partnerkom.",
      },
      {
        question: "Jesu li intruzivne misli nakon porođaja normalne?",
        answer:
          "Neželjene, zastrašujuće misli o bebi ili sebi su iznenađujuće česte kod novih roditelja i ne znače da ćeš postupiti po njima. Često su povezane sa anksioznošću i nedostatkom sna. Terapeut ti može pomoći da razumeš ove misli bez panike, naučiš tehnike defuzije (posmatranje misli bez reagovanja) i razviješ plan za trenutke kada se jave.",
      },
      {
        question: "Kako izgleda terapija za postporođajnu depresiju online?",
        answer:
          "Online sesija se odvija putem video poziva iz tvog doma, što je posebno praktično kad imaš bebu. Terapeut koristi pristupe poput KBT-a (rad na mislima i ponašanjima), interpersonalne terapije (poboljšanje odnosa i podrške) ili psihoedukacije o perinatalnom periodu. Ne moraš se brinuti o organizaciji čuvanja deteta, a sesije možeš zakazati u terminima koji odgovaraju tvom rasporedu.",
      },
      {
        question: "Kada da potražim pomoć za postporođajnu depresiju?",
        answer:
          "Ako tuga, anksioznost ili razdražljivost traju duže od dve nedelje, ako imaš teškoće u brizi o bebi ili sebi, ako osećaš da se ne povezuješ sa bebom ili ako imaš misli o samopovređivanju, važno je da se javiš terapeutu. Ranije traženje pomoći obično dovodi do bržeg oporavka. Ne postoji 'premali' razlog za razgovor sa stručnjakom.",
      },
      {
        question:
          "Da li se terapija može kombinovati sa lekovima za postporođajnu depresiju?",
        answer:
          "Da, mnoge žene imaju koristi od kombinacije terapije i medikamentoznog tretmana, posebno kod umerenih i teških simptoma. Terapeut sarađuje sa tvojim lekarom ili psihijatrom kako bi se osigurala koordinisana briga. Odluku o lekovima uvek donosiš sa lekarom, uzimajući u obzir i dojenje i individualne okolnosti.",
      },
    ],
  },

  // 33
  roditeljstvo: {
    label: "Roditeljstvo i savetovanje",
    therapistSearchTerms: [
      "Krize u roditeljstvu i adolescentske krize",
      "Roditeljstvo; unapredjenje blagostanja i roditelja i dece",
      "Roditeljstvo",
      "Krize u roditeljstvu i adolescentske krize i izazovi",
      "Podrska roditeljima u savremenom roditeljstvu",
    ],
    guideLink: {
      href: "/blog/roditeljstvo-i-mentalno-zdravlje-kako-se-brinuti-o-sebi-i-deci",
      label: "Pročitaj vodič o roditeljstvu i mentalnom zdravlju",
    },
    searchTerm:
      "roditeljstvo roditeljske veštine savetovanje za roditelje postavljanje granica disciplina komunikacija sa decom tinejdžeri ispadi besa ekransko vreme pozitivno roditeljstvo samohrani roditelj koroditeljstvo novi roditelji vaspitanje dece porodična terapija online psiholog za roditelje",
    description:
      "Roditeljstvo donosi radost, ali i izazove za koje nisi uvek spreman/na. Bilo da se boriš sa postavljanjem granica, komunikacijom sa tinejdžerom, ispadima besa ili balansom između posla i porodice, nisi sam/a u tome. Na platformi Rešimo Probleme možeš pronaći licencirane terapeute specijalizovane za roditeljske teme i zakazati online sesiju iz udobnosti svog doma. Dobij konkretne alate i strategije za svakodnevne izazove vaspitanja.",
    directoryContent: {
      title: "Online savetovanje za roditelje",
      description:
        "Ako želiš da razgovaraš o roditeljstvu, granicama, komunikaciji ili porodičnim promenama, ovde možeš da pronađeš terapeuta koji navodi rad sa roditeljskim temama. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Možeš početi od konkretne situacije koja ti je trenutno najteža, bez potrebe da unapred imaš rešenje.",
      seoTitle: "Savetovanje za roditelje online | Rešimo Probleme",
      seoDescription:
        "Pronađi online savetovanje za roditelje. Uporedi profile terapeuta, cene i slobodne termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za roditeljske teme?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode rad sa roditeljstvom. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Mogu li da zakažem razgovor samo za sebe kao roditelj?",
          answer:
            "Da. Možeš zakazati individualni razgovor o situaciji koja te opterećuje i pitati terapeuta koji format rada predlaže za tvoju temu.",
        },
        {
          question: "Da li dete treba da učestvuje u razgovoru?",
          answer:
            "To zavisi od teme i načina rada terapeuta. Pre zakazivanja proveri profil, a na prvom razgovoru možeš pitati da li i kako terapeut uključuje druge članove porodice.",
        },
        {
          question: "Da li je ovo procena ili dijagnoza za dete?",
          answer:
            "Ne. Ova stranica služi za pronalazak terapeuta za razgovor o roditeljskim temama. Za razvojnu ili zdravstvenu procenu obrati se odgovarajućem stručnjaku.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle:
      "Online savetovanje za roditelje i roditeljstvo | Rešimo Probleme",
    seoDescription:
      "Pronađi licencirane terapeute za roditeljske izazove. Online savetovanje za granice, komunikaciju sa decom i tinejdžerima. Zakaži sesiju.",
    relatedConditions: ["porodica", "stres", "razvod-razlaz"],
    passes: true,

    faqItems: [
      {
        question: "Kako online savetovanje za roditelje može da mi pomogne?",
        answer:
          "Terapeut specijalizovan za roditeljstvo pomaže ti da razumeš ponašanje svog deteta, razviješ efikasne strategije za komunikaciju i granice, i nađeš balans između podrške i discipline. Sesije su online, pa ih možeš zakazati u vreme koje ti odgovara.",
      },
      {
        question: "Kako postaviti granice detetu bez vikanja i konflikta?",
        answer:
          "Terapeut te uči tehnikama poput 'kada-onda' rečenica, davanja izbora između dve prihvatljive opcije i doslednih posledica povezanih sa ponašanjem. Cilj je da dete nauči samoregulaciju, a ti sačuvaš strpljenje.",
      },
      {
        question: "Kada treba potražiti stručnu pomoć za roditeljske izazove?",
        answer:
          "Ako osećaš da si stalno iscrpljen/a, da gubis strpljenje, da se konflikti sa detetom ponavljaju ili da ne znaš kako da reaguješ na određeno ponašanje, to je dobar trenutak za razgovor sa terapeutom. Rana podrška sprečava eskalaciju problema.",
      },
      {
        question: "Šta ako imamo različite stilove vaspitanja sa partnerom?",
        answer:
          "Razlike u roditeljskim stilovima su česte i ne moraju biti problem. Terapeut vam pomaže da nađete zajednički jezik, usaglasite pravila i izbegnete da dete koristi te razlike. Ovo je posebno korisno i kod koroditeljstva nakon razvoda.",
      },
      {
        question:
          "Kako da poboljšam komunikaciju sa tinejdžerom koji se zatvara?",
        answer:
          "Tinejdžeri se često povlače kao deo normalnog razvoja, ali to ne znači da ne možeš održati bliskost. Terapeut predlaže konkretne tehnike: aktivno slušanje bez osude, postavljanje otvorenih pitanja i poštovanje njihove potrebe za autonomijom.",
      },
      {
        question: "Da li mogu na online terapiju ako sam samohrani roditelj?",
        answer:
          "Apsolutno. Online terapija je posebno pogodna za samohrane roditelje jer ne zahteva putovanje ili čuvanje dece. Sesija traje 45 minuta, a terapeut razume specifične izazove sa kojima se suočavaš, od organizacije do emocionalne podrške.",
      },
      {
        question: "Koliko košta savetovanje za roditelje na Rešimo Probleme?",
        answer:
          "Sesija sa licenciranim terapeutom košta od 30 evra. Možeš pregledati profile terapeuta, njihove specijalizacije i ocene drugih korisnika pre nego što zakažeš prvi razgovor. Nema pretplate ni skrivenih troškova.",
      },
    ],
  },

  // 34
  "razvod-razlaz": {
    passes: true,
    label: "Razvod i razlaz",
    guideLink: {
      href: "/blog/razvod-braka-kako-preziveti-i-krenuti-dalje",
      label: "Pročitaj vodič o razvodu i razlazu",
    },
    title:
      "Online terapeuti za razvod i razlaz: emocionalna podrška tokom i nakon prekida",
    searchTerm:
      "razvod braka terapija razvoda razlaz prekid veze emocionalna podrška razvod kako preživeti razvod život posle razvoda razvod i deca posledice razvoda tuga nakon razvoda kada je vreme za razvod terapija nakon razvoda oporavak posle razvoda razvod iskustva razvod braka iskustva savetovanje razvod kako zaštititi decu razvod komunikacija nakon razvoda",
    therapistSearchTerms: ["razvod"],
    description:
      "Razvod ili razlaz donosi intenzivne emocije: tugu, bes, strah od neizvesnosti i osećaj gubitka. Bez obzira da li si ti doneo/la odluku ili je ona pala sa druge strane, proces prilagođavanja zahteva podršku. Posebno kada su deca u pitanju, važno je naći način za zdravu komunikaciju i zaštitu njihovog emocionalnog sveta. Na Rešimo Probleme možeš pronaći terapeuta sa iskustvom u radu sa osobama koje prolaze kroz razvod ili razlaz, uporediti profile i zakazati online sesiju u vreme koje tebi odgovara.",
    directoryContent: {
      title: "Online terapeut za razvod i razlaz",
      description:
        "Ako prolaziš kroz razlaz, razvod ili promenu porodičnog života, ovde možeš da pregledaš profile terapeuta koji navode rad sa razvodom. Uporedi opis rada, cenu i termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor o onome što ti je sada najteže.",
      seoTitle: "Terapeut za razvod i razlaz online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za razvod ili razlaz. Pregledaj profile, cene i termine, pa zakaži razgovor na Rešimo Probleme.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za razvod ili razlaz?",
          answer:
            "Na ovoj stranici su profili terapeuta čiji opisi rada navode razvod. Pročitaj njihov pristup, cenu i slobodne termine pre nego što zakažeš razgovor.",
        },
        {
          question:
            "Mogu li da zakažem razgovor iako još nisam donela odluku o razvodu?",
          answer:
            "Možeš da zakažeš individualni razgovor dok razmišljaš o svojoj situaciji ili posle prekida. Pri zakazivanju možeš ukratko opisati temu koju želiš da otvoriš, bez potrebe da unapred znaš odluku.",
        },
        {
          question:
            "Da li je terapija za parove isto što i podrška tokom razvoda?",
          answer:
            "Nije nužno isto. Ako želite zajednički razgovor, pogledaj direktorijum za terapiju za parove i proveri da li terapeut nudi rad sa parovima. Ova stranica prikazuje podršku za temu razvoda i razlaza.",
        },
        {
          question:
            "Da li terapeut daje pravne savete o razvodu ili starateljstvu?",
          answer:
            "Terapeut nije zamena za advokata, sud ili stručnu pravnu pomoć. Razgovor može biti prostor za emocije i lične odluke, dok pravna pitanja treba proveriti sa odgovarajućim stručnjakom.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije i izabereš termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Razvod i razlaz: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za emocionalnu podršku tokom razvoda ili razlaza online. Individualna i porodična terapija. Uporedi profile i zakaži sesiju na Rešimo Probleme.",
    relatedConditions: ["odnosi", "porodica", "zalovanje"],
    faqItems: [
      {
        question: "Kada je pravo vreme da potražim terapiju tokom razvoda?",
        answer:
          "Bilo kada kada osećaš da ti je teško da se nosiš sa emocijama, donosiš važne odluke ili komuniciraš sa bivšim partnerom. Mnogi ljudi se jave terapeutu dok još razmišljaju o razvodu, tokom samog procesa ili nakon što je sve završeno. Ne postoji 'prekasno' ili 'prerano', svaki trenutak je dobar za podršku.",
      },
      {
        question: "Kako terapija pomaže kod razvoda ili razlaza?",
        answer:
          "Terapeut ti pruža siguran prostor da obradiš tugu, bes i strah bez osude. Pomaže ti da razumeš svoje emocije, izgradiš strategije za suočavanje sa promenom i doneseš odluke iz pozicije jasnoće umesto reaktivnosti. Kod razvoda sa decom, terapija pomaže i u uspostavljanju zdrave komunikacije sa bivšim partnerom.",
      },
      {
        question: "Kako zaštititi decu tokom razvoda?",
        answer:
          "Ključno je izbegavati konflikt pred decom, ne stavljati ih u ulogu posrednika i održati doslednost u oba doma. Terapeut može pomoći da razviješ komunikacione strategije sa bivšim partnerom koje stavljaju decu na prvo mesto, kao i da prepoznaš znake da dete možda treba dodatnu podršku.",
      },
      {
        question:
          "Da li mogu ići na terapiju zajedno sa partnerom tokom razvoda?",
        answer:
          "Da, terapija razvoda za parove može pomoći da se proces odvija mirnije i konstruktivnije, posebno kada treba doneti dogovore o deci, finansijama ili podeli obaveza. Cilj nije nužno pomirenje, već zdrav i dostojanstven završetak odnosa koji smanjuje dugoročnu štetu za sve uključene.",
      },
      {
        question: "Koliko traje oporavak nakon razvoda?",
        answer:
          "Ne postoji univerzalan rok. Zavisi od dužine veze, okolnosti razvoda, mreže podrške i individualnih resursa. Većina ljudi oseti značajno poboljšanje unutar nekoliko meseci rada sa terapeutom. Terapija pomaže da ne 'zaglavljuješ' u tuzi ili besu, već da postepeno izgradiš novi identitet i rutinu.",
      },
      {
        question: "Šta ako se osećam krivim zbog odluke o razvodu?",
        answer:
          "Osećaj krivice je izuzetno čest, posebno ako imaš decu ili ako je partner bio protiv razvoda. Terapeut ti pomaže da razlikuješ zdravu odgovornost od nezdravog krivičnog osećaja, procesuiraš ambivalentne emocije i doneseš mir sa odlukom koju si doneo/la.",
      },
      {
        question: "Da li online terapija može pomoći kod razvoda?",
        answer:
          "Da, online terapija je posebno praktična tokom razvoda jer ti omogućava sesije bez logističkih komplikacija, iz svog prostora i u terminima koji odgovaraju tvom novom rasporedu. Na Rešimo Probleme možeš uporediti profile terapeuta sa iskustvom u radu sa razvodom i razlazom i zakazati prvu sesiju kada ti odgovara.",
      },
    ],
  },

  // 35
  "nasilje-u-porodici-podrska": {
    passes: true,
    label: "Nasilje u porodici",
    therapistSearchTerms: [
      "Prepoznavanje, mere zastite i izlazak iz nasilnih odnosa",
      "Emocionalno zlostavljanje",
      "Fizičko zlostavljanje",
      "Psihičko zlostavljanje",
      "Seksualno zlostavljenje",
      "Fizičko/psihičko/emocionalno zlostavljanje",
    ],
    guideLink: {
      href: "/blog/toksicna-veza-znaci-posledice-i-kako-da-izadjes",
      label: "Pročitaj vodič o toksičnim odnosima",
    },
    title:
      "Online terapeuti za podršku kod nasilja u porodici: diskretno, sigurno, trauma-informisano",
    searchTerm:
      "nasilje u porodici psihičko nasilje u porodici zlostavljanje emocionalno zlostavljanje partnersko nasilje kontrola izolacija manipulacija nasilje u vezi podrška žrtvama nasilja trauma nasilje oporavak nakon nasilja plan bezbednosti kako prepoznati nasilje psihičko nasilje simptomi nasilje u porodici pomoć terapija nasilje trauma-informisana terapija EMDR nasilje",
    description:
      "Nasilje u porodici obuhvata fizičko, psihičko, emocionalno i ekonomsko zlostavljanje, a posledice mogu biti duboke i dugotrajne. Ako prepoznaješ obrasce kontrole, izolacije, manipulacije ili straha u svom okruženju, važno je da znaš da podrška postoji i da nisi sam/a. Na Rešimo Probleme možeš diskretno pronaći terapeuta sa iskustvom u radu sa traumom i nasiljem u porodici. Online sesije ti omogućavaju siguran pristup podršci bez potrebe za fizičkim odlaskom, u terminima koji tebi odgovaraju.",
    directoryContent: {
      title: "Online terapeut za podršku nakon nasilja u porodici",
      description:
        "Ako želiš da razgovaraš o iskustvu nasilja, kontroli, strahu ili posledicama odnosa koji je bio štetan, ovde možeš da pregledaš profile terapeuta koji navode rad sa nasiljem ili zlostavljanjem. Pročitaj opis rada, cenu i termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor. Ako si trenutno u opasnosti, prvo se obrati hitnoj službi ili lokalnoj kriznoj pomoći.",
      seoTitle: "Podrška nakon nasilja u porodici online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za podršku nakon nasilja u porodici. Pregledaj profile, cene i termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za podršku nakon nasilja?",
          answer:
            "Na ovoj stranici su profili terapeuta koji u opisu rada navode nasilje ili zlostavljanje. Pre zakazivanja pročitaj opis rada, cenu i slobodne termine.",
        },
        {
          question:
            "Da li je ova stranica za hitnu pomoć kada sam u opasnosti?",
          answer:
            "Nije. Ako postoji neposredna opasnost, pretnja ili rizik od povrede, odmah se obrati lokalnoj hitnoj službi, policiji ili kriznoj organizaciji. Zakazivanje razgovora nije zamena za hitnu zaštitu.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako još nisam sigurna kako da opišem svoju situaciju?",
          answer:
            "Možeš da zakažeš razgovor i bez unapred postavljenog naziva ili odluke. Pri zakazivanju možeš kratko opisati temu koju želiš da otvoriš i proveriti da li terapeutov profil odgovara tvojoj situaciji.",
        },
        {
          question: "Da li terapeut daje pravne savete ili pravi plan zaštite?",
          answer:
            "Terapeut nije zamena za policiju, advokata, socijalnu službu ili specijalizovanu kriznu organizaciju. Za hitnu bezbednost, prijavu ili pravna pitanja obrati se odgovarajućoj službi.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije i izabereš termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "Nasilje u porodici: podrška online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za podršku kod nasilja u porodici online. Diskretna, trauma-informisana terapija. Uporedi profile i zakaži sesiju na Rešimo Probleme.",
    relatedConditions: ["emocionalno-zlostavljanje", "trauma", "ptsp"],
    faqItems: [
      {
        question: "Kako prepoznati da se radi o nasilju u porodici?",
        answer:
          "Nasilje u porodici nije samo fizičko. Uključuje i psihičko nasilje (ponižavanje, pretnje, zastrašivanje), emocionalnu manipulaciju, kontrolu finansija, izolaciju od prijatelja i porodice, praćenje kretanja i komunikacije. Ako osećaš strah od partnerovog ponašanja, hodaš 'na prstima' ili se konstantno prilagođavaš da izbegneš konflikt, to mogu biti znaci nasilja. Terapeut ti može pomoći da prepoznaš obrasce i razumeš svoju situaciju.",
      },
      {
        question: "Da li online terapija može biti sigurna i diskretna?",
        answer:
          "Da. Online sesije se odvijaju putem enkriptovanog video poziva, a termin možeš zakazati u vreme kada si sam/a. Važno je da koristiš uređaj kome partner nema pristup i da obrišeš istoriju pretraživanja ako je potrebno. Terapeut će sa tobom razgovarati o merama digitalne bezbednosti na prvoj sesiji.",
      },
      {
        question: "Šta je trauma-informisana terapija i kako pomaže?",
        answer:
          "Trauma-informisana terapija je pristup koji prepoznaje da su tvoje reakcije (hiperopreznost, anksioznost, teškoće sa poverenjem) normalan odgovor na nenormalne okolnosti. Terapeut radi tvojim tempom, bez pritiska, koristeći pristupe poput kognitivno-bihevioralne terapije (KBT) ili EMDR-a koji pomažu u obradi traumatskih iskustava i izgradnji osećaja sigurnosti.",
      },
      {
        question: "Da li je normalno da osećam krivicu ili stid?",
        answer:
          "Osećaj krivice, stida i samookrivljavanja je izuzetno čest kod osoba koje prolaze kroz nasilje i nije tvoja greška. Ovi osećaji su često posledica manipulacije i dugotrajnog obezvrednjavanja. Terapeut ti pomaže da razumeš odakle ovi osećaji dolaze, da prepoznaš da nasilje nikada nije odgovornost žrtve i da postepeno izgradiš samopouzdanje.",
      },
      {
        question: "Šta ako još nisam spreman/na da napustim nasilnu situaciju?",
        answer:
          "Terapija ne zahteva da si već doneo/la odluku o odlasku. Mnogi ljudi dolaze na terapiju dok su još u nasilnom odnosu. Terapeut ti pruža podršku bez pritiska i osude, pomaže ti da razviješ plan bezbednosti, razumeš svoje opcije i doneseš odluke u skladu sa sopstvenim tempom i okolnostima.",
      },
      {
        question: "Da li terapija pomaže i nakon izlaska iz nasilnog odnosa?",
        answer:
          "Da, oporavak od nasilja u porodici je proces koji često zahteva podršku i nakon što je nasilje prestalo. Posledice mogu uključivati PTSP, anksioznost, teškoće u novim odnosima i narušeno samopouzdanje. Terapija pomaže u obradi traumatskih iskustava, izgradnji zdravih obrazaca u odnosima i vraćanju osećaja kontrole nad sopstvenim životom.",
      },
      {
        question: "Da li i deca mogu dobiti podršku?",
        answer:
          "Deca koja su svedočila nasilju u porodici takođe mogu imati koristi od terapije. Terapeut može raditi sa roditeljima na tome kako da pruže emocionalnu sigurnost detetu, prepoznaju znake da dete treba dodatnu podršku i uspostave stabilnost u novom okruženju. Na Rešimo Probleme možeš pronaći terapeute sa iskustvom u radu sa porodicama.",
      },
    ],
  },

  // 36
  "samopovredjivanje-podrska": {
    passes: true,
    label: "Samopovređivanje",
    title:
      "Online terapeuti za samopovređivanje: DBT i podrška za regulaciju emocija",
    searchTerm:
      "samopovređivanje samopovreda samopovređivanje kod mladih samopovređivanje kod adolescenata samopovređivanje terapija DBT terapija regulacija emocija okidači samopovređivanja kako prestati sa samopovređivanjem samopovređivanje podrška samopovređivanje kod dece nesuicidalno samopovređivanje NSSP sečenje grebanje KBT samopovređivanje borderline samopovređivanje distres tolerancija emocionalna disregulacija",
    description:
      "Samopovređivanje je način na koji neke osobe pokušavaju da se nose sa intenzivnim emocijama, bolom ili osećajem gubitka kontrole. Nije znak slabosti, već signal da ti je potrebna podrška u izgradnji alternativnih strategija za regulaciju emocija. Na Rešimo Probleme možeš pronaći terapeuta sa iskustvom u radu sa samopovređivanjem, koji koristi pristupe poput dijalektičko-bihevioralne terapije (DBT) i kognitivno-bihevioralne terapije (KBT). Zakaži online sesiju diskretno, u vreme koje tebi odgovara.",
    seoTitle: "Samopovređivanje: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za podršku kod samopovređivanja online. DBT i KBT za regulaciju emocija i nove strategije. Zakaži sesiju diskretno na Rešimo Probleme.",
    relatedConditions: ["dbt-terapija", "depresija", "trauma"],
    faqItems: [
      {
        question: "Zašto se javlja samopovređivanje?",
        answer:
          "Samopovređivanje najčešće služi kao način da se osoba nosi sa intenzivnim emocijama koje ne zna kako drugačije da izrazi ili reguliše. Može biti odgovor na bol, prazninu, anksioznost ili osećaj gubitka kontrole. Važno je razumeti da samopovređivanje nije pokušaj privlačenja pažnje, već signal da je osobi potrebna pomoć u razvijanju zdravijih strategija za suočavanje sa emocijama.",
      },
      {
        question: "Šta je DBT terapija i kako pomaže kod samopovređivanja?",
        answer:
          "Dijalektičko-bihevioralna terapija (DBT) je pristup koji je posebno razvijen za rad sa intenzivnim emocijama i samopovređujućim ponašanjima. Uključuje četiri ključne oblasti: svesnu prisutnost (mindfulness), toleranciju na distres, regulaciju emocija i interpersonalnu efikasnost. Kroz DBT učiš konkretne veštine koje ti pomažu da preživiš teške trenutke bez samopovređivanja.",
      },
      {
        question:
          "Šta mogu da uradim u trenutku kada osećam nagon za samopovređivanjem?",
        answer:
          "Neke od strategija koje terapeut može sa tobom razviti uključuju: držanje kocke leda u ruci (fizička senzacija bez povrede), intenzivnu fizičku aktivnost, tehnike duboke disanja, pozivanje osobe od poverenja ili pisanje o tome šta osećaš. Cilj je naučiti da 'surfuješ' po nagonu dok ne prođe, jer intenzitet obično opada nakon 15-20 minuta.",
      },
      {
        question: "Da li samopovređivanje znači da sam suicidalan/na?",
        answer:
          "Ne nužno. Mnoge osobe koje se samopovređuju nemaju suicidalne namere, već koriste samopovredu kao mehanizam za regulaciju emocija. Ipak, samopovređivanje može povećati rizik, pa je važno razgovarati sa terapeutom o tome šta stoji iza ponašanja. Terapeut ti pomaže da razviješ plan sigurnosti i alternativne strategije.",
      },
      {
        question: "Kako mogu pomoći nekome ko se samopovređuje?",
        answer:
          "Najvažnije je reagovati bez panike, osude ili ultimatuma. Reci osobi da ti je stalo, da je čuješ i da želiš da joj pomogneš da pronađe podršku. Izbegavaj komentare poput 'samo prestani' jer to može pojačati stid. Ohrabri je da razgovara sa terapeutom i ponudi da joj pomogneš u pronalaženju stručnjaka.",
      },
      {
        question: "Da li je normalno da osećam stid zbog samopovređivanja?",
        answer:
          "Stid je izuzetno čest i često je jedna od najvećih prepreka traženju pomoći. U terapiji, samopovređivanje se ne tretira kao neuspeh već kao signal koji nosi informaciju o tvojim emocijama. Terapeut radi bez osude, pomažući ti da razumeš funkciju ponašanja i postepeno izgradiš alternativne načine za suočavanje sa teškim momentima.",
      },
      {
        question: "Da li online terapija može pomoći kod samopovređivanja?",
        answer:
          "Da, online terapija omogućava diskretnu i pristupačnu podršku, posebno za osobe kojima je teško da odu na sesiju uživo. DBT i KBT pristupi su jednako efikasni online. Na Rešimo Probleme možeš uporediti profile terapeuta sa iskustvom u radu sa samopovređivanjem i zakazati sesiju u vreme koje ti odgovara.",
      },
    ],
  },

  // 37
  perfekcionizam: {
    label: "Perfekcionizam",
    therapistSearchTerms: [
      "Perfekcionizam",
      "Perfekcionizam, strah od neuspeha i stres na poslu",
      "Prokrastinacija",
      "Prokrastinacija, perfekcionizam i problemi organizacije života",
      "Strah od greške",
      "Strah od neuspeha",
      "Loša slika o sebi, samokritičnost",
      "Samosaosećanje",
      "Samopouzdanje i samopoštovanje",
    ],
    guideLink: {
      href: "/blog/samopostovanje-kako-voleti-sebe",
      label: "Pročitaj vodič o samopoštovanju",
    },
    searchTerm:
      "perfekcionizam perfekcionisti visoki standardi samokritika prokrastinacija strah od greške kako prevazići perfekcionizam anksioznost sve ili ništa razmišljanje samokriticnost nedovoljno dobar terapija za perfekcionizam online psiholog KBT samoprihvatanje",
    description:
      "Stalni pritisak da sve bude savršeno, strah od greške koji te koči i samokritika koja ne prestaje. Ako ti perfekcionizam otežava svakodnevni život, odnose ili posao, razgovor sa terapeutom može napraviti razliku. Na platformi Rešimo Probleme pronađi licencirane terapeute koji koriste proverene pristupe poput kognitivno-bihejvioralne terapije (KBT) i pomažu ti da izgradiš realnije standarde, smanjiš unutrašnji pritisak i preuzmeš akciju bez straha od nesavršenosti.",
    directoryContent: {
      title: "Online terapeuti za perfekcionizam i samokritiku",
      description:
        "Ako te strogi standardi, strah od greške ili stalna samokritika opterećuju, ovde možeš da pregledaš profile terapeuta koji navode rad sa perfekcionizmom ili srodnim temama. Uporedi opis rada, cenu i termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor.",
      seoTitle: "Terapeut za perfekcionizam online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za perfekcionizam i samokritiku. Pregledaj profile, cene i termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za perfekcionizam?",
          answer:
            "Na ovoj stranici su profili terapeuta koji navode perfekcionizam ili srodne teme rada. Pre zakazivanja možeš da pročitaš opis rada, proveriš cenu i pogledaš slobodne termine.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako nisam sigurna da li je problem perfekcionizam?",
          answer:
            "Možeš da zakažeš razgovor i bez unapred postavljenog naziva za ono što te opterećuje. Pri zakazivanju možeš kratko opisati situaciju, na primer pritisak, samokritiku ili strah od greške.",
        },
        {
          question: "Da li je perfekcionizam povezan sa prokrastinacijom?",
          answer:
            "Kod nekih ljudi se ove teme prepliću, ali ne moraš sam da odrediš uzrok. Možeš pregledati i direktorijum za prokrastinaciju, pa izabrati profil koji najviše odgovara onome što želiš da otvoriš.",
        },
        {
          question: "Kako izgleda online razgovor sa terapeutom?",
          answer:
            "Nakon izbora termina zakazuješ video razgovor preko platforme. Cena je prikazana na profilu terapeuta pre rezervacije termina.",
        },
        {
          question: "Koliko košta online razgovor?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije pre izbora termina.",
        },
      ],
    },
    seoTitle:
      "Online terapija za perfekcionizam i samokritiku | Rešimo Probleme",
    seoDescription:
      "Prevazidi perfekcionizam uz licencirane terapeute online. KBT pristup za strah od greške, prokrastinaciju i visoke standarde. Zakaži sesiju.",
    relatedConditions: [
      "prokrastinacija-motivacija",
      "imposter-sindrom",
      "visokofunkcionalna-anksioznost",
      "stres",
    ],
    passes: true,

    faqItems: [
      {
        question: "Kako terapija pomaže kod perfekcionizma?",
        answer:
          "Terapeut ti pomaže da prepoznaš obrasce razmišljanja koji održavaju perfekcionizam, poput 'sve ili ništa' logike i preterane samokritike. Kroz kognitivno-bihejvioralnu terapiju (KBT) učiš da zameniš te obrasce realnijim očekivanjima i da preuzmeš akciju bez čekanja na savršen trenutak.",
      },
      {
        question: "Kako da znam da li je moj perfekcionizam problem?",
        answer:
          "Perfekcionizam postaje problem kada te koči umesto da te pokreće. Ako često odlažeš zadatke iz straha da neće biti dovoljno dobri, ako te greške dugo muče ili ako osećaš stalni pritisak i nezadovoljstvo uprkos uspehu, to su znaci da bi razgovor sa terapeutom mogao pomoći.",
      },
      {
        question: "Kako da smanjim samokritiku i budem blaži/a prema sebi?",
        answer:
          "Terapeut te vodi kroz vežbe samoprihvatanja i samosuosećanja. Umesto da se kažnjavaš za svaku grešku, učiš da se prema sebi odnosiš onako kako bi se odnosio/la prema bliskoj osobi. To nije popustljivost, već zdraviji način da ostvariš ciljeve bez iscrpljivanja.",
      },
      {
        question: "Zašto perfekcionizam vodi u prokrastinaciju?",
        answer:
          "Kada su ti standardi previsoki, svaki zadatak deluje pretećeg. Strah da rezultat neće biti savršen izaziva odlaganje, a odlaganje pojačava anksioznost. Terapeut ti pomaže da prekineš taj krug uvođenjem realnih rokova i prihvatanja da 'dovoljno dobro' jeste dovoljno.",
      },
      {
        question: "Da li je perfekcionizam povezan sa anksioznošću?",
        answer:
          "Da, istraživanja pokazuju visoku povezanost. Perfekcionizam održava anksioznost kroz stalni strah od procene, greške i neuspeha. Rad sa terapeutom na oba problema istovremeno daje najbolje rezultate jer se koreni često preklapaju.",
      },
      {
        question: "Koliko traje terapija za perfekcionizam?",
        answer:
          "Trajanje zavisi od intenziteta problema, ali mnogi ljudi osete olakšanje već nakon nekoliko sesija. KBT pristup je strukturiran i ciljan, pa se fokusirate na konkretne obrasce i promene. Terapeut sa tobom pravi plan rada prilagođen tvojim potrebama.",
      },
      {
        question:
          "Koliko košta online terapija za perfekcionizam na Rešimo Probleme?",
        answer:
          "Sesija sa licenciranim terapeutom košta od 30 evra. Možeš pregledati profile terapeuta, njihove specijalizacije i pristupe pre nego što zakažeš prvi razgovor. Bez pretplate i skrivenih troškova.",
      },
    ],
  },

  // 38
  "prokrastinacija-motivacija": {
    label: "Prokrastinacija i motivacija",
    therapistSearchTerms: ["Prokrastinacija", "Motivacija"],
    guideLink: {
      href: "/blog/prokrastinacija",
      label: "Pročitaj vodič o prokrastinaciji",
    },
    title:
      "Online terapeuti za prokrastinaciju: prevaziđi odlaganje i vrati motivaciju",
    searchTerm:
      "prokrastinacija odlaganje motivacija hronično odlaganje nedostatak volje fokus navike perfekcionizam anksioznost prokrastinacija lečenje prokrastinacija uzrok izvršne funkcije ciljevi rutine",
    description:
      "Prokrastinacija nije lenjost, već često znak anksioznosti, perfekcionizma ili straha od neuspeha. Ako stalno odlažeš obaveze, osećaš krivicu i frustraciju, ali ne možeš da se pokreneš, terapeut ti može pomoći da razumeš uzroke i izgradiš nove obrasce ponašanja. Na Rešimo Probleme pronađi licenciranog terapeuta za prokrastinaciju i započni online terapiju koja ti pomaže da prevaziđeš odlaganje kroz kognitivno-bihejvioralnu terapiju i praktične tehnike.",
    directoryContent: {
      title: "Online terapeuti za prokrastinaciju i motivaciju",
      description:
        "Ako često odlažeš obaveze, teško ti je da započneš ili želiš da razgovaraš o motivaciji i organizaciji, ovde možeš da pregledaš profile terapeuta koji navode rad sa prokrastinacijom. Uporedi opis rada, cenu i termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor.",
      seoTitle: "Terapeut za prokrastinaciju online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za prokrastinaciju i motivaciju. Pregledaj profile, cene i termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za prokrastinaciju?",
          answer:
            "Na ovoj stranici su profili terapeuta koji navode prokrastinaciju ili motivaciju kao temu rada. Pre zakazivanja možeš da pročitaš opis rada, proveriš cenu i pogledaš slobodne termine.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako ne znam zašto odlažem obaveze?",
          answer:
            "Možeš da zakažeš razgovor i kada nemaš odgovor na to pitanje. Pri zakazivanju možeš kratko opisati gde se odlaganje najviše javlja i šta želiš da razumeš.",
        },
        {
          question: "Da li je prokrastinacija isto što i ADHD ili anksioznost?",
          answer:
            "Nije moguće zaključiti to samo na osnovu odlaganja obaveza. Ako želiš, možeš da pregledaš i direktorijume za ADHD kod odraslih ili anksioznost i izabereš terapeuta prema profilu i temi koja ti je najbliža.",
        },
        {
          question: "Kako izgleda online razgovor sa terapeutom?",
          answer:
            "Nakon izbora termina zakazuješ video razgovor preko platforme. Cena je prikazana na profilu terapeuta pre rezervacije termina.",
        },
        {
          question: "Koliko košta online razgovor?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije pre izbora termina.",
        },
      ],
    },
    seoTitle: "Prokrastinacija: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za prokrastinaciju online. KBT pristup, praktične tehnike za motivaciju i prevazilaženje odlaganja. Zakaži sesiju.",
    relatedConditions: [
      "perfekcionizam",
      "adhd-odrasli",
      "visokofunkcionalna-anksioznost",
      "studije-ispitna-anksioznost",
    ],
    passes: true,
    faqItems: [
      {
        question: "Zašto prokrastiniram iako znam da mi škodi?",
        answer:
          "Prokrastinacija nije pitanje volje, već emocionalne regulacije. Mozak bira trenutno olakšanje (izbegavanje) umesto dugoročne koristi. Česti uzroci su strah od neuspeha, perfekcionizam, anksioznost ili poteškoće sa pažnjom. Terapeut ti pomaže da prepoznaš koji obrazac stoji iza tvog odlaganja.",
      },
      {
        question: "Kako terapija pomaže kod prokrastinacije?",
        answer:
          "Kognitivno-bihejvioralna terapija (KBT) je najefikasniji pristup. Radi se na prepoznavanju misaonih obrazaca koji pokreću odlaganje, razvijanju realističnih ciljeva i tehnikama za regulaciju emocija. Terapija ti pomaže da izgradid nove navike korak po korak, bez pritiska i samokritike.",
      },
      {
        question: "Da li je prokrastinacija znak nekog dubljeg problema?",
        answer:
          "Može biti. Hronično odlaganje često prati anksioznost, depresiju, ADHD ili perfekcionizam. Terapeut procenjuje širi kontekst i prilagođava pristup. Ako prokrastinacija značajno utiče na posao, studije ili odnose, vredi istražiti šta stoji iza nje.",
      },
      {
        question: "Koji su prvi koraci za prevazilaženje prokrastinacije?",
        answer:
          "Počni sa malim, konkretnim koracima: podeli zadatak na delove od 5-10 minuta, ukloni distrakcije i postavi jasan početni korak. Ove tehnike se uče i produbljuju na terapiji, gde terapeut pomaže da razviješ sistem koji odgovara tvom stilu.",
      },
      {
        question: "Koliko dugo traje terapija za prokrastinaciju?",
        answer:
          "Prve promene u ponašanju se obično primete nakon 4-6 sesija. Za dublje obrasce povezane sa anksioznošću ili perfekcionizmom, proces može trajati 3-6 meseci. Tempo zavisi od tvoje situacije, a terapeut prilagođava plan napredovanju.",
      },
      {
        question: "Da li online terapija funkcioniše za prokrastinaciju?",
        answer:
          "Da, KBT za prokrastinaciju je jednako efikasna online kao i uživo. Prednost online sesija je što možeš da zakazuješ u terminima koji ti odgovaraju, bez dodatnog odlaganja zbog putovanja do ordinacije.",
      },
      {
        question: "Koja je razlika između prokrastinacije i lenjosti?",
        answer:
          "Lenjost je odsustvo želje za akcijom. Prokrastinacija je odlaganje uprkos želji da se nešto uradi, praćeno krivicom i frustracijom. Osoba koja prokrastinira često želi da radi, ali nešto je blokira. Terapija pomaže da otkriješ i prevaziđeš te blokade.",
      },
    ],
  },

  // 39
  "karijera-posao": {
    label: "Stres na poslu i karijera",
    therapistSearchTerms: [
      "Karijerno savetovanje",
      "Profesionalne dileme i teškoće u karijeri",
    ],
    guideLink: {
      href: "/blog/promena-karijere-strah-identitet-i-sledeci-korak",
      label: "Pročitaj vodič o promeni karijere",
    },
    title:
      "Online terapeuti za stres na poslu: granice, mobing i karijerne promene",
    searchTerm:
      "stres na poslu mobing na poslu sagorevanje burnout granice na poslu konflikti na poslu karijerne promene promena posla nepravda na poslu kako izdržati na poslu toksično radno okruženje",
    description:
      "Stres na poslu, mobing i konflikti mogu ozbiljno uticati na mentalno zdravlje, san i odnose. Ako osećaš iscrpljenost, gubitak motivacije ili anksioznost vezanu za posao, terapeut ti može pomoći da postaviš zdrave granice, prevaziđeš posledice toksičnog okruženja i doneseš jasne odluke o karijeri. Na Rešimo Probleme pronađi licenciranog terapeuta za stres na poslu i započni online terapiju prilagođenu tvom rasporedu.",
    directoryContent: {
      title: "Online terapeut za stres na poslu i karijeru",
      description:
        "Ako želiš da razgovaraš o stresu na poslu, profesionalnim dilemama ili promeni karijere, ovde možeš da pregledaš profile terapeuta koji navode rad sa tim temama. Uporedi opis rada, cenu i termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor.",
      seoTitle: "Terapeut za stres na poslu online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za stres na poslu i karijerne dileme. Pregledaj profile, cene i termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za stres na poslu?",
          answer:
            "Na ovoj stranici su profili terapeuta koji navode profesionalne dileme, karijeru ili stres na poslu. Pre zakazivanja možeš da pročitaš opis rada, proveriš cenu i pogledaš slobodne termine.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako razmišljam o promeni posla?",
          answer:
            "Možeš da zakažeš razgovor ako želiš da razmotriš profesionalnu dilemu ili promenu. Pri zakazivanju možeš kratko opisati temu koju želiš da otvoriš.",
        },
        {
          question: "Da li terapeut daje pravne savete o mobingu ili radu?",
          answer:
            "Terapeut nije zamena za advokata, sindikat, inspekciju ili drugi pravni savet. Za pravna pitanja i prijavu obrati se odgovarajućoj službi.",
        },
        {
          question: "Kako izgleda online razgovor?",
          answer:
            "Nakon izbora termina zakazuješ video razgovor preko platforme. Cena je prikazana na profilu terapeuta pre rezervacije termina.",
        },
        {
          question: "Koliko košta online razgovor?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije pre izbora termina.",
        },
      ],
    },
    seoTitle: "Stres na poslu: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za stres na poslu, mobing i karijerne promene. Online KBT terapija za granice i oporavak. Zakaži sesiju.",
    relatedConditions: [
      "burnout",
      "mobing",
      "finansijski-stres",
      "imposter-sindrom",
    ],
    passes: true,
    faqItems: [
      {
        question: "Kako terapija pomaže kod stresa na poslu?",
        answer:
          "Kognitivno-bihejvioralna terapija (KBT) pomaže da prepoznaš obrasce mišljenja koji pojačavaju stres, naučiš tehnike postavljanja granica i razviješ strategije za upravljanje zahtevnim situacijama. Terapeut radi sa tobom na konkretnim alatima koje možeš primeniti odmah.",
      },
      {
        question: "Šta ako je problem mobing, a ne 'samo' stres?",
        answer:
          "Mobing na poslu ostavlja ozbiljne psihološke posledice: anksioznost, nesanicu, gubitak samopouzdanja. Terapija ti pomaže da prepoznaš da nisi ti problem, oporaviš samopoštovanje i doneseš odluke o sledećim koracima. Terapeut ne zamenjuje pravnu pomoć, ali pruža emotivnu podršku koja je ključna za oporavak.",
      },
      {
        question: "Kako da postavim granice na poslu bez posledica?",
        answer:
          "Postavljanje granica je veština koja se uči. Na terapiji vežbaš konkretne načine da komuniciraš svoje potrebe jasno i profesionalno. Pristup uključuje identifikovanje prioriteta, jasnu komunikaciju očekivanja i tehnike za situacije kada neko prelazi tvoje granice.",
      },
      {
        question: "Kada stres na poslu zahteva profesionalnu pomoć?",
        answer:
          "Ako stres utiče na san, apetit, odnose ili fizičko zdravlje, ili ako osećaš konstantnu iscrpljenost i gubitak motivacije, to su znaci da treba potražiti podršku. Takođe, ako razmišljaš o promeni posla ali ne možeš da doneseš odluku, terapeut ti može pomoći da razjasniš situaciju.",
      },
      {
        question: "Da li online terapija funkcioniše za probleme na poslu?",
        answer:
          "Da, istraživanja potvrđuju da je online KBT jednako efikasna kao terapija uživo za stres i anksioznost vezane za posao. Dodatna prednost je fleksibilnost zakazivanja, bez potrebe da tražiš slobodan termin u toku radnog dana.",
      },
      {
        question: "Kako se nositi sa toksičnim radnim okruženjem?",
        answer:
          "Toksično radno okruženje utiče na samopouzdanje i energiju. Terapija ti pomaže da razviješ otpornost, prepoznaš šta možeš da promeniš a šta ne, i doneseš informisanu odluku o tome da li ostati ili otići. Fokus je na zaštiti tvog mentalnog zdravlja.",
      },
      {
        question: "Koliko sesija je potrebno za poboljšanje?",
        answer:
          "Prve promene u upravljanju stresom se obično primete nakon 4-6 sesija. Za dublje teme poput oporavka od mobinga ili karijernog zaokreta, proces može trajati 3-6 meseci. Tempo prilagođavamo tvom napretku i ciljevima.",
      },
    ],
  },

  // 40
  "studije-ispitna-anksioznost": {
    label: "Ispitna anksioznost i studije",
    therapistSearchTerms: ["Pritisak škole, studija i očekivanja okoline"],
    guideLink: {
      href: "/blog/online-psihoterapija-vodic",
      label: "Pročitaj vodič za online psihoterapiju",
    },
    title:
      "Online terapeuti za ispitnu anksioznost: prevaziđi tremu i stres na studijama",
    searchTerm:
      "ispitna anksioznost trema na ispitu studijski stres strah od ispita strah od pada učenje fokus pamćenje odlaganje planiranje vremena perfekcionizam studenti anksioznost na fakultetu",
    description:
      "Ispitna anksioznost je više od obične treme. Ako blokiraš na ispitu uprkos dobrom učenju, osećaš strah od pada ili konstantan pritisak na studijama, terapeut ti može pomoći da razumeš uzroke anksioznosti i razviješ strategije za upravljanje stresom. Na Rešimo Probleme pronađi licenciranog terapeuta za ispitnu anksioznost i započni online KBT terapiju prilagođenu tvom studentskom rasporedu.",
    directoryContent: {
      title: "Online terapeut za ispitni stres i studije",
      description:
        "Ako želiš da razgovaraš o pritisku studija, ispitima ili očekivanjima koja te opterećuju, ovde možeš da pregledaš profil terapeuta koji navodi rad sa školskim i studijskim pritiskom. Pročitaj opis rada, cenu i termine, pa odluči da li želiš da zakažeš online razgovor.",
      seoTitle: "Terapeut za ispitni stres online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta za ispitni stres i pritisak studija. Pregledaj profil, cenu i termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta za ispitni stres?",
          answer:
            "Na ovoj stranici je profil terapeuta koji navodi rad sa pritiskom škole i studija. Pre zakazivanja možeš da pročitaš opis rada, proveriš cenu i pogledaš slobodne termine.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako ne znam da li je u pitanju anksioznost?",
          answer:
            "Možeš da zakažeš razgovor i bez unapred postavljenog naziva za ono što te opterećuje. Pri zakazivanju možeš kratko opisati situaciju sa ispitima ili studijama.",
        },
        {
          question: "Da li terapeut pomaže sa učenjem ili stručnim predmetima?",
          answer:
            "Terapeut nije zamena za predmetnog nastavnika, mentora ili obrazovno savetovanje. Razgovor je namenjen temi pritiska, emocija i situacija koje želiš da razumeš.",
        },
        {
          question: "Kako izgleda online razgovor?",
          answer:
            "Nakon izbora termina zakazuješ video razgovor preko platforme. Cena je prikazana na profilu terapeuta pre rezervacije termina.",
        },
        {
          question: "Koliko košta online razgovor?",
          answer:
            "Cenu određuje terapeut i vidiš je na profilu pre zakazivanja. Tako možeš da uporediš opcije pre izbora termina.",
        },
      ],
    },
    seoTitle: "Ispitna anksioznost: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za ispitnu anksioznost i stres na studijama. Online KBT terapija za tremu, fokus i samopouzdanje. Zakaži sesiju.",
    relatedConditions: [
      "anksioznost",
      "prokrastinacija-motivacija",
      "perfekcionizam",
      "stres",
    ],
    passes: true,
    faqItems: [
      {
        question: "Šta je ispitna anksioznost i zašto se javlja?",
        answer:
          "Ispitna anksioznost je intenzivan strah ili napetost vezana za ispitne situacije, koja može blokirati pamćenje i koncentraciju čak i kad si se dobro pripremio/la. Javlja se zbog perfekcionizma, straha od procene, negativnih iskustava ili visokih očekivanja od sebe.",
      },
      {
        question: "Kako KBT terapija pomaže kod ispitne anksioznosti?",
        answer:
          'Kognitivno-bihejvioralna terapija (KBT) je najefikasniji pristup za ispitnu anksioznost. Radi se na prepoznavanju negativnih misaonih obrazaca ("sigurno ću pasti"), tehnikama opuštanja i postepenom izlaganju ispitnim situacijama. Cilj je da ispit prestane biti pretnja, a postane zadatak koji možeš da rešiš.',
      },
      {
        question: "Da li ispitna anksioznost znači da nisam dovoljno učio/la?",
        answer:
          "Ne. Ispitna anksioznost često pogađa upravo one koji se mnogo pripremaju. Problem nije u znanju, već u tome što anksioznost blokira pristup informacijama koje već imaš. Terapija pomaže da naučiš kako da pristupiš znanju i u stresnim situacijama.",
      },
      {
        question: "Koje tehnike mogu primeniti odmah pred ispit?",
        answer:
          "Sporo duboko disanje (4-7-8 tehnika), fokusiranje na prvi konkretan zadatak umesto na celinu, i kratka vizualizacija uspešnog nastupa. Ove tehnike se uče i produbljuju na terapiji, gde terapeut prilagođava pristup tvojim specifičnim okidačima.",
      },
      {
        question: "Koliko sesija je potrebno za poboljšanje?",
        answer:
          "Prve promene u upravljanju tremom se obično primete nakon 3-5 sesija. Za dublje obrasce povezane sa perfekcionizmom ili generalizovanom anksioznošću, proces može trajati duže. Mnogi studenti zakazuju sesije u periodu pre ispitnog roka za ciljanu podršku.",
      },
      {
        question: "Da li online terapija funkcioniše za studente?",
        answer:
          "Da, online KBT je jednako efikasna kao terapija uživo za anksioznost. Za studente ima dodatnu prednost: zakazuješ u terminima koji ti odgovaraju, bez putovanja, iz svog prostora. Posebno korisno za studente koji žive u drugim gradovima ili inostranstvu.",
      },
      {
        question: "Može li ispitna anksioznost biti znak nečeg dubljeg?",
        answer:
          "Može. Ponekad ispitna anksioznost prati generalizovani anksiozni poremećaj, socijalni strah ili perfekcionizam koji se proteže i na druge oblasti života. Terapeut procenjuje širi kontekst i pomaže ti da razumeš da li je anksioznost vezana samo za ispite ili je deo šireg obrasca.",
      },
    ],
  },

  // 41
  "migracije-dijaspora": {
    label: "Dijaspora i život u inostranstvu",
    therapistSearchTerms: [
      "Migracija-prilagodjavanje, integracija, nostalgija, adaptacioni stres",
      "Psihoterapija i psihopatologija izbeglištva i migracija",
    ],
    guideLink: {
      href: "/blog/dijaspora-i-mentalno-zdravlje",
      label: "Pročitaj vodič o dijaspori i mentalnom zdravlju",
    },
    title:
      "Psihoterapeut na srpskom online: podrška za dijasporu i život u inostranstvu",
    searchTerm:
      "psihoterapeut na srpskom dijaspora terapija na srpskom jeziku online psiholog srpski jezik život u inostranstvu migracije nostalgija usamljenost integracija kulturni identitet domotožje psiholog za dijasporu",
    description:
      "Život u inostranstvu donosi specifične izazove: usamljenost, gubitak identiteta, nostalgiju, teškoće u integraciji i odnose na daljinu sa porodicom. Razgovor sa terapeutom koji razume tvoj jezik i kulturu čini veliku razliku. Na Rešimo Probleme pronađi licenciranog psihoterapeuta na srpskom jeziku, bez obzira na to gde živiš, i započni online terapiju prilagođenu tvom vremenu i rasporedu.",
    directoryContent: {
      title: "Online terapeut za migracije i život u dijaspori",
      description:
        "Ako želiš da razgovaraš o selidbi, prilagođavanju, nostalgiji ili iskustvu života između zemalja, ovde možeš da pregledaš profile terapeuta koji navode rad sa migracijama. Uporedi opis rada, cenu i termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor na srpskom jeziku.",
      seoTitle: "Terapeut za dijasporu na srpskom online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta na srpskom za migracije i dijasporu. Pregledaj profile, cene i termine, pa zakaži razgovor.",
      faqItems: [
        {
          question:
            "Kako da pronađem terapeuta na srpskom za život u dijaspori?",
          answer:
            "Na ovoj stranici su profili terapeuta koji navode rad sa migracijama. Pre zakazivanja možeš da pročitaš opis rada, proveriš cenu i pogledaš slobodne termine.",
        },
        {
          question: "Mogu li da zakažem razgovor ako živim van Srbije?",
          answer:
            "Možeš da pregledaš profile i dostupne termine, pa izabereš opciju koja ti odgovara. Pre rezervacije proveri detalje termina na profilu terapeuta.",
        },
        {
          question:
            "Da li terapeut daje savet o vizama, azilu ili imigracionim postupcima?",
          answer:
            "Terapeut nije zamena za advokata, imigracionog savetnika ili službu za azil. Za pravna i administrativna pitanja obrati se odgovarajućem stručnjaku ili instituciji.",
        },
        {
          question: "Da li moram da imam dijagnozu da bih zakazao razgovor?",
          answer:
            "Ne moraš. Pri zakazivanju možeš kratko opisati temu koju želiš da otvoriš, na primer promenu zemlje, usamljenost ili prilagođavanje.",
        },
        {
          question: "Koliko košta online razgovor?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije pre izbora termina.",
        },
      ],
    },
    seoTitle: "Terapeut na srpskom za dijasporu | Rešimo Probleme",
    seoDescription:
      "Pronađi psihoterapeuta na srpskom jeziku online. Podrška za dijasporu: usamljenost, identitet, integracija. Zakaži sesiju.",
    relatedConditions: ["kulturoloski-sok", "usamljenost", "zivotne-promene"],
    passes: true,
    faqItems: [
      {
        question: "Zašto je važno da terapeut govori moj jezik?",
        answer:
          "Emocije se najlakše izražavaju na maternjem jeziku. Kada pričaš o teškim temama, nema potrebe da tražiš reči na stranom jeziku ili da objašnjavaš kulturni kontekst. Terapeut koji razume srpski jezik i kulturu može brže i dublje da te razume.",
      },
      {
        question: "Kako terapija pomaže kod usamljenosti u inostranstvu?",
        answer:
          "Terapeut ti pomaže da razumeš obrasce koji otežavaju povezivanje sa ljudima u novoj sredini, razviješ strategije za građenje novih odnosa i obradiš tugu za onime što si ostavio/la. Usamljenost u dijaspori je normalna reakcija, ne slabost.",
      },
      {
        question: "Šta ako se osećam 'između dve kulture'?",
        answer:
          "Osećaj da ne pripadaš ni tamo ni ovde je čest kod života u dijaspori. Terapija pomaže da izgradiš identitet koji integriše obe kulture, umesto da biraš jednu. Cilj nije odricanje od korena, već pronalaženje ravnoteže koja ti odgovara.",
      },
      {
        question: "Da li online terapija funkcioniše iz bilo koje zemlje?",
        answer:
          "Da, online sesije možeš zakazati iz bilo kog dela sveta. Potreban ti je samo internet i privatan prostor. Terapeuti na platformi rade u različitim vremenskim zonama, tako da možeš naći termin koji ti odgovara bez obzira na to gde živiš.",
      },
      {
        question: "Kako da održim odnose sa porodicom na daljinu?",
        answer:
          "Terapeut ti pomaže da postaviš realistična očekivanja, smanjiš osećaj krivice i razviješ rituale koji održavaju bliskost uprkos fizičkoj udaljenosti. Rad na komunikaciji je ključan, posebno kada se dinamika u porodici promeni nakon selidbe.",
      },
      {
        question: "Kada treba potražiti pomoć za probleme vezane za dijasporu?",
        answer:
          "Ako osećaš konstantnu tugu ili nostalgiju, imaš poteškoća sa spavanjem, izbegavaš kontakt sa ljudima ili osećaš da gubiš sebe u novoj sredini, to su znaci da bi razgovor sa terapeutom mogao pomoći. Ne moraš čekati da situacija postane kritična.",
      },
      {
        question:
          "Da li terapeut može razumeti moju situaciju ako živi u Srbiji?",
        answer:
          "Terapeuti na platformi rade sa klijentima iz dijaspore svakodnevno i dobro poznaju izazove života u inostranstvu. Njihova stručnost u oblasti mentalnog zdravlja, kombinovana sa razumevanjem kulturnog konteksta, čini ih kvalifikovanim da pruže adekvatnu podršku.",
      },
    ],
  },

  // 42
  "kulturoloski-sok": {
    label: "Kulturološki šok i prilagođavanje",
    title:
      "Online terapeuti za kulturološki šok: podrška u prilagođavanju novoj sredini",
    searchTerm:
      "kulturološki šok kulturni šok prilagođavanje nova sredina integracija inostranstvo usamljenost dezorijentacija faze kulturnog šoka stres selidba identitet u dijaspori",
    therapistSearchTerms: ["Kulturološki šok i prilagođavanje"],
    description:
      "Kulturološki šok je normalna reakcija na život u novoj sredini, ali može izazvati anksioznost, usamljenost i osećaj izgubljenosti. Ako se boriš sa prilagođavanjem, osećaš frustraciju ili nostalgiju koja ne prolazi, terapeut ti može pomoći da prepoznaš u kojoj si fazi prilagođavanja i razviješ strategije za snalaženje. Na Rešimo Probleme pronađi terapeuta koji razume izazove kulturnog šoka i započni online terapiju na srpskom jeziku.",
    seoTitle: "Kulturološki šok: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za kulturološki šok i prilagođavanje. Online terapija na srpskom za podršku u novoj sredini. Zakaži sesiju.",
    relatedConditions: [
      "migracije-dijaspora",
      "zivotne-promene",
      "usamljenost",
    ],
    passes: true,
    faqItems: [
      {
        question: "Šta je kulturološki šok i koliko dugo traje?",
        answer:
          "Kulturološki šok je emotivna reakcija na život u novoj kulturnoj sredini. Prolazi kroz faze: euforija, frustracija, prilagođavanje i prihvatanje. Trajanje zavisi od osobe, ali faza frustracije obično traje nekoliko meseci. Terapija može pomoći da brže i lakše pređeš u fazu stabilnog prilagođavanja.",
      },
      {
        question:
          "Da li je normalno da mi bude teško posle početnog oduševljenja?",
        answer:
          "Potpuno normalno. Faza frustracije dolazi nakon početnog 'medenog meseca' i predstavlja najteži period prilagođavanja. Osećanja usamljenosti, ljutnje ili tuge su očekivana. Terapeut ti pomaže da razumeš da je to deo procesa, ne znak da si pogrešio/la.",
      },
      {
        question: "Kako terapija pomaže kod kulturološkog šoka?",
        answer:
          "Terapeut ti pomaže da prepoznaš u kojoj si fazi prilagođavanja, razviješ strategije za upravljanje stresom i izgradiš nove obrasce ponašanja u stranoj sredini. Radi se na emocionalnoj regulaciji, postavljanju realističnih očekivanja i građenju mreže podrške.",
      },
      {
        question: "Kako se nositi sa usamljenošću u novoj zemlji?",
        answer:
          "Usamljenost je jedan od najčešćih izazova kulturološkog šoka. Na terapiji radiš na identifikovanju prepreka za povezivanje sa ljudima, razvijanju strategija za upoznavanje novih ljudi i prihvatanju da je proces postepen. Male, redovne interakcije su ključ.",
      },
      {
        question:
          "Da li kulturološki šok može izazvati anksioznost ili depresiju?",
        answer:
          "Može. Produžena faza frustracije može preći u anksioznost ili depresivno raspoloženje, posebno ako nemaš podršku. Ako simptomi traju duže od nekoliko nedelja ili utiču na svakodnevno funkcionisanje, vredi razgovarati sa terapeutom koji može proceniti situaciju.",
      },
      {
        question: "Kako da zadržim svoj identitet dok se prilagođavam?",
        answer:
          "Prilagođavanje ne znači odricanje od onoga ko si. Terapija ti pomaže da izgradiš identitet koji integriše elemente obe kulture. Cilj je da se osećaš komforno u novoj sredini bez osećaja da gubiš sebe ili veze sa korenima.",
      },
      {
        question:
          "Da li online terapija na srpskom pomaže kod kulturološkog šoka?",
        answer:
          "Da, i posebno je korisna jer ti omogućava da razgovaraš na maternjem jeziku sa terapeutom koji razume tvoj kulturni kontekst. Ne moraš da objašnjavaš osnovne stvari o svom poreklu. Sesije zakazuješ online, bez obzira na to u kojoj si vremenskoj zoni.",
      },
    ],
  },

  // 43
  "visokofunkcionalna-anksioznost": {
    label: "Visokofunkcionalna anksioznost",
    // Do not index this as a specialty directory until a public profile states
    // this exact concern. Broad anxiety or stress labels are not qualifying
    // supply for a high-functioning-anxiety directory.
    therapistSearchTerms: [],
    title:
      "Online terapeuti za visokofunkcionalnu anksioznost: smanji unutrašnji pritisak",
    searchTerm:
      "visokofunkcionalna anksioznost visoko funkcionalna anksioznost perfekcionizam unutrašnja napetost kontrola stalna briga uspeh i anksioznost hronični stres nemir iza uspeha znaci anksioznosti",
    description:
      "Spolja deluješ organizovano i uspešno, ali iznutra osećaš konstantan nemir, brigu i pritisak. Visokofunkcionalna anksioznost znači da funkcionišeš uprkos anksioznosti, ne bez nje. Ako te stalna potreba za kontrolom iscrpljuje, terapeut ti može pomoći da razumeš obrasce koji stoje iza toga i razviješ zdraviji odnos prema postignućima. Na Rešimo Probleme pronađi licenciranog terapeuta za anksioznost i započni online KBT terapiju.",
    seoTitle: "Visokofunkcionalna anksioznost: terapeut | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za visokofunkcionalnu anksioznost online. KBT pristup za unutrašnji nemir, perfekcionizam i kontrolu. Zakaži sesiju.",
    relatedConditions: [
      "anksioznost",
      "perfekcionizam",
      "burnout",
      "imposter-sindrom",
    ],
    passes: true,
    faqItems: [
      {
        question: "Šta je visokofunkcionalna anksioznost?",
        answer:
          "Visokofunkcionalna anksioznost opisuje stanje u kome osoba spolja deluje uspešno i organizovano, ali iznutra oseća konstantnu brigu, nemir i pritisak. Nije zvanična dijagnoza, ali je realan obrazac koji može ozbiljno uticati na kvalitet života, san i odnose.",
      },
      {
        question: "Kako da prepoznam da imam visokofunkcionalnu anksioznost?",
        answer:
          "Česti znaci uključuju: stalnu potrebu za kontrolom, teškoće sa opuštanjem, strah od grešaka, prekomernu pripremu za sve, nesanicu zbog razmišljanja o sutrašnjim obavezama i osećaj da nikada nije dovoljno dobro. Spolja deluješ pouzdano, ali iznutra se boriš.",
      },
      {
        question:
          "Kako KBT terapija pomaže kod visokofunkcionalne anksioznosti?",
        answer:
          "Kognitivno-bihejvioralna terapija (KBT) pomaže da prepoznaš misaone obrasce koji pokreću anksioznost (npr. 'moram sve savršeno'), razviješ fleksibilnije standarde i naučiš tehnike za smanjenje unutrašnje napetosti. Cilj nije da prestaneš da budeš uspešan/na, već da uspeh ne košta toliko.",
      },
      {
        question:
          "Da li visokofunkcionalna anksioznost može preći u nešto ozbiljnije?",
        answer:
          "Može. Ako se ne adresira, hronični unutrašnji pritisak može dovesti do burnout-a, paničnih napada, depresije ili fizičkih simptoma (glavobolje, problemi sa varenjem, napetost mišića). Što ranije prepoznaš obrazac, lakše je raditi na promeni.",
      },
      {
        question: "Zašto je teško potražiti pomoć kada 'funkcionišeš'?",
        answer:
          "Upravo zato što spolja sve izgleda u redu, okolina retko prepoznaje problem. Možeš osećati da 'nemaš pravo' da se žališ jer postiž rezultate. Ali funkcionisanje uprkos anksioznosti zahteva ogromnu energiju. Terapija pomaže da taj unutrašnji trud postane manji.",
      },
      {
        question: "Koliko sesija je potrebno za promenu?",
        answer:
          "Prve uvide i male promene obično primećuješ nakon 4-6 sesija. Za dublje obrasce perfekcionizma i kontrole, proces može trajati 3-6 meseci. Terapeut prilagođava tempo tvom napretku i ciljevima.",
      },
      {
        question: "Da li online terapija funkcioniše za anksioznost?",
        answer:
          "Da, istraživanja potvrđuju da je online KBT jednako efikasna kao terapija uživo za anksiozne poremećaje. Za osobe sa visokofunkcionalnom anksioznošću, online format je često i praktičniji jer se uklapa u zahtevne rasporede bez dodatnog stresa oko organizacije.",
      },
    ],
  },

  // 44
  mindfulness: {
    label: "Mindfulness i relaksacija",
    therapistSearchTerms: ["Mindfulness"],
    guideLink: {
      href: "/blog/kako-kontrolisati-emocije-tehnike-i-strategije",
      label: "Pročitaj vodič o regulaciji emocija",
    },
    searchTerm:
      "mindfulness svesnost puna svesnost meditacija relaksacija tehnike relaksacije vežbe disanja smanjenje stresa progresivna mišićna relaksacija skeniranje tela anksioznost opuštanje fokus sadašnji trenutak mindfulness terapija online psiholog",
    description:
      "Stres, napetost i misli koje ne prestaju mogu da iscrpe i telo i um. Mindfulness (puna svesnost) i tehnike relaksacije pomažu ti da se vratiš u sadašnji trenutak, smiruješ nervni sistem i izgradis otpornost na svakodnevni pritisak. Na platformi Rešimo Probleme pronađi licencirane terapeute koji koriste mindfulness, vežbe disanja i progresivnu relaksaciju kao deo psihoterapijskog rada. Zakaži online sesiju i nauči tehnike koje možeš koristiti i između sesija.",
    directoryContent: {
      title: "Online terapeut za mindfulness i relaksaciju",
      description:
        "Ako želiš da razgovaraš o napetosti, stresu ili načinu na koji se odnosiš prema svojim mislima i emocijama, ovde možeš da pregledaš profil terapeuta koji navodi mindfulness u svom radu. Pročitaj opis rada, cenu i slobodne termine, pa odluči da li želiš da zakažeš online razgovor.",
      seoTitle:
        "Terapeut za mindfulness i relaksaciju online | Rešimo Probleme",
      seoDescription:
        "Pronađi online terapeuta koji navodi mindfulness i relaksaciju. Pregledaj profil, cenu i termine, pa zakaži razgovor.",
      faqItems: [
        {
          question: "Kako da pronađem terapeuta koji koristi mindfulness?",
          answer:
            "Na ovoj stranici je profil terapeuta koji u opisu rada navodi mindfulness. Pre zakazivanja možeš da pročitaš ceo profil, proveriš cenu i pogledaš slobodne termine.",
        },
        {
          question: "Da li je mindfulness isto što i terapija?",
          answer:
            "Mindfulness može biti jedna od tema ili praksi u radu terapeuta, ali nije automatski isto što i cela psihoterapija. Profil terapeuta je pravo mesto da proveriš kako opisuje svoj pristup.",
        },
        {
          question:
            "Mogu li da zakažem razgovor ako ne želim vežbe meditacije?",
          answer:
            "Možeš da opišeš šta ti odgovara, a šta ne, pre ili na početku razgovora. Terapeut može objasniti kako radi i zajedno sa tobom proceniti da li je taj pristup dobar izbor za tvoju temu.",
        },
        {
          question: "Kako izgleda online razgovor?",
          answer:
            "Nakon izbora termina zakazuješ video razgovor preko platforme. Cena je prikazana na profilu pre rezervacije termina.",
        },
        {
          question: "Da li je mindfulness pravi izbor za hitnu krizu?",
          answer:
            "Ako si u neposrednoj opasnosti ili razmišljaš da povrediš sebe ili nekog drugog, obrati se hitnoj službi ili lokalnoj kriznoj pomoći odmah. Ova stranica nije zamena za hitnu pomoć.",
        },
      ],
    },
    seoTitle:
      "Mindfulness i tehnike relaksacije uz terapeuta | Rešimo Probleme",
    seoDescription:
      "Smanji stres uz mindfulness i tehnike relaksacije sa licenciranim terapeutom online. Vežbe disanja, svesnost i opuštanje. Zakaži sesiju.",
    relatedConditions: ["act-terapija", "stres", "burnout"],
    passes: true,

    faqItems: [
      {
        question: "Kako mindfulness pomaže kod stresa i anksioznosti?",
        answer:
          "Mindfulness te uči da primetiš misli i telesne senzacije bez reagovanja na automatskom pilotu. Istraživanja pokazuju da redovna praksa smanjuje nivo kortizola (hormona stresa), ublažava simptome anksioznosti i poboljšava regulaciju emocija. Terapeut ti pomaže da nađeš tehnike koje odgovaraju tvom ritmu života.",
      },
      {
        question:
          "Šta je mindfulness i po čemu se razlikuje od obične meditacije?",
        answer:
          "Mindfulness ili puna svesnost je usmeravanje pažnje na sadašnji trenutak, bez osude. Za razliku od klasične meditacije, ne zahteva posebnu pozu ili dug vremenski blok. Možeš ga praktikovati dok jedeš, šetaš ili dišeš. U terapiji, terapeut te vodi kroz struktuirane vežbe prilagođene tvojim potrebama.",
      },
      {
        question: "Koje tehnike relaksacije mogu naučiti na terapiji?",
        answer:
          "Terapeut te uči više tehnika: vežbe dubokog disanja, progresivnu mišićnu relaksaciju (svesno zatezanje i opuštanje grupa mišića), skeniranje tela i vođenu vizualizaciju. Svaka tehnika ima drugačiji efekat, a terapeut bira pristup na osnovu tvog problema i preferencija.",
      },
      {
        question: "Koliko vremena dnevno treba za mindfulness praksu?",
        answer:
          "I pet minuta dnevno može napraviti razliku. Istraživanja potvrđuju da već kratke dnevne vežbe disanja kroz mesec dana poboljšavaju raspoloženje. Terapeut ti pomaže da ugradiš praksu u rutinu tako da bude održiva, a ne još jedna obaveza.",
      },
      {
        question: "Da li mindfulness može pojačati anksioznost na početku?",
        answer:
          "Kod nekih ljudi, naročito ako postoji jača anksioznost ili trauma, usmeravanje pažnje na telo može biti neprijatno u početku. Zato je rad sa terapeutom važan. Terapeut prilagođava vežbe, počevši od lakših tehnika poput svesne šetnje ili disanja, pre prelaska na dublje prakse.",
      },
      {
        question: "Da li je mindfulness terapija pogodna za online format?",
        answer:
          "Apsolutno. Online sesije su idealne za mindfulness jer se vežbe izvode u tvom prostoru, gde se osećaš opušteno. Terapeut te vodi uživo kroz video poziv, a tehnike koje naučiš možeš praktikovati samostalno između sesija.",
      },
      {
        question: "Koliko košta mindfulness terapija na Rešimo Probleme?",
        answer:
          "Sesija sa licenciranim terapeutom košta od 30 evra. Možeš pregledati profile terapeuta, njihove pristupe i ocene korisnika pre zakazivanja. Nema pretplate ni skrivenih troškova.",
      },
    ],
  },

  // 45
  "shema-terapija": {
    label: "Shema terapija",
    therapistSearchTerms: ["Shema terapija"],
    title:
      "Shema terapeuti online: prepoznaj i promeni duboke emocionalne obrasce",
    searchTerm:
      "shema terapija schema therapy shema terapeut shema obrasci rani maladaptivni obrasci modovi u shema terapiji detinjstvo emocionalne potrebe granice odnosi napušteno dete zdravi odrasli",
    description:
      "Shema terapija je integrativni pristup koji pomaže da prepoznaš i promeniš duboke emocionalne obrasce nastale u detinjstvu. Ako primećuješ da se ponavljaju isti problemi u vezama, na poslu ili u odnosu prema sebi, shema terapija ti pomaže da razumeš odakle ti obrasci dolaze i izgradiš zdravije odgovore. Na Rešimo Probleme pronađi licenciranog shema terapeuta i započni online terapiju.",
    seoTitle: "Shema terapija: terapeut online | Rešimo Probleme",
    seoDescription:
      "Pronađi shema terapeuta online. Rad na ranim obrascima, modovima i emocionalnim potrebama. Zakaži sesiju na Rešimo Probleme.",
    relatedConditions: [
      "trauma-iz-detinjstva",
      "problemi-vezivanja",
      "psihodinamska-terapija",
      "odnosi",
    ],
    passes: true,
    faqItems: [
      {
        question: "Šta je shema terapija i za koga je namenjena?",
        answer:
          "Shema terapija je integrativni psihoterapijski pristup koji kombinuje elemente KBT, geštalt terapije i teorije afektivne vezanosti. Namenjena je osobama sa ponavljajućim obrascima u odnosima, hroničnim emocionalnim poteškoćama ili problemima koji nisu reagovali na standardnu KBT terapiju.",
      },
      {
        question: "Šta su sheme i kako nastaju?",
        answer:
          "Sheme su duboki emocionalni obrasci koji nastaju u detinjstvu kada osnovne emocionalne potrebe (sigurnost, ljubav, autonomija) nisu zadovoljene. Na primer, ako si kao dete doživeo/la napuštanje, možeš razviti shemu napuštanja koja utiče na tvoje odnose u odraslom dobu.",
      },
      {
        question: "Šta su modovi u shema terapiji?",
        answer:
          "Modovi su emocionalna stanja koja se aktiviraju u određenim situacijama: Ranjivo dete (bol, strah), Ljutiti ili Impulsivni dete, Kritičar (samokritika), Zdravi odrasli (zreli odgovori). Terapija pomaže da prepoznaš koji mod je aktivan i naučiš da aktiviraš Zdravog odraslog.",
      },
      {
        question: "Koliko dugo traje shema terapija?",
        answer:
          "Shema terapija je obično duži proces, jer radi na dubokim obrascima. Tipično traje 6-12 meseci ili duže, u zavisnosti od složenosti problema. Prve uvide obično primećuješ nakon 2-3 meseca, ali trajne promene zahtevaju dosledan rad.",
      },
      {
        question: "Po čemu se shema terapija razlikuje od KBT?",
        answer:
          "KBT se fokusira na trenutne misli i ponašanje, dok shema terapija ide dublje i bavi se obrascima iz detinjstva. Uključuje rad na emocijama, terapijski odnos kao korektivno iskustvo i tehnike poput zamišljanja scena iz prošlosti. Posebno je korisna kada kratkoročniji pristupi nisu doneli rezultat.",
      },
      {
        question: "Da li shema terapija funkcioniše online?",
        answer:
          "Da, online shema terapija je efikasna jer ključni elementi (razgovor, vizualizacija, rad na emocijama) ne zahtevaju fizičko prisustvo. Mnogi klijenti smatraju da je lakše otvoriti se o teškim temama iz bezbednosti svog prostora.",
      },
      {
        question: "Kako da znam da li mi treba shema terapija?",
        answer:
          "Shema terapija može biti pravi izbor ako primećuješ ponavljajuće probleme u odnosima, osećaš da se ne možeš promeniti uprkos trudu, imaš hronično nizak osećaj sopstvene vrednosti ili se stalno vraćaš na iste obrasce ponašanja koje želiš da promeniš.",
      },
    ],
  },

  // 46
  "act-terapija": {
    label: "ACT terapija prihvatanja",
    title:
      "Online ACT terapeuti za psihološku fleksibilnost i život u skladu sa vrednostima",
    searchTerm:
      "ACT terapija prihvatanje posvećenost vrednosti kognitivna defuzija mindfulness psihološka fleksibilnost terapija prihvatanja i posvećenosti treći talas KBT izbegavanje emocija kontrola misli",
    description:
      "ACT (terapija prihvatanja i posvećenosti) pomaže ti da prestaNeš da se boriš sa neprijatnim mislima i emocijama, i umesto toga živiš u skladu sa onim što ti je zaista važno. Kroz šest ključnih procesa, uključujući kognitivnu defuziju, svesno prisustvo i rad na vrednostima, ACT te uči kako da deluješ uprkos unutrašnjem nelagodju. Na platformi Rešimo Probleme pronađi terapeute obučene za ACT pristup i zakaži online sesiju.",
    seoTitle:
      "ACT terapija online: prihvatanje i posvećenost | Rešimo Probleme",
    seoDescription:
      "Pronađi ACT terapeuta online. Terapija prihvatanja i posvećenosti za veću psihološku fleksibilnost. Zakaži sesiju sa licenciranim terapeutom.",
    relatedConditions: ["mindfulness", "kbt", "stres"],
    faqItems: [
      {
        question: "Šta je ACT terapija i kako funkcioniše?",
        answer:
          "ACT (terapija prihvatanja i posvećenosti) je pristup iz trećeg talasa kognitivno-bihevioralnih terapija. Umesto da pokušavaš eliminisati neprijatne misli i emocije, ACT te uči da ih prihvatiš kao deo života i fokusiraš se na akcije u skladu sa tvojim vrednostima. Radi se na šest procesa: prihvatanje, kognitivna defuzija, svesno prisustvo, self-as-context, vrednosti i posvećena akcija.",
      },
      {
        question: "Šta znači 'prihvatanje' u ACT-u, zar to nije odustajanje?",
        answer:
          "Prihvatanje u ACT-u ne znači odustajanje niti pasivnost. To je spremnost da doživiš neprijatne emocije bez borbe protiv njih, kako bi oslobodio/la energiju za ono što ti je zaista važno. Umesto da trošiš snagu na kontrolu misli, učiš da deluješ uprkos nelagodnosti.",
      },
      {
        question: "Kome može pomoći ACT terapija?",
        answer:
          "ACT je efikasan za širok spektar poteškoća: anksioznost, depresiju, hronični stres, bol, izbegavajuće ponašanje, perfekcionizam i životne tranzicije. Posebno pomaže osobama koje osećaju da su 'zaglavili' u borbi sa sopstvenim mislima ili koje izbegavaju situacije zbog straha od neprijatnih emocija.",
      },
      {
        question: "Kako izgleda ACT sesija online?",
        answer:
          "ACT sesija kombinuje razgovor sa praktičnim vežbama. Radiš na prepoznavanju obrazaca izbegavanja, vežbaš kognitivnu defuziju (odlepljivanje od misli), istražuješ lične vrednosti i planiraš konkretne korake u skladu sa njima. Online format omogućava iste tehnike kao i uživo, uz fleksibilnost zakazivanja.",
      },
      {
        question: "Šta je kognitivna defuzija i kako pomaže?",
        answer:
          "Kognitivna defuzija je tehnika kojom učiš da posmatraš svoje misli kao mentalne događaje, a ne kao činjenice. Na primer, umesto da veruješ misli 'nisam dovoljno dobar/dobra', naučiš da je prepoznaš kao misao koju tvoj um proizvodi. To smanjuje uticaj negativnih misli na tvoje ponašanje.",
      },
      {
        question: "Po čemu se ACT razlikuje od klasične KBT terapije?",
        answer:
          "Klasična KBT se fokusira na promenu sadržaja negativnih misli (kognitivno restrukturiranje), dok ACT menja tvoj odnos prema mislima. ACT ne pokušava da ubedi da je misao pogrešna, već te uči da je prihvatiš i deluješ uprkos njoj. Oba pristupa su naučno zasnovana, a terapeut ti može pomoći da odabereš onaj koji ti najviše odgovara.",
      },
      {
        question: "Koliko ACT sesija je potrebno da osetim promenu?",
        answer:
          "Mnogi klijenti primete prve promene nakon 4 do 6 sesija, posebno u smanjenju izbegavajućeg ponašanja i jasnijem razumevanju sopstvenih vrednosti. ACT je često kraći proces od tradicionalne terapije, ali tačan broj sesija zavisi od tvojih specifičnih ciljeva i situacije.",
      },
    ],
    passes: true,
  },

  // 47
  "dbt-terapija": {
    label: "DBT terapija",
    guideLink: {
      href: "/blog/online-psihoterapija-vodic",
      label: "Pročitaj kako izgleda online psihoterapija",
    },
    therapistSearchTerms: [
      "Dijalektičko-bihejvioralna terapija (DBT)",
      "RE&KBT DBT",
    ],
    title: "Online DBT terapeuti za regulaciju emocija i toleranciju na stres",
    searchTerm:
      "DBT dijalektičko-bihevioralna terapija regulacija emocija tolerancija na stres mindfulness interpersonalne veštine svesna prisutnost emocionalna disregulacija Marsha Linehan treći talas KBT",
    description:
      "Dijalektičko-bihevioralna terapija (DBT) kombinuje prihvatanje i promenu kako bi ti pomogla da upravljaš intenzivnim emocijama, smanjuješ impulsivno ponašanje i gradiš stabilnije odnose. Kroz četiri modula, uključujući svesnu prisutnost, toleranciju na stres, regulaciju emocija i interpersonalne veštine, DBT te uči konkretnim tehnikama za svakodnevne situacije. Na platformi Rešimo Probleme pronađi terapeute obučene za DBT pristup i zakaži online sesiju.",
    seoTitle: "DBT terapija online: regulacija emocija | Rešimo Probleme",
    seoDescription:
      "Pronađi DBT terapeuta online. Dijalektičko-bihevioralna terapija za regulaciju emocija i toleranciju na stres. Zakaži sesiju.",
    relatedConditions: [
      "borderline",
      "samopovredjivanje-podrska",
      "act-terapija",
      "ljutnja-bes",
    ],
    directoryContent: {
      title: "DBT terapeuti online za rad na intenzivnim emocijama",
      description:
        "DBT, odnosno dijalektičko-bihejvioralna terapija, je pristup koji neki terapeuti koriste kada rade sa intenzivnim emocijama, odnosima i svakodnevnim izazovima. Na Rešimo Probleme možeš da pregledaš profile terapeuta koji navode DBT, uporediš njihov pristup, cenu i slobodne termine, pa zakažeš online razgovor. Ne moraš unapred da znaš da li je DBT pravi izbor da bi počeo/la razgovor.",
      seoTitle: "DBT terapeuti online | Rešimo Probleme",
      seoDescription:
        "Pronađi online DBT terapeuta. Uporedi profile, pristupe, cene i slobodne termine, pa zakaži razgovor koji odgovara tvojim potrebama.",
      faqItems: [
        {
          question: "Kako da pronađem DBT terapeuta online?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji javno navode DBT pristup. Uporedi opis rada, cenu i dostupne termine pre zakazivanja razgovora.",
        },
        {
          question: "Šta znači DBT terapija?",
          answer:
            "DBT je skraćenica za dijalektičko-bihejvioralnu terapiju. Terapeut ti može objasniti kako taj pristup koristi u svom radu i kako bi razgovor mogao da izgleda.",
        },
        {
          question: "Da li moram unapred da znam da mi treba DBT?",
          answer:
            "Ne moraš. Možeš početi od konkretne situacije ili osećaja koji te opterećuje, a sa terapeutom razjasniti da li njegov pristup odgovara tvojim potrebama.",
        },
        {
          question: "Kako izgleda prvi online razgovor sa DBT terapeutom?",
          answer:
            "Prvi razgovor služi da predstaviš šta ti je važno, postaviš pitanja i čuješ kako terapeut radi. Zatim možeš odlučiti da li želiš da nastavite saradnju.",
        },
        {
          question: "Koliko košta online DBT sesija?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više profila i izabrati termin i cenu koji ti odgovaraju.",
        },
      ],
    },
    faqItems: [
      {
        question: "Šta je DBT terapija i kako funkcioniše?",
        answer:
          "DBT (dijalektičko-bihevioralna terapija) je strukturiran pristup iz trećeg talasa kognitivno-bihevioralnih terapija, koji je razvila Marsha Linehan. Zasniva se na dijalektici, balansiranju prihvatanja trenutnog stanja sa motivacijom za promenu. Radi se kroz četiri modula: svesna prisutnost (mindfulness), tolerancija na stres, regulacija emocija i interpersonalne veštine.",
      },
      {
        question: "Kome je DBT terapija namenjena?",
        answer:
          "DBT je prvobitno razvijena za osobe sa graničnim poremećajem ličnosti, ali se danas uspešno koristi za širok spektar poteškoća: intenzivne emocije koje teško kontrolišeš, impulsivno ponašanje, samopovređivanje, hroničnu prazninu, nestabilne odnose, depresiju i anksioznost. Posebno pomaže kada osećaš da te emocije 'preuzimaju'.",
      },
      {
        question: "Kako izgleda online DBT sesija?",
        answer:
          "DBT sesija kombinuje individualni rad sa terapeutom i učenje konkretnih veština. Na sesiji analizirate situacije u kojima su emocije bile intenzivne, identifikujete obrasce i vežbate nove načine reagovanja. Između sesija primenjuješ naučene tehnike u svakodnevnom životu. Online format omogućava isti kvalitet rada uz fleksibilnost zakazivanja.",
      },
      {
        question: "Šta su četiri DBT modula?",
        answer:
          "Svesna prisutnost (mindfulness) uči te da budeš prisutan/prisutna u trenutku bez prosuđivanja. Tolerancija na stres pomaže da izdržiš krizne situacije bez pogoršavanja. Regulacija emocija daje ti alate da razumeš i upravljaš intenzivnim osećanjima. Interpersonalne veštine pomažu ti da postaviš granice i komuniciraš jasnije u odnosima.",
      },
      {
        question: "Po čemu se DBT razlikuje od klasične KBT terapije?",
        answer:
          "Dok klasična KBT naglašava promenu misli i ponašanja, DBT dodaje komponentu prihvatanja, balansira oba pristupa. DBT takođe uključuje specifične module za toleranciju na stres i interpersonalne veštine kojih u klasičnoj KBT nema. DBT je strukturiranija i uključuje konkretne vežbe za svakodnevnu primenu između sesija.",
      },
      {
        question: "Koliko dugo traje DBT terapija?",
        answer:
          "Standardni DBT program traje 6 do 12 meseci, ali na individualnim sesijama tempo se prilagođava tvojim potrebama. Mnogi klijenti primete poboljšanje u regulaciji emocija već nakon prvih 8 do 12 sesija. Terapeut će zajedno sa tobom proceniti napredak i prilagoditi trajanje procesa.",
      },
      {
        question: "Da li DBT pomaže kod depresije i anksioznosti?",
        answer:
          "Da, DBT se uspešno koristi i za depresiju i anksioznost, posebno kada su praćeni intenzivnim emocijama ili impulsivnim ponašanjem. Veštine regulacije emocija i svesne prisutnosti direktno pomažu u smanjenju simptoma. Istraživanja pokazuju da DBT može biti posebno koristan kada klasični pristupi nisu doneli dovoljno poboljšanja.",
      },
    ],
    passes: true,
  },

  // 48
  "psihodinamska-terapija": {
    label: "Psihodinamska terapija",
    therapistSearchTerms: [
      "Psihodinamska psihoterapija i savetovanje",
      "Psihodinamski psihoterapeut psihodinamski integrativni pristup (OLI)",
      "O.L.I. integrativna psihodinamska psihoterapija, autogeni trening, asertivni trening",
    ],
    guideLink: {
      href: "/blog/online-psihoterapija-vodic",
      label: "Pročitaj vodič za online psihoterapiju",
    },
    title: "Online psihodinamski terapeuti za dubinski uvid i trajnu promenu",
    searchTerm:
      "psihodinamska terapija psihodinamička psihoterapija dubinska psihologija nesvesno obrasci transfer slobodne asocijacije uvid unutrašnji konflikti odnosi identitet prošlost",
    description:
      "Psihodinamska psihoterapija pomaže ti da razumeš kako nesvesni obrasci, rana iskustva i unutrašnji konflikti utiču na tvoje sadašnje ponašanje i odnose. Kroz dubinski rad sa terapeutom, koji uključuje istraživanje transfera, slobodne asocijacije i analizu obrazaca, stičeš uvid koji vodi ka trajnoj promeni. Na platformi Rešimo Probleme pronađi psihodinamske terapeute i zakaži online sesiju.",
    directoryContent: {
      title: "Online psihodinamski terapeuti",
      description:
        "Ako tražiš psihodinamsku psihoterapiju ili savetovanje, ovde možeš da pregledaš profile terapeuta koji taj pristup navode u svom radu. Uporedi opis rada, cenu i slobodne termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor.",
      seoTitle: "Psihodinamska psihoterapija online | Rešimo Probleme",
      seoDescription:
        "Pronađi online psihodinamskog terapeuta. Pregledaj profile, cene i termine, pa zakaži razgovor na Rešimo Probleme.",
      faqItems: [
        {
          question: "Kako da pronađem psihodinamskog terapeuta online?",
          answer:
            "Na ovoj stranici su profili terapeuta koji navode psihodinamsku psihoterapiju ili savetovanje. Pročitaj opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question:
            "Da li svi terapeuti na stranici imaju isti psihodinamski pristup?",
          answer:
            "Ne mora svaki profil opisivati isti oblik ili nivo edukacije. Zato pre izbora pročitaj kako konkretni terapeut predstavlja svoj pristup i iskustvo.",
        },
        {
          question:
            "Kako da izaberem između psihodinamske terapije i drugog pristupa?",
          answer:
            "Uporedi profile i pitanja koja želiš da otvoriš na razgovoru. Ako nisi siguran koji pristup ti odgovara, možeš to pitanje postaviti terapeutu pre ili na početku zakazivanja.",
        },
        {
          question: "Kako izgleda online psihodinamski razgovor?",
          answer:
            "Nakon izbora termina zakazuješ video razgovor preko platforme. Cena i dostupni termini su prikazani na profilu terapeuta pre rezervacije.",
        },
        {
          question: "Koliko košta psihodinamska terapija online?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije pre nego što izabereš termin.",
        },
      ],
    },
    seoTitle: "Psihodinamska terapija online: dubinski uvid | Rešimo Probleme",
    seoDescription:
      "Pronađi psihodinamskog terapeuta online. Dubinska psihoterapija za razumevanje obrazaca i trajnu promenu. Zakaži sesiju sa licenciranim terapeutom.",
    relatedConditions: [
      "shema-terapija",
      "trauma-iz-detinjstva",
      "odnosi",
      "gestalt-terapija",
    ],
    faqItems: [
      {
        question: "Šta je psihodinamska terapija i kako funkcioniše?",
        answer:
          "Psihodinamska terapija je dubinski oblik psihoterapije zasnovan na psihoanalitičkoj teoriji. Fokusira se na otkrivanje nesvesnih konflikata, potisnutih emocija i obrazaca iz prošlosti koji utiču na tvoje sadašnje ponašanje i odnose. Za razliku od klasične psihoanalize, sesije se odvijaju licem u lice, a proces je kraći i usmereniji na konkretne poteškoće.",
      },
      {
        question: "Kome može pomoći psihodinamska psihoterapija?",
        answer:
          "Psihodinamski pristup je efikasan za depresiju, anksioznost, traume, konflikte u odnosima, nisko samopoštovanje i probleme sa identitetom. Posebno pomaže osobama koje primećuju da se isti obrasci ponavljaju u njihovim odnosima ili da ih nešto 'koči' a ne mogu da identifikuju šta.",
      },
      {
        question: "Kako izgleda online psihodinamska sesija?",
        answer:
          "Sesija uključuje otvoren razgovor u kojem terapeut pomaže da osvestiš potisnute misli i osećanja. Koriste se tehnike poput slobodnih asocijacija, istraživanja odnosa sa terapeutom (transfer) i analize obrazaca. Online format omogućava istu dubinu rada kao i uživo, uz fleksibilnost zakazivanja iz bilo kog mesta.",
      },
      {
        question: "Po čemu se psihodinamska terapija razlikuje od KBT-a?",
        answer:
          "KBT se fokusira na promenu sadašnjih misli i ponašanja kroz konkretne tehnike, dok psihodinamska terapija istražuje dublje korene problema, nesvesne obrasce i uticaj prošlosti. KBT je obično kraća i strukturiranija, dok psihodinamski rad traži više vremena ali cilja na trajniju transformaciju. Terapeut ti može pomoći da odabereš pristup koji ti najbolje odgovara.",
      },
      {
        question: "Koliko dugo traje psihodinamska terapija?",
        answer:
          "Sesije se obično održavaju jednom nedeljno, a kod dubljeg rada ponekad i dva puta. Trajanje procesa zavisi od tvojih ciljeva: kratkoročna psihodinamska terapija može trajati 3 do 6 meseci, dok dugoročni rad može potrajati godinu ili više. Prve uvide mnogi klijenti stiču već u prvih nekoliko sesija.",
      },
      {
        question: "Šta je transfer u psihodinamskoj terapiji?",
        answer:
          "Transfer je pojava kada nesvesno prenosiš obrasce iz ranijih odnosa (sa roditeljima, partnerima) na odnos sa terapeutom. Na primer, možeš očekivati kritiku jer si je navikao/la od roditelja. Prepoznavanje ovih obrazaca u sigurnom terapijskom okruženju pomaže ti da ih promeniš i u svakodnevnom životu.",
      },
      {
        question:
          "Da li je psihodinamska terapija samo razgovor ili donosi konkretne rezultate?",
        answer:
          "Iako je razgovor osnovni alat, cilj je dubinska promena: razumevanje unutrašnjih konflikata, oslobađanje od ponavljajućih obrazaca i izgradnja zdravijih načina funkcionisanja. Istraživanja pokazuju da psihodinamska terapija može biti jednako efikasna kao i drugi pristupi, sa rezultatima koji se održavaju i nakon završetka terapije.",
      },
    ],
    passes: true,
  },

  // 49
  "gestalt-terapija": {
    label: "Geštalt terapija",
    therapistSearchTerms: ["Geštalt psihoterapija"],
    guideLink: {
      href: "/blog/gestalt-terapija-kako-da-se-ponovo-poveses-sa-sobom",
      label: "Pročitaj vodič o geštalt terapiji",
    },
    title: "Online geštalt terapeuti za svesnost, kontakt i lični rast",
    searchTerm:
      "gestalt geštalt terapija psihoterapija ovde i sada kontakt iskustveno svesnost granice prazna stolica Fritz Perls humanistička terapija celina osobe rad sa telom",
    description:
      "Geštalt terapija je iskustveni, humanističko-egzistencijalni pristup koji se fokusira na ono što se dešava 'ovde i sada', na tvoju svesnost o sopstvenim potrebama, emocijama i telesnim senzacijama. Kroz eksperimente poput tehnike prazne stolice, dijaloga i rada sa telom, geštalt te uči da prepoznaš kako sprečavaš sebe da živiš autentično. Na platformi Rešimo Probleme pronađi geštalt terapeute i zakaži online sesiju.",
    directoryContent: {
      title: "Online geštalt terapeuti",
      description:
        "Ako tražiš geštalt psihoterapiju, ovde možeš da pregledaš profil terapeuta koji taj pristup navodi u svom radu i kvalifikacijama. Pročitaj opis rada, cenu i slobodne termine, pa izaberi osobu sa kojom želiš da zakažeš online razgovor.",
      seoTitle: "Geštalt terapeut online | Rešimo Probleme",
      seoDescription:
        "Pronađi online geštalt terapeuta. Pregledaj profil, cenu i termine, pa zakaži razgovor na Rešimo Probleme.",
      faqItems: [
        {
          question: "Kako da pronađem geštalt terapeuta online?",
          answer:
            "Na ovoj stranici je profil terapeuta koji navodi geštalt psihoterapiju. Pre zakazivanja možeš da pročitaš opis rada, proveriš kvalifikacije, cenu i slobodne termine.",
        },
        {
          question: "Da li je geštalt terapija ista kod svakog terapeuta?",
          answer:
            "Terapeuti mogu različito opisivati svoj pristup i iskustvo. Zato je važno da pročitaš konkretan profil i izabereš osobu čiji način rada i termin ti odgovaraju.",
        },
        {
          question:
            "Da li moram da znam šta je geštalt terapija pre zakazivanja?",
          answer:
            "Ne moraš unapred da poznaješ pristup. Možeš da pročitaš vodič na ovoj stranici, a na razgovoru pitaš terapeuta kako radi i da li je taj pristup relevantan za tvoju temu.",
        },
        {
          question: "Kako izgleda online razgovor sa geštalt terapeutom?",
          answer:
            "Nakon izbora termina zakazuješ video razgovor preko platforme. Cena je prikazana na profilu pre rezervacije termina.",
        },
        {
          question: "Koliko košta geštalt terapija online?",
          answer:
            "Cenu određuje svaki terapeut i vidiš je na njegovom profilu pre zakazivanja. Tako možeš da uporediš opcije pre izbora termina.",
        },
      ],
    },
    seoTitle: "Geštalt terapija online: svesnost i kontakt | Rešimo Probleme",
    seoDescription:
      "Pronađi geštalt terapeuta online. Iskustvena psihoterapija za svesnost, autentičnost i lični rast. Zakaži sesiju sa licenciranim terapeutom.",
    relatedConditions: ["mindfulness", "act-terapija", "samopouzdanje"],
    faqItems: [
      {
        question: "Šta je geštalt terapija i kako funkcioniše?",
        answer:
          "Geštalt terapija je humanističko-egzistencijalni oblik psihoterapije koji su osnovali Fritz i Laura Perls. Fokusira se na povećanje svesnosti o sebi u sadašnjem trenutku, na tvoje emocije, telesne senzacije i način na koji uspostavljaš kontakt sa drugima. Umesto analize prošlosti, geštalt radi sa onim što se dešava 'ovde i sada'.",
      },
      {
        question: "Šta znači rad 'ovde i sada' u geštalt terapiji?",
        answer:
          "Princip 'ovde i sada' znači da se na sesiji fokusiraš na ono što osećaš, misliš i doživljavaš u ovom trenutku, a ne na prepričavanje prošlih događaja. Promena se dešava kroz neposredno iskustvo, jer tek kada postaneš svestan/svesna šta radiš i kako to radiš, možeš napraviti drugačiji izbor.",
      },
      {
        question: "Kome može pomoći geštalt psihoterapija?",
        answer:
          "Geštalt pristup je efikasan za anksioznost, depresiju, probleme sa samopouzdanjem, poteškoće u odnosima, potiskivanje emocija i osećaj da si 'zaglavljen/a'. Posebno pomaže osobama koje žele da bolje razumeju sebe, postave zdravije granice i žive autentičnije.",
      },
      {
        question: "Šta je tehnika prazne stolice?",
        answer:
          "Tehnika prazne stolice je poznati geštalt eksperiment u kojem vodiš dijalog sa zamišljenom osobom ili delom sebe koji sedi na praznoj stolici. Na primer, možeš razgovarati sa svojim unutrašnjim kritičarem ili osobom sa kojom imaš nerešen odnos. Ova tehnika pomaže da osvestiš emocije i pronađeš nove načine izražavanja.",
      },
      {
        question: "Kako izgleda online geštalt sesija?",
        answer:
          "Geštalt sesija kombinuje razgovor sa iskustvenim eksperimentima. Terapeut je aktivan partner koji te pita šta primećuješ u telu, koje emocije osećaš i kako uspostavljaš kontakt. Eksperimenti se prilagođavaju online formatu i uvek se rade uz tvoj pristanak i u tvom tempu. Online sesije omogućavaju isti kvalitet iskustvenog rada.",
      },
      {
        question:
          "Po čemu se geštalt razlikuje od drugih terapijskih pristupa?",
        answer:
          "Za razliku od KBT-a koji menja misli, ili psihodinamske terapije koja istražuje prošlost, geštalt se fokusira na celokupno iskustvo u sadašnjem trenutku, um, telo i emocije kao celinu. Terapeut nije neutralni posmatrač već aktivan partner u dijalogu. Geštalt koristi kreativne eksperimente umesto standardizovanih tehnika.",
      },
      {
        question: "Koliko dugo traje geštalt terapija?",
        answer:
          "Sesije obično traju 45 minuta, a terapija može trajati od nekoliko meseci za specifične teme do godinu i više za dublje lične promene. Mnogi klijenti primete prve pomake u svesnosti i načinu funkcionisanja već nakon prvih sesija. Terapeut zajedno sa tobom procenjuje tempo i trajanje procesa.",
      },
    ],
    passes: true,
  },

  // 50
  "narcisoidno-zlostavljanje": {
    label: "Narcisoidno zlostavljanje",
    title:
      "Online terapeuti za oporavak od narcisoidnog zlostavljanja i traumatskih veza",
    searchTerm:
      "narcisoidno zlostavljanje gaslighting idealizacija devalvacija granice oporavak traumatske veze narcisoidni poremećaj u vezi u braku trauma bond manipulacija samopouzdanje toksična veza",
    description:
      "Narcisoidno zlostavljanje ostavlja duboke tragove na samopouzdanje, osećaj realnosti i sposobnost da veruješ sebi. Kroz cikluse idealizacije i devalvacije, gaslighting i emocionalnu manipulaciju, možeš izgubiti kontakt sa sopstvenim potrebama i granicama. Terapija ti pomaže da razumeš obrasce traumatske veze, oporaviš se i ponovo izgradiš poverenje u sebe. Na platformi Rešimo Probleme pronađi terapeuta sa iskustvom u radu sa oporavkom od narcisoidnog zlostavljanja.",
    seoTitle:
      "Narcisoidno zlostavljanje: oporavak i terapija | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeuta za oporavak od narcisoidnog zlostavljanja online. Podrška za granice, samopouzdanje i izlazak iz toksične dinamike.",
    relatedConditions: [
      "emocionalno-zlostavljanje",
      "kodependencija",
      "nasilje-u-porodici-podrska",
      "samopouzdanje",
    ],
    faqItems: [
      {
        question: "Kako prepoznati narcisoidno zlostavljanje u vezi?",
        answer:
          "Narcisoidno zlostavljanje često prati obrazac: na početku idealizacija (osećaš se posebno i voljeno), zatim postepena devalvacija (kritike, omalovažavanje, kontrola), i na kraju odbacivanje ili pretnja odbacivanjem. Gaslighting, tretman ćutanjem, prebacivanje krivice i izolacija od bliskih ljudi su česti znaci. Ako sumiraš da ti se dešava, razgovor sa terapeutom može pomoći da raščistiš situaciju.",
      },
      {
        question: "Šta je trauma bond i kako se osloboditi?",
        answer:
          "Trauma bond je emocionalna vezanost za osobu koja te zlostavi, nastala kroz cikluse nagrade i kazne. Oslobađanje podrazumeva prepoznavanje ciklusa idealizacija-devalvacija, ograničavanje kontakta (po mogućstvu 'no contact' ili 'low contact'), izgradnju mreže podrške van veze i rad sa terapeutom na razumevanju obrazaca koji te drže u vezi.",
      },
      {
        question:
          "Kako terapija pomaže kod oporavka od narcisoidnog zlostavljanja?",
        answer:
          "Terapija ti pomaže da dekonstruišeš gaslight-poruke i vratiš poverenje u sopstvenu percepciju. Radiš na prepoznavanju obrazaca koji te privlače ka toksičnim dinamikama, obnavljaš granice i samopouzdanje, i obrađuješ traumu koju je zlostavljanje ostavilo. Terapeut je siguran prostor u kojem možeš da proveriš svoju realnost bez prosuđivanja.",
      },
      {
        question: "Koliko dugo traje oporavak od narcisoidnog zlostavljanja?",
        answer:
          "Oporavak je individualan i zavisi od trajanja i intenziteta zlostavljanja. Mnogi klijenti primete prve pomake nakon 8 do 12 sesija, posebno u vraćanju osećaja sopstvene vrednosti i jasnijem postavljanju granica. Dublja obrada traumatskih obrazaca može zahtevati duži proces, ali svaki korak ka razumevanju sebe je napredak.",
      },
      {
        question:
          "Da li terapija može pomoći ako sam još uvek u vezi sa narcisoidnom osobom?",
        answer:
          "Da, terapija može pomoći bez obzira na to da li si još u vezi ili si izašao/izašla. Dok si u vezi, terapeut ti pomaže da prepoznaš dinamiku, postaviš granice i napraviš plan sigurnosti. Ne postoji pritisak da odmah napustiš vezu, cilj je da doneseš informisanu odluku sa jasnom predstavom o tome šta ti se dešava.",
      },
      {
        question:
          "Kako povratiti samopouzdanje posle narcisoidnog zlostavljanja?",
        answer:
          "Oporavak samopouzdanja uključuje dekonstrukciju gaslight-poruka ('ti si luda/lud', 'preteruješ'), vraćanje kontakta sa sopstvenim osećanjima i potrebama, i građenje malih svakodnevnih pobeda. Terapeut ti pomaže da razlikuješ kritiku zlostavljača od realnosti i da ponovo naučiš da veruješ sebi.",
      },
      {
        question:
          "Da li online terapija funkcioniše za oporavak od traumatskih veza?",
        answer:
          "Online terapija je posebno pogodna za oporavak od narcisoidnog zlostavljanja jer pruža siguran i privatan prostor, bez rizika da te neko vidi kako odlaziš na terapiju. To je važno ako si još u situaciji kontrole. Sesije sa licenciranim terapeutom na platformi Rešimo Probleme pružaju isti kvalitet podrške kao i uživo.",
      },
    ],
    passes: true,
  },

  // 51
  "gubitak-ljubimca": {
    label: "Gubitak kućnog ljubimca",
    title:
      "Online terapeuti za gubitak ljubimca, podršku kroz tugu i žalovanje",
    searchTerm:
      "gubitak ljubimca smrt ljubimca tuga žalovanje kućni ljubimac psi mačke tugovanje kako preboleti smrt kućnog ljubimca tuga za psom kako prihvatiti gubitak ljubimca žalost gubitak psa mačke",
    description:
      "Gubitak kućnog ljubimca može izazvati tugu koja je jednako duboka kao gubitak člana porodice, a okolina to često ne razume. Osećanje praznine, krivice ili čak stida zbog intenziteta bola potpuno je normalno. Naši terapeuti pružaju siguran prostor gde tvoja tuga biva validirana, bez osuđivanja. Zakaži online sesiju i dobij podršku kroz proces žalovanja, u svom ritmu i bez pritiska.",
    seoTitle: "Gubitak ljubimca: online terapijska podrška | Rešimo Probleme",
    seoDescription:
      "Kako preboleti smrt kućnog ljubimca? Online terapeuti koji razumeju tvoju tugu. Zakaži sesiju i dobij podršku kroz žalovanje.",
    relatedConditions: ["zalovanje", "zivotne-promene", "depresija"],
    faqItems: [
      {
        question: "Zašto je tuga zbog gubitka ljubimca toliko intenzivna?",
        answer:
          "Kućni ljubimci su prisutni u svakom delu tvog dana, pružaju bezuslovnu ljubav i stvaraju rutinu koja daje osećaj sigurnosti. Kada ih izgubiš, gubiš ne samo biće koje voliš, već čitav niz svakodnevnih rituala. Ta praznina je realna i validna, bez obzira na to šta okolina kaže.",
      },
      {
        question: "Kako preboleti smrt psa ili mačke?",
        answer:
          "Ne postoji 'pravi' način žalovanja, ali pomaže da dozvoliš sebi tugu umesto da je potiskuješ. Rituali poput albuma uspomena, pisanja pisma ljubimcu ili sadnje biljke mogu pomoći. Ako osećaš da tuga ne popušta mesecima ili remeti tvoje funkcionisanje, razgovor sa terapeutom može biti korisna podrška.",
      },
      {
        question: "Da li je normalno osećati krivicu nakon gubitka ljubimca?",
        answer:
          "Krivica je jedna od najčešćih emocija kod gubitka ljubimca, posebno ako si doneo/la odluku o eutanaziji. Pitanja 'da li sam mogao/la više' potpuno su normalna. Terapeut ti može pomoći da razumeš ove misli i da se pomiriš sa odlukama koje si doneo/la iz ljubavi.",
      },
      {
        question: "Kako pomoći deci da razumeju smrt ljubimca?",
        answer:
          "Koristi jednostavan jezik prilagođen uzrastu, bez eufemizama poput 'otišao je na put'. Dozvoli detetu da izrazi tugu na svoj način, bilo kroz plakanje, crtanje ili pričanje priča o ljubimcu. Ne požuruj zamenu ljubimca, jer detetu treba vreme da prežali gubitak.",
      },
      {
        question: "Kada je pravi trenutak za novog ljubimca?",
        answer:
          "Ne postoji univerzalni odgovor. Neki ljudi osete spremnost posle nekoliko nedelja, drugima treba godinu ili više. Ključno je da novi ljubimac ne bude pokušaj zamene, već nova, zasebna veza. Ako osećaš pritisak od okoline da 'nabaviš novog', terapeut ti može pomoći da doneseš odluku u skladu sa sopstvenim ritmom.",
      },
      {
        question: "Da li online terapija pomaže kod tuge za ljubimcem?",
        answer:
          "Da. Mnogi ljudi osećaju stid ili nelagodu kada govore o tuzi za ljubimcem 'uživo'. Online sesija pruža privatan i siguran prostor gde tvoja tuga neće biti minimizovana. Terapeut može pomoći sa tehnikama za upravljanje tugom, radom na krivici i postepenim prilagođavanjem na život bez ljubimca.",
      },
      {
        question: "Šta ako okolina ne razume moju tugu?",
        answer:
          "Komentari poput 'pa to je samo pas' ili 'nabavi drugog' su česti, ali ne umanjuju tvoj bol. Tuga za ljubimcem je legitimna i naučno potvrđena. Ako ti okolina ne pruža razumevanje, razgovor sa terapeutom koji validira tvoja osećanja može napraviti veliku razliku u procesu oporavka.",
      },
    ],
    passes: true,
  },

  // 52
  "finansijski-stres": {
    label: "Finansijski stres",
    title:
      "Online terapeuti za finansijski stres, anksioznost oko novca i dugova",
    searchTerm:
      "finansijski stres dugovi novac troškovi kredit nezaposlenost anksioznost oko novca briga oko novca finansijska anksioznost stres zbog dugova strah od siromaštva novčani problemi stres",
    therapistSearchTerms: ["Finansijski stres"],
    description:
      "Dugovi, nezaposlenost ili stalni pritisak oko novca mogu izazvati hronični stres koji utiče na san, odnose i svakodnevno funkcionisanje. Anksioznost oko finansija često dolazi sa osećajem stida i izolacije, pa je teško tražiti pomoć. Naši terapeuti ti pomažu da razumeš emocionalne obrasce vezane za novac, da smiriš anksioznost i izgradiš zdraviji odnos prema finansijama. Zakaži online sesiju i napravi prvi korak ka emocionalnom rasterećenju.",
    seoTitle: "Finansijski stres i anksioznost oko novca | Rešimo Probleme",
    seoDescription:
      "Anksioznost zbog novca, dugova ili nezaposlenosti? Online terapeuti za finansijski stres. Zakaži sesiju i smanji pritisak.",
    relatedConditions: ["stres", "anksioznost", "karijera-posao", "burnout"],
    faqItems: [
      {
        question: "Kako finansijski stres utiče na mentalno zdravlje?",
        answer:
          "Finansijski stres može izazvati hroničnu anksioznost, nesanicu, razdražljivost i osećaj bespomoćnosti. Istraživanja pokazuju da novčane brige aktiviraju iste stresne reakcije kao i fizička pretnja. Dugoročno, to može dovesti do depresije, problema u odnosima i čak fizičkih simptoma poput glavobolja i bolova u stomaku.",
      },
      {
        question: "Da li mi terapeut može pomoći ako imam dugove?",
        answer:
          "Terapeut ne rešava finansijske probleme direktno, ali ti pomaže da upravljaš anksioznošću i stresom koji dugovi izazivaju. Kada smanjiš emocionalnu blokadu, lakše donosiš racionalne odluke o budžetu i otplati. Mnogi ljudi izbegavaju čak i da pogledaju stanje računa, a terapija pomaže da prevaziđeš tu paralizu.",
      },
      {
        question: "Zašto osećam stid kad pričam o novcu?",
        answer:
          "Stid oko novca je izuzetno čest jer u našoj kulturi finansijski status često se izjednačava sa ličnom vrednošću. Terapeut ti pomaže da razdvojiš sopstvenu vrednost od finansijske situacije i da razumeš odakle ti uverenja o novcu dolaze, često iz porodice ili okruženja u kom si odrastao/la.",
      },
      {
        question:
          "Kako prepoznati da mi je potrebna pomoć za finansijski stres?",
        answer:
          "Znaci da stres prevazilazi tvoje kapacitete uključuju: konstantno razmišljanje o novcu, izbegavanje računa i obaveza, probleme sa snom, napetost u odnosima zbog finansija, osećaj beznadežnosti ili fizičke simptome poput glavobolje. Ako prepoznaješ nešto od ovoga, razgovor sa terapeutom može pomoći.",
      },
      {
        question: "Kako razgovarati o novcu sa partnerom bez konflikta?",
        answer:
          "Finansije su jedan od najčešćih izvora sukoba u vezama. Terapeut može pomoći da naučiš tehnike komunikacije o novcu: zakazivanje redovnih razgovora o budžetu, fokus na zajedničke ciljeve umesto na optužbe i razumevanje različitih stilova trošenja. Ključ je da novac postane zajednički projekat, a ne tabu tema.",
      },
      {
        question: "Da li online terapija pomaže kod anksioznosti oko novca?",
        answer:
          "Da. Online sesije su posebno pogodne jer smanjuju dodatni trošak prevoza i odsustva s posla. Terapeutske tehnike za finansijski stres uključuju kognitivno restrukturiranje negativnih misli o novcu, tehnike opuštanja za akutnu anksioznost i postepeno izlaganje situacijama koje izbegavaš (npr. pregled računa).",
      },
      {
        question: "Da li nezaposlenost može izazvati depresiju?",
        answer:
          "Nezaposlenost značajno povećava rizik od depresije i anksioznosti jer utiče na osećaj svrhe, identiteta i socijalne povezanosti. Ako se osećaš bezvoljno, povučeno ili beznadežno tokom perioda bez posla, razgovor sa terapeutom može pomoći da očuvaš mentalno zdravlje dok tražiš novo zaposlenje.",
      },
    ],
    passes: true,
  },

  // 53
  "digitalna-zavisnost": {
    label: "Digitalna zavisnost i gaming",
    guideLink: {
      href: "/blog/online-psihoterapija-vodic",
      label: "Pročitaj kako izgleda online psihoterapija",
    },
    therapistSearchTerms: [
      "Bolesti zavisnosti -alkohol, PAS, kocka, internet, videoigre, kupovina",
    ],
    directoryContent: {
      title: "Online terapeut za prekomerno korišćenje interneta i igrica",
      description:
        "Ako internet, igrice ili telefon zauzimaju više prostora nego što želiš, pregledaj profil terapeuta koji javno navodi rad sa zavisnostima, internetom i videoigrama. Prvi razgovor može pomoći da opišeš obrazac koji želiš da promeniš.",
      seoTitle: "Terapeut za digitalnu zavisnost online | Rešimo Probleme",
      seoDescription:
        "Pregledaj profil terapeuta koji navodi rad sa zavisnostima, internetom i videoigrama. Zakaži online razgovor na srpskom jeziku.",
      faqItems: [
        {
          question: "Šta mogu da iznesem na prvom razgovoru?",
          answer:
            "Možeš opisati kada i kako koristiš internet ili igrice, šta ti smeta i šta si već pokušao/la. Ne moraš unapred znati naziv problema.",
        },
        {
          question: "Da li terapeut mora da obeća potpun prestanak korišćenja?",
          answer:
            "Ne. Ciljevi se dogovaraju sa terapeutom i zavise od tvoje situacije, obaveza i razloga zbog kojih tražiš podršku.",
        },
        {
          question: "Kako biram terapeuta na ovoj stranici?",
          answer:
            "Pregledaj javno navedene teme i kvalifikacije u profilima. Ako imaš pitanje o iskustvu sa internetom ili videoigrama, postavi ga na prvom razgovoru.",
        },
        {
          question: "Da li je ovo stranica za hitnu pomoć?",
          answer:
            "Nije. Ako postoji neposredan rizik po tvoju bezbednost ili bezbednost druge osobe, obrati se lokalnoj hitnoj službi.",
        },
      ],
    },
    title:
      "Online terapeuti za digitalnu zavisnost, zavisnost od interneta i gaminga",
    searchTerm:
      "digitalna zavisnost internet gaming video igre kompulsivno korišćenje ekrani društvene mreže preterano korišćenje zavisnost od interneta zavisnost od telefona zavisnost od igrica zavisnost od tehnologije scrollovanje",
    description:
      "Preterano korišćenje interneta, društvenih mreža ili video igara može preći iz navike u kompulsivno ponašanje koje narušava san, produktivnost i odnose. Ako osećaš da ne možeš da prestaneš da skroluješ, da gaming zauzima sve više vremena ili da ti telefon izaziva anksioznost, nisi sam/a. Naši terapeuti ti pomažu da razumeš šta stoji iza kompulsivnog korišćenja ekrana i da izgradiš zdraviji digitalni balans. Zakaži online sesiju i napravi prvi korak ka promeni.",
    seoTitle:
      "Digitalna zavisnost: internet i gaming terapija | Rešimo Probleme",
    seoDescription:
      "Zavisnost od interneta, telefona ili igrica? Online terapeuti za digitalnu zavisnost. Zakaži sesiju i uspostavi zdrav balans.",
    relatedConditions: ["zavisnosti", "prokrastinacija-motivacija", "kockanje"],
    faqItems: [
      {
        question: "Kako prepoznati da imam problem sa digitalnom zavisnošću?",
        answer:
          "Znaci uključuju: nemogućnost da kontrolišeš vreme provedeno na ekranu, zanemarivanje obaveza i odnosa zbog interneta ili igrica, osećaj nemira ili razdražljivosti kada nisi online, korišćenje ekrana kao primarnog načina za regulaciju emocija i laganje o vremenu provedenom na telefonu ili računaru.",
      },
      {
        question: "Da li je zavisnost od društvenih mreža prava zavisnost?",
        answer:
          "Kompulsivno korišćenje društvenih mreža aktivira iste centre nagrade u mozgu kao i druge zavisnosti. Iako nije formalno klasifikovana kao dijagnoza u svim priručnicima, posledice su realne: anksioznost, poremećaj sna, upoređivanje sa drugima i smanjena produktivnost. Terapeut može pomoći da prepoznaš obrasce i uspostaviš granice.",
      },
      {
        question: "Kako smanjiti gaming bez odricanja od društvenog života?",
        answer:
          "Cilj terapije nije potpuno ukidanje igrica, već uspostavljanje kontrole. Terapeut ti pomaže da postaviš realistična vremenska ograničenja, razviješ alternativne aktivnosti za druženje i prepoznaš situacije koje te dovode do prekomernog igranja. Mnogi igrači koriste gaming za regulaciju stresa, pa rad na alternativnim strategijama za stres čini veliku razliku.",
      },
      {
        question:
          "Da li online terapija funkcioniše za nekoga ko je zavisan od ekrana?",
        answer:
          "Može zvučati paradoksalno, ali online terapija je efikasna jer se fokusira na promenu odnosa prema tehnologiji, ne na potpuno izbegavanje. Sesija je strukturirana, vremenski ograničena i usmerena na konkretan cilj, što se razlikuje od kompulsivnog scrollovanja. Terapeut ti pomaže da razlikuješ produktivno od neproduktivnog vremena na ekranu.",
      },
      {
        question: "Šta ako mi je telefon neophodan za posao?",
        answer:
          "Većina ljudi ne može potpuno eliminisati ekrane iz života, i to nije cilj. Terapeut ti pomaže da razviješ strategije poput razdvajanja radnog i ličnog korišćenja telefona, postavljanja digitalnih granica (npr. bez notifikacija posle radnog vremena) i pronalaženja trenutaka za odmor od ekrana tokom dana.",
      },
      {
        question:
          "Zašto deca i tinejdžeri posebno razvijaju digitalnu zavisnost?",
        answer:
          "Mladi mozgovi su posebno osetljivi na dopaminske nagrade koje pružaju igrice i društvene mreže. Algoritmi su dizajnirani da zadrže pažnju, a tinejdžeri često koriste ekrane za socijalizaciju i regulaciju emocija. Ako primetiš da tvoje dete zanemaruje školu, spavanje ili druženje uživo, razgovor sa terapeutom može pomoći celoj porodici.",
      },
      {
        question: "Da li dopamin detoks zaista funkcioniše?",
        answer:
          "Potpuni 'dopamin detoks' nema naučnu osnovu jer dopamin nije toksična supstanca. Umesto toga, terapija koristi pristup postepenog smanjivanja stimulacije: uklanjanje notifikacija, ograničavanje vremena na pojedinim aplikacijama i zamena ekranskih aktivnosti alternativama koje pružaju zadovoljstvo. Ovaj pristup je održiviji od radikalnog odricanja.",
      },
    ],
    passes: true,
  },

  // 54
  "imposter-sindrom": {
    label: "Imposter sindrom",
    title:
      "Online terapeuti za imposter sindrom, sindrom varalice i samosumnju",
    searchTerm:
      "imposter sindrom sindrom varalice osećaj prevare nesigurnost na poslu strah od razotkrivanja ne zaslužujem uspeh samosumnja profesionalna nesigurnost sindrom uljeza perfekcionizam strah od neuspeha",
    description:
      "Imposter sindrom (sindrom varalice) je osećaj da nisi dovoljno dobar/dobra uprkos dokazima o sposobnostima. Strah da ćeš biti 'razotkriven/a' kao prevarant, minimiziranje uspeha i pripisivanje dostignuća sreći pogađaju čak i najuspešnije ljude. Ovaj obrazac može sabotirati karijeru, odnose i samopouzdanje. Pronađi online terapeuta koji razume imposter sindrom i nauči da internalizuješ svoje uspehe kroz KBT tehnike, rad na samoproceni i prepoznavanje automatskih misli.",
    seoTitle: "Imposter sindrom: online terapija i podrška | Rešimo Probleme",
    seoDescription:
      "Osećaš da nisi dovoljno dobar/a uprkos uspesima? Online terapeuti za imposter sindrom. Zakaži sesiju i prevaziđi samosumnju.",
    relatedConditions: [
      "samopouzdanje",
      "perfekcionizam",
      "karijera-posao",
      "prokrastinacija-motivacija",
    ],
    faqItems: [
      {
        question: "Šta je imposter sindrom i kako ga prepoznati?",
        answer:
          "Imposter sindrom je psihološki obrazac gde osoba sumnja u svoje sposobnosti i ima stalni strah da će biti 'razotkrivena' kao nekompetentna, uprkos dokazima o uspehu. Prepoznaješ ga po minimiziranju uspeha ('imao/la sam sreće'), pripisivanju dostignuća okolnostima, stalnoj samosumnji pre prezentacija ili projekata i izbegavanju novih izazova iz straha da ćeš podbaciti.",
      },
      {
        question: "Zašto imposter sindrom pogađa uspešne ljude?",
        answer:
          "Paradoksalno, imposter sindrom često pogađa visoko uspešne ljude jer imaju više prilika da se uporede sa drugima i veća očekivanja od sebe. Svaki novi uspeh stvara pritisak da se on ponovi, a perfekcionizam pojačava strah od greške. Istraživanja pokazuju da čak 70% ljudi bar jednom u životu iskusi simptome imposter sindroma.",
      },
      {
        question: "Kako terapija pomaže kod imposter sindroma?",
        answer:
          "Terapeut ti pomaže da prepoznaš automatske misli koje potkrepljuju osećaj prevare i da ih zameniš realističnijom procenom sebe. KBT tehnike su posebno efikasne: rad na kognitivnim distorzijama, vođenje dnevnika uspeha, postepeno prihvatanje komplimenata i razvijanje tolerancije na nesavršenost. Cilj nije eliminisati sumnju, već naučiti da deluješ uprkos njoj.",
      },
      {
        question: "Koji tipovi imposter sindroma postoje?",
        answer:
          "Psihologinja Valerie Young identifikovala je pet tipova: perfekcionista (nikad dovoljno dobar), solista (mora sve sam/a), superheroj (dokazivanje kroz preopterećenost), ekspert (nikad dovoljno znanja) i prirodni genije (ako moram da se trudim, nisam dovoljno sposoban/na). Prepoznavanje svog tipa pomaže u ciljanijem radu na promeni.",
      },
      {
        question: "Da li imposter sindrom može uticati na karijeru?",
        answer:
          "Da. Imposter sindrom može dovesti do izbegavanja unapređenja, odbijanja novih projekata, preopterećivanja (kompenzacija strahom da nisi dovoljno dobar/a) ili potpunog sagorevanja. Mnogi ljudi ostaju na pozicijama ispod svojih mogućnosti jer veruju da 'ne zaslužuju' više. Rad sa terapeutom pomaže da prevaziđeš ove blokade.",
      },
      {
        question: "Kako razlikovati imposter sindrom od stvarne nesigurnosti?",
        answer:
          "Ključna razlika je u neskladu između objektivnih dokaza i tvoje percepcije. Ako imaš rezultate, pozitivne povratne informacije i iskustvo, ali i dalje osećaš da 'varaš' ljude oko sebe, to ukazuje na imposter sindrom. Terapeut ti pomaže da napraviš realniju sliku svojih sposobnosti i dostignuća.",
      },
      {
        question: "Da li se imposter sindrom može potpuno prevazići?",
        answer:
          "Imposter sindrom nije dijagnoza koja se 'izleči', već obrazac razmišljanja koji se menja. Većina ljudi nauči da prepozna kada ih samosumnja koči i razvije strategije za nošenje sa tim. Terapija značajno ubrzava ovaj proces jer ti daje konkretne alate umesto da se boriš sam/a sa unutrašnjim kritičarem.",
      },
    ],
    passes: true,
  },

  // 55
  "lgbtq-podrska": {
    label: "LGBTQ+ podrška",
    guideLink: {
      href: "/blog/online-psihoterapija-vodic",
      label: "Pročitaj kako izgleda online psihoterapija",
    },
    title:
      "Online terapeuti za LGBTQ+ osobe, siguran prostor za tvoj identitet",
    searchTerm:
      "lgbtq lgbt gej gay lezbejka biseksualno trans transrodno nebinarno coming out identitet seksualnost homoseksualnost queer diskriminacija internalizovana homofobija afirmativna terapija psihoterapeut lgbtq minority stress",
    therapistSearchTerms: [
      "LGBTQ+ pitanja prihvatanja; stigma; mentalno zdravlje-razvoj vestina",
      "LGBTQ+ afirmativna psihoterapija",
    ],
    description:
      "LGBTQ+ osobe se suočavaju sa specifičnim izazovima: internalizovana homofobija, coming out, diskriminacija, porodično odbacivanje i minority stress. Ovi izazovi mogu izazvati anksioznost, depresiju i osećaj izolacije. Pronađi terapeuta koji pruža LGBTQ+ afirmativan pristup i razume teme identiteta, samoprihvatanja, partnerskih odnosa i traume od diskriminacije. Zakaži online sesiju u sigurnom, neosuđujućem prostoru.",
    directoryContent: {
      title: "LGBTQ+ afirmativni terapeut online",
      description:
        "Ako želiš da razgovaraš o identitetu, coming outu, odnosima ili iskustvima stigme i pritiska, ovde možeš da pronađeš terapeuta koji na profilu navodi LGBTQ+ afirmativan rad. Uporedi opis rada, cenu i slobodne termine, pa zakaži online razgovor. Ne moraš unapred da znaš kako ćeš nazvati svoje iskustvo da bi počeo/la.",
      seoTitle: "LGBTQ+ psiholog online | Rešimo Probleme",
      seoDescription:
        "Pronađi LGBTQ+ afirmativnog psihologa online. Uporedi profile, cene i slobodne termine, pa zakaži razgovor koji ti odgovara.",
      faqItems: [
        {
          question: "Kako da pronađem LGBTQ+ afirmativnog terapeuta?",
          answer:
            "Na ovoj stranici možeš da pregledaš profile terapeuta koji navode LGBTQ+ afirmativan rad. Uporedi opis rada, cenu i slobodne termine pre zakazivanja.",
        },
        {
          question: "Da li moram da govorim o identitetu na prvom razgovoru?",
          answer:
            "Ne moraš. Na prvom razgovoru odlučuješ o čemu želiš da govoriš i možeš početi od teme koja ti je trenutno najvažnija.",
        },
        {
          question: "Mogu li da zakažem razgovor zbog coming outa ili odnosa?",
          answer:
            "Da. Možeš na profilu proveriti kako terapeut opisuje svoj rad, a na prvom razgovoru pitati da li je njegov pristup dobar izbor za tvoju temu.",
        },
        {
          question: "Šta mogu da proverim pre zakazivanja?",
          answer:
            "Na profilu možeš da proveriš opis rada terapeuta, cenu sesanse i slobodne termine. To ti pomaže da izabereš osobu sa kojom želiš da razgovaraš.",
        },
        {
          question: "Koliko košta online razgovor sa terapeutom?",
          answer:
            "Cenu svake sesanse vidiš na profilu terapeuta pre zakazivanja. Možeš uporediti više opcija i izabrati termin koji ti odgovara.",
        },
      ],
    },
    seoTitle: "LGBTQ+ afirmativna terapija online | Rešimo Probleme",
    seoDescription:
      "Pronađi LGBTQ+ afirmativnog terapeuta online. Siguran prostor za coming out, identitet, odnose i prevladavanje diskriminacije.",
    relatedConditions: ["odnosi", "usamljenost", "porodica"],
    faqItems: [
      {
        question: "Šta znači LGBTQ+ afirmativna terapija?",
        answer:
          "Afirmativna terapija podrazumeva da terapeut u potpunosti prihvata tvoj identitet kao validan, ne pokušava da ga 'promeni' i razume specifične izazove LGBTQ+ osoba. Fokus je na tvom blagostanju, a seksualna orijentacija ili rodni identitet se nikada ne tretiraju kao 'problem' koji treba rešiti.",
      },
      {
        question: "Kako terapija može pomoći sa coming out procesom?",
        answer:
          "Terapeut ti pomaže da istražiš svoje osećaje oko identiteta, pripremiš se za razgovore sa bliskim ljudima, razviješ strategije za različite reakcije okoline i izgradiš mrežu podrške. Coming out je proces, ne jednokratan događaj, i terapija pruža siguran prostor za svaki korak tog puta.",
      },
      {
        question: "Šta je internalizovana homofobija i može li se prevazići?",
        answer:
          "Internalizovana homofobija su negativna osećanja prema sopstvenom identitetu usvojena iz društva, porodice ili okruženja. Manifestuje se kao stid, samoosuđivanje ili skrivanje identiteta. U terapiji se radi na prepoznavanju ovih obrazaca, razumevanju njihovog porekla i postepenom razvijanju samoprihvatanja.",
      },
      {
        question: "Da li postoji terapija za parove iz LGBTQ+ zajednice?",
        answer:
          "Da. LGBTQ+ parovi se suočavaju sa specifičnim izazovima poput društvenog pritiska, nejednakog coming out statusa partnera, porodičnog neprihvatanja ili internalizovane stigme. Terapeut koji razume ove dinamike može pomoći u komunikaciji, rešavanju konflikata i jačanju veze bez pretpostavki zasnovanih na heteronormativnim modelima.",
      },
      {
        question: "Šta je minority stress i kako utiče na mentalno zdravlje?",
        answer:
          "Minority stress je hronični stres koji proizlazi iz pripadnosti marginalizovanoj grupi: stalna budnost, očekivanje diskriminacije, prikrivanje identiteta i iskustva odbacivanja. Istraživanja pokazuju da LGBTQ+ osobe imaju povišen rizik od anksioznosti i depresije upravo zbog ovog stresa. Terapija pomaže da razviješ strategije za nošenje sa ovim pritiskom.",
      },
      {
        question: "Da li terapeut mora biti LGBTQ+ osoba da bi me razumeo/la?",
        answer:
          "Ne mora. Ono što je ključno jeste da terapeut ima afirmativan pristup, edukaciju o LGBTQ+ temama i iskreno razumevanje za tvoja iskustva. Neki klijenti preferiraju terapeute iz zajednice, dok drugima je važnija stručnost i pristup. Na platformi možeš videti profil terapeuta i odabrati osobu sa kojom se osećaš bezbedno.",
      },
      {
        question: "Zašto je online terapija pogodna za LGBTQ+ osobe?",
        answer:
          "Online terapija pruža veću privatnost i dostupnost, posebno za osobe iz manjih sredina gde je stigma izraženija ili gde nema LGBTQ+ afirmativnih terapeuta. Ne moraš brinuti da ćeš biti viđen/a kako ulaziš u ordinaciju, a možeš izabrati terapeuta iz bilo kog grada, što značajno proširuje tvoje opcije.",
      },
    ],
    passes: true,
  },

  // 56
  usamljenost: {
    label: "Usamljenost i izolacija",
    title:
      "Online terapeuti za usamljenost, socijalnu izolaciju i osećaj nepripadanja",
    searchTerm:
      "usamljenost izolacija socijalna izolacija nemam prijatelje osećam se sam sama usamljen usamljena nedostatak veza društvena anksioznost povlačenje usamljenost u braku kako pobediti usamljenost samoća",
    description:
      "Usamljenost nije samo fizička izolacija. Možeš se osećati usamljeno i okružen/a ljudima ako veze nisu duboke i smislene. Hronična usamljenost povećava rizik od depresije, anksioznosti i čak fizičkih zdravstvenih problema. Ako osećaš da nemaš s kim da podeliš ono što ti je važno, nisi sam/a u tome. Pronađi online terapeuta koji će ti pomoći da razumeš uzroke usamljenosti i napraviš konkretne korake ka izgradnji smislenih odnosa.",
    seoTitle: "Usamljenost i izolacija: online terapija | Rešimo Probleme",
    seoDescription:
      "Osećaš se usamljeno ili izolovano? Online terapeuti za usamljenost. Zakaži sesiju i izgradi smislene odnose i osećaj pripadanja.",
    relatedConditions: [
      "odnosi",
      "socijalna-anksioznost",
      "depresija",
      "migracije-dijaspora",
    ],
    faqItems: [
      {
        question: "Zašto se osećam usamljeno iako imam ljude oko sebe?",
        answer:
          "Usamljenost je subjektivni osećaj nedostatka smislene povezanosti, ne samo fizičkog prisustva drugih. Možeš imati mnogo površnih kontakata, ali osećati da ti nedostaje dubina, razumevanje ili autentičnost u odnosima. Terapija pomaže da identifikuješ šta ti zapravo nedostaje i koji obrasci te sprečavaju da uspostaviš bliskost.",
      },
      {
        question: "Da li je usamljenost zaista zdravstveni problem?",
        answer:
          "Da. Istraživanja pokazuju da hronična usamljenost povećava rizik od kardiovaskularnih bolesti, slabi imunitet i može biti jednako štetna kao pušenje 15 cigareta dnevno. Svetska zdravstvena organizacija prepoznaje usamljenost kao rastući javnozdravstveni izazov. Upravo zato je važno raditi na njoj, a ne samo čekati da prođe.",
      },
      {
        question: "Kako mogu da počnem da gradim nove odnose?",
        answer:
          "Terapeut ti pomaže da počneš sa malim, konkretnim koracima: pridruživanje grupi sa zajedničkim interesovanjem, produbljivanje postojećih poznanstava ili vežbanje iniciranja kontakta. Jednako važno je raditi na unutrašnjim barijerama poput straha od odbacivanja, uverenja da nisi dovoljno zanimljiv/a ili navike da se povlačiš pre nego što bliskost postane nelagodna.",
      },
      {
        question: "Zašto se osećam usamljeno u braku ili vezi?",
        answer:
          "Usamljenost u vezi je česta i nastaje kada se emocionalna bliskost izgubi, kada se komunikacija svede na logistiku ili kada se osećaš neviđeno i neshvaćeno od partnera. Ovo ne znači da veza ne vredi, već da postoji prostor za rad na ponovnom povezivanju. Individualna ili partnerska terapija može pomoći da obnoviš osećaj bliskosti.",
      },
      {
        question: "Da li online terapija može pomoći sa usamljenošću?",
        answer:
          "Da. Iako može delovati paradoksalno, online terapija pruža siguran prostor za vežbanje povezivanja i razumevanje obrazaca koji te drže u izolaciji. Terapeut ti pomaže da razviješ veštine koje ćeš primeniti u svakodnevnom životu, od otvaranja u razgovorima do postavljanja granica koje omogućavaju bliskost.",
      },
      {
        question: "Da li usamljenost prolazi sama od sebe?",
        answer:
          "Situaciona usamljenost (npr. nakon preseljenja ili raskida) može se smanjiti vremenom. Međutim, hronična usamljenost koja traje mesecima ili godinama obično ima dublje uzroke: naučeni obrasci izbegavanja, nisko samopouzdanje ili socijalna anksioznost. U tim slučajevima, terapija značajno ubrzava promenu jer pomaže da prepoznaš i promeniš obrasce koji te drže u izolaciji.",
      },
      {
        question: "Kako prepoznati da mi je potrebna pomoć za usamljenost?",
        answer:
          "Ako se osećaš hronično izolovano, ako izbegavaš društvene situacije, ako ti nedostaje motivacija za kontakte, ako osećaš da te niko ne razume ili ako usamljenost utiče na san, raspoloženje i svakodnevno funkcionisanje, razgovor sa terapeutom može pomoći. Ne moraš čekati da postane nepodnošljivo.",
      },
    ],
    passes: true,
  },

  // 57
  kodependencija: {
    label: "Kodependencija",
    title:
      "Online terapeuti za kodependenciju, zavisne odnose i postavljanje granica",
    searchTerm:
      "kodependencija zavisni odnosi toksični odnosi prekomerna briga ugađanje drugima gubitak sebe u vezi nezdrava vezanost kontrola partnera spašavanje drugih postavljanje granica people pleasing",
    description:
      "Kodependencija je obrazac odnosa gde tvoja vrednost zavisi od brige o drugima, često na uštrb sopstvenih potreba. Teškoća da kažeš 'ne', preuzimanje odgovornosti za tuđe emocije, ostajanje u nezdravim odnosima i stalni osećaj da nikad ne daješ dovoljno mogu iscrpeti tvoju energiju i identitet. Pronađi online terapeuta koji razume kodependenciju i nauči da postaviš zdrave granice, vratiš fokus na sebe i voliš druge bez gubljenja sebe.",
    seoTitle: "Kodependencija: online terapija i podrška | Rešimo Probleme",
    seoDescription:
      "Gubiš sebe u brizi o drugima? Online terapeuti za kodependenciju. Zakaži sesiju i nauči da postaviš granice bez krivice.",
    relatedConditions: [
      "problemi-vezivanja",
      "emocionalno-zlostavljanje",
      "odnosi",
      "samopouzdanje",
    ],
    faqItems: [
      {
        question: "Kako prepoznati kodependenciju?",
        answer:
          "Znakovi kodependencije uključuju: teškoću da kažeš 'ne', osećaj odgovornosti za tuđe emocije, strah od napuštanja, stalnu potrebu za odobravanjem, zanemarivanje sopstvenih potreba i ostajanje u odnosima koji te povređuju. Ako prepoznaješ ove obrasce, to ne znači da si 'loša osoba', već da si naučio/la obrasce koje je moguće promeniti.",
      },
      {
        question: "Odakle dolazi kodependencija?",
        answer:
          "Kodependencija se najčešće razvija u detinjstvu, u porodicama gde je postojala zavisnost, emocionalna nestabilnost ili gde je dete moralo da preuzme ulogu odraslog. U takvom okruženju naučiš da tvoja vrednost zavisi od brige o drugima i da su tvoje potrebe manje važne. Ovi obrasci se zatim ponavljaju u odraslim odnosima.",
      },
      {
        question: "Da li mogu da budem brižan/a a da nisam kodependentan/a?",
        answer:
          "Apsolutno. Zdrava briga znači podršku bez preuzimanja odgovornosti za tuđe odluke, ljubav bez gubljenja sopstvenog identiteta i postavljanje granica bez krivice. U terapiji učiš da razlikuješ brižnost od kodependentnog spašavanja i da razviješ odnose zasnovane na uzajamnosti.",
      },
      {
        question: "Kako terapija pomaže kod kodependencije?",
        answer:
          "Terapeut ti pomaže da prepoznaš kodependentne obrasce, razumeš njihovo poreklo i razviješ nove načine ponašanja u odnosima. To uključuje vežbanje postavljanja granica, učenje da tolerišeš nelagodu kada kažeš 'ne', rad na samovrednovanju koje ne zavisi od drugih i razvijanje zdravijeg odnosa prema sopstvenim potrebama.",
      },
      {
        question: "Kako postavljati granice bez osećaja krivice?",
        answer:
          "Krivica pri postavljanju granica je normalna, posebno ako si ceo život učio/la da su tuđe potrebe važnije od tvojih. Terapeut ti pomaže da razumeš da granice nisu sebičnost, već zdrav deo svakog odnosa. Rad uključuje postepeno vežbanje malih granica, prepoznavanje razlike između zdrave i nezdrave krivice i jačanje unutrašnjeg uverenja da imaš pravo na sopstvene potrebe.",
      },
      {
        question: "Da li kodependencija utiče samo na romantične veze?",
        answer:
          "Ne. Kodependentni obrasci se mogu javiti u svim vrstama odnosa: sa roditeljima, decom, prijateljima, kolegama ili čak prema šefu. Ako primećuješ da u različitim odnosima preuzmaš ulogu 'spasioca', da ti je teško da kažeš 'ne' ili da tvoja raspoloženja zavise od tuđih, terapija može pomoći da promeniš ove obrasce globalno.",
      },
      {
        question: "Koliko dugo traje rad na kodependenciji?",
        answer:
          "Kodependencija je duboko ukorenjen obrazac, pa promena zahteva vreme i strpljenje. Mnogi klijenti primete prve pomake nakon 8 do 12 sesija, posebno u sposobnosti da prepoznaju obrasce i postave osnovne granice. Dugoročnija promena, poput stabilnog osećaja sopstvene vrednosti nezavisnog od drugih, obično zahteva duži terapijski proces.",
      },
    ],
    passes: true,
  },

  // 58
  "problemi-vezivanja": {
    label: "Stilovi vezivanja",
    guideLink: {
      href: "/blog/stilovi-vezivanja-u-vezi",
      label: "Pročitaj vodič o stilovima vezivanja u vezi",
    },
    title:
      "Online terapeuti za probleme sa vezivanjem, anksiozni i izbegavajući stil",
    searchTerm:
      "problemi vezivanja attachment stil vezivanja izbegavajući anksiozni strah od bliskosti strah od napuštanja intimnost emocionalna nedostupnost stilovi vezivanja sigurni stil preokupirani dezorganizovani afektivno vezivanje",
    description:
      "Stil vezivanja formiran u detinjstvu utiče na odrasle odnose: kako se osećaš u bliskosti, kako reaguješ na konflikt i da li veruješ partnerima. Anksiozni stil donosi strah od napuštanja i stalnu potrebu za potvrdom, dok izbegavajući stil znači distancu od emocija i povlačenje kada veza postane intenzivna. Pronađi online terapeuta koji razume stilove vezivanja i pomozi sebi da izgradiš sigurnije, stabilnije odnose.",
    seoTitle:
      "Stil vezivanja: online terapija za sigurne odnose | Rešimo Probleme",
    seoDescription:
      "Anksiozni ili izbegavajući stil vezivanja sabotira tvoje veze? Online terapeuti za probleme sa vezivanjem. Zakaži sesiju.",
    relatedConditions: [
      "odnosi",
      "kodependencija",
      "za-parove",
      "trauma-iz-detinjstva",
    ],
    faqItems: [
      {
        question: "Šta je stil vezivanja i kako utiče na odnose?",
        answer:
          "Stil vezivanja je obrazac kako se povezuješ sa drugima, formiran u ranom detinjstvu kroz odnos sa roditeljima ili starateljima. Siguran stil znači komfor sa bliskošću i nezavisnošću; anksiozni donosi strah od napuštanja i stalnu potrebu za potvrdom; izbegavajući znači distancu od emocija; dezorganizovani kombinuje oba obrasca. Ovi stilovi se ponavljaju u odraslim vezama dok ih svesno ne promenimo.",
      },
      {
        question: "Da li se stil vezivanja može promeniti?",
        answer:
          "Da. Iako je formiran rano, stil vezivanja nije fiksan. Kroz terapiju i korektivna iskustva u odnosima možeš razviti ono što psiholozi nazivaju 'zarađena sigurnost'. To znači učenje novih obrazaca reagovanja, postepeno građenje poverenja u bliskost i sposobnost da tolerišeš nelagodu bez automatskog povlačenja ili hvatanja za partnera.",
      },
      {
        question: "Kako prepoznati izbegavajući stil vezivanja?",
        answer:
          "Izbegavajući stil karakteriše nelagoda sa emocionalnom bliskošću, snažna potreba za nezavisnošću, povlačenje kada partner traži više povezanosti i racionalizovanje zašto veze 'nisu za tebe'. Često izgleda kao samodovolјnost, ali ispod je strah od povrede koji je naučen u detinjstvu. Terapeut ti pomaže da prepoznaš ovaj obrazac i postepeno razviješ toleranciju za bliskost.",
      },
      {
        question: "Kako prepoznati anksiozni stil vezivanja?",
        answer:
          "Anksiozni stil vezivanja manifestuje se kao strah od napuštanja, stalna potreba za potvrdom od partnera, preterano analiziranje poruka i ponašanja, osećaj panike kada partner nije dostupan i tendencija da se previše brzo zbližavaš. U terapiji učiš da razviješ unutrašnju sigurnost koja ne zavisi od stalnog partnerovog uveravanja.",
      },
      {
        question:
          "Šta se dešava kada su anksiozni i izbegavajući partner zajedno?",
        answer:
          "Ovo je jedan od najčešćih i najizazovnijih parova obrazaca: anksiozni partner traži bliskost, izbegavajući se povlači, što pojačava anksioznost prvog i potrebu drugog za distancom. Ovaj ciklus se naziva 'goni-beži' dinamika. Terapija (individualna ili partnerska) pomaže da oba partnera razumeju svoje obrasce i prekinu ovaj krug.",
      },
      {
        question: "Kako terapija pomaže kod problema sa vezivanjem?",
        answer:
          "Terapeut pruža ono što se naziva 'sigurna baza', odnos u kom možeš istraživati svoje obrasce bez straha od osuđivanja. Rad uključuje razumevanje porekla tvog stila vezivanja, prepoznavanje automatskih reakcija u odnosima, učenje novih načina komuniciranja potreba i postepeno vežbanje bliskosti koja se pre činila nelagodnom.",
      },
      {
        question:
          "Da li online terapija funkcioniše za rad na stilovima vezivanja?",
        answer:
          "Da. Sam terapijski odnos je ključni alat za promenu stila vezivanja, bez obzira na format sesije. Online terapija pruža dodatnu prednost jer je dostupnija i manje zastrašujuća za osobe sa izbegavajućim stilom, dok redovnost sesija pruža strukturu koja pomaže osobama sa anksioznim stilom.",
      },
    ],
    passes: true,
  },

  // 59
  hipohondrija: {
    label: "Hipohondrija (zdravstvena anksioznost)",
    therapistSearchTerms: ["Anksioznost, fobije, zdravstvena anksioznost"],
    guideLink: {
      href: "/blog/hipohondrija-simptomi-strah-od-bolesti-i-sta-pomaze",
      label: "Pročitaj vodič o zdravstvenoj anksioznosti",
    },
    directoryContent: {
      title: "Online terapeut za zdravstvenu anksioznost",
      description:
        "Ako te briga o zdravlju, proveravanje simptoma ili stalno traženje potvrde iscrpljuju, ovde možeš pregledati profil terapeuta koji javno navodi zdravstvenu anksioznost. Razgovor ne zamenjuje medicinski pregled ili savet lekara.",
      seoTitle: "Terapeut za zdravstvenu anksioznost | Rešimo Probleme",
      seoDescription:
        "Pregledaj profil terapeuta koji navodi zdravstvenu anksioznost i zakaži online razgovor na srpskom jeziku.",
      faqItems: [
        {
          question:
            "Da li je zdravstvena anksioznost dijagnoza sa ove stranice?",
          answer:
            "Ne. Stranica služi za izbor terapeuta. Terapeut i lekar mogu sa tobom razgovarati o tvojoj situaciji u okviru svojih uloga.",
        },
        {
          question: "Da li terapija zamenjuje pregled kod lekara?",
          answer:
            "Ne. Novi, jaki ili zabrinjavajući telesni simptomi zahtevaju medicinsku procenu. Terapeut ne procenjuje fizičko zdravstveno stanje preko ovog direktorijuma.",
        },
        {
          question: "Kako da izaberem terapeuta?",
          answer:
            "U profilu pogledaj javno navedene teme i kvalifikacije, pa na prvoj sesiji pitaj kako terapeut radi sa brigom o zdravlju.",
        },
        {
          question: "Šta ako se osećam preplavljeno?",
          answer:
            "Ako si u neposrednoj opasnosti ili imaš misli da povrediš sebe, obrati se lokalnoj hitnoj službi ili osobi od poverenja.",
        },
      ],
    },
    title: "Online terapija za hipohondriju: oslobodi se straha od bolesti",
    searchTerm:
      "hipohondrija zdravstvena anksioznost strah od bolesti strah od raka strah od smrti guglanje simptoma cyberchondria briga o zdravlju opsesija zdravljem kako se leci hipohondrija hipohondrija lecenje hipohondrija simptomi kompulsivno proveravanje tolerancija na neizvesnost",
    description:
      "Zdravstvena anksioznost (hipohondrija) drži te u začaranom krugu straha od bolesti: svaki simptom postaje dokaz najgoreg scenarija, guglanje donosi privremeno olakšanje pa veću paniku, a ni negativni nalazi ne uklanjaju strah. Pronađi online terapeuta specijalizovanog za zdravstvenu anksioznost i nauči KBT tehnike za prekidanje kompulsivnog proveravanja i razvijanje tolerancije na neizvesnost.",
    seoTitle: "Online KBT terapija za hipohondriju | Rešimo Probleme",
    seoDescription:
      "Strah od bolesti ti ne da mira? Pronađi online terapeuta za zdravstvenu anksioznost i nauči da živiš bez stalnog straha.",
    relatedConditions: ["anksioznost", "okp", "panika"],
    faqItems: [
      {
        question:
          "Kako da znam da li imam zdravstvenu anksioznost ili pravu brigu?",
        answer:
          "Kod zdravstvene anksioznosti briga je neproporcionalna simptomima, negativni nalazi ne donose trajno olakšanje, stalno tražiš uveravanje i proveravaš telo ili internet, a strah se vraća i nakon što lekari kažu da si zdrav/a. Prava briga obično prestaje kad dobiješ informaciju.",
      },
      {
        question: "Zašto guglanje simptoma pogoršava stvar?",
        answer:
          "Guglanje je kompulsija koja kratkoročno smanjuje anksioznost, ali dugoročno je pojačava. Internet uvek prikazuje najgore scenarije, a privremeno olakšanje te uči da moraš stalno proveravati, čime se održava ciklus anksioznosti. Ovo se ponekad naziva kiberohondrija (cyberchondria).",
      },
      {
        question: "Kako KBT terapija pomaže kod zdravstvene anksioznosti?",
        answer:
          "KBT terapija pomaže da prepoznaješ automatske katastrofične misli, postepeno smanjuješ proveravanje i guglanje, razviješ toleranciju na neizvesnost i naučiš da živiš sa normalnom nesigurnošću bez stalnog straha. Istraživanja potvrđuju da je KBT najefikasniji pristup za hipohondriju.",
      },
      {
        question: "Da li hipohondrija može izazvati prave fizičke simptome?",
        answer:
          "Da. Anksioznost aktivira nervni sistem i može izazvati ubrzan rad srca, napetost mišića, vrtoglavicu, mučninu ili bol u grudima. Ovi simptomi su stvarni, ali nisu znak ozbiljne bolesti, već posledica stresne reakcije tela. Terapija pomaže da razumeš ovu vezu.",
      },
      {
        question: "Kako prestati biti hipohondar?",
        answer:
          "Ključ je postepeno smanjivanje kompulsivnog ponašanja: guglanja simptoma, traženja uveravanja od lekara ili bliskih osoba i stalnog proveravanja tela. Terapeut te vodi kroz ovaj proces korak po korak, uz razvijanje zdravijeg odnosa prema neizvesnosti.",
      },
      {
        question:
          "Da li je hipohondrija povezana sa OKP (opsesivno-kompulzivnim poremećajem)?",
        answer:
          "Postoje sličnosti: obe uključuju nametljive misli i kompulsivno ponašanje (proveravanje, traženje uveravanja). Razlika je u fokusu, kod hipohondrije su to bolesti, dok su kod OKP teme šire. Terapeut može precizno razlikovati i prilagoditi tretman.",
      },
      {
        question:
          "Kako online terapija funkcioniše za zdravstvenu anksioznost?",
        answer:
          "Video sesije sa terapeutom su jednako efikasne kao sesije uživo za KBT tretman zdravstvene anksioznosti. Prednost online formata je što možeš raditi iz sigurnosti svog doma, bez dodatnog stresa od odlaska u ordinaciju.",
      },
    ],
    passes: true,
  },

  // 60
  "hronicna-bolest": {
    label: "Hronična bolest i bol",
    guideLink: {
      href: "/blog/online-psihoterapija-vodic",
      label: "Pročitaj kako izgleda online psihoterapija",
    },
    therapistSearchTerms: [
      "Hronicna bolest / Hronicna bolest clana porodice",
      "Hronične bolesti",
    ],
    directoryContent: {
      title: "Online terapeut za podršku uz hroničnu bolest",
      description:
        "Život sa hroničnom bolešću ili bolešću bliske osobe može otvoriti pitanja stresa, odnosa, promena u svakodnevnici i osećaja gubitka. Ovde možeš pregledati profile terapeuta koji javno navode rad sa hroničnim bolestima.",
      seoTitle: "Terapeut za podršku uz hroničnu bolest | Rešimo Probleme",
      seoDescription:
        "Pregledaj profile terapeuta koji navode rad sa hroničnim bolestima i zakaži online razgovor na srpskom jeziku.",
      faqItems: [
        {
          question: "Da li je ovo medicinska podrška za bolest?",
          answer:
            "Ne. Terapeut ne leči fizičku bolest niti menja plan lečenja. Za medicinska pitanja obrati se svom lekaru ili drugom zdravstvenom stručnjaku.",
        },
        {
          question: "O čemu mogu razgovarati sa terapeutom?",
          answer:
            "Možeš govoriti o promenama u svakodnevnici, odnosima, stresu, neizvesnosti ili iskustvu života sa bolešću. Fokus dogovarate na sesiji.",
        },
        {
          question: "Da li stranica uključuje i podršku članu porodice?",
          answer:
            "Jedan od javno navedenih profila uključuje hroničnu bolest člana porodice. Pročitaj profile i proveri sa terapeutom da li je tvoja tema u njegovom ili njenom okviru rada.",
        },
        {
          question: "Kako biram terapeuta?",
          answer:
            "Pregledaj javno navedene teme i kvalifikacije, a na prvom razgovoru postavi pitanja koja su važna za tvoju situaciju.",
        },
      ],
    },
    title: "Online terapija za hroničnu bolest: podrška u životu sa dijagnozom",
    searchTerm:
      "hronična bolest hronični bol dijagnoza život sa bolešću prihvatanje bolesti invaliditet autoimune bolesti fibromijalgija multipla skleroza dijabetes psihološka podrška hronična bolest upravljanje bolom ACT terapija mindfulness bol tuga za zdravljem",
    description:
      "Život sa hroničnom bolešću donosi izazove koji prevazilaze fizičke simptome: tuga za izgubljenim zdravljem, prilagođavanje novoj realnosti, neizvesnost i promene u identitetu. Pronađi online terapeuta za podršku u prihvatanju dijagnoze, upravljanju hroničnim bolom kroz psihološke tehnike (ACT, mindfulness) i pronalaženju kvaliteta života uprkos bolesti.",
    seoTitle: "Online terapija za hroničnu bolest | Rešimo Probleme",
    seoDescription:
      "Živiš sa hroničnom bolešću ili bolom? Pronađi online terapeuta za podršku u prihvatanju dijagnoze i kvalitetan život.",
    relatedConditions: ["zivotne-promene", "zalovanje", "depresija"],
    faqItems: [
      {
        question: "Kako terapija može pomoći ako je bolest fizička?",
        answer:
          "Telo i um su povezani. Terapija pomaže da upravljaš stresom koji pogoršava simptome, obradiš tugu za izgubljenim zdravljem, razviješ tehnike za upravljanje bolom i prilagodiš se na novi identitet. Mnogi pacijenti prijavljuju bolji kvalitet života nakon psihoterapije.",
      },
      {
        question: "Da li je normalno tugovati za zdravljem?",
        answer:
          "Apsolutno. Dijagnoza hronične bolesti je gubitak: gubitak zdravlja, izvesnosti, planiranog života. Tugovanje je normalan i potreban proces. Terapeut ti pruža prostor da izraziš tugu bez pritiska da budeš 'pozitivan/na' ili 'jak/a'.",
      },
      {
        question: "Kako objasniti drugima šta prolazim?",
        answer:
          "Mnogi sa hroničnom bolešću se osećaju neshvaćeno, posebno ako bolest nije vidljiva spolja. U terapiji radiš na postavljanju granica, komunikaciji potreba i pronalaženju zajednice ljudi koji razumeju tvoje iskustvo.",
      },
      {
        question:
          "Koje psihološke tehnike pomažu u upravljanju hroničnim bolom?",
        answer:
          "ACT (terapija prihvatanjem i posvećenošću) i mindfulness tehnike pomažu da promeniš odnos prema bolu, umesto da se boriš protiv njega. KBT pomaže u prepoznavanju misaonih obrazaca koji pojačavaju doživljaj bola. Istraživanja pokazuju da ovi pristupi mogu značajno poboljšati kvalitet života.",
      },
      {
        question:
          "Da li hronična bolest može izazvati depresiju ili anksioznost?",
        answer:
          "Da, i to je veoma čest proces. Studije pokazuju da osobe sa hroničnim bolestima imaju dva do tri puta veći rizik od depresije. Bol, ograničenja, neizvesnost i socijalna izolacija doprinose pogoršanju mentalnog zdravlja. Terapija adresira obe strane istovremeno.",
      },
      {
        question:
          "Kako se nositi sa osećajem krivice što ne mogu da funkcionišem kao pre?",
        answer:
          "Krivica je česta kod hroničnih bolesti, posebno kad ne možeš ispuniti očekivanja (svoja ili tuđa). Terapeut pomaže da preispitaš nerealna očekivanja, razviješ saosećanje prema sebi i pronađeš nove načine da živiš ispunjeno u okviru svojih mogućnosti.",
      },
      {
        question:
          "Zašto je online terapija pogodna za osobe sa hroničnom bolešću?",
        answer:
          "Online format uklanja fizičke barijere: ne moraš putovati, čekati u čekaonici niti se prilagođavati lošim danima. Možeš zakazati sesiju iz kreveta ako je to potrebno, čime terapija postaje dostupna i u periodima pogoršanja simptoma.",
      },
    ],
    passes: true,
  },

  // 61
  "telesna-slika": {
    label: "Telesna slika i odnos prema telu",
    title:
      "Online terapija za telesnu sliku: izgradi zdraviji odnos prema telu",
    searchTerm:
      "telesna slika body image nezadovoljstvo telom kompleksi izgled mržnja prema telu dismorfija dismorfofobija selfi društvene mreže poređenje filteri telesna dismorfija odnos prema telu perfekcionizam izgled",
    description:
      "Nezadovoljstvo sopstvenim telom utiče na samopouzdanje, odnose i svakodnevno funkcionisanje, od izbegavanja ogledala do opsesivnog vežbanja. Društvene mreže i filteri dodatno pogoršavaju nerealna očekivanja. Pronađi online terapeuta koji radi sa body image temama i izgradi zdraviji odnos prema sopstvenom izgledu, bez stalnog ratovanja sa ogledalom.",
    seoTitle: "Online terapija za telesnu sliku | Rešimo Probleme",
    seoDescription:
      "Muči te nezadovoljstvo sopstvenim izgledom? Pronađi online terapeuta i izgradi zdraviji odnos prema svom telu.",
    relatedConditions: ["poremecaji-ishrane", "samopouzdanje", "depresija"],
    faqItems: [
      {
        question: "Da li je normalno mrzeti svoje telo?",
        answer:
          "Nezadovoljstvo telom je rasprostranjeno u kulturi koja promoviše nerealne standarde, ali to ne znači da je zdravo ili neizbežno. U terapiji možeš naučiti drugačiji odnos prema telu, ne nužno ljubav, ali prihvatanje i prestanak stalnog ratovanja sa ogledalom.",
      },
      {
        question: "Kako društvene mreže utiču na telesnu sliku?",
        answer:
          "Algoritmi favorizuju idealizovane slike, filteri normalizuju nerealan izgled, a stalno poređenje aktivira osećaj nedovoljnosti. U terapiji radiš na kritičkom odnosu prema sadržaju koji konzumiraš i smanjenju štete od poređenja.",
      },
      {
        question:
          "Koja je razlika između nezadovoljstva telom i poremećaja ishrane?",
        answer:
          "Nezadovoljstvo telom je osećaj, dok poremećaji ishrane uključuju ozbiljne promene u ponašanju (restrikcija, prejedanje, čišćenje). Nezadovoljstvo može voditi u poremećaj ishrane, zato je važno raditi na njemu preventivno.",
      },
      {
        question: "Šta je telesna dismorfija (dismorfofobija)?",
        answer:
          "Telesna dismorfija je stanje u kojem se osoba opsesivno fokusira na percipirane nedostatke u izgledu koje drugi ne primećuju ili smatraju minimalnim. Razlikuje se od uobičajenog nezadovoljstva po intenzitetu, jer može ozbiljno narušiti svakodnevno funkcionisanje. KBT je najefikasniji tretman.",
      },
      {
        question: "Da li muškarci takođe imaju probleme sa telesnom slikom?",
        answer:
          "Da, nezadovoljstvo telom pogađa sve polove. Kod muškaraca se često manifestuje kao opsesija mišićnom masom, visinom ili gubitkom kose. Muškarci ređe traže pomoć jer se telesna slika često posmatra kao 'ženski problem', što otežava prepoznavanje.",
      },
      {
        question: "Kako perfekcionizam utiče na odnos prema telu?",
        answer:
          "Perfekcionizam i nezadovoljstvo telom su usko povezani. Nerealni standardi lepote postaju 'pravilo' koje nikad ne možeš ispuniti, što stvara ciklus nezadovoljstva. Terapija pomaže da preispitaš ove standarde i razviješ fleksibilniji odnos prema sebi.",
      },
      {
        question: "Kako online terapija može pomoći sa telesnom slikom?",
        answer:
          "Online format je posebno pogodan za rad na telesnoj slici jer si u sigurnom prostoru svog doma. Ne moraš brinuti o tome kako izgledaš u čekaonici ili pred drugim ljudima. Fokus je na tome kako se osećaš, ne kako izgledaš.",
      },
    ],
    passes: true,
  },

  // 62
  mobing: {
    label: "Mobing (zlostavljanje na poslu)",
    title: "Online terapija za mobing: oporavi se od zlostavljanja na poslu",
    searchTerm:
      "mobing zlostavljanje na poslu šikaniranje workplace bullying toksično radno okruženje šef zlostavlja psihičko nasilje na poslu mobbing mobing na poslu iskustva kako dokazati mobing oporavak od mobinga mobing posledice",
    description:
      "Mobing je sistematsko psihičko zlostavljanje na radnom mestu koje uništava samopouzdanje, zdravlje i karijeru. Žrtve često sumnjaju u sebe i pitaju se da li preuveličavaju. Pronađi online terapeuta koji validira tvoje iskustvo, pomaže u oporavku samopouzdanja i podržava te u donošenju odluka o daljim koracima.",
    seoTitle: "Online terapija za mobing | Rešimo Probleme",
    seoDescription:
      "Doživljavaš zlostavljanje na poslu? Pronađi online terapeuta za podršku u oporavku od mobinga i vraćanju samopouzdanja.",
    relatedConditions: [
      "karijera-posao",
      "emocionalno-zlostavljanje",
      "burnout",
      "stres",
    ],
    faqItems: [
      {
        question: "Kako da znam da li je mobing ili samo težak šef?",
        answer:
          "Mobing je sistematski obrazac: ponavljano ponižavanje, izolacija, nerealni zadaci, uskraćivanje informacija ili javno kritikovanje. Težak šef ima loš stil, ali mobing ima nameru da te slomi. Ako se pitaš svaki dan da li si dovoljno dobar/a, verovatno je više od 'teškog šefa'.",
      },
      {
        question: "Zašto se osećam kao da je moja krivica?",
        answer:
          "Internalizacija krivice je česta reakcija na zlostavljanje. Moberi često koriste gaslighting, osporavaju tvoju percepciju realnosti dok ne počneš sumnjati u sopstvenu procenu. U terapiji radiš na vraćanju poverenja u sebe i oslobađanju od lažne krivice.",
      },
      {
        question: "Da li treba da dam otkaz?",
        answer:
          "Ta odluka je tvoja i zavisi od mnogih faktora: finansija, tržišta rada, dokaza za prijavu, tvog zdravlja. Terapeut ti pomaže da sagledaš opcije bez pritiska, napraviš plan i doneseš odluku koja je najbolja za tebe dugoročno.",
      },
      {
        question: "Koje su psihološke posledice mobinga?",
        answer:
          "Mobing može izazvati anksioznost, depresiju, nesanicu, gubitak samopouzdanja, i u težim slučajevima simptome posttraumatskog stresnog poremećaja (PTSP). Mnoge žrtve razvijaju i fizičke simptome: glavobolje, probleme sa varenjem, hroničan umor. Što pre potražiš podršku, oporavak je brži.",
      },
      {
        question: "Da li mobing od strane kolega zahteva drugačiji pristup?",
        answer:
          "Mobing može dolaziti od šefa (vertikalni) ili kolega (horizontalni). Kada zlostavljaju kolege, često je teže prepoznati jer nema jasne hijerarhije moći. U terapiji radimo na razumevanju dinamike, postavljanju granica i strategijama za zaštitu.",
      },
      {
        question: "Kako terapija pomaže dok sam još uvek na tom poslu?",
        answer:
          "Terapija pruža siguran prostor gde možeš obraditi svakodnevni stres, razviti strategije za postavljanje granica, očuvati mentalno zdravlje i doneti promišljene odluke. Terapeut te podržava bez obzira na to da li ostaneš ili odeš.",
      },
      {
        question: "Zašto je online terapija pogodna za žrtve mobinga?",
        answer:
          "Online terapija pruža diskreciju jer ne moraš odlaziti u ordinaciju gde te neko može videti. Možeš zakazati sesiju van radnog vremena, bez dodatnog stresa od izostajanja s posla. Razgovor iz sigurnosti svog doma olakšava otvaranje o teškim iskustvima.",
      },
    ],
    passes: true,
  },

  // 63
  "zivotne-promene": {
    label: "Životne promene i tranzicije",
    title: "Online terapija za životne promene: podrška u prelaznim periodima",
    searchTerm:
      "životne promene tranzicija prelazni period srednja dob penzija selidba promena karijere novi početak kriza srednjih godina empty nest životne krize prilagođavanje promena identiteta",
    therapistSearchTerms: ["životne promene"],
    description:
      "Životne tranzicije (penzija, empty nest, selidba, promena karijere, srednje godine) donose gubitak poznatog identiteta, čak i kad su željene. Pronađi online terapeuta za podršku u navigaciji kroz prelazne periode, prihvatanju prošlosti i pronalaženju smisla u novoj fazi života.",
    seoTitle: "Online terapija za životne promene | Rešimo Probleme",
    seoDescription:
      "Prolaziš kroz veliku životnu promenu? Pronađi online terapeuta za podršku u navigaciji kroz tranzicije i nove početke.",
    relatedConditions: [
      "migracije-dijaspora",
      "kulturoloski-sok",
      "karijera-posao",
      "zalovanje",
    ],
    faqItems: [
      {
        question: "Zašto se osećam loše zbog pozitivne promene?",
        answer:
          "Svaka promena, čak i željena, uključuje gubitak: gubitak poznatog, ranijeg identiteta, rutine. Normalno je tugovati za onim što ostavljaš, čak i dok se raduješ novom. Terapeut pruža prostor za obe emocije bez pritiska da budeš samo srećan/na.",
      },
      {
        question: "Šta je 'kriza srednjih godina' i da li je stvarna?",
        answer:
          "Srednje godine često donose preispitivanje: postigao/la si nešto, ali pitaš se da li je to to. Nije uvek dramatična 'kriza', ali može biti period duboke refleksije o smislu, vremenu koje ostaje i neispunjenim snovima. Terapija pomaže da ovo bude prilika za rast.",
      },
      {
        question: "Kako se nositi sa 'empty nest' sindromom?",
        answer:
          "Kada deca odu, mnogi roditelji osećaju gubitak identiteta i svrhe. U terapiji radiš na redefinisanju odnosa sa odraslom decom, vraćanju fokusa na partnerski odnos i lične interese, i pronalaženju novog smisla van roditeljske uloge.",
      },
      {
        question: "Da li mi treba terapija ako je promena željena?",
        answer:
          "Da. Čak i željene promene (nova karijera, selidba, brak) nose stres prilagođavanja. Istraživanja pokazuju da pozitivni životni događaji mogu biti gotovo jednako stresni kao negativni. Terapija pomaže da proces prilagođavanja bude lakši i brži.",
      },
      {
        question: "Kako se nositi sa penzionisanjem?",
        answer:
          "Penzija je više od prestanka rada. Za mnoge ljude posao je izvor identiteta, strukture i socijalnih kontakata. Gubitak tih elemenata može dovesti do praznine, depresije ili gubitka smisla. Terapeut pomaže da pronađeš nove izvore ispunjenosti i strukture.",
      },
      {
        question: "Kako promena karijere utiče na mentalno zdravlje?",
        answer:
          "Promena karijere aktivira strah od neuspeha, nesigurnost, sindrom prevaranta i pritisak da se dokaže iznova. Istovremeno, ostanak na poslu koji te ne ispunjava dugoročno šteti. Terapija pomaže da razlučiš šta je strah od promene, a šta realna procena.",
      },
      {
        question: "Kako online terapija pomaže kod životnih tranzicija?",
        answer:
          "Online format je posebno praktičan tokom prelaznih perioda jer je fleksibilan i dostupan bez obzira na to gde se nalaziš. Seliš se u drugi grad ili državu? Terapeut ostaje isti. Nemaš ustaljenu rutinu? Sesiju zakažeš kad ti odgovara.",
      },
    ],
    passes: true,
  },

  // 64
  "stres-negovatelja": {
    label: "Stres negovatelja",
    title: "Online terapija za negovatelje: podrška onima koji brinu o drugima",
    searchTerm:
      "negovatelj caregiver briga o bolesnima starački dom roditelj bolestan briga o starijima burnout negovatelja iscrpljenost porodični negovatelj sindrom negovatelja sagorevanje negovatelja anticipatorna tuga stres zbog nege člana porodice emocionalna iscrpljenost negovatelja krivica negovatelja",
    description:
      "Briga o bolesnom ili starom članu porodice je iscrpljujuća. Negovatelji zanemaruju sopstvene potrebe, osećaju krivicu i suočavaju se sa anticipatornom tugom i izolacijom. Pronađi online terapeuta na platformi Rešimo Probleme gde tvoje potrebe dolaze na prvo mesto. Briga o sebi nije sebičnost, tvoje zdravlje je uslov da nastaviš da brineš o drugima.",
    seoTitle: "Online terapija za negovatelje | Rešimo Probleme",
    seoDescription:
      "Brineš o bolesnom članu porodice i iscrpljen/a si? Pronađi online terapeuta za podršku negovateljima i brigu o sebi.",
    relatedConditions: ["zalovanje", "stres", "hronicna-bolest"],
    faqItems: [
      {
        question: "Zašto se osećam krivim/om kad pomislim na sebe?",
        answer:
          "Negovatelji često veruju da moraju potpuno da se žrtvuju. Ali krivica zbog brige o sebi je lažna krivica, tvoje zdravlje je uslov da nastaviš da pružaš negu. U terapiji se radi na razdvajanju zdrave brige od samouništavajućeg žrtvovanja.",
      },
      {
        question:
          "Da li je normalno osećati ljutnju prema osobi o kojoj brinem?",
        answer:
          "Apsolutno normalno. Možeš voleti nekoga i istovremeno osećati ljutnju, frustraciju ili resentiment zbog situacije. Potiskivanje ovih osećanja samo ih pojačava. Terapija je siguran prostor da izraziš sve emocije bez osude.",
      },
      {
        question: "Šta je sindrom negovatelja i kako ga prepoznati?",
        answer:
          "Sindrom negovatelja (caregiver burnout) nastaje kada briga o drugima potpuno iscrpi tvoje fizičke i emocionalne resurse. Prepoznaješ ga po hroničnom umoru, razdražljivosti, povlačenju od prijatelja, zanemarivanju sopstvenog zdravlja i osećaju beznadežnosti. Terapeut ti može pomoći da prepoznaš rane znake i napraviš plan prevencije.",
      },
      {
        question: "Kako da nađem vremena za sebe kad stalno brinem o nekome?",
        answer:
          "Počinje se sa malim koracima: 15 minuta dnevno samo za tebe, delegiranje makar jednog zadatka, traženje pomoći od drugih članova porodice. U terapiji se radi na konkretnom planu i prevazilaženju unutrašnjeg otpora da tražiš i prihvatiš podršku.",
      },
      {
        question:
          "Šta je anticipatorna tuga i zašto je osećam dok je osoba još živa?",
        answer:
          "Anticipatorna tuga je žalovanje za gubitkom koji se još nije desio, ali ga predosećaš. Česta je kod negovatelja koji brinu o osobama sa progresivnim bolestima. To je potpuno normalna reakcija i ne znači da si odustao/la. Terapeut ti može pomoći da proživiš ovu tugu bez krivice.",
      },
      {
        question: "Da li mogu da idem na terapiju i ako nemam dijagnozu?",
        answer:
          "Naravno. Ne moraš imati dijagnozu da bi potražio/la podršku. Sama uloga negovatelja je emocionalno zahtevna i donosi specifične izazove za koje terapija može biti od velike pomoći. Terapeut razume kontekst tvoje situacije i prilagođava pristup.",
      },
      {
        question: "Kako funkcioniše online terapija za negovatelje?",
        answer:
          "Online terapija je idealna za negovatelje jer ne zahteva odlazak iz kuće. Sesije traju 45 minuta putem video poziva, a termin možeš zakazati kad ti odgovara. Na platformi Rešimo Probleme biraš terapeuta koji ima iskustvo sa stresom negovatelja i zakazuješ online.",
      },
    ],
    passes: true,
  },

  // 65
  "emocionalno-zlostavljanje": {
    label: "Emocionalno zlostavljanje",
    therapistSearchTerms: ["Emocionalno zlostavljanje"],
    guideLink: {
      href: "/blog/gaslighting-sta-je-kako-ga-prepoznati-i-kako-se-zastititi",
      label: "Pročitaj vodič o gaslightingu",
    },
    directoryContent: {
      title: "Online terapeut za podršku nakon emocionalnog zlostavljanja",
      description:
        "Ako doživljavaš ponižavanje, kontrolu, manipulaciju ili stalno preispitivanje sopstvenog iskustva, ovde možeš pregledati profil terapeuta koji javno navodi emocionalno zlostavljanje. Ne moraš unapred donositi odluku o odnosu da bi zatražio/la razgovor.",
      seoTitle: "Terapeut za emocionalno zlostavljanje | Rešimo Probleme",
      seoDescription:
        "Pregledaj profil terapeuta koji navodi emocionalno zlostavljanje i zakaži online razgovor na srpskom jeziku.",
      faqItems: [
        {
          question: "Da li terapeut odlučuje da li treba da ostanem ili odem?",
          answer:
            "Ne. Terapeut može pomoći da razmotriš svoje iskustvo i opcije, dok odluke o odnosu ostaju tvoje.",
        },
        {
          question: "Kako da izaberem terapeuta?",
          answer:
            "U profilima pogledaj javno navedene teme i kvalifikacije. Na prvom razgovoru možeš pitati kako terapeut pristupa tvojoj situaciji i granicama.",
        },
        {
          question: "Šta ako postoji neposredna opasnost?",
          answer:
            "Ako postoji neposredna opasnost, pozovi lokalnu hitnu službu ili kontaktiraj osobu od poverenja. Ova stranica nije zamena za hitnu pomoć ili pravni savet.",
        },
        {
          question: "Da li stranica obećava privatnost ili bezbednost uređaja?",
          answer:
            "Ne daje takva obećanja. Ako neko može nadzirati tvoj uređaj ili komunikaciju, koristi bezbedan način da potražiš lokalnu pomoć.",
        },
      ],
    },
    title:
      "Online terapija za emocionalno zlostavljanje: prepoznaj, zaštiti se, oporavi se",
    searchTerm:
      "emocionalno zlostavljanje psihičko nasilje manipulacija gaslighting kontrola toksična veza verbalno zlostavljanje ponižavanje kritikovanje emotivno zlostavljanje psihičko zlostavljanje u braku emocionalno zlostavljanje u vezi trauma bonding oporavak od zlostavljanja psihičko maltretiranje",
    description:
      "Emocionalno zlostavljanje (manipulacija, gaslighting, konstantna kritika, kontrola) ne ostavlja vidljive tragove, ali razara samopouzdanje i percepciju realnosti. Mnoge žrtve ne prepoznaju zlostavljanje jer nema fizičkog nasilja. Pronađi online terapeuta na platformi Rešimo Probleme koji validira tvoje iskustvo i pomaže ti da povratiš poverenje u sebe.",
    seoTitle: "Online terapija za emocionalno zlostavljanje | Rešimo Probleme",
    seoDescription:
      "Doživljavaš manipulaciju, gaslighting ili kontrolu? Pronađi online terapeuta za oporavak od emocionalnog zlostavljanja.",
    relatedConditions: [
      "narcisoidno-zlostavljanje",
      "nasilje-u-porodici-podrska",
      "kodependencija",
      "trauma",
    ],
    faqItems: [
      {
        question: "Kako da znam da li je emocionalno zlostavljanje?",
        answer:
          "Znakovi uključuju: stalno hodanje po jajima, osećaj da nikad nisi dovoljno dobar/dobra, sumnju u sopstvenu percepciju, izolaciju od prijatelja i porodice, osećaj da si 'lud/luda'. Ako se pitaš da li je zlostavljanje, verovatno jeste. Zdrave veze ne izazivaju ova pitanja.",
      },
      {
        question: "Zašto ne mogu da odem iako znam da je loše?",
        answer:
          "Ostajanje u zlostavljajućem odnosu nije znak slabosti. Trauma bonding, finansijska zavisnost, strah, nadanje da će se promeniti, nisko samopouzdanje: sve su to razumljive prepreke. U terapiji se radi na razumevanju tvoje situacije bez osude i pravljenju plana kada budeš spreman/spremna.",
      },
      {
        question: "Da li će me terapeut terati da odem iz veze?",
        answer:
          "Ne. Uloga terapeuta nije da ti govori šta da radiš, već da ti pomogne da vidiš jasno i doneseš sopstvene odluke. Terapeut poštuje tvoj tempo i izbore, dok radite na tvom oporavku i sigurnosti bez obzira da li ostaneš ili odeš.",
      },
      {
        question: "Šta je gaslighting i kako ga prepoznati?",
        answer:
          "Gaslighting je oblik manipulacije u kojem te druga osoba navodi da sumnjaš u sopstvenu percepciju, sećanja i osećanja. Primeri uključuju: 'To se nije desilo', 'Preteruješ', 'Ti si previše osetljiv/a'. Terapeut ti može pomoći da ponovo poveruješ u sopstveno iskustvo.",
      },
      {
        question:
          "Kako se razlikuje emocionalno zlostavljanje u braku od konflikta u vezi?",
        answer:
          "U zdravom konfliktu obe strane imaju pravo na svoje mišljenje i oba partnera se trude da pronađu rešenje. Kod emocionalnog zlostavljanja u braku ili vezi postoji obrazac kontrole, ponižavanja i manipulacije gde jedna strana sistematski podriva samopouzdanje druge. Ključna razlika je obrazac, ne pojedinačni incident.",
      },
      {
        question: "Da li emocionalno zlostavljanje ostavlja trajne posledice?",
        answer:
          "Emocionalno zlostavljanje može dovesti do anksioznosti, depresije, PTSP-a, problema sa samopouzdanjem i teškoća u budućim odnosima. Dobra vest je da se uz terapiju ove posledice mogu značajno ublažiti. Terapeut ti pomaže da obnoviš poverenje u sebe i izgradiš zdravije obrasce u odnosima.",
      },
      {
        question:
          "Kako funkcioniše online terapija za oporavak od zlostavljanja?",
        answer:
          "Online terapija ti pruža siguran prostor iz tvog doma, što je posebno važno ako si još u zlostavljajućem okruženju. Sesije traju 45 minuta putem video poziva, a na platformi Rešimo Probleme biraš terapeuta sa iskustvom u radu sa žrtvama emocionalnog zlostavljanja. Sve je diskretno i poverljivo.",
      },
    ],
    passes: true,
  },

  // 66
  agorafobija: {
    label: "Agorafobija",
    title:
      "Online terapeuti za agorafobiju: prevaziđi strah i povrati slobodu kretanja",
    searchTerm:
      "agorafobija strah od otvorenog prostora strah od gužve strah od javnog prevoza panični napad agorafobija agorafobija simptomi agorafobija lecenje KBT za agorafobiju terapija izlaganjem strah od izlaska iz kuće izbegavanje situacija anksiozni poremećaj agorafobija iskustva kako pobediti agorafobiju online terapija agorafobija strah od mesta",
    description:
      "Agorafobija je anksiozni poremećaj koji podrazumeva intenzivan strah od situacija iz kojih je teško pobegnuti ili u kojima pomoć nije lako dostupna: gužve, javni prevoz, otvoreni prostori ili čak izlazak iz kuće. Često se javlja uz panične napade i značajno ograničava svakodnevni život.\n\nNa Rešimo Probleme pronađi online terapeuta sa iskustvom u radu sa agorafobijom. Kognitivno-bihevioralna terapija (KBT) i postepeno izlaganje pomažu da korak po korak vratiš kontrolu nad svojim kretanjem i smanjiš izbegavanje. Pregledaj profile terapeuta i zakaži prvu sesiju iz svog doma.",
    seoTitle: "Online terapija za agorafobiju: KBT i podrška | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za agorafobiju. KBT i postepeno izlaganje uz licencirane psihologe. Zakaži termin iz svog doma, brzo i diskretno.",
    relatedConditions: [
      "panika",
      "anksioznost",
      "fobije",
      "socijalna-anksioznost",
    ],
    passes: true,

    faqItems: [
      {
        question: "Šta je agorafobija i koji su simptomi?",
        answer:
          "Agorafobija je anksiozni poremećaj u kojem osoba oseća intenzivan strah od situacija gde bi pomoć bila teško dostupna ili bekstvo otežano. Simptomi uključuju ubrzan rad srca, vrtoglavicu, znojenje, strah od gubitka kontrole i izbegavanje mesta poput tržnih centara, javnog prevoza ili gužvi. Mnogi počnu da izbegavaju sve više situacija, a u težim slučajevima i izlazak iz kuće.",
      },
      {
        question: "Kako se leči agorafobija?",
        answer:
          "Najefikasniji pristup je kognitivno-bihevioralna terapija (KBT) u kombinaciji sa terapijom izlaganjem. Terapeut ti pomaže da prepoznaš obrasce razmišljanja koji pojačavaju strah i da se postepeno, kontrolisano suočavaš sa situacijama koje izbegavaš. Po potrebi, psihijatar može preporučiti i lekove (najčešće SSRI antidepresive) kao podršku terapijskom procesu.",
      },
      {
        question: "Kakva je veza između paničnih napada i agorafobije?",
        answer:
          "Agorafobija se često razvija nakon jednog ili više paničnih napada. Osoba počne da izbegava mesta gde se napad dogodio ili gde veruje da bi mogao da se ponovi. Taj obrazac izbegavanja se širi i vremenom obuhvata sve više situacija. Terapija radi na oba problema istovremeno, smanjujući i paniku i izbegavanje.",
      },
      {
        question: "Da li online terapija pomaže kod agorafobije?",
        answer:
          "Da, istraživanja pokazuju da je online KBT jednako efikasna kao terapija uživo za agorafobiju. Online format je posebno koristan jer možeš da započneš terapiju iz svog doma, bez potrebe da se prvo suočiš sa izlaskom. Na Rešimo Probleme biraš terapeuta sa iskustvom u radu sa agorafobijom i zakazuješ video sesije koje ti odgovaraju.",
      },
      {
        question: "Koliko traje terapija za agorafobiju?",
        answer:
          "Trajanje zavisi od težine simptoma i koliko dugo stanje traje. Većina osoba primeti poboljšanje nakon 8 do 16 sesija KBT terapije sa postepenim izlaganjem. Kod dugotrajne agorafobije proces može trajati duže, ali svaki korak napred donosi konkretne promene u svakodnevnom životu.",
      },
      {
        question: "Po čemu se agorafobija razlikuje od specifičnih fobija?",
        answer:
          "Specifične fobije podrazumevaju strah od jednog konkretnog objekta ili situacije (npr. visina, pauci, letenje). Agorafobija je šira i obuhvata strah od više vrsta situacija gde bi bekstvo bilo otežano ili pomoć nedostupna. Često uključuje strah od gužvi, otvorenih prostora, javnog prevoza i boravka izvan kuće, a može značajno ograničiti slobodu kretanja.",
      },
      {
        question: "Kako pronaći terapeuta za agorafobiju na Rešimo Probleme?",
        answer:
          "Pretraži terapeute po specijalnosti 'agorafobija', pregledaj njihove profile, pristup (KBT, terapija izlaganjem) i recenzije klijenata. Izaberi terapeuta koji ti odgovara i zakaži prvi termin online. Ne moraš da izlaziš iz kuće, sesije su putem video poziva iz okruženja u kojem se osećaš sigurno.",
      },
    ],
  },

  // 67
  "postporodjajna-depresija": {
    passes: true,
    label: "Postporođajna depresija",
    title:
      "Online terapeuti za postporođajnu depresiju: podrška kad ti je najpotrebnija",
    searchTerm:
      "postporođajna depresija postpartalna depresija postporođajna depresija simptomi postporođajna depresija lečenje koliko traje postporođajna depresija postporođajna depresija iskustva baby blues ili depresija postporođajna depresija kod očeva terapija za postporođajnu depresiju KBT postporođajna depresija online psiholog za mame posle porođaja depresija",
    description:
      "Postporođajna depresija pogađa do 1 od 5 žena nakon porođaja i može se javiti i kod očeva. Za razliku od baby blues-a koji prolazi za dve nedelje, postporođajna depresija donosi dugotrajnu tugu, anksioznost, nesanicu, teškoće u povezivanju sa bebom i intruzivne misli koje mogu biti zastrašujuće. Ovi simptomi nisu znak slabosti, već signal da ti je potrebna stručna podrška.\n\nNa Rešimo Probleme možeš pronaći online psihoterapeuta sa iskustvom u perinatalnom mentalnom zdravlju. Kognitivno-bihevioralna terapija (KBT) i interpersonalna terapija (IPT) su među najefikasnijim pristupima za postporođajnu depresiju. Sesije se odvijaju iz tvog doma putem video poziva, bez potrebe za organizacijom čuvanja deteta.\n\nPretraži profile terapeuta, uporedi pristupe i zakaži online sesiju u terminu koji ti odgovara. Ranije traženje pomoći obično dovodi do bržeg oporavka.",
    seoTitle:
      "Postporođajna depresija: online terapeut i podrška | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za postporođajnu depresiju. KBT i interpersonalna terapija za mame i tate. Zakaži sesiju iz svog doma na Rešimo Probleme.",
    relatedConditions: [
      "perinatalna-podrska",
      "depresija",
      "anksioznost",
      "roditeljstvo",
    ],
    faqItems: [
      {
        question: "Šta je postporođajna depresija i koji su simptomi?",
        answer:
          "Postporođajna depresija je oblik depresije koji se javlja nakon porođaja, obično u prvih nekoliko nedelja do meseci. Simptomi uključuju dugotrajnu tugu ili prazninu, gubitak interesovanja, anksioznost, nesanicu (čak i kad beba spava), teškoće u povezivanju sa bebom, promene apetita i intruzivne misli. Razlikuje se od baby blues-a po intenzitetu i trajanju: ako simptomi traju duže od dve nedelje, razgovor sa terapeutom je važan korak.",
      },
      {
        question:
          "Koja je razlika između baby blues i postporođajne depresije?",
        answer:
          "Baby blues pogađa do 80% žena prvih dana nakon porođaja i prolazi sam za 10 do 14 dana. Manifestuje se kao blaga tuga, plačljivost i umor. Postporođajna depresija je intenzivnija, traje duže od dve nedelje i značajno utiče na funkcionisanje, brigu o bebi i odnose. Ako se osećanja pojačavaju umesto da slabe, to je signal da potraziš stručnu pomoć.",
      },
      {
        question: "Koliko traje postporođajna depresija?",
        answer:
          "Bez tretmana, postporođajna depresija može trajati mesecima, pa i duže od godinu dana. Uz odgovarajuću terapiju, većina žena oseti značajno poboljšanje unutar nekoliko nedelja do meseci. KBT i interpersonalna terapija pokazuju dobre rezultate, a raniji početak terapije obično dovodi do bržeg oporavka.",
      },
      {
        question: "Da li postporođajna depresija može pogoditi i očeve?",
        answer:
          "Da. Istraživanja pokazuju da oko 10% očeva doživi postporođajnu depresiju, posebno u prvih godinu dana nakon rođenja deteta. Simptomi kod očeva mogu uključivati povlačenje, iritabilnost, gubitak motivacije i teškoće u povezivanju sa bebom. Terapija je jednako dostupna i korisna za tate, bilo individualno ili kao par.",
      },
      {
        question: "Kako izgleda online terapija za postporođajnu depresiju?",
        answer:
          "Online sesija traje 45 minuta putem video poziva iz tvog doma, što je posebno praktično kad imaš bebu. Terapeut koristi KBT (rad na mislima i ponašanjima), interpersonalnu terapiju (poboljšanje odnosa i podrške) ili psihoedukaciju o perinatalnom periodu. Ne moraš brinuti o organizaciji čuvanja deteta ili putovanju.",
      },
      {
        question: "Kada potražiti pomoć za postporođajnu depresiju?",
        answer:
          "Ako tuga, anksioznost ili razdražljivost traju duže od dve nedelje, ako imaš teškoće u brizi o bebi ili sebi, ako osećaš da se ne povezuješ sa bebom ili imaš misli o samopovređivanju, važno je da se javiš terapeutu. Ne postoji premali razlog za razgovor sa stručnjakom. U akutnoj krizi pozovi Centar Srce: 0800-300-303.",
      },
      {
        question:
          "Da li se terapija može kombinovati sa lekovima za postporođajnu depresiju?",
        answer:
          "Da. Kod umerenih i teških simptoma, kombinacija psihoterapije i medikamentoznog tretmana često daje najbolje rezultate. Terapeut sarađuje sa tvojim lekarom ili psihijatrom kako bi se plan lečenja prilagodio tvojim potrebama. Odluku o lekovima uvek donosiš sa lekarom, uzimajući u obzir dojenje i individualne okolnosti.",
      },
    ],
  },
} as const;

export const SPECIALTY_ICON_IDS = {
  anksioznost: "wind",
  depresija: "cloud",
  stres: "zap",
  odnosi: "users",
  trauma: "shield",
  samopouzdanje: "user-round",
  "za-parove": "heart",
  porodica: "users-round",
  panika: "circle-dot-dashed",
  burnout: "flame",
  emdr: "eye-closed",
  kbt: "brain",
  ptsp: "shield-alert",
  okp: "repeat-2",
  "bipolarni-poremecaj": "activity",
  borderline: "heart-crack",
  "adhd-odrasli": "focus",
  "autizam-podrska": "puzzle",
  zavisnosti: "link-2-off",
  alkoholizam: "wine-off",
  kockanje: "dice-5",
  "pusenje-odvikavanje": "cigarette-off",
  nesanica: "moon",
  zalovanje: "flower-2",
  "poremecaji-ishrane": "utensils",
  "socijalna-anksioznost": "message-circle",
  fobije: "eye",
  "ljutnja-bes": "angry",
  "trauma-iz-detinjstva": "baby",
  "seksualna-terapija": "heart-pulse",
  "fertilitet-infertilitet": "sprout",
  "perinatalna-podrska": "baby",
  roditeljstvo: "baby",
  "razvod-razlaz": "git-fork",
  "nasilje-u-porodici-podrska": "hand",
  "samopovredjivanje-podrska": "shield-plus",
  perfekcionizam: "badge-check",
  "prokrastinacija-motivacija": "timer",
  "karijera-posao": "briefcase-business",
  "studije-ispitna-anksioznost": "graduation-cap",
  "migracije-dijaspora": "plane",
  "kulturoloski-sok": "languages",
  "visokofunkcionalna-anksioznost": "gauge",
  mindfulness: "flower-2",
  "shema-terapija": "map",
  "act-terapija": "route",
  "dbt-terapija": "scale",
  "psihodinamska-terapija": "brain-cog",
  "gestalt-terapija": "shapes",
  "narcisoidno-zlostavljanje": "user-round-x",
  "gubitak-ljubimca": "paw-print",
  "finansijski-stres": "wallet-cards",
  "digitalna-zavisnost": "smartphone",
  "imposter-sindrom": "badge-alert",
  "lgbtq-podrska": "rainbow",
  usamljenost: "user-round-x",
  kodependencija: "link",
  "problemi-vezivanja": "unlink",
  hipohondrija: "stethoscope",
  "hronicna-bolest": "heart-pulse",
  "telesna-slika": "scan-face",
  mobing: "briefcase-business",
  "zivotne-promene": "route",
  "stres-negovatelja": "hand-heart",
  "emocionalno-zlostavljanje": "message-circle-warning",
  agorafobija: "door-open",
  "postporodjajna-depresija": "cloud-rain",
} as const satisfies Record<keyof typeof THERAPY_SPECIALTIES_CONTENT, string>;

export type SpecialtyIconId =
  (typeof SPECIALTY_ICON_IDS)[keyof typeof SPECIALTY_ICON_IDS];

export type FAQItem = {
  question: string;
  answer: string;
};

export type SpecialtyDirectoryContent = {
  title: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  faqItems: readonly FAQItem[];
};

export type SpecialtyData = {
  maxMinimumClientAge?: number;
  indexable?: boolean;
  requireDeclaredInventory?: boolean;
  label: string; // Short name for navigation/breadcrumbs (e.g., "Anksioznost")
  iconId: SpecialtyIconId;
  title?: string; // SEO-optimized H1 title (e.g., "Online terapeuti za anksioznost – smanji brigu i napetost")
  searchTerm: string;
  /**
   * Controlled inventory terms for specialty directories. Unlike `searchTerm`,
   * these are never SEO copy and match only declared therapist specialties.
   */
  therapistSearchTerms?: readonly string[];
  /**
   * Controlled credential terms for modalities that therapists declare as a
   * qualification rather than as a client-facing specialty.
   */
  therapistQualificationTerms?: readonly string[];
  description: string;
  guideLink?: {
    href: string;
    label: string;
  };
  seoTitle: string;
  seoDescription: string;
  couples?: boolean;
  relatedConditions?: readonly SpecialtySlug[];
  faqItems?: readonly FAQItem[];
  /**
   * Content explicitly reviewed for a public, transactional specialty directory.
   * Legacy specialty fields remain available for internal configuration until each
   * page has passed its content and YMYL review.
   */
  directoryContent?: SpecialtyDirectoryContent;
  passes?: boolean; // SEO optimization completion status (temporary field)
};

export const THERAPY_SPECIALTIES: Record<string, SpecialtyData> = {
  rebt: {
    label: "REBT",
    iconId: "brain",
    searchTerm: "rebt REBT",
    therapistSearchTerms: ["rebt"],
    therapistQualificationTerms: ["rebt", "REBT"],
    description:
      "Ako želiš da istražiš stroge zahteve koje postavljaš sebi ili drugima, možeš da potražiš terapeuta koji navodi REBT. Na primer, tema za razgovor može biti uverenje da moraš uvek da uspeš, a da greška znači da ne vrediš. Ovde možeš da uporediš profile terapeuta koji navode ovaj pravac, njihove cene i slobodne termine. Na prvom razgovoru pitaj kako bi izgledao rad na onome što te trenutno muči.",
    title: "REBT terapija online: terapeuti i cene",
    seoTitle: "REBT terapija online: terapeuti i cene | Rešimo Probleme",
    seoDescription:
      "Tražiš REBT terapeuta? Uporedi profile, obrazovanje, cene i termine za online razgovor. Pročitaj vodič o REBT-u i pripremi pitanja za prvi susret.",
    relatedConditions: ["kbt", "anksioznost"],
    guideLink: {
      href: "/blog/rebt-terapija",
      label: "Pročitaj vodič o REBT terapiji",
    },
    requireDeclaredInventory: true,
    directoryContent: {
      title: "REBT terapija online: terapeuti i cene",
      description:
        "Ako želiš da istražiš stroge zahteve koje postavljaš sebi ili drugima, možeš da potražiš terapeuta koji navodi REBT. Na primer, tema za razgovor može biti uverenje da moraš uvek da uspeš, a da greška znači da ne vrediš. Ovde možeš da uporediš profile terapeuta koji navode ovaj pravac, njihove cene i slobodne termine. Na prvom razgovoru pitaj kako bi izgledao rad na onome što te trenutno muči.",
      seoTitle: "REBT terapija online: terapeuti i cene | Rešimo Probleme",
      seoDescription:
        "Tražiš REBT terapeuta? Uporedi profile, obrazovanje, cene i termine za online razgovor. Pročitaj vodič o REBT-u i pripremi pitanja za prvi susret.",
      faqItems: [
        {
          question: "O čemu mogu da razgovaram sa REBT terapeutom?",
          answer:
            "Možeš da kreneš od konkretne situacije i očekivanja koja imaš prema sebi ili drugima. Na primer, kako doživljavaš grešku ili neuspeh. Pitaj terapeuta kako biste istraživali ta uverenja.",
        },
        {
          question: "Da li se između sesija dogovaraju vežbe?",
          answer:
            "Pitaj terapeuta da li predlaže zadatke između susreta i kako ih dogovarate. Način rada proveri na konkretnom profilu i prvom razgovoru.",
        },
      ],
    },
  },
  "integrativna-terapija": {
    label: "Integrativna terapija",
    iconId: "brain",
    searchTerm: "integrative Integrativna terapija",
    therapistSearchTerms: ["integrative"],
    therapistQualificationTerms: ["integrative", "Integrativna terapija"],
    description:
      "Naziv integrativna terapija ne govori sam po sebi kako će izgledati tvoja sesija. Terapeuti mogu da povezuju različite načine rada, pa je važno da pročitaš opis svakog profila. Pitaj koje pristupe terapeut kombinuje, zašto ih bira i kako biste odlučivali šta ti odgovara. Ovde su profili koji navode integrativni pristup, uz cene i dostupne termine.",
    title: "Integrativna terapija online: izbor terapeuta",
    seoTitle: "Integrativna terapija online: izbor terapeuta | Rešimo Probleme",
    seoDescription:
      "Pronađi terapeute koji navode integrativni pristup. Pročitaj koje pravce povezuju u svom radu i uporedi cene i termine za online terapiju.",
    relatedConditions: ["kbt", "psihodinamska-terapija"],
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja koja možeš da postaviš pri izboru terapeuta",
    },
    requireDeclaredInventory: true,
    directoryContent: {
      title: "Integrativna terapija online: izbor terapeuta",
      description:
        "Naziv integrativna terapija ne govori sam po sebi kako će izgledati tvoja sesija. Terapeuti mogu da povezuju različite načine rada, pa je važno da pročitaš opis svakog profila. Pitaj koje pristupe terapeut kombinuje, zašto ih bira i kako biste odlučivali šta ti odgovara. Ovde su profili koji navode integrativni pristup, uz cene i dostupne termine.",
      seoTitle:
        "Integrativna terapija online: izbor terapeuta | Rešimo Probleme",
      seoDescription:
        "Pronađi terapeute koji navode integrativni pristup. Pročitaj koje pravce povezuju u svom radu i uporedi cene i termine za online terapiju.",
      faqItems: [
        {
          question: "Da li svi integrativni terapeuti rade na isti način?",
          answer:
            "Ne. Naziv obuhvata povezivanje različitih pristupa. Pročitaj koje pravce i obuke terapeut navodi i pitaj kako ih koristi u razgovoru.",
        },
        {
          question: "Kako da proverim da li mi način rada odgovara?",
          answer:
            "Reci šta ti je trenutno potrebno i pitaj kako bi izgledale prve sesije. Možeš da pitaš i kako dogovarate ciljeve i menjate način rada kada nešto ne odgovara.",
        },
      ],
    },
  },
  "sistemska-porodicna-terapija": {
    label: "Sistemska porodična terapija",
    iconId: "brain",
    searchTerm: "systemic Sistemska porodična terapija",
    therapistSearchTerms: ["systemic"],
    therapistQualificationTerms: ["systemic", "Sistemska porodična terapija"],
    description:
      "Kada te muče ponavljajući sukobi ili odnosi u porodici, možeš da razmotriš sistemski porodični pristup. Tema razgovora može biti kako članovi porodice utiču jedni na druge i šta se ponavlja u njihovoj komunikaciji. Proveri sa terapeutom da li razgovor podrazumeva tvoj individualni dolazak ili zajedničke sesije. Navođenje ovog pravca na profilu ne znači automatski da terapeut nudi zakazivanje za parove ili celu porodicu.",
    title: "Sistemska porodična terapija online",
    seoTitle: "Sistemska porodična terapija online | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji navode sistemski porodični pristup, njihove cene i termine. Proveri da li nude individualne ili zajedničke razgovore.",
    relatedConditions: ["porodica", "za-parove"],
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja koja možeš da postaviš pri izboru terapeuta",
    },
    requireDeclaredInventory: true,
    directoryContent: {
      title: "Sistemska porodična terapija online",
      description:
        "Kada te muče ponavljajući sukobi ili odnosi u porodici, možeš da razmotriš sistemski porodični pristup. Tema razgovora može biti kako članovi porodice utiču jedni na druge i šta se ponavlja u njihovoj komunikaciji. Proveri sa terapeutom da li razgovor podrazumeva tvoj individualni dolazak ili zajedničke sesije. Navođenje ovog pravca na profilu ne znači automatski da terapeut nudi zakazivanje za parove ili celu porodicu.",
      seoTitle: "Sistemska porodična terapija online | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji navode sistemski porodični pristup, njihove cene i termine. Proveri da li nude individualne ili zajedničke razgovore.",
      faqItems: [
        {
          question: "Da li na razgovor dolazim samostalno ili sa porodicom?",
          answer:
            "To dogovaraš sa terapeutom. Profil koji navodi sistemski pravac ne potvrđuje da je moguće zakazati sesiju za celu porodicu. Proveri format pre zakazivanja.",
        },
        {
          question: "Da li je ovo isto što i terapija za parove?",
          answer:
            "Pravac rada i format sesije su različite informacije. Ako želite da dođete zajedno, proverite da li terapeut nudi partnersku sesiju i koji uslovi važe.",
        },
      ],
    },
  },
  "transakciona-analiza": {
    label: "Transakciona analiza",
    iconId: "brain",
    searchTerm: "transactional_analysis Transakciona analiza",
    therapistSearchTerms: ["transactional_analysis"],
    therapistQualificationTerms: [
      "transactional_analysis",
      "Transakciona analiza",
    ],
    description:
      "Ako primećuješ da u različitim odnosima ulaziš u slične razgovore i sukobe, transakciona analiza može biti pravac o kome želiš da saznaš više. Na prvom razgovoru možeš da pitaš kako terapeut istražuje obrasce komunikacije i uloge koje preuzimaš u odnosima. Ovde možeš da pregledaš profile koji navode transakcionu analizu i uporediš opis rada, cenu i termine.",
    title: "Transakciona analiza online: terapeuti i cene",
    seoTitle: "Transakciona analiza online: terapeuti i cene | Rešimo Probleme",
    seoDescription:
      "Uporedi terapeute koji navode transakcionu analizu, njihovo obrazovanje, cene i slobodne termine. Pročitaj vodič o ovom pristupu pre prvog razgovora.",
    relatedConditions: ["odnosi", "gestalt-terapija"],
    guideLink: {
      href: "/blog/transakciona-analiza",
      label: "Pročitaj vodič o transakcionoj analizi",
    },
    requireDeclaredInventory: true,
    directoryContent: {
      title: "Transakciona analiza online: terapeuti i cene",
      description:
        "Ako primećuješ da u različitim odnosima ulaziš u slične razgovore i sukobe, transakciona analiza može biti pravac o kome želiš da saznaš više. Na prvom razgovoru možeš da pitaš kako terapeut istražuje obrasce komunikacije i uloge koje preuzimaš u odnosima. Ovde možeš da pregledaš profile koji navode transakcionu analizu i uporediš opis rada, cenu i termine.",
      seoTitle:
        "Transakciona analiza online: terapeuti i cene | Rešimo Probleme",
      seoDescription:
        "Uporedi terapeute koji navode transakcionu analizu, njihovo obrazovanje, cene i slobodne termine. Pročitaj vodič o ovom pristupu pre prvog razgovora.",
      faqItems: [
        {
          question: "Od čega mogu da počnem razgovor?",
          answer:
            "Možeš da opišeš razgovor ili sukob koji ti se često ponavlja. Pitaj terapeuta kako koristi transakcionu analizu da istraži komunikaciju i tvoje odnose.",
        },
        {
          question: "Moram li unapred da poznajem stručne pojmove?",
          answer:
            "Ne. Dovoljno je da objasniš šta te muči. Ako terapeut koristi pojmove koje ne poznaješ, traži da ih objasni kroz tvoju konkretnu situaciju.",
        },
      ],
    },
  },
  adhd: {
    label: "ADHD",
    title: "Online terapeuti: ADHD",
    description:
      "Organizacija vremena, pažnja i svakodnevne obaveze mogu biti teme razgovora u okviru ADHD-a. Ovde možeš da pronađeš terapeute koji navode ADHD kao oblast rada. Na profilu proveri sa kojim uzrastom terapeut radi. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: ADHD | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: ADHD. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "ADHD",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: ADHD",
      description:
        "Organizacija vremena, pažnja i svakodnevne obaveze mogu biti teme razgovora u okviru ADHD-a. Ovde možeš da pronađeš terapeute koji navode ADHD kao oblast rada. Na profilu proveri sa kojim uzrastom terapeut radi. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: ADHD | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: ADHD. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "emocije-i-raspolozenje": {
    label: "Emocije i raspoloženje",
    title: "Online terapeuti: Emocije i raspoloženje",
    description:
      "Kada te emocije preplave, tema razgovora može biti bes, stid, krivica ili promena raspoloženja. Ova oblast obuhvata razgovor o različitim emocijama i reakcijama, pa na profilu proveri iskustvo terapeuta sa onim što tebe muči. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Emocije i raspoloženje | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Emocije i raspoloženje. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Emocije i raspoloženje",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Emocije i raspoloženje",
      description:
        "Kada te emocije preplave, tema razgovora može biti bes, stid, krivica ili promena raspoloženja. Ova oblast obuhvata razgovor o različitim emocijama i reakcijama, pa na profilu proveri iskustvo terapeuta sa onim što tebe muči. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Emocije i raspoloženje | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Emocije i raspoloženje. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "partnerski-odnosi": {
    label: "Partnerski odnosi",
    title: "Online terapeuti: Partnerski odnosi",
    description:
      "Ljubomora, ponavljajući sukobi, udaljavanje ili raskid mogu biti razlog da potražiš podršku. O partnerskom odnosu možeš razgovarati i samostalno. Ako želite da dođete zajedno, proveri da li terapeut nudi partnerske sesije. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Partnerski odnosi | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Partnerski odnosi. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Partnerski odnosi",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Partnerski odnosi",
      description:
        "Ljubomora, ponavljajući sukobi, udaljavanje ili raskid mogu biti razlog da potražiš podršku. O partnerskom odnosu možeš razgovarati i samostalno. Ako želite da dođete zajedno, proveri da li terapeut nudi partnerske sesije. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Partnerski odnosi | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Partnerski odnosi. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  disocijacija: {
    label: "Disocijacija",
    title: "Online terapeuti: Disocijacija",
    description:
      "Ako želiš da razgovaraš o osećaju odvojenosti od sebe ili okoline, proveri iskustvo terapeuta sa disocijativnim iskustvima. Možeš opisati kada se taj osećaj javlja i kako utiče na tvoju svakodnevicu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Disocijacija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Disocijacija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Disocijacija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Disocijacija",
      description:
        "Ako želiš da razgovaraš o osećaju odvojenosti od sebe ili okoline, proveri iskustvo terapeuta sa disocijativnim iskustvima. Možeš opisati kada se taj osećaj javlja i kako utiče na tvoju svakodnevicu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Disocijacija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Disocijacija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "teskoce-u-ponasanju": {
    label: "Teškoće u ponašanju",
    title: "Online terapeuti: Teškoće u ponašanju",
    description:
      "Ponavljajući sukobi zbog ponašanja ili teškoće sa poštovanjem dogovora mogu biti početak razgovora. Navedi konkretnu situaciju i uzrast osobe za koju tražiš podršku. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Teškoće u ponašanju | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Teškoće u ponašanju. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Teškoće u ponašanju",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Teškoće u ponašanju",
      description:
        "Ponavljajući sukobi zbog ponašanja ili teškoće sa poštovanjem dogovora mogu biti početak razgovora. Navedi konkretnu situaciju i uzrast osobe za koju tražiš podršku. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Teškoće u ponašanju | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Teškoće u ponašanju. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "telesni-dismorfni-poremecaj": {
    label: "Telesni dismorfni poremećaj",
    title: "Online terapeuti: Telesni dismorfni poremećaj",
    description:
      "Ako tražiš podršku za teškoće u okviru telesnog dismorfnog poremećaja, pogledaj kako terapeut opisuje svoj rad. Tema razgovora može biti zaokupljenost izgledom i njen uticaj na svakodnevicu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Telesni dismorfni poremećaj | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Telesni dismorfni poremećaj. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Telesni dismorfni poremećaj",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Telesni dismorfni poremećaj",
      description:
        "Ako tražiš podršku za teškoće u okviru telesnog dismorfnog poremećaja, pogledaj kako terapeut opisuje svoj rad. Tema razgovora može biti zaokupljenost izgledom i njen uticaj na svakodnevicu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Telesni dismorfni poremećaj | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Telesni dismorfni poremećaj. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "razvojne-teskoce-kod-dece": {
    label: "Razvojne teškoće kod dece",
    title: "Online terapeuti: Razvojne teškoće kod dece",
    description:
      "Kada tražiš podršku zbog razvojnih teškoća deteta, opiši šta primećuješ, koliko dete ima godina i kakva podrška već postoji. Pre zakazivanja proveri da li terapeut radi sa tim uzrastom i da li je predviđen razgovor sa detetom, roditeljem ili oboje. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Razvojne teškoće kod dece | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Razvojne teškoće kod dece. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Razvojne teškoće kod dece",
    requireDeclaredInventory: true,
    maxMinimumClientAge: 12,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Razvojne teškoće kod dece",
      description:
        "Kada tražiš podršku zbog razvojnih teškoća deteta, opiši šta primećuješ, koliko dete ima godina i kakva podrška već postoji. Pre zakazivanja proveri da li terapeut radi sa tim uzrastom i da li je predviđen razgovor sa detetom, roditeljem ili oboje. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Razvojne teškoće kod dece | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Razvojne teškoće kod dece. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "iskustva-diskriminacije": {
    label: "Iskustva diskriminacije",
    title: "Online terapeuti: Iskustva diskriminacije",
    description:
      "Odbacivanje, predrasude i nejednak tretman mogu biti tema razgovora sa terapeutom. Možeš govoriti o konkretnom iskustvu i tome kako utiče na tvoj osećaj sigurnosti, odnose ili sliku o sebi. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Iskustva diskriminacije | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Iskustva diskriminacije. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Iskustva diskriminacije",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Iskustva diskriminacije",
      description:
        "Odbacivanje, predrasude i nejednak tretman mogu biti tema razgovora sa terapeutom. Možeš govoriti o konkretnom iskustvu i tome kako utiče na tvoj osećaj sigurnosti, odnose ili sliku o sebi. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Iskustva diskriminacije | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Iskustva diskriminacije. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  trihotilomanija: {
    label: "Čupanje kose i dlaka (trihotilomanija)",
    title: "Online terapeuti: Čupanje kose i dlaka (trihotilomanija)",
    description:
      "Ako želiš podršku zbog ponavljajućeg čupanja kose, obrva ili drugih dlaka, proveri iskustvo terapeuta sa tom temom. Možeš opisati situacije u kojima se ponašanje javlja i teškoće koje ti stvara. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Čupanje kose i dlaka (trihotilomanija) | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Čupanje kose i dlaka (trihotilomanija). Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Čupanje kose i dlaka (trihotilomanija)",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Čupanje kose i dlaka (trihotilomanija)",
      description:
        "Ako želiš podršku zbog ponavljajućeg čupanja kose, obrva ili drugih dlaka, proveri iskustvo terapeuta sa tom temom. Možeš opisati situacije u kojima se ponašanje javlja i teškoće koje ti stvara. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Čupanje kose i dlaka (trihotilomanija) | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Čupanje kose i dlaka (trihotilomanija). Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "gomilanje-stvari": {
    label: "Kompulzivno gomilanje stvari",
    title: "Online terapeuti: Kompulzivno gomilanje stvari",
    description:
      "Teškoće sa odvajanjem od stvari i gomilanje koje ometa svakodnevni život mogu biti razlog za razgovor. Sa terapeutom možeš početi od toga kako izgleda tvoj prostor i šta ti je teško kada pokušaš nešto da odvojiš. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Kompulzivno gomilanje stvari | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Kompulzivno gomilanje stvari. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Kompulzivno gomilanje stvari",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Kompulzivno gomilanje stvari",
      description:
        "Teškoće sa odvajanjem od stvari i gomilanje koje ometa svakodnevni život mogu biti razlog za razgovor. Sa terapeutom možeš početi od toga kako izgleda tvoj prostor i šta ti je teško kada pokušaš nešto da odvojiš. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Kompulzivno gomilanje stvari | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Kompulzivno gomilanje stvari. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "nametljive-misli": {
    label: "Neželjene i nametljive misli",
    title: "Online terapeuti: Neželjene i nametljive misli",
    description:
      "Misli koje se uporno vraćaju i uznemiravaju te mogu biti tema razgovora i bez unapred postavljene dijagnoze. Opiši kako ih doživljavaš, kada se javljaju i kako utiču na ono što radiš. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Neželjene i nametljive misli | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Neželjene i nametljive misli. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Neželjene i nametljive misli",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Neželjene i nametljive misli",
      description:
        "Misli koje se uporno vraćaju i uznemiravaju te mogu biti tema razgovora i bez unapred postavljene dijagnoze. Opiši kako ih doživljavaš, kada se javljaju i kako utiču na ono što radiš. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Neželjene i nametljive misli | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Neželjene i nametljive misli. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "teskoce-u-ucenju": {
    label: "Teškoće u učenju",
    title: "Online terapeuti: Teškoće u učenju",
    description:
      "Ako tražiš podršku zbog teškoća u učenju, navedi konkretne zadatke koji predstavljaju problem i uzrast osobe. Proveri iskustvo terapeuta sa tom vrstom teškoća i kome je razgovor namenjen. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Teškoće u učenju | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Teškoće u učenju. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Teškoće u učenju",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Teškoće u učenju",
      description:
        "Ako tražiš podršku zbog teškoća u učenju, navedi konkretne zadatke koji predstavljaju problem i uzrast osobe. Proveri iskustvo terapeuta sa tom vrstom teškoća i kome je razgovor namenjen. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Teškoće u učenju | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Teškoće u učenju. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "podrska-osobama-sa-gojaznoscu": {
    label: "Psihološka podrška osobama sa gojaznošću",
    title: "Online terapeuti: Psihološka podrška osobama sa gojaznošću",
    description:
      "Tema razgovora može biti odnos prema sebi i telu, iskustvo stigme ili emocionalno opterećenje povezano sa gojaznošću. Ovde tražiš psihološku podršku; pitanja ishrane i medicinskog lečenja proveri sa odgovarajućim stručnjakom. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Psihološka podrška osobama sa gojaznošću | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Psihološka podrška osobama sa gojaznošću. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Psihološka podrška osobama sa gojaznošću",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Psihološka podrška osobama sa gojaznošću",
      description:
        "Tema razgovora može biti odnos prema sebi i telu, iskustvo stigme ili emocionalno opterećenje povezano sa gojaznošću. Ovde tražiš psihološku podršku; pitanja ishrane i medicinskog lečenja proveri sa odgovarajućim stručnjakom. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Psihološka podrška osobama sa gojaznošću | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Psihološka podrška osobama sa gojaznošću. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "poremecaji-licnosti": {
    label: "Poremećaji ličnosti",
    title: "Online terapeuti: Poremećaji ličnosti",
    description:
      "Ako tražiš psihološku podršku u okviru poremećaja ličnosti, proveri sa kojim teškoćama terapeut ima iskustva. Opiši šta ti trenutno otežava odnose ili svakodnevicu i pitaj kako bi izgledao rad. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Poremećaji ličnosti | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Poremećaji ličnosti. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Poremećaji ličnosti",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Poremećaji ličnosti",
      description:
        "Ako tražiš psihološku podršku u okviru poremećaja ličnosti, proveri sa kojim teškoćama terapeut ima iskustva. Opiši šta ti trenutno otežava odnose ili svakodnevicu i pitaj kako bi izgledao rad. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Poremećaji ličnosti | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Poremećaji ličnosti. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "problematicna-upotreba-pornografije": {
    label: "Problematična upotreba pornografije",
    title: "Online terapeuti: Problematična upotreba pornografije",
    description:
      "Ako ti upotreba pornografije stvara teškoće ili je teško kontrolišeš, možeš da razgovaraš o njenom uticaju na obaveze, odnose i intimnost. Proveri iskustvo terapeuta sa ovom konkretnom temom. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Problematična upotreba pornografije | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Problematična upotreba pornografije. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Problematična upotreba pornografije",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Problematična upotreba pornografije",
      description:
        "Ako ti upotreba pornografije stvara teškoće ili je teško kontrolišeš, možeš da razgovaraš o njenom uticaju na obaveze, odnose i intimnost. Proveri iskustvo terapeuta sa ovom konkretnom temom. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Problematična upotreba pornografije | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Problematična upotreba pornografije. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "psihosomatske-teskoce": {
    label: "Psihosomatske teškoće",
    title: "Online terapeuti: Psihosomatske teškoće",
    description:
      "Ako telesne tegobe povezuješ sa stresom ili emocionalnim opterećenjem, o tom iskustvu možeš razgovarati sa terapeutom. Psihološka podrška ne zamenjuje medicinsku procenu telesnih simptoma. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Psihosomatske teškoće | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Psihosomatske teškoće. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Psihosomatske teškoće",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Psihosomatske teškoće",
      description:
        "Ako telesne tegobe povezuješ sa stresom ili emocionalnim opterećenjem, o tom iskustvu možeš razgovarati sa terapeutom. Psihološka podrška ne zamenjuje medicinsku procenu telesnih simptoma. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Psihosomatske teškoće | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Psihosomatske teškoće. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "psihoticna-iskustva": {
    label: "Psihotična iskustva",
    title: "Online terapeuti: Psihotična iskustva",
    description:
      "Ako tražiš psihološku podršku zbog psihotičnih iskustava, pre zakazivanja proveri iskustvo terapeuta i mogućnost saradnje sa psihijatrom koji prati lečenje. Online zakazivanje nije služba za hitnu pomoć. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Psihotična iskustva | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Psihotična iskustva. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Psihotična iskustva",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Psihotična iskustva",
      description:
        "Ako tražiš psihološku podršku zbog psihotičnih iskustava, pre zakazivanja proveri iskustvo terapeuta i mogućnost saradnje sa psihijatrom koji prati lečenje. Online zakazivanje nije služba za hitnu pomoć. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Psihotična iskustva | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Psihotična iskustva. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "licni-razvoj-i-identitet": {
    label: "Lični razvoj i identitet",
    title: "Online terapeuti: Lični razvoj i identitet",
    description:
      "Upoznavanje sebe, lične vrednosti, donošenje odluka i pitanja identiteta mogu biti dovoljan razlog za razgovor. Ne moraš imati jednu jasno definisanu teškoću da bi pitao terapeuta kako biste radili na onome što ti je važno. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Lični razvoj i identitet | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Lični razvoj i identitet. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Lični razvoj i identitet",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Lični razvoj i identitet",
      description:
        "Upoznavanje sebe, lične vrednosti, donošenje odluka i pitanja identiteta mogu biti dovoljan razlog za razgovor. Ne moraš imati jednu jasno definisanu teškoću da bi pitao terapeuta kako biste radili na onome što ti je važno. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Lični razvoj i identitet | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Lični razvoj i identitet. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "cackanje-koze": {
    label: "Kompulzivno čačkanje kože",
    title: "Online terapeuti: Kompulzivno čačkanje kože",
    description:
      "Ponavljajuće čačkanje kože koje teško kontrolišeš ili koje ometa svakodnevicu može biti tema za razgovor. Proveri iskustvo terapeuta sa ovim ponašanjem i opiši kada ti se najčešće događa. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Kompulzivno čačkanje kože | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Kompulzivno čačkanje kože. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Kompulzivno čačkanje kože",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Kompulzivno čačkanje kože",
      description:
        "Ponavljajuće čačkanje kože koje teško kontrolišeš ili koje ometa svakodnevicu može biti tema za razgovor. Proveri iskustvo terapeuta sa ovim ponašanjem i opiši kada ti se najčešće događa. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Kompulzivno čačkanje kože | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Kompulzivno čačkanje kože. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "suicidalne-misli": {
    label: "Suicidalnost",
    title: "Online terapeuti: Suicidalnost",
    description:
      "Ova stranica je namenjena traženju terapeuta koji navodi rad sa suicidalnim mislima. Zakazivanje na platformi nije način da dobiješ hitnu pomoć. Pre dogovora o radu proveri dostupnost terapeuta i kakva podrška je potrebna u tvojoj situaciji. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Suicidalnost | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Suicidalnost. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Suicidalnost",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Suicidalnost",
      description:
        "Ova stranica je namenjena traženju terapeuta koji navodi rad sa suicidalnim mislima. Zakazivanje na platformi nije način da dobiješ hitnu pomoć. Pre dogovora o radu proveri dostupnost terapeuta i kakva podrška je potrebna u tvojoj situaciji. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Suicidalnost | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Suicidalnost. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-decom": {
    label: "Rad sa decom",
    title: "Online terapeuti: Rad sa decom",
    description:
      "Ovde su terapeuti koji navode rad sa decom i prihvataju klijente mlađe od 13 godina. Proveri tačan uzrast na profilu i dogovori da li razgovor uključuje dete, roditelja ili oboje. Pitaj kako se procenjuje da li online razgovor odgovara detetu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Rad sa decom | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa decom. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa decom",
    requireDeclaredInventory: true,
    maxMinimumClientAge: 12,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa decom",
      description:
        "Ovde su terapeuti koji navode rad sa decom i prihvataju klijente mlađe od 13 godina. Proveri tačan uzrast na profilu i dogovori da li razgovor uključuje dete, roditelja ili oboje. Pitaj kako se procenjuje da li online razgovor odgovara detetu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Rad sa decom | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa decom. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-adolescentima": {
    label: "Rad sa adolescentima",
    title: "Online terapeuti: Rad sa adolescentima",
    description:
      "Ovde su terapeuti koji navode rad sa adolescentima i prihvataju maloletne klijente. Proveri tačan uzrast na profilu i pitaj kako se dogovaraju učešće roditelja i privatnost razgovora. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Rad sa adolescentima | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa adolescentima. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa adolescentima",
    requireDeclaredInventory: true,
    maxMinimumClientAge: 17,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa adolescentima",
      description:
        "Ovde su terapeuti koji navode rad sa adolescentima i prihvataju maloletne klijente. Proveri tačan uzrast na profilu i pitaj kako se dogovaraju učešće roditelja i privatnost razgovora. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Rad sa adolescentima | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa adolescentima. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-odraslima": {
    label: "Rad sa odraslima",
    title: "Online terapeuti: Rad sa odraslima",
    description:
      "Prikazani terapeuti izričito navode rad sa odraslim osobama. Uporedi i oblasti rada: podatak o uzrastu ne govori sam po sebi da terapeut radi sa svakom temom koja ti je važna. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Rad sa odraslima | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa odraslima. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa odraslima",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa odraslima",
      description:
        "Prikazani terapeuti izričito navode rad sa odraslim osobama. Uporedi i oblasti rada: podatak o uzrastu ne govori sam po sebi da terapeut radi sa svakom temom koja ti je važna. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Rad sa odraslima | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa odraslima. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-starijim-osobama": {
    label: "Rad sa starijim osobama",
    title: "Online terapeuti: Rad sa starijim osobama",
    description:
      "Ako tražiš razgovor za sebe ili stariju blisku osobu, proveri iskustvo terapeuta sa starijim klijentima. Pitaj kako izgleda online susret i da li su potrebna praktična prilagođavanja za razgovor. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Rad sa starijim osobama | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa starijim osobama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa starijim osobama",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa starijim osobama",
      description:
        "Ako tražiš razgovor za sebe ili stariju blisku osobu, proveri iskustvo terapeuta sa starijim klijentima. Pitaj kako izgleda online susret i da li su potrebna praktična prilagođavanja za razgovor. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Rad sa starijim osobama | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa starijim osobama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-studentima": {
    label: "Rad sa studentima",
    title: "Online terapeuti: Rad sa studentima",
    description:
      "Studiranje može otvoriti pitanja o pritisku, izboru pravca, samostalnosti i odnosima. Ovde su terapeuti koji navode rad sa studentima, a na profilu možeš da proveriš teme sa kojima rade. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Rad sa studentima | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa studentima. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa studentima",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa studentima",
      description:
        "Studiranje može otvoriti pitanja o pritisku, izboru pravca, samostalnosti i odnosima. Ovde su terapeuti koji navode rad sa studentima, a na profilu možeš da proveriš teme sa kojima rade. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Rad sa studentima | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa studentima. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-osobama-sa-invaliditetom": {
    label: "Rad sa osobama sa invaliditetom",
    title: "Online terapeuti: Rad sa osobama sa invaliditetom",
    description:
      "Prikazani terapeuti navode rad sa osobama sa invaliditetom. Pre zakazivanja razgovaraj o potrebama za pristupačnošću, komunikaciji i eventualnim prilagođavanjima online susreta. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Rad sa osobama sa invaliditetom | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa osobama sa invaliditetom. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa osobama sa invaliditetom",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa osobama sa invaliditetom",
      description:
        "Prikazani terapeuti navode rad sa osobama sa invaliditetom. Pre zakazivanja razgovaraj o potrebama za pristupačnošću, komunikaciji i eventualnim prilagođavanjima online susreta. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Rad sa osobama sa invaliditetom | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa osobama sa invaliditetom. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-neurodivergentnim-osobama": {
    label: "Rad sa neurodivergentnim osobama",
    title: "Online terapeuti: Rad sa neurodivergentnim osobama",
    description:
      "Prikazani terapeuti navode rad sa neurodivergentnim osobama. Proveri iskustvo sa tvojim konkretnim potrebama i pitaj kako možete prilagoditi komunikaciju i tok susreta. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Rad sa neurodivergentnim osobama | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa neurodivergentnim osobama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa neurodivergentnim osobama",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa neurodivergentnim osobama",
      description:
        "Prikazani terapeuti navode rad sa neurodivergentnim osobama. Proveri iskustvo sa tvojim konkretnim potrebama i pitaj kako možete prilagoditi komunikaciju i tok susreta. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Rad sa neurodivergentnim osobama | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa neurodivergentnim osobama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-roditeljima": {
    label: "Rad sa roditeljima",
    title: "Online terapeuti: Rad sa roditeljima",
    description:
      "Ovde su terapeuti koji navode rad sa roditeljima kao klijentima. Razgovor može biti o roditeljskoj ulozi ili drugim temama koje su tebi važne. Rad sa roditeljima ne znači automatski i rad sa detetom. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Rad sa roditeljima | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa roditeljima. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa roditeljima",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa roditeljima",
      description:
        "Ovde su terapeuti koji navode rad sa roditeljima kao klijentima. Razgovor može biti o roditeljskoj ulozi ili drugim temama koje su tebi važne. Rad sa roditeljima ne znači automatski i rad sa detetom. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Rad sa roditeljima | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa roditeljima. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-negovateljima": {
    label: "Rad sa osobama koje brinu o članu porodice",
    title: "Online terapeuti: Rad sa osobama koje brinu o članu porodice",
    description:
      "Kada brineš o bolesnom ili zavisnom članu porodice, možda ti je potreban prostor za sopstvene potrebe i opterećenje. Prikazani terapeuti navode rad sa osobama koje imaju tu ulogu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Rad sa osobama koje brinu o članu porodice | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa osobama koje brinu o članu porodice. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa osobama koje brinu o članu porodice",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa osobama koje brinu o članu porodice",
      description:
        "Kada brineš o bolesnom ili zavisnom članu porodice, možda ti je potreban prostor za sopstvene potrebe i opterećenje. Prikazani terapeuti navode rad sa osobama koje imaju tu ulogu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Rad sa osobama koje brinu o članu porodice | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa osobama koje brinu o članu porodice. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-lgbtq-osobama": {
    label: "Rad sa LGBTQ osobama",
    title: "Online terapeuti: Rad sa LGBTQ osobama",
    description:
      "Ovde su terapeuti koji navode rad sa LGBTQ osobama. Tema razgovora može biti identitet, odnosi ili bilo šta drugo što te opterećuje. Na profilu proveri oblasti rada i pitaj o iskustvu koje ti je važno. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Rad sa LGBTQ osobama | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa LGBTQ osobama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa LGBTQ osobama",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa LGBTQ osobama",
      description:
        "Ovde su terapeuti koji navode rad sa LGBTQ osobama. Tema razgovora može biti identitet, odnosi ili bilo šta drugo što te opterećuje. Na profilu proveri oblasti rada i pitaj o iskustvu koje ti je važno. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Rad sa LGBTQ osobama | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa LGBTQ osobama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-osobama-u-inostranstvu": {
    label: "Rad sa osobama koje žive u inostranstvu",
    title: "Online terapeuti: Rad sa osobama koje žive u inostranstvu",
    description:
      "Prikazani terapeuti navode rad sa osobama koje žive u inostranstvu. Pored teme razgovora, proveri vremensku zonu, jezik i mogućnost rada sa klijentima u zemlji u kojoj se nalaziš. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Rad sa osobama koje žive u inostranstvu | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa osobama koje žive u inostranstvu. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa osobama koje žive u inostranstvu",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa osobama koje žive u inostranstvu",
      description:
        "Prikazani terapeuti navode rad sa osobama koje žive u inostranstvu. Pored teme razgovora, proveri vremensku zonu, jezik i mogućnost rada sa klijentima u zemlji u kojoj se nalaziš. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Rad sa osobama koje žive u inostranstvu | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa osobama koje žive u inostranstvu. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "rad-sa-trudnicama-i-osobama-posle-porodjaja": {
    label: "Rad sa trudnicama i osobama posle porođaja",
    title: "Online terapeuti: Rad sa trudnicama i osobama posle porođaja",
    description:
      "Ovde su terapeuti koji navode rad sa trudnicama i osobama posle porođaja. Reci u kom si periodu i kakva ti je podrška potrebna, pa proveri iskustvo terapeuta i način rada. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Rad sa trudnicama i osobama posle porođaja | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Rad sa trudnicama i osobama posle porođaja. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Rad sa trudnicama i osobama posle porođaja",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Rad sa trudnicama i osobama posle porođaja",
      description:
        "Ovde su terapeuti koji navode rad sa trudnicama i osobama posle porođaja. Reci u kom si periodu i kakva ti je podrška potrebna, pa proveri iskustvo terapeuta i način rada. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Rad sa trudnicama i osobama posle porođaja | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Rad sa trudnicama i osobama posle porođaja. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "terapija-usmerena-na-saosecanje": {
    label: "Terapija usmerena na saosećanje",
    title: "Online terapeuti: Terapija usmerena na saosećanje",
    description:
      "Ako ti je važan razgovor o samokritičnosti i odnosu prema sebi, pitaj terapeuta kako koristi pristup usmeren na saosećanje. Proveri kako bi izgledale prve sesije i eventualne vežbe. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Terapija usmerena na saosećanje | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Terapija usmerena na saosećanje. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Terapija usmerena na saosećanje",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Terapija usmerena na saosećanje",
      description:
        "Ako ti je važan razgovor o samokritičnosti i odnosu prema sebi, pitaj terapeuta kako koristi pristup usmeren na saosećanje. Proveri kako bi izgledale prve sesije i eventualne vežbe. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Terapija usmerena na saosećanje | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Terapija usmerena na saosećanje. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  psihoanaliza: {
    label: "Psihoanaliza",
    title: "Online terapeuti: Psihoanaliza",
    description:
      "Ako razmatraš psihoanalizu, pitaj terapeuta kako istražujete ponavljajuća iskustva, odnose i ono što ti spontano pada na pamet. Proveri učestalost susreta i kako se dogovara tok rada. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Psihoanaliza | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Psihoanaliza. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Psihoanaliza",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Psihoanaliza",
      description:
        "Ako razmatraš psihoanalizu, pitaj terapeuta kako istražujete ponavljajuća iskustva, odnose i ono što ti spontano pada na pamet. Proveri učestalost susreta i kako se dogovara tok rada. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Psihoanaliza | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Psihoanaliza. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "terapija-usmerena-na-osobu": {
    label: "Terapija usmerena na osobu",
    title: "Online terapeuti: Terapija usmerena na osobu",
    description:
      "Kod terapeuta koji navodi terapiju usmerenu na osobu možeš da pitaš kako tvoje iskustvo i potrebe određuju tok razgovora. Opiši šta želiš od terapije i proveri koliko ti takav način rada odgovara. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Terapija usmerena na osobu | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Terapija usmerena na osobu. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Terapija usmerena na osobu",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Terapija usmerena na osobu",
      description:
        "Kod terapeuta koji navodi terapiju usmerenu na osobu možeš da pitaš kako tvoje iskustvo i potrebe određuju tok razgovora. Opiši šta želiš od terapije i proveri koliko ti takav način rada odgovara. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Terapija usmerena na osobu | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Terapija usmerena na osobu. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  erp: {
    label: "Izlaganje i prevencija odgovora (ERP)",
    title: "Online terapeuti: Izlaganje i prevencija odgovora (ERP)",
    description:
      "Kod terapeuta koji navodi ERP pitaj kako se dogovaraju koraci izlaganja i rad sa uobičajenim ritualima. Pre zakazivanja proveri iskustvo sa tvojim teškoćama; primer pitanja na ovoj stranici nije uputstvo za samostalnu vežbu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Izlaganje i prevencija odgovora (ERP) | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Izlaganje i prevencija odgovora (ERP). Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Izlaganje i prevencija odgovora (ERP)",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Izlaganje i prevencija odgovora (ERP)",
      description:
        "Kod terapeuta koji navodi ERP pitaj kako se dogovaraju koraci izlaganja i rad sa uobičajenim ritualima. Pre zakazivanja proveri iskustvo sa tvojim teškoćama; primer pitanja na ovoj stranici nije uputstvo za samostalnu vežbu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Izlaganje i prevencija odgovora (ERP) | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Izlaganje i prevencija odgovora (ERP). Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "egzistencijalna-terapija": {
    label: "Egzistencijalna terapija",
    title: "Online terapeuti: Egzistencijalna terapija",
    description:
      "Ako želiš da razgovaraš o smislu, izborima ili odgovornosti, pitaj terapeuta kako tim temama pristupa u egzistencijalnoj terapiji. Možeš početi od konkretne odluke ili neizvesnosti koju trenutno doživljavaš. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Egzistencijalna terapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Egzistencijalna terapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Egzistencijalna terapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Egzistencijalna terapija",
      description:
        "Ako želiš da razgovaraš o smislu, izborima ili odgovornosti, pitaj terapeuta kako tim temama pristupa u egzistencijalnoj terapiji. Možeš početi od konkretne odluke ili neizvesnosti koju trenutno doživljavaš. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Egzistencijalna terapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Egzistencijalna terapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "konstruktivisticka-psihoterapija": {
    label: "Konstruktivistička psihoterapija",
    title: "Online terapeuti: Konstruktivistička psihoterapija",
    description:
      "Pitaj terapeuta kako istražujete značenja koja daješ događajima i odnosima. Konkretna situacija koju različiti ljudi vide drugačije može biti dobar početak pitanja o konstruktivističkom pristupu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Konstruktivistička psihoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Konstruktivistička psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Konstruktivistička psihoterapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Konstruktivistička psihoterapija",
      description:
        "Pitaj terapeuta kako istražujete značenja koja daješ događajima i odnosima. Konkretna situacija koju različiti ljudi vide drugačije može biti dobar početak pitanja o konstruktivističkom pristupu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Konstruktivistička psihoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Konstruktivistička psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  psihodrama: {
    label: "Psihodrama",
    title: "Online terapeuti: Psihodrama",
    description:
      "Pitaj terapeuta da li u radu koristi odigravanje situacija ili uloga i kako to izgleda online. Proveri da li je ponuđeni susret individualan i kako se dogovarate o vežbama. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Psihodrama | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Psihodrama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Psihodrama",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Psihodrama",
      description:
        "Pitaj terapeuta da li u radu koristi odigravanje situacija ili uloga i kako to izgleda online. Proveri da li je ponuđeni susret individualan i kako se dogovarate o vežbama. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Psihodrama | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Psihodrama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "telesna-psihoterapija": {
    label: "Telesna psihoterapija",
    title: "Online terapeuti: Telesna psihoterapija",
    description:
      "Ako razmatraš telesnu psihoterapiju, pitaj kako se tokom razgovora obraća pažnja na telesne senzacije i emocije. Proveri koje vežbe terapeut predlaže i kako ih prilagođava online susretu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Telesna psihoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Telesna psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Telesna psihoterapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Telesna psihoterapija",
      description:
        "Ako razmatraš telesnu psihoterapiju, pitaj kako se tokom razgovora obraća pažnja na telesne senzacije i emocije. Proveri koje vežbe terapeut predlaže i kako ih prilagođava online susretu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Telesna psihoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Telesna psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "emocionalno-fokusirana-terapija-parova": {
    label: "Emocionalno fokusirana terapija parova",
    title: "Online terapeuti: Emocionalno fokusirana terapija parova",
    description:
      "Ako želite da razgovarate o emocionalnim potrebama i ponavljajućim sukobima u vezi, pitajte terapeuta kako koristi ovaj pristup. Proverite da li nudi partnersku sesiju i kako se dogovara zajednički rad. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Emocionalno fokusirana terapija parova | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Emocionalno fokusirana terapija parova. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Emocionalno fokusirana terapija parova",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Emocionalno fokusirana terapija parova",
      description:
        "Ako želite da razgovarate o emocionalnim potrebama i ponavljajućim sukobima u vezi, pitajte terapeuta kako koristi ovaj pristup. Proverite da li nudi partnersku sesiju i kako se dogovara zajednički rad. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Emocionalno fokusirana terapija parova | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Emocionalno fokusirana terapija parova. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "grupna-analiza": {
    label: "Grupna analiza",
    title: "Online terapeuti: Grupna analiza",
    description:
      "Navođenje grupne analize na profilu opisuje pristup terapeuta, ali ne potvrđuje da je dostupna terapijska grupa. Pre zakazivanja proveri da li se nudi individualni razgovor ili grupni rad i pod kojim uslovima. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Grupna analiza | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Grupna analiza. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Grupna analiza",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Grupna analiza",
      description:
        "Navođenje grupne analize na profilu opisuje pristup terapeuta, ali ne potvrđuje da je dostupna terapijska grupa. Pre zakazivanja proveri da li se nudi individualni razgovor ili grupni rad i pod kojim uslovima. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Grupna analiza | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Grupna analiza. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "integrativna-art-psihoterapija": {
    label: "Integrativna art psihoterapija",
    title: "Online terapeuti: Integrativna art psihoterapija",
    description:
      "Pitaj terapeuta kako u razgovor uključuje crtež, boju ili druge oblike izražavanja. Proveri kako se takav rad odvija online i da li je potrebno nešto da pripremiš za susret. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Integrativna art psihoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Integrativna art psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Integrativna art psihoterapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Integrativna art psihoterapija",
      description:
        "Pitaj terapeuta kako u razgovor uključuje crtež, boju ili druge oblike izražavanja. Proveri kako se takav rad odvija online i da li je potrebno nešto da pripremiš za susret. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Integrativna art psihoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Integrativna art psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "integrativna-decja-psihoterapija": {
    label: "Integrativna dečja psihoterapija",
    title: "Online terapeuti: Integrativna dečja psihoterapija",
    description:
      "Pitaj terapeuta kako prilagođava razgovor ili igru detetovom uzrastu i potrebama. Proveri sa kojim uzrastom radi, kako uključuje roditelja i da li je online format odgovarajući za dete. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Integrativna dečja psihoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Integrativna dečja psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Integrativna dečja psihoterapija",
    requireDeclaredInventory: true,
    maxMinimumClientAge: 12,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Integrativna dečja psihoterapija",
      description:
        "Pitaj terapeuta kako prilagođava razgovor ili igru detetovom uzrastu i potrebama. Proveri sa kojim uzrastom radi, kako uključuje roditelja i da li je online format odgovarajući za dete. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Integrativna dečja psihoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Integrativna dečja psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "eklekticka-psihoterapija": {
    label: "Eklektička psihoterapija",
    title: "Online terapeuti: Eklektička psihoterapija",
    description:
      "Naziv eklektička psihoterapija nije dovoljan da znaš kako će susret izgledati. Pitaj terapeuta koje pristupe koristi, zašto ih bira i kako biste odlučivali šta ti odgovara. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Eklektička psihoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Eklektička psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Eklektička psihoterapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Eklektička psihoterapija",
      description:
        "Naziv eklektička psihoterapija nije dovoljan da znaš kako će susret izgledati. Pitaj terapeuta koje pristupe koristi, zašto ih bira i kako biste odlučivali šta ti odgovara. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Eklektička psihoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Eklektička psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "jungovska-psihoterapija": {
    label: "Jungovska analitička psihoterapija",
    title: "Online terapeuti: Jungovska analitička psihoterapija",
    description:
      "Ako razmatraš Jungovsku psihoterapiju, pitaj kako terapeut razgovara o ličnim značenjima, slikama ili snovima. Proveri kako taj način rada povezuje sa onim što te trenutno muči. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Jungovska analitička psihoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Jungovska analitička psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Jungovska analitička psihoterapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Jungovska analitička psihoterapija",
      description:
        "Ako razmatraš Jungovsku psihoterapiju, pitaj kako terapeut razgovara o ličnim značenjima, slikama ili snovima. Proveri kako taj način rada povezuje sa onim što te trenutno muči. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Jungovska analitička psihoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Jungovska analitička psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  logoterapija: {
    label: "Logoterapija",
    title: "Online terapeuti: Logoterapija",
    description:
      "Ako su ti važna pitanja smisla i ličnih vrednosti, pitaj terapeuta kako ih istražujete kroz logoterapiju. Možeš početi od onoga što ti trenutno daje pravac ili što ti nedostaje. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Logoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Logoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Logoterapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Logoterapija",
      description:
        "Ako su ti važna pitanja smisla i ličnih vrednosti, pitaj terapeuta kako ih istražujete kroz logoterapiju. Možeš početi od onoga što ti trenutno daje pravac ili što ti nedostaje. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Logoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Logoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "narativna-terapija": {
    label: "Narativna terapija",
    title: "Online terapeuti: Narativna terapija",
    description:
      "Pitaj terapeuta kako istražujete priče koje gradiš o sebi i svom problemu. Možeš navesti iskustvo koje se ne uklapa u način na koji obično opisuješ sebe. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Narativna terapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Narativna terapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Narativna terapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Narativna terapija",
      description:
        "Pitaj terapeuta kako istražujete priče koje gradiš o sebi i svom problemu. Možeš navesti iskustvo koje se ne uklapa u način na koji obično opisuješ sebe. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Narativna terapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Narativna terapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "terapija-usmerena-na-resenje": {
    label: "Terapija usmerena na rešenje",
    title: "Online terapeuti: Terapija usmerena na rešenje",
    description:
      "Pitaj terapeuta kako dogovarate konkretne ciljeve i istražujete trenutke kada je teškoća bila manja. Proveri kako biste pratili promene koje su tebi važne. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Terapija usmerena na rešenje | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Terapija usmerena na rešenje. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Terapija usmerena na rešenje",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Terapija usmerena na rešenje",
      description:
        "Pitaj terapeuta kako dogovarate konkretne ciljeve i istražujete trenutke kada je teškoća bila manja. Proveri kako biste pratili promene koje su tebi važne. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Terapija usmerena na rešenje | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Terapija usmerena na rešenje. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  simboldrama: {
    label: "Simboldrama",
    title: "Online terapeuti: Simboldrama",
    description:
      "Ako razmatraš simboldramu, pitaj terapeuta da objasni ulogu zamišljenih slika u susretu. Proveri kako se vežbe dogovaraju i kako možeš da kažeš da ti nešto ne odgovara. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Simboldrama | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Simboldrama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Simboldrama",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Simboldrama",
      description:
        "Ako razmatraš simboldramu, pitaj terapeuta da objasni ulogu zamišljenih slika u susretu. Proveri kako se vežbe dogovaraju i kako možeš da kažeš da ti nešto ne odgovara. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Simboldrama | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Simboldrama. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "integralna-kauzalna-psihoterapija": {
    label: "Integralna kauzalna psihoterapija",
    title: "Online terapeuti: Integralna kauzalna psihoterapija",
    description:
      "Pre izbora terapeuta koji navodi integralnu kauzalnu psihoterapiju, traži konkretan opis načina rada i obuke. Pitaj šta bi se dešavalo na susretu i kako bi to bilo povezano sa tvojim potrebama. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle:
      "Online terapeuti: Integralna kauzalna psihoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Integralna kauzalna psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Integralna kauzalna psihoterapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Integralna kauzalna psihoterapija",
      description:
        "Pre izbora terapeuta koji navodi integralnu kauzalnu psihoterapiju, traži konkretan opis načina rada i obuke. Pitaj šta bi se dešavalo na susretu i kako bi to bilo povezano sa tvojim potrebama. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle:
        "Online terapeuti: Integralna kauzalna psihoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Integralna kauzalna psihoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  hipnoterapija: {
    label: "Hipnoterapija",
    title: "Online terapeuti: Hipnoterapija",
    description:
      "Pitaj terapeuta kako koristi hipnozu, kakvu obuku ima i kako se dogovarate o vežbama. Proveri šta možeš da očekuješ tokom online susreta i kako možeš da prekineš vežbu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: Hipnoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: Hipnoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "Hipnoterapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: Hipnoterapija",
      description:
        "Pitaj terapeuta kako koristi hipnozu, kakvu obuku ima i kako se dogovarate o vežbama. Proveri šta možeš da očekuješ tokom online susreta i kako možeš da prekineš vežbu. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: Hipnoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: Hipnoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  "dcr-regresoterapija": {
    label: "DCR regresoterapija",
    title: "Online terapeuti: DCR regresoterapija",
    description:
      "Pre izbora terapeuta koji navodi DCR regresoterapiju, traži objašnjenje postupka, obuke i ograničenja tog rada. Navođenje pristupa u profilu nije potvrda njegove delotvornosti. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
    seoTitle: "Online terapeuti: DCR regresoterapija | Rešimo Probleme",
    seoDescription:
      "Pregledaj terapeute koji na profilu navode: DCR regresoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
    iconId: "brain",
    searchTerm: "DCR regresoterapija",
    requireDeclaredInventory: true,
    indexable: false,
    guideLink: {
      href: "/blog/kako-odabrati-online-psihologa-vodic-sa-pitanjima-i-znakovima-za-brigu",
      label: "Pitanja za izbor terapeuta",
    },
    directoryContent: {
      title: "Online terapeuti: DCR regresoterapija",
      description:
        "Pre izbora terapeuta koji navodi DCR regresoterapiju, traži objašnjenje postupka, obuke i ograničenja tog rada. Navođenje pristupa u profilu nije potvrda njegove delotvornosti. Na profilima možeš da uporediš opis rada, cenu i dostupne termine.",
      seoTitle: "Online terapeuti: DCR regresoterapija | Rešimo Probleme",
      seoDescription:
        "Pregledaj terapeute koji na profilu navode: DCR regresoterapija. Uporedi opis rada, cene i dostupne termine pre zakazivanja razgovora.",
      faqItems: [],
    },
  },
  ...Object.fromEntries(
    Object.entries(THERAPY_SPECIALTIES_CONTENT).map(([slug, specialty]) => [
      slug,
      {
        ...specialty,
        iconId:
          SPECIALTY_ICON_IDS[slug as keyof typeof THERAPY_SPECIALTIES_CONTENT],
      },
    ]),
  ),
};

export type SpecialtySlug = keyof typeof THERAPY_SPECIALTIES;

const ADDITIONAL_DIRECTORY_CONTENT: Partial<
  Record<SpecialtySlug, SpecialtyDirectoryContent>
> = {
  "bipolarni-poremecaj": {
    title: "Online terapeuti za podršku kod bipolarnog poremećaja",
    description:
      "Ako živiš sa bipolarnim poremećajem ili podržavaš blisku osobu, razgovor sa terapeutom može biti prostor za rutine, odnose i pitanja koja te opterećuju. Ova stranica nije zamena za psihijatrijsku procenu ili plan lekova. Pregledaj profile, pročitaj kako terapeut radi i zakaži online razgovor sa osobom koja ti odgovara.",
    seoTitle: "Podrška kod bipolarnog poremećaja online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o bipolarnom poremećaju. Uporedi profile, cene i termine, pa zakaži sesiju na Rešimo Probleme.",
    faqItems: [
      {
        question: "Da li terapija može biti podrška kod bipolarnog poremećaja?",
        answer:
          "Terapija može biti prostor za razgovor o svakodnevici, odnosima i rutinama uz postojeći plan lečenja. Za dijagnozu, promene lekova ili hitne simptome obrati se psihijatru ili hitnoj službi.",
      },
      {
        question: "Kako da izaberem terapeuta ako imam bipolarni poremećaj?",
        answer:
          "Pročitaj profil, pitaj terapeuta da li radi uz psihijatrijski plan lečenja i proveri da li ti njegov pristup odgovara. Na prvom razgovoru možeš objasniti šta ti je trenutno najvažnije.",
      },
      {
        question: "Da li mogu na online razgovor dok sam u stabilnom periodu?",
        answer:
          "Da. Razgovor može biti koristan i kada želiš da govoriš o rutinama, odnosima, stresu ili pripremi za promene koje te brinu.",
      },
      {
        question: "Kada je potreban hitan kontakt sa zdravstvenom službom?",
        answer:
          "Ako postoji neposredan rizik po tebe ili druge, jaka dezorganizacija, više dana bez sna ili misli o samopovređivanju, nemoj čekati online termin, već se odmah obrati lokalnoj hitnoj službi ili psihijatrijskoj pomoći.",
      },
    ],
  },
  "autizam-podrska": {
    title: "Online terapeuti za podršku osobama na autističnom spektru",
    description:
      "Ako želiš prostor za razgovor o senzornom preopterećenju, komunikaciji, odnosima ili prilagođavanju svakodnevici, ovde možeš da pregledaš profile online terapeuta. Terapija ne menja autistični identitet i ova stranica nije dijagnostička procena. Pročitaj profile, uporedi pristupe i zakaži razgovor koji ti odgovara.",
    seoTitle: "Podrška za autizam kod odraslih online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o autizmu kod odraslih, odnosima i svakodnevnim izazovima. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question:
          "Da li mi je potrebna dijagnoza da bih razgovarao/la sa terapeutom o autizmu?",
        answer:
          "Ne. Možeš razgovarati o iskustvima, sumnjama ili praktičnim teškoćama bez postavljanja dijagnoze na ovoj stranici.",
      },
      {
        question: "O čemu mogu da razgovaram na terapiji ako sam na spektru?",
        answer:
          "Ljudi često žele da govore o senzornom opterećenju, komunikaciji, maskiranju, odnosima, granicama ili stresu u svakodnevici. Ti određuješ šta ti je važno.",
      },
      {
        question: "Kako da proverim da li terapeut ima relevantan pristup?",
        answer:
          "Pročitaj opis rada i pitaj terapeuta kako pristupa neurodivergentnosti, komunikacionim potrebama i prilagođavanju online razgovora.",
      },
      {
        question: "Da li se online razgovor može prilagoditi mojim potrebama?",
        answer:
          "Pre zakazivanja možeš pitati za tempo, kameru, pauze i način komunikacije. Dogovor o uslovima razgovora je deo izbora terapeuta.",
      },
    ],
  },
  "pusenje-odvikavanje": {
    title: "Online terapeuti za podršku pri odvikavanju od pušenja",
    description:
      "Ako želiš da govoriš o navikama, okidačima, stresu ili ambivalenciji oko prestanka pušenja, pregledaj profile online terapeuta i izaberi osobu za razgovor. Psihoterapija nije zamena za medicinski savet o nikotinskoj terapiji, lekovima ili simptomima apstinencije. Uporedi pristupe, cenu i termine, pa zakaži sesiju.",
    seoTitle: "Podrška za odvikavanje od pušenja online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o odvikavanju od pušenja, navikama i okidačima. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question: "Može li mi terapeut pomoći ako želim da prestanem da pušim?",
        answer:
          "Razgovor može pomoći da razumeš navike, okidače i podršku koja ti je potrebna dok radiš na promeni. Ne obećava prestanak niti zamenjuje savet lekara.",
      },
      {
        question: "Da li treba da već imam datum prestanka pušenja?",
        answer:
          "Ne moraš. Možeš početi razgovorom o tome šta želiš da promeniš, šta si ranije pokušao/la i šta ti otežava odluku.",
      },
      {
        question: "Da li terapija zamenjuje lekove ili nikotinsku terapiju?",
        answer:
          "Ne. Za lekove, nikotinsku terapiju i izražene telesne simptome razgovaraj sa lekarom ili farmaceutom.",
      },
      {
        question: "Šta da proverim na profilu terapeuta?",
        answer:
          "Pogledaj kako terapeut opisuje rad sa navikama, zavisnostima, stresom ili promenom ponašanja, zatim cenu i slobodne termine.",
      },
    ],
  },
  "shema-terapija": {
    title: "Online terapeuti za shema terapiju",
    description:
      "Ako tražiš shema terapiju, na ovoj stranici možeš da pregledaš profile terapeuta i proveriš da li navode taj pristup, obuku ili srodno iskustvo. Shema terapija je pretraživan pojam, ali samo profil terapeuta potvrđuje njegovu konkretnu edukaciju. Uporedi profile, cenu i termine, pa zakaži online razgovor.",
    seoTitle: "Shema terapija online | Terapeuti | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za shema terapiju. Proveri pristup, obuku, cenu i slobodne termine, pa zakaži razgovor.",
    faqItems: [
      {
        question: "Kako da pronađem terapeuta za shema terapiju?",
        answer:
          "Počni od profila koji navode shema terapiju ili relevantnu obuku, a zatim na prvom razgovoru pitaj kako terapeut koristi taj pristup.",
      },
      {
        question: "Da li svaki terapeut na stranici radi shema terapiju?",
        answer:
          "Ne. Profili sa relevantno navedenim pristupom prikazani su ranije, ali pre zakazivanja uvek proveri konkretan opis i postavi pitanje terapeutu.",
      },
      {
        question: "Da li je shema terapija isto što i KBT?",
        answer:
          "Nisu isto, iako mogu imati dodirne tačke. Za tačno objašnjenje pristupa koji terapeut nudi, pročitaj njegov profil i pitaj ga direktno.",
      },
      {
        question: "Šta da pitam na prvom razgovoru?",
        answer:
          "Možeš pitati o edukaciji, načinu rada, ciljevima razgovora, učestalosti termina i tome da li je pristup dobar izbor za tvoju temu.",
      },
    ],
  },
  "act-terapija": {
    title: "Online terapeuti za ACT terapiju prihvatanja i posvećenosti",
    description:
      "ACT terapija, odnosno terapija prihvatanja i posvećenosti, ima vidljivu komercijalnu pretragu. Ovde možeš da pregledaš profile terapeuta i proveriš da li navode ACT ili srodan pristup. Ne pretpostavljaj obuku samo na osnovu naziva stranice, već pročitaj profil, uporedi cenu i termine, pa zakaži razgovor.",
    seoTitle: "ACT terapija online | Terapeuti | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za ACT terapiju. Proveri pristup, profil, cenu i slobodne termine, pa zakaži razgovor.",
    faqItems: [
      {
        question: "Kako da pronađem ACT terapeuta online?",
        answer:
          "Pregledaj profile koji navode ACT, terapiju prihvatanja i posvećenosti ili srodan pristup, pa pitaj terapeuta o njegovom konkretnom načinu rada.",
      },
      {
        question: "Da li svaki prikazani terapeut radi ACT terapiju?",
        answer:
          "Ne. Relevantni profili su prikazani ranije, a zatim sledi širi izbor javno dostupnih terapeuta. Proveri profil pre zakazivanja.",
      },
      {
        question: "O čemu mogu da pitam terapeuta pre zakazivanja?",
        answer:
          "Pitaj o ACT obuci, ciljevima rada, načinu na koji vodi online razgovor i tome da li njegov pristup odgovara temi zbog koje dolaziš.",
      },
      {
        question: "Da li je ACT terapija medicinski tretman?",
        answer:
          "Ne. To je psihoterapijski pristup, a za medicinska pitanja, dijagnozu ili lekove treba da se obratiš odgovarajućem zdravstvenom stručnjaku.",
      },
    ],
  },
  "fertilitet-infertilitet": {
    title: "Online terapeuti za psihološku podršku tokom infertiliteta",
    description:
      "Proces infertiliteta, VTO ili čekanja rezultata može doneti tugu, pritisak, usamljenost i napetost u odnosu. Ova stranica je za razgovor o emocionalnoj strani tog iskustva, ne za medicinsko tumačenje nalaza ili savete o lečenju. Pregledaj profile online terapeuta, uporedi pristupe i zakaži razgovor.",
    seoTitle: "Psihološka podrška za infertilitet online | Rešimo Probleme",
    seoDescription:
      "Pronađi online terapeuta za razgovor o infertilitetu, VTO, stresu i odnosu. Uporedi profile, cene i slobodne termine.",
    faqItems: [
      {
        question:
          "O čemu mogu da razgovaram sa terapeutom tokom infertiliteta?",
        answer:
          "Možeš govoriti o neizvesnosti, tuzi, pritisku, odnosu sa partnerom, granicama prema okolini i odlukama koje ti emotivno teško padaju.",
      },
      {
        question: "Da li terapija daje medicinske savete o VTO ili nalazima?",
        answer:
          "Ne. Za medicinske odluke, nalaze i terapiju obrati se timu koji vodi tvoje lečenje, a razgovor sa terapeutom može biti podrška uz taj proces.",
      },
      {
        question: "Mogu li partner i ja razgovarati o ovoj temi?",
        answer:
          "Možeš proveriti na profilu da li terapeut nudi rad sa parovima i da li njegov pristup odgovara vašoj situaciji.",
      },
      {
        question: "Kako da izaberem terapeuta za ovu temu?",
        answer:
          "Pročitaj profil i pitaj da li terapeut može da podrži razgovor o procesu infertiliteta bez obećanja medicinskog ishoda.",
      },
    ],
  },
  "samopovredjivanje-podrska": {
    title: "Online terapeuti za razgovor o samopovređivanju",
    description:
      "Ako se samopovređuješ ili te plaše porivi da to uradiš, zaslužuješ podršku bez osude. Ova stranica može pomoći da pronađeš terapeuta za razgovor, ali nije hitna služba. Pregledaj profile, pitaj kako terapeut radi sa intenzivnim emocijama i zakaži razgovor. Ako postoji neposredna opasnost, odmah se obrati lokalnoj hitnoj službi.",
    seoTitle: "Podrška kod samopovređivanja online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o samopovređivanju i intenzivnim emocijama. Uporedi profile, cene i termine, pa zakaži sesiju.",
    faqItems: [
      {
        question:
          "Da li mogu da razgovaram sa terapeutom ako se samopovređujem?",
        answer:
          "Da. Možeš otvoreno reći šta se dešava i pitati kako terapeut pristupa porivima, okidačima i bezbednosti u razgovoru.",
      },
      {
        question: "Da li je online termin dovoljan u akutnoj krizi?",
        answer:
          "Ne. Ako postoji neposredan rizik da se povrediš ili ne možeš da ostaneš bezbedan/na, odmah se obrati lokalnoj hitnoj službi ili nekome ko može biti uz tebe.",
      },
      {
        question: "Moram li da prestanem sa samopovređivanjem pre terapije?",
        answer:
          "Ne moraš da čekaš da bi potražio/la podršku. Važno je da na prvom razgovoru iskreno kažeš šta ti se dešava.",
      },
      {
        question: "Kako da izaberem terapeuta za ovaj razgovor?",
        answer:
          "Pročitaj profil i pitaj kako terapeut radi sa regulacijom emocija, planiranjem bezbednosti i saradnjom sa drugim stručnjacima kada je to potrebno.",
      },
    ],
  },
  "kulturoloski-sok": {
    title: "Online terapeuti za kulturološki šok i prilagođavanje",
    description:
      "Selidba, život u dijaspori ili povratak mogu doneti nostalgiju, usamljenost, zbunjenost i osećaj da nigde ne pripadaš potpuno. Ovde možeš da pregledaš online terapeute za razgovor o prilagođavanju novoj sredini. Uporedi profile, cenu i termine, pa zakaži razgovor na srpskom jeziku.",
    seoTitle: "Terapeut za kulturološki šok online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o kulturološkom šoku, selidbi i životu u dijaspori. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question: "Da li je kulturološki šok normalan posle selidbe?",
        answer:
          "Prilagođavanje novoj sredini može doneti različita i promenljiva osećanja. Razgovor može pomoći da ih bolje razumeš bez potrebe da ih odmah nazoveš dijagnozom.",
      },
      {
        question: "Mogu li da razgovaram na srpskom ako živim u inostranstvu?",
        answer:
          "Da. Na profilima proveri jezik, cenu i termine, pa izaberi osobu sa kojom želiš da razgovaraš online.",
      },
      {
        question: "O čemu ljudi najčešće govore na ovim razgovorima?",
        answer:
          "Česte teme su usamljenost, identitet, odnosi na daljinu, povratak, jezik, posao i osećaj pripadanja.",
      },
      {
        question: "Kako da izaberem terapeuta iz druge vremenske zone?",
        answer:
          "Proveri slobodne termine na profilu i pitaj terapeuta da li raspored može da odgovara tvojoj vremenskoj zoni.",
      },
    ],
  },
  "visokofunkcionalna-anksioznost": {
    title: "Online terapeuti za unutrašnji pritisak i stalnu brigu",
    description:
      "Možda spolja deluješ organizovano, a iznutra te prate stalna briga, potreba za kontrolom ili iscrpljenost. Izraz visokofunkcionalna anksioznost nije dijagnoza, ali može opisati iskustvo o kome želiš da razgovaraš. Pregledaj profile online terapeuta, uporedi pristupe i zakaži termin koji ti odgovara.",
    seoTitle:
      "Terapeut za visokofunkcionalnu anksioznost online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o stalnoj brizi, unutrašnjem pritisku i perfekcionizmu. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question: "Šta znači visokofunkcionalna anksioznost?",
        answer:
          "To je neformalni izraz koji ljudi koriste kada funkcionišu uprkos stalnoj brizi, pritisku ili napetosti. Sam izraz nije dijagnoza.",
      },
      {
        question: "Da li moram da imam dijagnozu da bih zakazao/la razgovor?",
        answer:
          "Ne. Možeš krenuti od onoga što ti je teško, bez potrebe da unapred znaš kako se to stručno zove.",
      },
      {
        question: "O čemu mogu da govorim sa terapeutom?",
        answer:
          "Možeš govoriti o perfekcionizmu, granicama, osećaju krivice, potrebi za kontrolom, odnosima i tempu svakodnevice.",
      },
      {
        question: "Kako da izaberem terapeuta?",
        answer:
          "Pročitaj profil, opis pristupa, cenu i slobodne termine, pa na prvom razgovoru proveri da li ti način rada odgovara.",
      },
    ],
  },
  "narcisoidno-zlostavljanje": {
    title: "Online terapeuti za razgovor o narcisoidnom zlostavljanju",
    description:
      "Ako prepoznaješ kontrolu, ponižavanje, gaslighting ili osećaj da gubiš poverenje u sebe, razgovor sa terapeutom može biti prostor da sagledaš svoju situaciju. Izraz narcisoidno zlostavljanje ne postavlja dijagnozu drugoj osobi. Pregledaj profile, uporedi pristupe i zakaži online razgovor. Ako si u neposrednoj opasnosti, prvo se obrati lokalnoj hitnoj službi ili osobi od poverenja.",
    seoTitle: "Podrška kod narcisoidnog zlostavljanja online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o gaslightingu, kontroli i granicama. Uporedi profile, cene i termine, pa zakaži sesiju.",
    faqItems: [
      {
        question:
          "Mogu li da razgovaram sa terapeutom ako mislim da doživljavam narcisoidno zlostavljanje?",
        answer:
          "Da. Možeš opisati konkretne situacije i kako utiču na tebe, bez potrebe da ti ili terapeut postavljate dijagnozu drugoj osobi.",
      },
      {
        question: "Da li je ovo stranica za hitnu pomoć?",
        answer:
          "Ne. Ako postoji neposredna opasnost, nasilje ili strah za bezbednost, odmah se obrati lokalnoj hitnoj službi, policiji ili osobi od poverenja.",
      },
      {
        question: "Šta da proverim pre zakazivanja?",
        answer:
          "Pročitaj profil i pitaj kako terapeut pristupa razgovoru o kontroli, granicama, traumatskim iskustvima i bezbednosti.",
      },
      {
        question:
          "Da li terapija odlučuje umesto mene da li da ostanem u odnosu?",
        answer:
          "Ne. Terapeut može pomoći da jasnije sagledaš iskustvo i opcije, ali tvoje odluke ostaju tvoje.",
      },
    ],
  },
  "gubitak-ljubimca": {
    title: "Online terapeuti za tugu nakon gubitka kućnog ljubimca",
    description:
      "Gubitak psa, mačke ili drugog ljubimca može doneti snažnu tugu, krivicu, prazninu ili osećaj da drugi ne razumeju kroz šta prolaziš. Ako želiš da o tome razgovaraš, pregledaj profile online terapeuta, uporedi pristupe i zakaži termin koji ti odgovara.",
    seoTitle: "Podrška posle gubitka ljubimca online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o tugovanju posle gubitka kućnog ljubimca. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question: "Da li je normalno da me gubitak ljubimca mnogo pogodi?",
        answer:
          "Tuga posle gubitka ljubimca može biti vrlo snažna i ne mora da izgleda isto kod svih. Razgovor može dati prostor iskustvu koje ti je važno.",
      },
      {
        question: "O čemu mogu da govorim sa terapeutom?",
        answer:
          "Možeš govoriti o tugovanju, krivici, uspomenama, odluci o eutanaziji, promeni rutine ili tome kako gubitak utiče na porodicu.",
      },
      {
        question: "Da li moram da čekam da tuga prođe?",
        answer:
          "Ne. Možeš zakazati razgovor kada osetiš da bi ti podrška ili prostor za razgovor prijali.",
      },
      {
        question: "Kako da izaberem terapeuta?",
        answer:
          "Pročitaj profil, cenu i termine, pa izaberi osobu sa kojom možeš zamisliti otvoren razgovor o svom iskustvu.",
      },
    ],
  },
  "finansijski-stres": {
    title: "Online terapeuti za razgovor o finansijskom stresu",
    description:
      "Dugovi, neizvesni prihodi, troškovi ili sukobi oko novca mogu doneti stid, napetost i osećaj da nema izlaza. Terapeut ne daje finansijske savete niti rešava dugove, ali razgovor može pomoći da sagledaš kako pritisak utiče na tebe. Pregledaj profile, uporedi pristupe i zakaži online termin.",
    seoTitle: "Terapeut za finansijski stres online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o finansijskom stresu, pritisku i odnosima. Uporedi profile, cene i termine.",
    faqItems: [
      {
        question: "Može li terapeut da mi pomogne sa finansijskim stresom?",
        answer:
          "Razgovor može pomoći da razumeš kako finansijski pritisak utiče na san, odnose, odluke i osećanja. Za konkretne finansijske odluke potreban je odgovarajući finansijski ili pravni savet.",
      },
      {
        question: "Da li moram da pričam o iznosu duga ili prihoda?",
        answer:
          "Ne moraš deliti detalje koje ne želiš. Možeš početi od osećaja pritiska i situacija koje ti trenutno najviše otežavaju svakodnevicu.",
      },
      {
        question: "Mogu li partner i ja razgovarati o novcu?",
        answer:
          "Možeš proveriti da li terapeut nudi rad sa parovima ako želiš razgovor o tome kako finansije utiču na vaš odnos.",
      },
      {
        question: "Kako da izaberem terapeuta?",
        answer:
          "Pogledaj profil, pristup radu, cenu i dostupne termine, pa na prvom razgovoru proveri da li ti taj način rada odgovara.",
      },
    ],
  },
  "imposter-sindrom": {
    title: "Online terapeuti za imposter sindrom i samosumnju",
    description:
      "Ako često umanjuješ sopstvene rezultate, plašiš se da će drugi otkriti da nisi dovoljno dobar/dobra ili ti uspeh ne donosi olakšanje, možda želiš da o tome razgovaraš. Imposter sindrom je opis iskustva, ne dijagnoza. Pregledaj profile online terapeuta, uporedi pristupe i zakaži termin.",
    seoTitle: "Terapeut za imposter sindrom online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o imposter sindromu, samosumnji i perfekcionizmu. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question: "Šta je imposter sindrom?",
        answer:
          "To je neformalni izraz za doživljaj samosumnje i osećaj da uspeh nije zaslužen uprkos dokazima o sposobnosti. Nije medicinska dijagnoza.",
      },
      {
        question:
          "Da li mogu da razgovaram o ovome i ako mi ide dobro na poslu?",
        answer:
          "Da. Neki ljudi traže podršku baš kada spolja deluje da funkcionišu dobro, a iznutra ih prate pritisak, strah od greške ili iscrpljenost.",
      },
      {
        question: "O čemu mogu da govorim sa terapeutom?",
        answer:
          "Možeš govoriti o poređenju sa drugima, perfekcionizmu, strahu od procene, karijeri, granicama i načinu na koji doživljavaš uspeh.",
      },
      {
        question: "Kako da izaberem terapeuta?",
        answer:
          "Pročitaj profile i izaberi osobu čiji ti opis rada deluje relevantno, zatim na prvom razgovoru proveri da li ti pristup odgovara.",
      },
    ],
  },
  usamljenost: {
    title: "Online terapeuti za razgovor o usamljenosti i izolaciji",
    description:
      "Usamljenost može postojati i kada si okružen/a ljudima. Ako ti nedostaju bliskost, pripadanje ili siguran prostor za razgovor, ovde možeš da pregledaš profile online terapeuta. Uporedi pristupe, cenu i termine, pa zakaži razgovor sa osobom koja ti odgovara.",
    seoTitle: "Terapeut za usamljenost i izolaciju online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o usamljenosti, izolaciji i odnosima. Uporedi profile, cene i slobodne termine.",
    faqItems: [
      {
        question: "Da li je usamljenost razlog za razgovor sa terapeutom?",
        answer:
          "Da. Možeš razgovarati o usamljenosti, izolaciji, odnosima ili osećaju da ne pripadaš, bez potrebe da to unapred nazoveš dijagnozom.",
      },
      {
        question: "Koja je razlika između samoće i usamljenosti?",
        answer:
          "Samoća je vreme provedeno bez drugih, dok usamljenost opisuje doživljaj nedostatka povezanosti. Tvoja iskustva mogu biti složenija od te kratke razlike.",
      },
      {
        question: "O čemu mogu da govorim na prvom razgovoru?",
        answer:
          "Možeš početi od trenutaka kada je osećaj najjači, odnosa koji ti nedostaju ili promena koje su prethodile izolaciji.",
      },
      {
        question: "Kako da izaberem terapeuta online?",
        answer:
          "Pogledaj profil, pristup, cenu i termine. Ako imaš pitanje o načinu rada, postavi ga pre ili na prvom razgovoru.",
      },
    ],
  },
  "problemi-vezivanja": {
    title: "Online terapeuti za razgovor o stilovima vezivanja",
    description:
      "Pregledaj profile online terapeuta, uporedi pristupe, cenu i slobodne termine, pa zakaži razgovor o onome što ti se dešava u odnosima. Ne moraš unapred da znaš koja etiketa opisuje tvoje iskustvo.",
    seoTitle: "Terapeut za stilove vezivanja online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o anksioznom, izbegavajućem i drugim stilovima vezivanja. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question:
          "Da li mogu da razgovaram o anksioznom ili izbegavajućem stilu vezivanja?",
        answer:
          "Da. Možeš opisati konkretne odnose, reakcije i potrebe koje želiš bolje da razumeš.",
      },
      {
        question: "Da li terapeut određuje moj stil vezivanja?",
        answer:
          "Ne mora da ti dodeli etiketu. Razgovor može biti usmeren na iskustva i obrasce koji su ti važni, a ne na svrstavanje u kategoriju.",
      },
      {
        question: "Kako da izaberem terapeuta za odnose?",
        answer:
          "Pročitaj profil, opis rada sa odnosima i granicama, pa na prvom razgovoru proveri da li ti način rada odgovara.",
      },
      {
        question: "Kako da zakažem razgovor?",
        answer:
          "Na profilu izaberi slobodan termin koji ti odgovara. Pre zakazivanja vidiš cenu i osnovne informacije o načinu rada terapeuta.",
      },
    ],
  },
  mobing: {
    title: "Online terapeuti za razgovor o mobingu na poslu",
    description:
      "Ako doživljavaš ponižavanje, izolaciju, pretnje ili stalan pritisak na poslu, razgovor sa terapeutom može biti mesto da sagledaš kako to utiče na tebe. Terapija nije pravni savet niti zamena za zaštitu na radu. Pregledaj profile online terapeuta, uporedi pristupe i zakaži razgovor.",
    seoTitle: "Terapeut za mobing na poslu online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o mobingu, pritisku na poslu i posledicama stresa. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question: "Da li mogu da razgovaram sa terapeutom o mobingu?",
        answer:
          "Da. Možeš govoriti o konkretnim situacijama i tome kako utiču na tvoj san, raspoloženje, samopouzdanje i svakodnevicu.",
      },
      {
        question: "Da li terapeut daje pravni savet o mobingu?",
        answer:
          "Ne. Za radnopravna pitanja i formalne korake potreban je odgovarajući pravni ili sindikalni savet, dok razgovor može biti podrška za emocionalni deo iskustva.",
      },
      {
        question: "Šta ako se plašim da neko vidi da tražim podršku?",
        answer:
          "Pregledaj uslove privatnosti platforme i izaberi miran prostor za online razgovor. Ako postoji neposredna opasnost, obrati se lokalnim službama koje mogu reagovati.",
      },
      {
        question: "Kako da izaberem terapeuta?",
        answer:
          "Pročitaj profil, pristup radu sa stresom i granicama, cenu i termine, pa proveri da li se osećaš dovoljno sigurno da otvoreno razgovaraš.",
      },
    ],
  },
  kodependencija: {
    title: "Online terapeuti za razgovor o kodependenciji i granicama",
    description:
      "Ako se u odnosima često odričeš svojih potreba, osećaš odgovornost za tuđe izbore ili ti je teško da postaviš granice, možda želiš da o tome razgovaraš. Kodependencija je opis obrasca, ne dijagnoza. Pregledaj profile online terapeuta, uporedi pristupe i zakaži termin koji ti odgovara.",
    seoTitle: "Terapeut za kodependenciju online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o kodependenciji, granicama i odnosima. Uporedi profile, cene i slobodne termine.",
    faqItems: [
      {
        question: "Šta znači kodependencija?",
        answer:
          "To je izraz koji se koristi za neke obrasce preterane usmerenosti na tuđe potrebe, izbore ili raspoloženja u odnosima. Nije medicinska dijagnoza.",
      },
      {
        question: "O čemu mogu da govorim sa terapeutom?",
        answer:
          "Možeš govoriti o granicama, osećaju krivice, odgovornosti za druge, konfliktima i odnosima u kojima se gubiš.",
      },
      {
        question: "Da li terapija govori da treba da prekinem odnos?",
        answer:
          "Ne. Terapeut ne donosi odluku umesto tebe, već može pomoći da jasnije sagledaš iskustvo, potrebe i opcije.",
      },
      {
        question: "Kako da izaberem terapeuta?",
        answer:
          "Pogledaj profil, opis rada sa odnosima i granicama, cenu i termine, pa na prvom razgovoru proveri da li ti pristup odgovara.",
      },
    ],
  },
  "telesna-slika": {
    title: "Online terapeuti za razgovor o telesnoj slici",
    description:
      "Odnos prema telu može biti opterećen poređenjem, stidom, promenama izgleda ili osećajem da nikad nisi dovoljno dobar/dobra. Ako želiš da o tome razgovaraš, pregledaj profile online terapeuta, uporedi pristupe i zakaži termin. Za ozbiljne promene u ishrani ili fizičkom zdravlju potraži i odgovarajuću medicinsku podršku.",
    seoTitle: "Terapeut za telesnu sliku online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o telesnoj slici, stidu i odnosu prema telu. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question: "Da li je telesna slika razlog za razgovor sa terapeutom?",
        answer:
          "Da. Možeš razgovarati o poređenju, stidu, promenama tela, komentarima drugih i načinu na koji to utiče na tvoj život.",
      },
      {
        question: "Da li terapeut daje plan ishrane?",
        answer:
          "Ne nužno. Za plan ishrane i medicinske posledice promena u telesnoj težini ili ishrani obrati se odgovarajućem zdravstvenom stručnjaku.",
      },
      {
        question: "Kako da izaberem terapeuta za ovu temu?",
        answer:
          "Pročitaj profil i pitaj kako terapeut pristupa telesnoj slici, samopoštovanju i osetljivim temama oko hrane ili izgleda.",
      },
      {
        question: "Mogu li prvo da razgovaram samo o osećaju stida?",
        answer:
          "Da. Ne moraš unapred znati ceo problem niti koristiti stručne izraze da bi počeo/la razgovor.",
      },
    ],
  },
  "zivotne-promene": {
    title: "Online terapeuti za životne promene i tranzicije",
    description:
      "Promena posla, selidba, roditeljstvo, raskid, povratak u zemlju ili druga velika tranzicija mogu poremetiti osećaj sigurnosti i identiteta. Ako želiš prostor da razmisliš o sledećem koraku, pregledaj profile online terapeuta, uporedi pristupe i zakaži razgovor.",
    seoTitle: "Terapeut za životne promene online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o životnim promenama, tranzicijama i sledećim koracima. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question: "Da li je normalno da me i dobra promena uznemiri?",
        answer:
          "Da. I željene promene mogu doneti neizvesnost, pritisak i gubitak poznate rutine.",
      },
      {
        question: "O čemu mogu da govorim na terapiji tokom promene?",
        answer:
          "Možeš govoriti o odlukama, strahu, identitetu, odnosima, praktičnim pritiscima i tome šta ti je potrebno da se osećaš stabilnije.",
      },
      {
        question: "Da li terapeut odlučuje šta treba da uradim?",
        answer:
          "Ne. Razgovor može pomoći da razjasniš prioritete i opcije, ali odluke ostaju tvoje.",
      },
      {
        question: "Kako da izaberem terapeuta online?",
        answer:
          "Uporedi profile, cenu, termine i opis pristupa, pa na prvom razgovoru proveri da li ti način rada odgovara.",
      },
    ],
  },
  "stres-negovatelja": {
    title: "Online terapeuti za stres negovatelja",
    description:
      "Briga o bolesnoj, starijoj ili zavisnoj osobi može doneti iscrpljenost, krivicu, bes i osećaj da nemaš prostora za sebe. Ako želiš da o tome razgovaraš, pregledaj profile online terapeuta, uporedi pristupe i zakaži termin. Ova stranica ne zamenjuje medicinsku ili socijalnu pomoć osobi o kojoj brineš.",
    seoTitle: "Terapeut za stres negovatelja online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o stresu negovatelja, iscrpljenosti i granicama. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question:
          "Da li je stres negovatelja razlog za razgovor sa terapeutom?",
        answer:
          "Da. Možeš govoriti o iscrpljenosti, krivici, sukobima u porodici, granicama i osećaju da stalno moraš biti dostupan/na.",
      },
      {
        question: "Da li to znači da nisam dovoljno dobar negovatelj?",
        answer:
          "Ne. Potreba za podrškom ne govori ništa loše o tvojoj brizi za drugu osobu, već prepoznaje da i ti imaš granice i potrebe.",
      },
      {
        question: "Da li terapeut organizuje medicinsku negu?",
        answer:
          "Ne. Za medicinsku negu, hitne simptome ili socijalne usluge potrebne su odgovarajuće lokalne službe.",
      },
      {
        question: "Kako da izaberem terapeuta?",
        answer:
          "Pročitaj profil, pristup radu sa stresom i granicama, cenu i termine, pa izaberi osobu sa kojom želiš da razgovaraš.",
      },
    ],
  },
  agorafobija: {
    title: "Online terapeuti za razgovor o agorafobiji i izbegavanju",
    description:
      "Ako te strah od izlaska, gužve, prevoza ili situacija iz kojih je teško otići ograničava, možda želiš da o tome razgovaraš. Ova stranica nije dijagnostička procena niti zamena za hitnu medicinsku pomoć. Pregledaj profile online terapeuta, uporedi pristupe i zakaži razgovor.",
    seoTitle: "Terapeut za agorafobiju online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o agorafobiji, strahu od izlaska i izbegavanju. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question: "Šta je agorafobija?",
        answer:
          "Agorafobija je stručni naziv za određeni obrazac straha i izbegavanja, ali samo kvalifikovani stručnjak može dati procenu. Na razgovor možeš doći i bez dijagnoze.",
      },
      {
        question:
          "Da li mogu da zakazem online razgovor ako mi je teško da izlazim?",
        answer:
          "Da. Online format može ti omogućiti da razgovaraš iz prostora u kojem se trenutno osećaš sigurnije.",
      },
      {
        question: "Da li terapeut obećava da će strah nestati?",
        answer:
          "Ne. Razgovor ne daje garancije ishoda, ali može biti prostor da opišeš šta doživljavaš i proveriš da li ti pristup terapeuta odgovara.",
      },
      {
        question: "Kada treba da potražim hitnu medicinsku pomoć?",
        answer:
          "Ako imaš nov ili jak telesni simptom, sumnju na medicinsko stanje ili neposredan rizik po bezbednost, odmah se obrati lokalnoj hitnoj službi.",
      },
    ],
  },
  "postporodjajna-depresija": {
    title: "Online terapeuti za podršku posle porođaja",
    description:
      "Posle porođaja možeš doživeti tugu, anksioznost, razdražljivost, krivicu ili osećaj da ti je teško da se snađeš. Ne moraš sam/a da odlučiš da li je to postporođajna depresija da bi potražio/la razgovor. Pregledaj profile online terapeuta, uporedi pristupe i zakaži termin. Ako postoji neposredna opasnost za tebe ili bebu, odmah se obrati lokalnoj hitnoj službi.",
    seoTitle: "Podrška posle porođaja online | Rešimo Probleme",
    seoDescription:
      "Pregledaj online terapeute za razgovor o postporođajnoj depresiji, anksioznosti i prilagođavanju. Uporedi profile i zakaži termin.",
    faqItems: [
      {
        question:
          "Da li moram da znam da imam postporođajnu depresiju pre razgovora?",
        answer:
          "Ne. Možeš početi od onoga što osećaš i kako ti je u svakodnevici, bez samostalnog postavljanja dijagnoze.",
      },
      {
        question: "O čemu mogu da govorim sa terapeutom posle porođaja?",
        answer:
          "Možeš govoriti o raspoloženju, anksioznosti, umoru, promeni identiteta, odnosu sa partnerom, pritisku i podršci koja ti nedostaje.",
      },
      {
        question: "Da li je online razgovor zamena za psihijatra ili lekara?",
        answer:
          "Ne. Za medicinsku procenu, lekove, simptome koji te ozbiljno brinu ili pitanja o bebi obrati se odgovarajućem zdravstvenom stručnjaku.",
      },
      {
        question: "Kada je potrebna hitna pomoć?",
        answer:
          "Ako postoji neposredan rizik da povrediš sebe ili bebu, osećaš da gubiš dodir sa stvarnošću ili ne možeš da ostaneš bezbedan/na, odmah se obrati lokalnoj hitnoj službi.",
      },
    ],
  },
};

const SPECIALTY_GUIDE_FALLBACKS: Partial<
  Record<SpecialtySlug, NonNullable<SpecialtyData["guideLink"]>>
> = {
  samopouzdanje: {
    href: "/blog/samopouzdanje-sta-je-i-kako-ga-steci",
    label: "Pročitaj vodič o samopouzdanju",
  },
  porodica: {
    href: "/blog/vodic-za-mentalno-zdravlje-i-odnose",
    label: "Pročitaj vodič o odnosima i mentalnom zdravlju",
  },
  "bipolarni-poremecaj": {
    href: "/blog/bipolarni-poremecaj",
    label: "Pročitaj vodič o bipolarnom poremećaju",
  },
  "autizam-podrska": {
    href: "/blog/autizam-kod-odraslih-znaci-dijagnoza-podrska",
    label: "Pročitaj vodič o autizmu kod odraslih i podršci",
  },
  "pusenje-odvikavanje": {
    href: "/blog/kako-da-znas-da-ti-je-potrebna-psihoterapija",
    label: "Pročitaj kako izgleda prvi korak ka psihoterapiji",
  },
  "fertilitet-infertilitet": {
    href: "/blog/postporodjajna-depresija",
    label: "Pročitaj o mentalnom zdravlju u perinatalnom periodu",
  },
  "samopovredjivanje-podrska": {
    href: "/blog/samopovredjivanje-razumevanje-prepoznavanje-i-pruzanje-pomoci",
    label: "Pročitaj vodič o samopovređivanju i podršci",
  },
  "kulturoloski-sok": {
    href: "/blog/dijaspora-i-mentalno-zdravlje",
    label: "Pročitaj vodič o dijaspori i mentalnom zdravlju",
  },
  "visokofunkcionalna-anksioznost": {
    href: "/blog/anksioznost-simptomi-lecenje",
    label: "Pročitaj vodič o anksioznosti",
  },
  "shema-terapija": {
    href: "/blog/kognitivno-bihevioralna-terapija-kako-promeniti-misli-i-zivot",
    label: "Pročitaj vodič o terapijskim pristupima",
  },
  "act-terapija": {
    href: "/blog/online-psihoterapija-vodic",
    label: "Pročitaj kako izgleda online psihoterapija",
  },
  "narcisoidno-zlostavljanje": {
    href: "/blog/gaslighting-sta-je-kako-ga-prepoznati-i-kako-se-zastititi",
    label: "Pročitaj vodič o gaslightingu i zaštiti",
  },
  "gubitak-ljubimca": {
    href: "/blog/kako-se-nositi-s-gubitkom-i-tugom",
    label: "Pročitaj vodič o gubitku i tugovanju",
  },
  "finansijski-stres": {
    href: "/blog/finansijska-anksioznost-kako-novac-utice-na-mentalno-zdravlje",
    label: "Pročitaj kako novac utiče na mentalno zdravlje",
  },
  "imposter-sindrom": {
    href: "/blog/imposter-sindrom-sta-je-i-kako-da-ga-prevazidjes",
    label: "Pročitaj vodič o imposter sindromu",
  },
  usamljenost: {
    href: "/blog/usamljenost-zasto-se-osecamo-usamljeno",
    label: "Pročitaj vodič o usamljenosti",
  },
  kodependencija: {
    href: "/blog/emocionalna-zavisnost-u-vezi",
    label: "Pročitaj vodič o emocionalnoj zavisnosti u vezi",
  },
  "problemi-vezivanja": {
    href: "/blog/odnosi-i-mentalno-zdravlje-kako-izgraditi-zdrave-veze",
    label: "Pročitaj vodič o zdravim odnosima",
  },
  "telesna-slika": {
    href: "/blog/poremecaji-prehrane-prepoznavanje-i-put-do-oporavka",
    label: "Pročitaj vodič o odnosu prema telu i ishrani",
  },
  mobing: {
    href: "/blog/mobing-na-poslu-kako-zastititi-mentalno-zdravlje",
    label: "Pročitaj vodič o mobingu na poslu",
  },
  "zivotne-promene": {
    href: "/blog/promena-karijere-strah-identitet-i-sledeci-korak",
    label: "Pročitaj kako promene utiču na identitet i svakodnevicu",
  },
  "stres-negovatelja": {
    href: "/blog/roditeljski-stres-kako-brinuti-o-sebi-dok-se-brinus-o-deci",
    label: "Pročitaj vodič o brizi o sebi dok brineš o drugima",
  },
  agorafobija: {
    href: "/blog/agorafobija",
    label: "Pročitaj vodič o agorafobiji",
  },
  "postporodjajna-depresija": {
    href: "/blog/postporodjajna-depresija",
    label: "Pročitaj vodič o postporođajnoj depresiji",
  },
};

export const getSpecialtyBySlug = (slug: string): SpecialtyData | null => {
  const specialty = THERAPY_SPECIALTIES[slug as SpecialtySlug];

  if (!specialty) return null;

  return {
    ...specialty,
    directoryContent:
      specialty.directoryContent ??
      ADDITIONAL_DIRECTORY_CONTENT[slug as SpecialtySlug],
    guideLink:
      specialty.guideLink ?? SPECIALTY_GUIDE_FALLBACKS[slug as SpecialtySlug],
  };
};

export const getAllSpecialtySlugs = (): SpecialtySlug[] => {
  return Object.keys(THERAPY_SPECIALTIES) as SpecialtySlug[];
};

// Default FAQ items for specialties that don't have specific ones
const DEFAULT_FAQ_ITEMS: FAQItem[] = [
  {
    question: "Kako mogu pronaći dobrog terapeuta?",
    answer:
      "Na našoj platformi možeš da pretražiš terapeute po specijalnostima, pristupu terapiji, dostupnosti i cenama. Svaki terapeut ima detaljan profil sa iskustvom i kvalifikacijama. Preporučujemo da pročitaš profile i izabereš terapeuta čiji pristup i specijalnost najbolje odgovaraju tvojim potrebama. Možeš lako promeniti terapeuta ako se osetiš da ti ne odgovara.",
  },
  {
    question: "Kako izabrati terapeuta?",
    answer:
      "Razmisli o svojim potrebama i problemima koje želiš da rešiš. Pregledaj profile terapeuta, njihove specijalnosti i pristup radu. Proveri dostupnost termina i cene. Prva sesija je prilika da vidiš da li se osećaš prijatno sa terapeutom. Ako ne, možeš lako promeniti terapeuta.",
  },
  {
    question: "Koje kvalifikacije treba tražiti kod terapeuta?",
    answer:
      "Svi naši terapeuti su licencirani stručnjaci sa potrebnim obrazovanjem i sertifikatima. U profilu svakog terapeuta možeš videti njihove kvalifikacije, godine iskustva i specijalnosti. Traži terapeute koji imaju relevantno iskustvo sa problemima sličnim tvojim i koji su sertifikovani za metode koje koriste.",
  },
  {
    question: "Kako izgleda prva sesija sa terapeutom?",
    answer:
      "Prva sesija je prilika za upoznavanje. Terapeut će te pitati o tebi, tvojim potrebama i očekivanjima od terapije. To je tvoja prilika da vidiš da li se osećaš prijatno i razumevan. Ne očekuj da odmah rešite sve probleme, prva sesija je više o uspostavljanju poverenja i razumevanju kako će terapija izgledati.",
  },
  {
    question: "Kako da izaberem pravog terapeuta za sebe?",
    answer:
      "Možeš da pregledaš profile terapeuta, njihove specijalnosti i stil rada. Ako nisi siguran/na, počni s terapeutom čiji ti se opis najviše sviđa. Posle svake sesije, slobodno možeš da probaš i druge terapeute dok ne nađeš osobu koja ti najviše odgovara.",
  },
  {
    question: "Šta ako mi se ne svidi prvi terapeut?",
    answer:
      "Nema frke! Možeš da promeniš terapeuta kad god poželiš. Cilj nam je da nađeš pravog terapeuta za sebe, i zato smo ti omogućili da isprobaš različite terapeute dok se ne osetiš skroz prijatno.",
  },
  {
    question: "Kako funkcioniše otkazivanje termina?",
    answer:
      "Ako otkažeš termin više od 24 sata ranije, možeš ga pomeriti na novi datum koji ti odgovara. Ako ne želiš novi termin, javi se našoj podršci i pomoći ćemo ti oko drugih opcija.",
  },
  {
    question: "Da li je online terapija isto dobra kao uživo?",
    answer:
      "Istraživanja pokazuju da jeste! Možeš da budeš u svom prostoru gde se osećaš opušteno, što često pomaže da se lakše otvoriš i pričaš o svemu.",
  },
  {
    question: "Kako da se spremim za prvu sesiju?",
    answer:
      "Treba ti samo miran kutak i dobra internet veza. Opusti se i imaj na umu da je prvi susret samo za upoznavanje. Cilj je da se osetiš prijatno i spremno za sledeći korak.",
  },
  {
    question: "Kako da platim sesije?",
    answer:
      "Plaćanje je online dok zakazuješ termin. Imamo razne načine plaćanja da ti bude što lakše i sigurnije.",
  },
  {
    question: "Da li je moj razgovor sa terapeutom privatan?",
    answer:
      "Apsolutno! Tvoja privatnost nam je najbitnija. Svi razgovori na platformi Rešimo Probleme su enkriptovani i dostupni samo tebi i tvom terapeutu.",
  },
  {
    question: "Šta ako imam tehničkih problema tokom sesije?",
    answer:
      "Ako zapne nešto s tehnikom, samo se ponovo uloguj na sesiju ili nam se javi za pomoć. Naš tim će se pobrinuti da sve radi kako treba.",
  },
  {
    question: "Mogu li da pričam o različitim problemima sa istim terapeutom?",
    answer:
      "Naravno! Terapeuti znaju kako da ti pomognu sa raznim izazovima, od anksioznosti i stresa do problema u odnosima. Samo reci terapeutu šta te muči, i prilagodiće pristup tvojim potrebama.",
  },
];

export const getFAQItemsForSpecialty = (slug?: string): readonly FAQItem[] => {
  if (!slug) return DEFAULT_FAQ_ITEMS;

  const specialty = getSpecialtyBySlug(slug);
  return specialty?.faqItems || DEFAULT_FAQ_ITEMS;
};
